// MARKER-XYZ123-BUILD-TEST
//
// PRIVATE_ENTRY_NOTES (MVP)
// Entries can be marked private (entry.private === true). Private means:
//   - Visible only to the entry's author. Other diary members (even
//     owners) don't see them in their timeline. (Client-side filter
//     in isEntryVisible.)
//   - Excluded from all share/export surfaces: CSV, Excel, expense
//     CSV, Share modal (single-entry shares are blocked).
//   - Owner sees them with a 🔒 indicator in the timeline header.
// What private DOES NOT mean (yet):
//   - No Firestore security rule yet — a non-author with direct DB
//     access could still read private entries. Add a security rule
//     for the production hardening pass.
//   - No PIN/passcode gate. The owner sees private entries with no
//     friction. This is a "don't accidentally leak" feature, not a
//     cryptographic privacy feature.
//   - No encryption. Field values are stored as plain Firestore data.
//
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  doc, getDoc, getDocs, setDoc, collection, query, where, orderBy,
  onSnapshot, addDoc, updateDoc, deleteDoc, serverTimestamp, deleteField,
  arrayUnion, arrayRemove, limit, writeBatch,
} from "firebase/firestore";
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, storage } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";

// xlsx (SheetJS) is loaded lazily via dynamic import inside the Excel
// export handler so users who only use CSV don't pay the bundle cost
// (~400KB minified). The library is large enough to be worth deferring.

const ORANGE = "#C83803";
const ORANGE_DARK = "#7a1e00";
const NAVY = "#0B1F4A";

const THEME = {
  deskBg: "#1a1208",
  spineBg: "#8B3A0A",
  spineTextColor: "rgba(255,220,180,0.7)",
  pageBg: "#fffef5",
  pageEdgeBg: "#e8e4dc",
  headerBorderColor: "rgba(200,56,3,0.2)",
  diaryNameColor: NAVY,
  diarySubColor: "#aaa",
  fontFamily: "-apple-system,sans-serif",
  ambientColor: "rgba(200,56,3,0.06)",
  spineTexture: "repeating-linear-gradient(0deg,transparent,transparent 8px,rgba(0,0,0,0.07) 8px,rgba(0,0,0,0.07) 9px)",
};

const ENTRIES_PER_PAGE = 10;

// Cover themes available in Cover tab (must stay in sync with Dashboard.js BOOK_THEMES)
const COVER_THEMES = [
  { id: "notebook",  label: "Notebook",  spine: "#8B3A0A", cover: "#fffef5", nameColor: NAVY },
  { id: "scroll",    label: "Scroll",    spine: "#3d1f0a", cover: "#d4a853", nameColor: "#3d1f0a" },
  { id: "scrapbook", label: "Scrapbook", spine: "#d4720a", cover: "#fdf3c8", nameColor: "#3d1f0a" },
  { id: "comic",     label: "Comic",     spine: NAVY,       cover: "#ffffff", nameColor: "#111" },
  { id: "telegram",  label: "Telegram",  spine: NAVY,       cover: "#e0d080", nameColor: "#1a0a00" },
  { id: "nfl",       label: "Game Day",  spine: NAVY,       cover: NAVY,      nameColor: ORANGE },
  { id: "ransom",    label: "Ransom",    spine: "#1a1a1a",  cover: "#f0ebe0", nameColor: "#111" },
];

// ── TAG CONFIG ────────────────────────────────────────────────────────────────

// Tag styling — neutral two-state palette across all UI surfaces. Selected
// chips get a darker fill with outline; unselected chips are greyed out.
// We don't color-code by category anymore — too much visual noise competing
// with the more important "is this selected" signal. Special cases:
//   - follow-up keeps its own color so it stays visually distinct from
//     content tags (it's a status flag, not a content category)
//   - All other tags share a uniform palette regardless of name
function getTagMeta(tag) {
  if (tag === "follow-up" || tag === "followup") {
    return {
      emoji: "🚩",
      color: "#7a1e00",
      bg: "#fff3e0",
      border: "#ffcc80",
      type: "records",
    };
  }
  // Neutral palette for everything else. Designed for the common pattern
  // used across the app:
  //   selected:   background = m.color (dark grey),  text = white
  //   unselected: background = m.bg    (very light), text = m.color
  // The contrast difference between active/inactive does the work of
  // showing what's selected. No category-specific hues.
  return {
    emoji: "",
    color: "#3a3a3a",   // active fill / inactive text
    bg: "#f0f0f0",       // inactive fill (very light grey)
    border: "#d0d0d0",   // inactive outline
    type: "memories",
  };
}

// True if a tag string is the follow-up tag, in either canonical form. We
// renamed "followup" → "follow-up" but defensively recognize both during the
// transition; once all entries are migrated this is just a safety net.
const isFollowupTag = (t) => t === "follow-up" || t === "followup";

// ── KEYWORD MAP ───────────────────────────────────────────────────────────────

const TAG_KEYWORDS = {
  tennis:   ["tennis","match","serve","volley","volleys","forehand","backhand","racket","racquet","court","usta","utr","lesson","drill","tournament","doubles","singles","tiebreak","deuce","ace","rally","rallies","point","points","overhead","slice","topspin","lob","dropshot","approach shot","passing shot","smash","footwork","groundstroke","baseline"],
  medical:  ["doctor","dr.","physician","clinic","hospital","appointment","checkup","prescription","medication","pharmacy","diagnosis","surgery","blood test","mri","dentist","dental","vaccine","copay"],
  food:     ["restaurant","dinner","lunch","breakfast","brunch","meal","eat","ate","food","dish","menu","reservation","chef","delicious","tasty","dessert","cafe","coffee","wine","cocktail","takeout"],
  auto:     ["car","truck","oil change","tire","tires","brake","engine","battery","body shop","mechanic","dealership","registration","inspection","fill up","license plate","car wash","smog"],
  property: ["tenant","lease","landlord","rent","rental","repair","maintenance","plumber","electrician","hvac","roof","inspection","mortgage","move in","move out","paint","contractor","renovation"],
  travel:   ["flight","flew","airport","hotel","airbnb","resort","trip","vacation","cruise","passport","visa","boarding","layover","beach","mountain","abroad"],
  family:   ["kids","children","son","daughter","wife","husband","mom","dad","mother","father","grandma","grandpa","brother","sister","birthday","anniversary","baby","wedding"],
  fitness:  ["workout","gym","exercise","run","running","jog","hike","bike","swim","lift","crossfit","yoga","pilates","cardio","marathon","personal trainer","5k"],
  school:   ["school","class","homework","exam","quiz","grade","teacher","professor","college","application","sat","act","graduation","campus","semester"],
  finance:  ["invest","stock","portfolio","401k","savings","bank","transfer","deposit","tax","budget","expense","income","salary","credit card","loan","mortgage"],
  sports:   ["game","practice","coach","team","league","season","championship","goal","touchdown","homerun","basketball","football","soccer","hockey","lacrosse","baseball"],
  "follow-up": ["follow up","follow-up","followup","call back","need to schedule","need to call","remind me","don't forget","referral","will get us","will send","waiting on","pending","come back in","next appointment","reach out","get back to"],
  // Money-flow tags — only suggested when the entry also has an amount
  // captured. Listed in AMOUNT_REQUIRED_TAGS below.
  income:   ["income","salary","paycheck","payday","earned","earnings","commission","bonus","royalty","royalties","rent collected","rent received","dividends","interest paid","wages"],
  rent:     ["rent","rental income","tenant paid","monthly rent","lease payment"],
  royalty:  ["royalty","royalties","licensing fee","licensing"],
  refund:   ["refund","refunded","money back","reimbursement","reimbursed","chargeback"],
  credit:   ["store credit","gift card","credit applied","account credit"],
  return:   ["returned","brought back","sent back","return for"],
  rebate:   ["rebate","cashback","cash back","manufacturer rebate"],
};

// Tags that should ONLY be auto-suggested when the entry has a structured
// amount captured. Otherwise "I returned the book to the library" would
// get tagged 'return', "I'll rent a movie tonight" would get 'rent', etc.
const AMOUNT_REQUIRED_TAGS = new Set([
  "income", "rent", "royalty", "refund", "credit", "return", "rebate",
]);

function inferTagsFromText(text, allowedTags, hasAmount = false) {
  // allowedTags: optional array/Set of tag names this diary actually uses
  // or declares. When provided, we ONLY suggest tags from that pool —
  // a tennis-team diary won't get phantom 'auto' or 'medical' tags just
  // because the body happens to mention 'miles' or 'doctor'. The global
  // TAG_KEYWORDS dictionary is still useful for synonym matching (e.g.,
  // 'forehand' → tennis) but only fires when 'tennis' is in allowedTags.
  // When allowedTags is omitted, falls back to the legacy behavior of
  // suggesting any matching tag from TAG_KEYWORDS — kept for backward
  // compatibility with non-diary callers (e.g., email ingestion).
  // hasAmount: when false (default), AMOUNT_REQUIRED_TAGS (income, refund,
  // rent, etc.) are skipped to avoid false positives like tagging "I'll
  // rent a movie" with 'rent'. Pass true when the entry has a parsed
  // amount, which strongly suggests money-flow context.
  const lower = (text || "").toLowerCase();
  const found = [];
  const allowed = allowedTags
    ? new Set([...(allowedTags instanceof Set ? allowedTags : allowedTags)].map(s => String(s).toLowerCase()))
    : null;
  for (const [tag, keywords] of Object.entries(TAG_KEYWORDS)) {
    if (allowed && !allowed.has(tag.toLowerCase())) continue;
    if (!hasAmount && AMOUNT_REQUIRED_TAGS.has(tag)) continue;
    for (const kw of keywords) {
      const isPhrase = kw.includes(" ");
      const matched = isPhrase
        ? lower.includes(kw)
        : new RegExp(`\\b${kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`).test(lower);
      if (matched) { found.push(tag); break; }
    }
  }
  return [...new Set(found)];
}

// Tag words that are ALSO mood values. When user types `#happy` or `#frustrated`,
// the system stores it as a tag AND populates the mood field. Lower-case match.
const MOOD_TAGS = new Set([
  // Positive
  "happy", "excited", "energized", "pumped", "satisfied", "calm", "content",
  "grateful", "hopeful", "optimistic", "proud", "relaxed", "peaceful", "joyful",
  "confident", "motivated", "inspired", "loved", "great",
  // Negative
  "frustrated", "anxious", "tired", "sad", "angry", "stressed", "worried",
  "overwhelmed", "exhausted", "annoyed", "lonely", "depressed", "discouraged",
  "irritated", "upset", "drained", "scared", "nervous", "disappointed",
  // Neutral / mixed
  "focused", "curious", "thoughtful", "reflective", "neutral",
]);

// Tag words that are ALSO payment-type values. When user types `#cash` or `#amex`,
// the system stores it as a tag AND populates the payment_type library field.
const PAYMENT_TAGS = new Set([
  "cash", "credit", "debit", "check", "ach", "wire", "venmo", "zelle", "paypal",
  "amex", "visa", "mastercard", "discover", "chase", "boa", "wells", "citi",
  "applepay", "googlepay",
]);

// ── FIELD LIBRARY ─────────────────────────────────────────────────────────────
// Pre-defined optional structured fields that can be enabled per-diary.
// Built-ins are universal (always available unless hidden); library fields
// are off by default and user-enables them via diary settings.
//
// Each entry: { key, icon, label, multi (bool), parse (str→stored), format (val→display) }
//   multi: true means values are arrays (comma-separated input). false = single value.
//
// `parsePrefixes` is the list of shorthand prefixes that should match this field
// in body text (e.g., "payment:" or "pay:" for payment_type). Lower-case.

const FIELD_LIBRARY = {
  payment_type: {
    key: "payment_type", icon: "💳", label: "Payment", multi: false,
    parsePrefixes: ["pay", "pmt", "payment"],
    parse: v => v.trim() || null,
    format: v => v,
  },
  spend_category: {
    key: "spend_category", icon: "🛒", label: "Spend Category", multi: false,
    parsePrefixes: ["cat", "spend", "category", "spend_category"],
    parse: v => v.trim() || null,
    format: v => v,
  },
  body_part: {
    key: "body_part", icon: "🩺", label: "Body Part", multi: true,
    parsePrefixes: ["body", "body_part"],
    parse: v => { const list = v.split(/,/).map(s => s.trim()).filter(Boolean); return list.length ? list : null; },
    format: v => Array.isArray(v) ? v.join(", ") : v,
  },
  medication: {
    key: "medication", icon: "💊", label: "Medication", multi: true,
    parsePrefixes: ["rx", "med", "meds", "medication"],
    parse: v => { const list = v.split(/,/).map(s => s.trim()).filter(Boolean); return list.length ? list : null; },
    format: v => Array.isArray(v) ? v.join(", ") : v,
  },
  subject: {
    key: "subject", icon: "📚", label: "Subject", multi: true,
    parsePrefixes: ["sub", "subj", "subject"],
    parse: v => { const list = v.split(/,/).map(s => s.trim()).filter(Boolean); return list.length ? list : null; },
    format: v => Array.isArray(v) ? v.join(", ") : v,
  },
  cuisine: {
    key: "cuisine", icon: "🍽️", label: "Cuisine", multi: false,
    parsePrefixes: ["food", "cuisine"],
    parse: v => v.trim() || null,
    format: v => v,
  },
  project: {
    key: "project", icon: "📦", label: "Project", multi: false,
    parsePrefixes: ["proj", "project"],
    parse: v => v.trim() || null,
    format: v => v,
  },
  symptom: {
    key: "symptom", icon: "🩹", label: "Symptom", multi: true,
    parsePrefixes: ["sx", "symptom", "symptoms"],
    parse: v => { const list = v.split(/,/).map(s => s.trim()).filter(Boolean); return list.length ? list : null; },
    format: v => Array.isArray(v) ? v.join(", ") : v,
  },
  property: {
    key: "property", icon: "🏠", label: "Property", multi: true,
    parsePrefixes: ["prop", "property"],
    parse: v => { const list = v.split(/,/).map(s => s.trim()).filter(Boolean); return list.length ? list : null; },
    format: v => Array.isArray(v) ? v.join(", ") : v,
  },
};

// All shorthand prefixes claimed by library fields. Used by the parser to
// know which "word: value" patterns are library fields vs free-form custom.
const LIBRARY_PREFIX_TO_KEY = (() => {
  const m = {};
  for (const [key, def] of Object.entries(FIELD_LIBRARY)) {
    for (const p of def.parsePrefixes) m[p.toLowerCase()] = key;
  }
  return m;
})();

// ── HELPERS ───────────────────────────────────────────────────────────────────

const fmtDate = ts => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) + " · " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
};

// Render one or more entries to a single PDF Blob, suitable for downloading or
// passing to navigator.share() as a File. Uses lazy-loaded jspdf + html2canvas
// so the ~400KB cost is only paid when a user actually shares as PDF.
//
// Approach: build an off-screen, print-styled DOM node containing all entries,
// snapshot it with html2canvas, then slice that tall canvas across A4 pages.
// Photos are inlined as data URLs to dodge CORS issues during canvas capture
// (Firebase Storage URLs *do* allow CORS, but fetching to data URL avoids any
// taint-canvas surprises).
async function buildEntryPdf(entries) {
  const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
    import("jspdf"),
    import("html2canvas"),
  ]);

  const escapeHtml = s => String(s).replace(/[&<>"']/g, c => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));

  // Convert a photo URL to a data URL with EXIF orientation already applied.
  // iPhone photos are stored sideways with metadata that says "rotate when
  // displaying"; without this canvas pass, html2canvas can embed the raw
  // (sideways) pixels and the PDF shows photos rotated 90°.
  const urlToOrientedDataUrl = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();
    let bitmap = null;
    try {
      bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
    } catch {
      // Older browser fallback: <img> with object URL, then draw to canvas.
      bitmap = await new Promise((resolve, reject) => {
        const img = new Image();
        const objUrl = URL.createObjectURL(blob);
        img.onload = () => { URL.revokeObjectURL(objUrl); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(objUrl); reject(new Error("img load failed")); };
        img.src = objUrl;
      });
    }
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width || bitmap.naturalWidth;
    canvas.height = bitmap.height || bitmap.naturalHeight;
    canvas.getContext("2d").drawImage(bitmap, 0, 0);
    return canvas.toDataURL("image/jpeg", 0.9);
  };

  const container = document.createElement("div");
  container.style.cssText = "position:fixed;left:-9999px;top:0;width:720px;padding:40px;background:white;font-family:-apple-system,sans-serif;color:#222;";

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const section = document.createElement("div");
    section.style.cssText = `margin-bottom:32px;${i < entries.length - 1 ? "border-bottom:1px solid #eee;padding-bottom:24px;" : ""}`;
    section.innerHTML = `
      <h1 style="font-size:22px;color:#0B1F4A;margin:0 0 6px;font-weight:700;">${escapeHtml(entry.title || "Untitled")}</h1>
      <div style="font-size:12px;color:#888;margin-bottom:16px;">${entry.createdAt ? escapeHtml(fmtDate(entry.createdAt)) : ""}</div>
      <div style="font-size:14px;line-height:1.6;">${entry.bodyHtml || ""}</div>
    `;

    const photos = entry.photos || [];
    if (photos.length) {
      const grid = document.createElement("div");
      grid.style.cssText = "display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:16px;";
      for (const p of photos) {
        if (!p?.url) continue;
        try {
          const dataUrl = await urlToOrientedDataUrl(p.url);
          const img = document.createElement("img");
          img.src = dataUrl;
          img.style.cssText = "width:100%;border-radius:6px;display:block;";
          // Wait for the image to be fully decoded before appending. Without
          // this, html2canvas can snapshot the DOM before the browser has
          // finished decoding, producing a PDF with blank or missing photos.
          // img.decode() is the modern reliable way; fall back to onload for
          // any edge cases.
          try {
            await img.decode();
          } catch {
            await new Promise(resolve => {
              img.onload = resolve;
              img.onerror = resolve;
            });
          }
          grid.appendChild(img);
        } catch (e) {
          // Skip photos that fail to fetch — usually a CORS issue. Don't abort
          // the whole PDF over one missing image.
          console.warn("PDF: skipping photo, fetch failed:", p.url, e);
        }
      }
      section.appendChild(grid);
    }
    container.appendChild(section);
  }

  document.body.appendChild(container);
  try {
    const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: "#ffffff", imageTimeout: 30000 });
    const pdf = new jsPDF({ unit: "px", format: "a4", orientation: "portrait" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgH = (canvas.height * pageW) / canvas.width;
    let y = 0;
    while (y < imgH) {
      pdf.addImage(canvas, "PNG", 0, -y, pageW, imgH);
      y += pageH;
      if (y < imgH) pdf.addPage();
    }
    return pdf.output("blob");
  } finally {
    document.body.removeChild(container);
  }
}

// Build a .docx Blob from one or more entries. Lazy-loads the `docx` library
// (~250KB) so it's only paid when actually used. The result is a real Word
// document — opens in Word, Pages, LibreOffice, and (uploaded to Drive)
// converts cleanly to a native Google Doc with photos and formatting intact.
//
// Layout per entry: title (Heading 1), small date line, then the body content,
// then photos as a 2-column table. Multiple entries are separated by a page
// break and a horizontal divider for visual continuity.
async function buildEntryDocx(entries) {
  const { Document, Packer, Paragraph, TextRun, HeadingLevel, ImageRun, AlignmentType, PageBreak, Table, TableRow, TableCell, WidthType, BorderStyle } = await import("docx");

  // Strip HTML to plain paragraphs while preserving line breaks. We don't
  // attempt full HTML→docx conversion (that's an open problem in the npm
  // ecosystem); a clean plain-text body is more reliable than a half-broken
  // formatted one.
  const htmlToParagraphs = (html) => {
    if (!html) return [];
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    tmp.querySelectorAll("br").forEach(b => b.replaceWith("\n"));
    tmp.querySelectorAll("p,div,li").forEach(el => { el.append("\n"); });
    const text = (tmp.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
    return text.split(/\n+/).filter(Boolean).map(line =>
      new Paragraph({ children: [new TextRun({ text: line, size: 22 })] })
    );
  };

  // Fetch a photo URL → ArrayBuffer suitable for ImageRun. Returns null on
  // failure (CORS, deleted file, etc.) so a single bad photo doesn't kill
  // the whole export.
  // Fetch a photo URL → bytes ready for ImageRun.
  // Important: iPhone photos are stored with EXIF orientation metadata rather
  // than physically-rotated pixels. The docx library just embeds raw bytes, so
  // a sideways-stored portrait photo would appear rotated 90° in the document.
  // Fix: render through a canvas first — the browser applies EXIF orientation
  // automatically when drawing an Image() element, so the output PNG has the
  // correct visual orientation baked into the pixels.
  const fetchImage = async (url) => {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      try {
        // Load the image. createImageBitmap honors EXIF orientation in modern
        // browsers (iOS 13.4+, Chrome 80+) when called with imageOrientation.
        // It's also faster than <img> for this. Fall back to <img> if needed.
        let bitmap = null;
        try {
          bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
        } catch {
          // Fallback: <img> element. Modern browsers also auto-orient img tags
          // when drawing to canvas.
          bitmap = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error("img load failed"));
            img.src = objUrl;
          });
        }
        const naturalW = bitmap.width || bitmap.naturalWidth;
        const naturalH = bitmap.height || bitmap.naturalHeight;
        if (!naturalW || !naturalH) return null;

        // Cap width at ~220pt — fits two photos side-by-side in a 2-column
        // grid (page width is ~456pt usable, minus a small gap between cells).
        const maxW = 220;
        const scale = Math.min(1, maxW / naturalW);
        const targetW = Math.round(naturalW * scale);
        const targetH = Math.round(naturalH * scale);

        // Render to canvas at the target size — this both applies orientation
        // and downscales in one pass.
        const canvas = document.createElement("canvas");
        // We render at 2x the target for retina-ish quality, then docx scales
        // back down via the transformation.width/height setting.
        const renderScale = 2;
        canvas.width = targetW * renderScale;
        canvas.height = targetH * renderScale;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

        // Convert to blob → ArrayBuffer
        const outBlob = await new Promise(resolve =>
          canvas.toBlob(resolve, "image/jpeg", 0.85)
        );
        if (!outBlob) return null;
        const buffer = await outBlob.arrayBuffer();
        return { buffer, width: targetW, height: targetH };
      } finally {
        URL.revokeObjectURL(objUrl);
      }
    } catch (e) {
      console.warn("Image fetch/render failed for docx:", e);
      return null;
    }
  };

  const children = [];
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (i > 0) {
      // Soft separator between entries — a thin horizontal rule. Continuous
      // flow keeps title-only entries from leaving a full empty page below
      // them as a hard page break would.
      children.push(new Paragraph({
        children: [new TextRun({ text: "" })],
        border: {
          bottom: { color: "CCCCCC", space: 1, style: BorderStyle.SINGLE, size: 6 },
        },
        spacing: { before: 240, after: 240 },
      }));
    }
    // Title
    children.push(new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: entry.title || "Untitled", bold: true, size: 32, color: "0B1F4A" })],
    }));
    // Date
    if (entry.entryDate || entry.createdAt) {
      const ts = entry.entryDate || entry.createdAt;
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      const dateStr = d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) + " · " +
        d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
      children.push(new Paragraph({
        children: [new TextRun({ text: dateStr, size: 18, color: "888888" })],
        spacing: { after: 240 },
      }));
    }
    // Body — emit paragraphs only when there's actual content.
    const bodyParas = htmlToParagraphs(entry.bodyHtml || entry.body || "");
    if (bodyParas.length) {
      children.push(...bodyParas);
    }

    // Photos — fetch all in parallel, then arrange in a 2-column table so
    // they sit side-by-side on the page. Tables are docx's main side-by-side
    // primitive; cells with no borders look like a clean grid.
    const photos = entry.photos || [];
    if (photos.length) {
      // Small spacer above the photo grid, only if body content preceded it.
      if (bodyParas.length) {
        children.push(new Paragraph({ children: [new TextRun({ text: "" })], spacing: { before: 120 } }));
      }
      const fetched = (await Promise.all(photos.map(p => p?.url ? fetchImage(p.url) : Promise.resolve(null))))
        .filter(Boolean);

      // Helper: a photo as a single ImageRun-bearing paragraph.
      const photoPara = (img) => new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [new ImageRun({ data: img.buffer, transformation: { width: img.width, height: img.height } })],
      });

      // No-border cell wrapper around the photo. Word complains about cells
      // whose paragraph has no children, so even "empty" cells must hold a
      // paragraph with at least one TextRun.
      const noBorders = {
        top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      };
      const photoCell = (img) => new TableCell({
        children: [photoPara(img)],
        width: { size: 50, type: WidthType.PERCENTAGE },
        borders: noBorders,
      });
      const emptyCell = () => new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text: "" })] })],
        width: { size: 50, type: WidthType.PERCENTAGE },
        borders: noBorders,
      });

      const rows = [];
      for (let p = 0; p < fetched.length; p += 2) {
        const left = fetched[p];
        const right = fetched[p + 1];
        rows.push(new TableRow({
          children: right ? [photoCell(left), photoCell(right)] : [photoCell(left), emptyCell()],
        }));
      }

      if (rows.length) {
        children.push(new Table({
          rows,
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            ...noBorders,
            insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
            insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          },
        }));
      }
    }
  }

  const doc = new Document({
    sections: [{ properties: {}, children }],
  });
  return await Packer.toBlob(doc);
}

// Convert one or more entries to a CSV string. Always emits the same column
// schema: core entry fields plus parsed "expense-style" fields (Vendor folds
// into Location; Amount / Property / Item) extracted from the title when it
// has the "vendor / amount / property / item" shape. Columns whose values
// are empty for every entry are dropped automatically so users only see
// what they actually track.
//
// Excel/Numbers/Google Sheets all import this directly. The leading BOM
// (added at download time) makes Excel correctly read it as UTF-8.
function buildEntriesCsv(entries, diary = null) {
  // HTML body → readable plain text. Preserves paragraph/line breaks so a
  // multi-paragraph entry stays multi-line inside a CSV cell (CSV supports
  // newlines inside quoted fields, which Excel handles fine).
  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    tmp.querySelectorAll("br").forEach(b => b.replaceWith("\n"));
    tmp.querySelectorAll("p,div,li").forEach(el => { el.append("\n"); });
    return (tmp.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
  };

  const escape = (val) => {
    const s = val == null ? "" : String(val);
    // Quote if the value contains anything that would break CSV parsing.
    if (/[",\r\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };

  // Discover all custom-field names across these entries so we can give
  // each its own column.
  const customFieldNames = (() => {
    const set = new Set();
    for (const e of entries) {
      const c = e.structured?.custom;
      if (c && typeof c === "object") {
        for (const k of Object.keys(c)) set.add(k.toLowerCase());
      }
    }
    return [...set];
  })();

  // Pre-compute per-entry values. We need this twice — once to count
  // non-empty columns (for sorting/dropping), once to render the rows —
  // so we cache the work.
  const computed = entries.map(e => {
    const d = e.entryDate?.toDate ? e.entryDate.toDate() : (e.entryDate ? new Date(e.entryDate) : null);
    const dateStr = d ? d.toISOString().slice(0, 10) : "";
    const timeStr = d ? d.toTimeString().slice(0, 5) : "";
    const bodyText = stripHtml(e.bodyHtml || "") || (e.body || "");
    const tagList = (e.tags || []).filter(t => !isFollowupTag(t));
    const tags = tagList.join(", ");
    const followup = (e.tags || []).some(isFollowupTag) ? "yes" : "";
    const favorite = e.favorite ? "yes" : "";
    const author = e._who || "";
    // Prefer pre-parsed structured data; fall back to title parsing for legacy entries.
    const parsed = e.expenseData || parseExpenseTitle(e.title || "") || { vendor: "", amount: "", unit: "", description: "" };
    const sd = e.structured || (() => {
      const t = `${e.title || ""} ${bodyText}`.trim();
      const ps = parseStructuredFields(t);
      const out = {};
      if (ps.amount !== null) out.amount = ps.amount;
      if (ps.location && ps.location.length) out.location = ps.location;
      if (ps.people && ps.people.length) out.people = ps.people;
      if (ps.timeMinutes !== null) out.timeMinutes = ps.timeMinutes;
      if (ps.distanceMiles !== null) out.distanceMiles = ps.distanceMiles;
      if (ps.calories !== null) out.calories = ps.calories;
      if (ps.mood && ps.mood.length) out.mood = ps.mood;
      if (ps.rating) out.rating = ps.rating;
      if (ps.score) out.score = ps.score;
      return out;
    })();
    const customVals = (sd.custom && typeof sd.custom === "object") ? sd.custom : {};
    return { e, parsed, sd, customVals, dateStr, timeStr, bodyText, tags, followup, favorite, author };
  });

  // Helper for the CSV getters below: scan title+body for `prefix:value`
  // shorthand even when the field wasn't declared as a custom/library field
  // in the diary's settings. Last-resort fallback so an email like
  // `$10 @safeway cleaning supplies property:2427a` populates the Property
  // column on first use, before the user has formally declared `property` in
  // settings (after which it'd be in sd.custom and we wouldn't need this).
  const scanShorthand = (text, prefix) => {
    if (!text) return "";
    const re = new RegExp(`\\b${prefix}:\\s*(?:"([^"]+)"|([A-Za-z0-9][A-Za-z0-9_-]*(?:\\s*,\\s*[A-Za-z0-9][A-Za-z0-9_-]*)*))`, "i");
    const m = text.match(re);
    if (!m) return "";
    return (m[1] || m[2] || "").trim();
  };
  const readArrayOrScalar = (v) => {
    if (v == null) return "";
    if (Array.isArray(v)) return v.join(", ");
    return String(v);
  };

  // Tags that indicate money INTO the user's pocket (income or expense
  // reversal). For these, the amount is negated in CSV output so a
  // spreadsheet's SUM(Amount) column nets correctly: total expenses minus
  // total income/refunds = true net spend.
  const INCOME_TAGS = new Set(["income","rent","royalty","refund","credit","return","rebate","reimbursement"]);
  const isIncomeRow = (r) => {
    if (!r.tags) return false;
    const list = String(r.tags).split(/\s*,\s*/).map(t => t.toLowerCase()).filter(Boolean);
    return list.some(t => INCOME_TAGS.has(t));
  };

  // Column registry. Each column knows how to extract a string from a
  // computed row. Spine columns (firstCols) are pinned in fixed order at
  // the front — these are the ones every reader expects up front when they
  // open the CSV in a spreadsheet, even if some are auto-generated. Middle
  // columns are sorted by usage and drop when unused. There's no last-pin
  // column anymore: with Body in the spine, nothing else needs to be forced
  // to the right edge.
  const middleCols = [
    // Expense-derived columns. Each falls back through several sources:
    //   1. Slash-format parsed (legacy "vendor / amount / property / item" titles)
    //   2. Library field (if user has enabled `property`/`item` as library fields)
    //   3. Custom field (if user has declared them in diary settings)
    //   4. Body re-scan for `property:` / `item:` shorthand even if undeclared
    // This makes shorthand-style emails like `$10 @safeway property:2427a`
    // populate the same columns as a power-user slash-format entry.
    // Note: Internally the parser still names slots `unit`/`description`;
    // we expose them as Property/Item. Vendor folds into spine Location.
    { id: "Amount", get: r => {
      // Resolve the numeric amount from any source, then negate if the
      // entry is income/refund/etc. Negative amounts let a spreadsheet
      // SUM the column directly to get net spend.
      let n = null;
      if (typeof r.parsed.amount === "number") n = r.parsed.amount;
      else if (r.parsed.amount) {
        const parsed = parseFloat(String(r.parsed.amount).replace(/[^0-9.-]/g, ""));
        if (Number.isFinite(parsed)) n = parsed;
        else return r.parsed.amount;
      } else if (typeof r.sd.amount === "number") n = r.sd.amount;
      else if (r.sd.amount != null) {
        const parsed = parseFloat(String(r.sd.amount).replace(/[^0-9.-]/g, ""));
        if (Number.isFinite(parsed)) n = parsed;
        else return String(r.sd.amount);
      }
      if (n == null) return "";
      return (isIncomeRow(r) ? -n : n).toFixed(2);
    } },
    { id: "Property", get: r => {
      if (r.parsed.unit) return r.parsed.unit;
      const lib = r.sd.library?.property;
      if (lib != null && (Array.isArray(lib) ? lib.length : true)) return readArrayOrScalar(lib);
      const cust = r.sd.custom?.property;
      if (cust != null && (Array.isArray(cust) ? cust.length : true)) return readArrayOrScalar(cust);
      return scanShorthand(`${r.e.title || ""} ${r.bodyText}`, "property");
    } },
    { id: "Item", get: r => {
      if (r.parsed.description) return r.parsed.description;
      const lib = r.sd.library?.item;
      if (lib != null && (Array.isArray(lib) ? lib.length : true)) return readArrayOrScalar(lib);
      const cust = r.sd.custom?.item;
      if (cust != null && (Array.isArray(cust) ? cust.length : true)) return readArrayOrScalar(cust);
      return scanShorthand(`${r.e.title || ""} ${r.bodyText}`, "item");
    } },
    { id: "Author",       get: r => r.author },
    { id: "Favorite",     get: r => r.favorite },
    { id: "Followup",     get: r => r.followup },
    // Structured-shorthand fields ($25, w:Person, 1.5h, etc.). Location is
    // pinned to the spine, not here.
    { id: "Time_Minutes", get: r => r.sd.timeMinutes != null ? String(r.sd.timeMinutes) : "" },
    { id: "Distance_Mi",  get: r => r.sd.distanceMiles != null ? Number(r.sd.distanceMiles).toFixed(2) : "" },
    { id: "Calories",     get: r => r.sd.calories != null ? String(r.sd.calories) : "" },
    { id: "People",       get: r => Array.isArray(r.sd.people) ? r.sd.people.join(", ") : "" },
    // City and address come from photo EXIF GPS resolution. Both auto-drop
    // from the CSV when no entries have them (the empty-column filter
    // below handles this), so a diary with no photos won't carry empty
    // geo columns.
    { id: "City",         get: r => r.sd.city || "" },
    { id: "Address",      get: r => r.sd.address || "" },
    { id: "Mood",         get: r => Array.isArray(r.sd.mood) ? r.sd.mood.join(", ") : (r.sd.mood || "") },
    { id: "Rating",       get: r => r.sd.rating || "" },
    { id: "Score",        get: r => r.sd.score || "" },
    // Library fields — one column per enabled library field, except
    // property and item which already have dedicated columns above.
    // Each library field reads from structured.library[key] (the
    // canonical store), then falls back to structured.custom[key] for
    // older entries created before the field was promoted to library.
    // Disabled when no diary context is passed (e.g. legacy callers).
    ...((diary?.enabledLibrary || [])
      .filter(k => FIELD_LIBRARY[k] && k !== "property" && k !== "item")
      .map(key => {
        const def = FIELD_LIBRARY[key];
        // Capitalize the key for the column header: "payment_type" →
        // "Payment_Type". This keeps the export header matching the
        // CSV import header convention (case-insensitive name match).
        const header = key.split("_").map(p => p.charAt(0).toUpperCase() + p.slice(1)).join("_");
        return {
          id: header,
          get: r => {
            const lib = r.sd.library?.[key];
            if (lib != null && (Array.isArray(lib) ? lib.length : true)) return readArrayOrScalar(lib);
            const cust = r.sd.custom?.[key];
            if (cust != null && (Array.isArray(cust) ? cust.length : true)) return readArrayOrScalar(cust);
            return scanShorthand(`${r.e.title || ""} ${r.bodyText}`, key);
          },
        };
      })),
    // Custom fields — one column per discovered name, prefixed with "X_".
    // Skip property/item — those have their own non-X columns above so we
    // don't double-print them. Also skip any library-field key — those
    // have dedicated columns above and we don't want a duplicate "X_*"
    // column for the same data.
    ...customFieldNames.filter(n => {
      if (n === "property" || n === "item") return false;
      if (FIELD_LIBRARY[n] && (diary?.enabledLibrary || []).includes(n)) return false;
      return true;
    }).map(name => ({
      id: `X_${name}`,
      get: r => {
        const v = r.customVals[name];
        if (!v) return "";
        return Array.isArray(v) ? v.join(", ") : String(v);
      },
    })),
  ];

  // Count non-empty values per middle column.
  const counts = new Map();
  for (const col of middleCols) counts.set(col.id, 0);
  for (const r of computed) {
    for (const col of middleCols) {
      if (col.get(r)) counts.set(col.id, counts.get(col.id) + 1);
    }
  }
  // Drop empty columns; sort the rest by usage descending (stable for ties,
  // preserving the registry order above as a tiebreaker so related fields
  // — e.g. Amount/Property/Item, or Mood/Rating/Score — still cluster
  // together when they share the same usage count).
  const indexed = middleCols.map((col, i) => ({ col, i, n: counts.get(col.id) }));
  const usedMiddle = indexed
    .filter(x => x.n > 0)
    .sort((a, b) => (b.n - a.n) || (a.i - b.i))
    .map(x => x.col);

  // Build a tag→group lookup. Each tag is in at most one group (the data
  // model enforces this), so this map is well-defined. Used for the
  // "Groups" export column so spreadsheets can pivot by property/category
  // grouping even though the entry itself only stores raw tags.
  const tagGroups = Array.isArray(diary?.tagGroups) ? diary.tagGroups : [];
  const tagToGroupName = new Map();
  for (const g of tagGroups) {
    if (!g || !g.name || !Array.isArray(g.tags)) continue;
    for (const t of g.tags) tagToGroupName.set(t, g.name);
  }

  // Spine columns are always shown at the front in this fixed order.
  // Matches the Expense CSV export (Date / Location / Amount / Tags /
  // Title / ... / Body) so users get a consistent header pattern across
  // the two download formats. Groups column surfaces the named buckets
  // a tag belongs to — pivot-friendly for spreadsheet analysis.
  const firstCols = [
    { id: "Date",     get: r => r.dateStr },
    { id: "Location", get: r => {
      if (r.parsed.vendor) return r.parsed.vendor;
      if (Array.isArray(r.sd.location)) return r.sd.location.join(", ");
      return r.sd.location || "";
    } },
    { id: "Amount", get: r => {
      // Mirror of the Amount middle-column logic; we hoist this into the
      // spine so its position is predictable. The middle-col registry
      // below dedupes on id so we don't double-print Amount.
      let n = null;
      if (typeof r.parsed.amount === "number") n = r.parsed.amount;
      else if (r.parsed.amount) {
        const parsed = parseFloat(String(r.parsed.amount).replace(/[^0-9.-]/g, ""));
        if (Number.isFinite(parsed)) n = parsed;
        else return r.parsed.amount;
      } else if (typeof r.sd.amount === "number") n = r.sd.amount;
      else if (r.sd.amount != null) {
        const parsed = parseFloat(String(r.sd.amount).replace(/[^0-9.-]/g, ""));
        if (Number.isFinite(parsed)) n = parsed;
        else return String(r.sd.amount);
      }
      if (n == null) return "";
      // Mirror the sign-negation from the middle-col logic. Income is
      // negative so SUM-ing the column yields net spend.
      const incomeRe = /\b(income|refund|reimburs|payment\s*received|credit|deposit|sale\b)\b/i;
      const titleTxt = String(r.e.title || "");
      const bodyTxt = String(r.bodyText || "");
      if (incomeRe.test(titleTxt) || incomeRe.test(bodyTxt)) n = -Math.abs(n);
      return n;
    } },
    { id: "Tags",     get: r => r.tags },
    { id: "Groups",   get: r => {
      const tagsList = (r.e.tags || []).filter(t => !isFollowupTag(t));
      const groupNames = new Set();
      for (const t of tagsList) {
        const g = tagToGroupName.get(t);
        if (g) groupNames.add(g);
      }
      return [...groupNames].join(", ");
    } },
    { id: "Title",    get: r => r.e.title || "" },
    { id: "Time",     get: r => r.timeStr },
    { id: "Body",     get: r => r.bodyText },
  ];
  // Drop pinned columns whose values are empty for every entry — a CSV
  // export of a diary with no titles shouldn't have a blank "Title" column,
  // and a diary with no @-locations or vendor-style expenses shouldn't
  // carry an empty "Location".
  const keepIfUsed = (cols) => cols.filter(col => computed.some(r => col.get(r)));
  const keptFirst = keepIfUsed(firstCols);
  // De-dup: if a column appears in firstCols (e.g. Amount, hoisted to
  // the spine), drop it from the middle so we don't double-print.
  const firstIds = new Set(keptFirst.map(c => c.id));
  const middleWithoutDup = usedMiddle.filter(c => !firstIds.has(c.id));
  const finalCols = [...keptFirst, ...middleWithoutDup];

  const headers = finalCols.map(c => c.id);
  const rows = [headers];
  for (const r of computed) rows.push(finalCols.map(c => c.get(r)));

  return {
    csv: rows.map(r => r.map(escape).join(",")).join("\r\n"),
    headers,
    rows: rows.slice(1), // data rows only, no header
  };
}

// Build an XLSX (Excel) workbook from a set of entries. Shares the same
// data assembly as buildEntriesCsv via the headers/rows it returns; we
// just feed those into SheetJS instead of formatting as CSV text.
//
// What this adds over CSV:
//   - Sensible column widths so Title/Body don't get cut off
//   - Header row formatted bold (light styling only; no fancy themes)
//   - Frozen header row so it stays visible on scroll
//   - No text-wrap on body content — long bodies are clipped at column
//     edge instead of ballooning row height (users can widen the column
//     or re-enable wrap themselves if they want)
//
// `XLSX` parameter is the SheetJS module — passed in because we load it
// lazily via dynamic import at the call site to avoid bundling the
// ~400KB library for users who only ever use CSV.
function buildEntriesXlsx(entries, XLSX, diary = null) {
  const { headers, rows } = buildEntriesCsv(entries, diary);
  const aoa = [headers, ...rows];

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Column widths in "characters" (approximate). Wider for the columns
  // that typically hold long content (Title, Body), narrower for the
  // structured fields (Date, Amount, etc.).
  const widthByName = {
    Date: 12,
    Time: 10,
    Title: 50,
    Body: 80,
    Tags: 20,
    Location: 25,
    City: 14,
    State: 8,
    Country: 12,
    Address: 35,
    Amount: 10,
    Property: 18,
    Item: 30,
    People: 22,
    Vendor: 22,
    Description: 40,
    Mood: 10,
    Rating: 8,
    Score: 8,
    Distance: 10,
    Calories: 10,
  };
  ws["!cols"] = headers.map(h => ({ wch: widthByName[h] || 16 }));

  // Freeze the top row so it stays visible while scrolling.
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };
  ws["!views"] = [{ state: "frozen", ySplit: 1, xSplit: 0, topLeftCell: "A2", activePane: "bottomLeft" }];

  const amountColIdx = headers.indexOf("Amount");
  const dateColIdx = headers.indexOf("Date");

  // Bold the header row. SheetJS Community Edition supports basic styling
  // when we set `cellStyles: true` at write time. We attach the style
  // directly to each header cell.
  for (let c = 0; c < headers.length; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[addr]) {
      const isAmount = c === amountColIdx;
      ws[addr].s = {
        font: { bold: true },
        alignment: {
          vertical: "top",
          horizontal: isAmount ? "right" : "left",
          wrapText: false,
        },
      };
    }
  }
  // Data cells: no text-wrap. Two columns get special treatment:
  //   - Amount: coerced to a number (not a string) with #,##0.00 format
  //     so Excel sums work and the cell sorts numerically. No currency
  //     symbol — keeps the column tight and avoids visual clutter.
  //   - Date: coerced to a real Excel date (serial number) with
  //     mm/dd/yyyy display format. CSV path stays ISO ("YYYY-MM-DD") for
  //     downstream tooling; XLSX users get the familiar US format and
  //     can sort/filter chronologically.
  // Either column may be absent from this export (the entries CSV drops
  // empty columns), so guard on indexOf returning -1.
  for (let r = 1; r < aoa.length; r++) {
    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) continue;
      const isAmount = amountColIdx !== -1 && c === amountColIdx;
      const isDate = dateColIdx !== -1 && c === dateColIdx;
      if (isAmount) {
        const raw = ws[addr].v;
        const num = parseFloat(String(raw));
        if (Number.isFinite(num)) {
          ws[addr].t = "n";
          ws[addr].v = num;
          ws[addr].z = '#,##0.00_);(#,##0.00)';
        }
      } else if (isDate) {
        const raw = ws[addr].v;
        if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
          const [y, m, d] = raw.split("-").map(Number);
          // Local-noon construction avoids DST/timezone edge cases that
          // would otherwise display the date as the day before.
          const date = new Date(y, m - 1, d, 12, 0, 0);
          ws[addr].t = "d";
          ws[addr].v = date;
          ws[addr].z = "mm/dd/yyyy";
        }
      }
      ws[addr].s = {
        alignment: {
          vertical: "top",
          horizontal: isAmount ? "right" : "left",
          wrapText: false,
        },
      };
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Entries");
  return wb;
}

// Focused expense report: any entry with a detectable amount. New schema:
// Date, Location, Amount, Property, Tags, Item.
//
// "Has an amount" is the inclusion test — replaces the older "has expense
// tag" gate. An amount counts if it shows up in any of these sources:
//   - entry.structured.amount   (set by the shorthand parser when the user
//                                wrote `$24` anywhere in title/body, or
//                                `@25.50` as a fallback price)
//   - parseExpenseTitle / expenseData  (legacy "vendor / amount / unit / item"
//                                       slash-format titles)
//   - last-resort body re-parse  (catches entries saved before parser
//                                 populated structured fields)
//
// Each non-Date column cascades through the same set of sources so power
// users (slash format) and shorthand users (`$24 @safeway property:2427a`)
// land in the same columns. The Tags column is included so users who
// classify by tag (e.g. `#2427a`) instead of the explicit `property:` field
// still see their grouping label without duplicating it under Property.
//
// Returns { csv, rowCount } so the caller can show a friendly message when
// nothing matched.
function buildExpensesCsv(entries) {
  const escape = (val) => {
    const s = val == null ? "" : String(val);
    if (/[",\r\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    tmp.querySelectorAll("br").forEach(b => b.replaceWith("\n"));
    tmp.querySelectorAll("p,div,li").forEach(el => { el.append("\n"); });
    return (tmp.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
  };
  const scanShorthand = (text, prefix) => {
    if (!text) return "";
    const re = new RegExp(`\\b${prefix}:\\s*(?:"([^"]+)"|([A-Za-z0-9][A-Za-z0-9_-]*(?:\\s*,\\s*[A-Za-z0-9][A-Za-z0-9_-]*)*))`, "i");
    const m = text.match(re);
    if (!m) return "";
    return (m[1] || m[2] || "").trim();
  };
  const readArrayOrScalar = (v) => {
    if (v == null) return "";
    if (Array.isArray(v)) return v.join(", ");
    return String(v);
  };

  const headers = ["Date", "Location", "Amount", "Tags", "Title", "Property", "Item", "Body"];
  const rows = [headers];

  // Tag sort: alphabetical with one tweak — tags whose first character is
  // a digit sort BEFORE letter-starting tags. Useful for tag-as-property
  // schemes like #1026Riley, #55Park where the leading number is the
  // property identifier and grouping those together makes scanning easier.
  const sortTagsForExport = (list) => {
    const arr = [...(list || [])];
    arr.sort((a, b) => {
      const aDigit = /^\d/.test(a);
      const bDigit = /^\d/.test(b);
      if (aDigit && !bDigit) return -1;
      if (!aDigit && bDigit) return 1;
      return a.localeCompare(b, undefined, { sensitivity: "base" });
    });
    return arr;
  };

  for (const e of entries) {
    const sd = e.structured || {};
    const parsed = e.expenseData || parseExpenseTitle(e.title || "") || { vendor: "", amount: "", unit: "", description: "" };
    const bodyText = stripHtml(e.bodyHtml || "") || (e.body || "");
    const titleAndBody = `${e.title || ""} ${bodyText}`.trim();

    // Amount cascade: shorthand (sd.amount) → slash-format → body re-parse.
    // Re-parse only when neither structured nor expenseData carried an amount,
    // so we don't pay the parser cost on every row.
    let amount = null;
    if (typeof sd.amount === "number") amount = sd.amount;
    else if (typeof parsed.amount === "number") amount = parsed.amount;
    else if (parsed.amount) amount = parsed.amount;
    else {
      const ps = parseStructuredFields(titleAndBody);
      if (ps.amount != null) amount = ps.amount;
    }
    if (amount == null || amount === "") continue; // no amount → not an expense

    // Tags first — needed for income/refund detection below. Sort
    // alphabetically (with numeric-prefixed tags first) so the exported
    // tag column is scannable.
    const tagList = sortTagsForExport((e.tags || []).filter(t => !isFollowupTag(t)));
    const tags = tagList.join(", ");

    // Income tags: amount is negated so a spreadsheet's SUM nets correctly.
    const INCOME_TAGS = new Set(["income","rent","royalty","refund","credit","return","rebate","reimbursement"]);
    const isIncome = tagList.some(t => INCOME_TAGS.has(t.toLowerCase()));
    const numericAmount = typeof amount === "number"
      ? amount
      : (() => {
          const n = parseFloat(String(amount).replace(/[^0-9.-]/g, ""));
          return Number.isFinite(n) ? n : null;
        })();
    const amountStr = numericAmount != null
      ? (isIncome ? -numericAmount : numericAmount).toFixed(2)
      : String(amount);

    const d = e.entryDate?.toDate ? e.entryDate.toDate() : (e.entryDate ? new Date(e.entryDate) : null);
    const dateStr = d ? d.toISOString().slice(0, 10) : "";

    // Location: slash-format vendor wins (most explicit), else @-shorthand.
    let location = parsed.vendor || "";
    if (!location) location = readArrayOrScalar(sd.location);

    // Property: slash slot 3 → library.property → custom.property → body scan
    let property = parsed.unit || "";
    if (!property) property = readArrayOrScalar(sd.library?.property);
    if (!property) property = readArrayOrScalar(sd.custom?.property);
    if (!property) property = scanShorthand(titleAndBody, "property");

    // Item: slash slot 4 → library.item → custom.item → body scan for
    // explicit item:value → last-resort, the parser's "leftover" — whatever's
    // left in the body after every recognized shorthand token (amount,
    // location, tags, mood, time, etc.) has been stripped. That leftover IS
    // the natural-language item description for entries where the user
    // didn't bother with explicit `item:` syntax (e.g. body
    // `@Lowes $34.50 #277shipub appliance bulbs` → leftover "appliance bulbs").
    let item = parsed.description || "";
    if (!item) item = readArrayOrScalar(sd.library?.item);
    if (!item) item = readArrayOrScalar(sd.custom?.item);
    if (!item) item = scanShorthand(titleAndBody, "item");
    if (!item) {
      // Parse body alone (not title+body) so a phrase appearing in both
      // doesn't double-print. Fall back to title if body is empty.
      const itemSource = bodyText || (e.title || "");
      if (itemSource) {
        const ps = parseStructuredFields(itemSource);
        item = (ps.description || "").trim();
      }
    }

    // Column order: Date, Location, Amount, Tags, Title, Property, Item, Body
    rows.push([dateStr, location, amountStr, tags, e.title || "", property, item, bodyText]);
  }

  return {
    csv: rows.map(r => r.map(escape).join(",")).join("\r\n"),
    headers,
    rows: rows.slice(1),
    rowCount: rows.length - 1,
  };
}

// XLSX variant of the expense report. Same column schema as
// buildExpensesCsv, plus column widths and no text-wrap so long
// descriptions don't inflate row heights. See buildEntriesXlsx for
// the broader design notes.
function buildExpensesXlsx(entries, XLSX) {
  const { headers, rows, rowCount } = buildExpensesCsv(entries);
  if (rowCount === 0) return { wb: null, rowCount: 0 };

  const aoa = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Column widths tuned for the expense schema. Location narrowed from
  // 25 → 13 since vendor names are usually short. Amount sized to fit
  // the widest typical value like "$3,713.00" (~10 chars) with breathing
  // room. The wider Title/Body columns absorb the freed space.
  const widthByName = {
    Date: 12,
    Location: 13,
    Amount: 11,
    Tags: 22,
    Title: 40,
    Property: 18,
    Item: 30,
    Body: 60,
  };
  ws["!cols"] = headers.map(h => ({ wch: widthByName[h] || 16 }));

  // Freeze header row.
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };
  ws["!views"] = [{ state: "frozen", ySplit: 1, xSplit: 0, topLeftCell: "A2", activePane: "bottomLeft" }];

  const amountColIdx = headers.indexOf("Amount");
  const dateColIdx = headers.indexOf("Date");

  // Bold the header.
  for (let c = 0; c < headers.length; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[addr]) {
      // Right-align the Amount header so it lines up with the values
      // below. Other headers stay left-aligned (the default).
      const isAmount = c === amountColIdx;
      ws[addr].s = {
        font: { bold: true },
        alignment: {
          vertical: "top",
          horizontal: isAmount ? "right" : "left",
          wrapText: false,
        },
      };
    }
  }
  // Data cells: no text-wrap. Two columns get special treatment:
  //   - Amount: coerced to a number (not a string) with #,##0.00 format
  //     so Excel sums work and the cell sorts numerically. No currency
  //     symbol — keeps the column tight and avoids visual clutter.
  //   - Date: coerced to an Excel date (real serial number) with
  //     mm/dd/yyyy display format. CSV stays in ISO since that's better
  //     for downstream tooling; XLSX users want a familiar US format.
  for (let r = 1; r < aoa.length; r++) {
    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) continue;
      const isAmount = c === amountColIdx;
      const isDate = c === dateColIdx;
      if (isAmount) {
        const raw = ws[addr].v;
        const num = parseFloat(String(raw));
        if (Number.isFinite(num)) {
          ws[addr].t = "n";
          ws[addr].v = num;
          // Plain number with thousands separators, two decimals, parens
          // for negatives (accountant style — income/refunds show as
          // (45.20)). No currency symbol per user preference.
          ws[addr].z = '#,##0.00_);(#,##0.00)';
        }
      } else if (isDate) {
        // CSV path emits ISO "YYYY-MM-DD". Parse it back to a Date and
        // let SheetJS encode it as an Excel date serial. The display
        // format below makes Excel render it as MM/DD/YYYY.
        const raw = ws[addr].v;
        if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
          const [y, m, d] = raw.split("-").map(Number);
          // Construct as local-time noon to avoid DST/timezone boundary
          // issues that could push the date back by a day on display.
          const date = new Date(y, m - 1, d, 12, 0, 0);
          ws[addr].t = "d";
          ws[addr].v = date;
          ws[addr].z = "mm/dd/yyyy";
        }
      }
      ws[addr].s = {
        alignment: {
          vertical: "top",
          horizontal: isAmount ? "right" : "left",
          wrapText: false,
        },
      };
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Expenses");
  return { wb, rowCount };
}

// Build an entry timestamp from a date-picker string (YYYY-MM-DD) and an optional
// existing timestamp.
// Rules:
//   - No date string given → return current moment.
//   - Date string matches today (local time) → return current moment (so "Add now" shows
//     actual current time, not midnight or 12pm).
//   - Date string is in the past → combine that date with the existing entry's original
//     time if provided (preserves the time on edit), otherwise default to noon of that
//     day. Noon avoids timezone-boundary issues that would otherwise shift the entry to
//     the day before/after.
// Parsing trick: "YYYY-MM-DD" gets parsed as UTC midnight by the Date constructor, which
// can drift to the wrong day in negative timezones. We split the string and use the
// (y, m-1, d, h, min, s) constructor which interprets all values as LOCAL time.
// Apply a date-shorthand offset to a YYYY-MM-DD string. The offset object
// shape mirrors the shorthand parsed by the TagPicker:
//   { kind: "absolute", iso: "2026-04-01" } → set the date to that day
//   { kind: "relative", amount: N, unit: "d"|"w"|"mo"|"y" } → subtract
// Returns a new YYYY-MM-DD string. Used by the add/edit modals to wire
// the "(-1d)" tag shorthand into date state without leaving the picker.
function applyOffsetToYmd(ymd, off) {
  if (!off) return ymd;
  if (off.kind === "absolute") return off.iso;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || "");
  if (!m) return ymd;
  const d = new Date(+m[1], +m[2] - 1, +m[3], 12, 0, 0);
  const n = off.amount || 0;
  if (off.unit === "d") d.setDate(d.getDate() - n);
  else if (off.unit === "w") d.setDate(d.getDate() - n * 7);
  else if (off.unit === "mo") d.setMonth(d.getMonth() - n);
  else if (off.unit === "y") d.setFullYear(d.getFullYear() - n);
  const pad = x => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Convert the date-offset string captured by extractLeadingTagPrefix
// (e.g. "1", "1d", "2w", "3mo", "1y", "2026-04-01") into the offset
// object shape expected by applyOffsetToYmd. Returns null if it doesn't
// parse — caller should treat that as "no offset".
function parseOffsetString(s) {
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return { kind: "absolute", iso: s };
  const m = s.match(/^(\d+)(d|w|mo|y)?$/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (!Number.isFinite(n)) return null;
  return { kind: "relative", amount: n, unit: (m[2] || "d").toLowerCase() };
}

function buildEntryDate(dateStr, existingTs) {
  const now = new Date();
  if (!dateStr) return now;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!match) return now;
  const [, yStr, mStr, dStr] = match;
  const y = +yStr, mo = +mStr - 1, dy = +dStr;
  const todayY = now.getFullYear(), todayMo = now.getMonth(), todayDy = now.getDate();
  const isToday = y === todayY && mo === todayMo && dy === todayDy;
  if (isToday) return now;
  // For other days: preserve the existing time if editing, else use noon (12:00:00)
  // as a safe default that won't cross a local-timezone day boundary.
  let h = 12, min = 0, s = 0;
  if (existingTs) {
    const ex = existingTs.toDate ? existingTs.toDate() : new Date(existingTs);
    if (!isNaN(ex.getTime())) { h = ex.getHours(); min = ex.getMinutes(); s = ex.getSeconds(); }
  }
  return new Date(y, mo, dy, h, min, s);
}
const fmtShort = ts => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const toInput = ts => {
  // Convert a Firestore timestamp to the YYYY-MM-DD string format used by <input type="date">.
  // Uses LOCAL date components (not toISOString, which is UTC) so an entry timestamped
  // April 30 at 6:30pm PDT shows as "2026-04-30" in the picker, not "2026-05-01" (UTC tomorrow).
  const d = ts ? (ts.toDate ? ts.toDate() : new Date(ts)) : new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dy}`;
};
// Local-time "today" string for <input type="date"> defaults. Avoids the UTC drift
// that toISOString() introduces in late-evening timezones west of UTC.
const todayInput = () => toInput(new Date());
const getTs = ts => ts ? (ts.toDate ? ts.toDate().getTime() : new Date(ts).getTime()) : 0;

// ─────────────────────────────────────────────────────────────────────────────
// Member access helpers
// ─────────────────────────────────────────────────────────────────────────────
// `members` map values can be either:
//   • a string ("owner" | "contributor" | "viewer") — legacy, no tag filter
//   • an object { role, tags?: string[]|null, includeUntagged?: boolean }
// `tags === null` (or the field missing) means "no filter" — all tags visible.
// `tags === []` means "no tags allowed" — only untagged entries (if includeUntagged).

// Extract the role string from a member entry in either shape.
// Extract the role string from a member entry in either shape.
function getMemberRole(memberEntry) {
  if (!memberEntry) return null;
  if (typeof memberEntry === "string") return memberEntry;
  return memberEntry.role || null;
}

// Returns access settings: { tags: array | null, includeUntagged: boolean }
// `tags = null` means no filter (sees everything). Owner always returns null.
function getMemberAccess(memberEntry) {
  if (!memberEntry) return { tags: null, includeUntagged: true };
  if (typeof memberEntry === "string") {
    return { tags: null, includeUntagged: true }; // legacy = no filter
  }
  if (memberEntry.role === "owner") return { tags: null, includeUntagged: true };
  return {
    tags: Array.isArray(memberEntry.tags) ? memberEntry.tags : null,
    includeUntagged: memberEntry.includeUntagged !== false, // default ON unless explicitly false
  };
}

// Auto-link bare URLs in text nodes (preserves any <a> tags already present).
// Walks the DOM and only modifies text nodes that aren't already inside <a>.
function autolinkHtml(html) {
  if (!html) return "";
  let container;
  try {
    container = document.createElement("div");
    container.innerHTML = html;
  } catch { return html; }
  const URL_RE = /(\b(?:https?:\/\/|www\.)[^\s<>"']+)/gi;
  const walk = node => {
    // Skip nodes inside an existing <a>
    if (node.nodeType === 1 && node.tagName === "A") return;
    if (node.nodeType === 3) { // TEXT_NODE
      const text = node.nodeValue;
      if (!URL_RE.test(text)) return;
      URL_RE.lastIndex = 0;
      const frag = document.createDocumentFragment();
      let lastIdx = 0;
      let m;
      while ((m = URL_RE.exec(text)) !== null) {
        if (m.index > lastIdx) frag.appendChild(document.createTextNode(text.slice(lastIdx, m.index)));
        const raw = m[0];
        // Trim trailing punctuation that's almost certainly not part of the URL
        const trim = raw.replace(/[.,;:!?)\]"']+$/, "");
        const tail = raw.slice(trim.length);
        const href = trim.startsWith("http") ? trim : `https://${trim}`;
        const a = document.createElement("a");
        a.href = href;
        a.textContent = trim;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        frag.appendChild(a);
        if (tail) frag.appendChild(document.createTextNode(tail));
        lastIdx = m.index + raw.length;
      }
      if (lastIdx < text.length) frag.appendChild(document.createTextNode(text.slice(lastIdx)));
      node.parentNode.replaceChild(frag, node);
      return;
    }
    if (node.childNodes) {
      // Snapshot child list because we may replace nodes mid-iteration
      [...node.childNodes].forEach(walk);
    }
  };
  walk(container);
  return container.innerHTML;
}

// Convert plain text → HTML with URLs linkified and newlines preserved as <br>.
// Used for entries that don't have bodyHtml (old entries, plain-text emails).
function textToLinkifiedHtml(text) {
  if (!text) return "";
  // HTML-escape, then convert newlines to <br>, then linkify
  const escaped = String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/\r?\n/g, "<br>");
  return autolinkHtml(escaped);
}

// Robust check for whether HTML body contains any visible content.
// Strips tags, &nbsp;, and standalone <br>/<p> wrappers, then checks for any non-whitespace.
function hasHtmlContent(html) {
  if (!html) return false;
  const stripped = html
    .replace(/<br\s*\/?>/gi, "")
    .replace(/<p[^>]*>\s*<\/p>/gi, "")
    .replace(/<div[^>]*>\s*<\/div>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]+>/g, "")
    .trim();
  return stripped.length > 0;
}

const sanitize = html => {
  if (!html) return "";
  const cleaned = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "").replace(/\son\w+='[^']*'/gi, "")
    .replace(/javascript:/gi, "")
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, "")
    .replace(/(<br\s*\/?>)+$/gi, "")
    .replace(/(<p[^>]*>\s*<\/p>)+$/gi, "").replace(/\s+$/, "").trim();
  return autolinkHtml(cleaned);
};
// Convert HTML to plain text while preserving line breaks. innerText
// respects block-element boundaries (each <div>/<p>/<br> emits a newline)
// where textContent does not — '<div>#letsgo</div><div>#uhs</div>' would
// concatenate to '#letsgo#uhs' via textContent, mangling subsequent tag
// extraction. innerText also normalizes whitespace and CSS-hidden text.
// Falls back to textContent for environments without innerText.
const toText = html => {
  const t = document.createElement("div");
  t.innerHTML = html;
  // Mount-into-DOM trick: innerText only respects layout when the element
  // is in the document. We attach hidden, read, then detach. Skip the
  // mount for empty inputs (faster, no observable difference).
  if (!html) return "";
  document.body.appendChild(t);
  t.style.position = "absolute";
  t.style.left = "-9999px";
  t.style.top = "-9999px";
  t.style.visibility = "hidden";
  const text = t.innerText || t.textContent || "";
  document.body.removeChild(t);
  return text;
};

// Touch devices (phones, tablets) get the native share sheet — fast path for

// Extract a leading "(tag1, tag2) " prefix from a title, plus an optional "!!"
// follow-up marker. Mirrors parseTags + stripTagPrefix in the inbound-email
// Cloud Function so a user typing the same shorthand on the web gets the
// same behavior they'd get by emailing the entry in.
//
// Inputs:
//   title — the raw title string the user typed
// Returns:
//   { tags: string[], cleanedTitle: string }
//
// Notes:
//   - Always strips the prefix when present, even if it parses to zero tags
//     (e.g. "() something") — matches email behavior, and keeps the saved
//     title clean. Trade-off: someone typing "(2nd quarter results) ..." as
//     actual prose loses those words. The common case (intentional tag
//     shorthand) wins; the false positive can be edited after save.
//   - "!!" as a leading or trailing marker (or anywhere preceded by space
//     or "(") adds the "follow-up" tag, matching the email-side convention.
// Token-level pattern for a date offset. Matches "-1", "-1d", "-2w", "-3mo",
// "-1y", or "-2026-04-01". Used to detect when a "(...)" title-prefix
// contains a date shorthand so we can pull it out as a date adjustment
// instead of leaving it as a literal tag. Mirrors the same regex on the
// Cloud Function (index.js).
// Mirror of DATE_OFFSET_TOKEN_RE on the server (index.js). Allows either
// an absolute ISO date YYYY-MM-DD (with OPTIONAL leading "-") OR a relative
// offset (-1, -1d, -2w, -3mo, -1y; "-" required because the unit alone is
// ambiguous). Two capture groups — pick whichever matched.
const TITLE_DATE_OFFSET_RE = /^(?:-?(\d{4}-\d{2}-\d{2})|-(\d+(?:d|w|mo|y)?))$/i;

// Tokens inside a leading "(...)" title prefix that mark an entry PRIVATE
// rather than acting as a literal tag. Case-insensitive. Kept in sync with
// PRIVATE_TAG_TOKENS in the inbound-email Cloud Function (index.js) so the
// web composer and email give identical behavior.
const PRIVATE_TAG_TOKENS = new Set(["private", "priv"]);

/* "$" in the tag parens means the expense tag — with or without the amount
   written onto it ("$", "$5000", "-$400"). Kept identical to MONEY_TOKEN_RE in
   functions/index.js: typing it here and emailing it in must do the same
   thing, or the shorthand is worse than useless. The email side also reads the
   figure off it; here the editor has its own amount box. */
const MONEY_TAG_TOKEN_RE = /^-?\$(?:[0-9]{1,3}(?:,[0-9]{3})+(?:\.[0-9]+)?|[0-9]+(?:\.[0-9]+)?)?$/;

function extractLeadingTagPrefix(title) {
  if (!title || typeof title !== "string") {
    return { tags: [], cleanedTitle: title || "", dateOffset: null };
  }
  const tags = [];
  let cleaned = title;
  let dateOffset = null;  // populated if a -1/-1d/-YYYY-MM-DD token is found inside (...)
  let isPrivate = false;  // set if a "private"/"priv" token is found inside (...)

  // Pull out leading "(tag1, tag2) " — must be at the very start.
  // Inside the parens we also recognize two shortcuts (matching the email
  // pipeline):
  //   - "!" or "!!" → adds "follow-up" instead of literal "!"
  //   - "-1", "-1d", "-2w", "-3mo", "-1y", "-YYYY-MM-DD" → date offset
  //     captured in dateOffset; consumed so it doesn't become a tag.
  //
  // Note: unlike the email handler, we do NOT treat a naked "!!" anywhere
  // in the subject as a follow-up marker. In-app, the user has a
  // dedicated "Follow-up" button in the modal — embedding it in the
  // subject is unnecessary and surprising (e.g. "Wow that was fun!!"
  // would silently add the tag). Email keeps that rule for legacy reasons
  // (typing on phones); in-app uses only the (!...) form.
  const match = cleaned.match(/^\(([^)]+)\)\s*/);
  if (match) {
    const explicit = match[1]
      .split(",")
      .map(t => t.trim().toLowerCase())
      .filter(Boolean);
    for (const t of explicit) {
      // Date offset → capture and skip (don't add as tag). The regex has
      // two capture groups (ISO vs relative); pick whichever matched.
      const offMatch = t.match(TITLE_DATE_OFFSET_RE);
      if (offMatch) {
        if (!dateOffset) dateOffset = offMatch[1] || offMatch[2];
        continue;
      }
      // Bang shorthand → follow-up. Cheat sheet documents only "(!)" but
      // we still accept "(!!)" for users with the old muscle memory.
      if (t === "!" || t === "!!") {
        if (!tags.includes("follow-up")) tags.push("follow-up");
        continue;
      }
      // "$" → expense.
      if (MONEY_TAG_TOKEN_RE.test(t)) {
        if (!tags.includes("expense")) tags.push("expense");
        continue;
      }
      // Private marker → set flag, consume (don't add as a literal tag).
      if (PRIVATE_TAG_TOKENS.has(t)) { isPrivate = true; continue; }
      if (!tags.includes(t)) tags.push(t);
    }
    cleaned = cleaned.slice(match[0].length).trim();
  }

  return { tags, cleanedTitle: cleaned, dateOffset, isPrivate };
}

// Parse a free-form expense title into structured fields, OR return null if
// the title doesn't look like an expense.
//
// Recognized shapes (case-insensitive on "cash"):
//   "vendor / amount / unit / description"           — common expense
//   "cash / vendor / amount / unit / description"   — paid in cash variant
//
// To avoid false positives (e.g. "Note from 4/26: paid Sylvano $1320" has a
// '/' from the date but isn't an expense), the parser is strict:
//   - All 4 segments must be present and non-empty
//   - The amount segment must look like a clean number (digits, optional $,
//     optional decimal, optional commas) and not contain extra words
//   - Empty trailing segments fail the parse
//
// Returns: { cash: bool, vendor: str, amount: Number, unit: str, description: str } | null
function parseExpenseTitle(title) {
  if (!title || typeof title !== "string" || !title.includes("/")) return null;
  const parts = title.split("/").map(s => s.trim());

  let cash = false;
  let offset = 0;
  if (parts[0] && parts[0].toLowerCase() === "cash") {
    cash = true;
    offset = 1;
  }

  // Need exactly 4 parts after the optional cash prefix: vendor, amount,
  // unit, description. More than 4 means the description had "/" — those
  // are joined back. Less than 4 means it's not an expense title.
  const remaining = parts.slice(offset);
  if (remaining.length < 4) return null;

  const vendor = remaining[0];
  const amountRaw = remaining[1];
  const unit = remaining[2];
  const description = remaining.slice(3).join(" / ").trim();

  if (!vendor || !amountRaw || !unit || !description) return null;

  // Strict amount validation: optional $, digits, optional thousands commas,
  // optional decimal. No trailing words. Anything else → not an expense.
  const amountTrimmed = amountRaw.replace(/\s/g, "");
  if (!/^\$?-?[\d,]+(\.\d+)?$/.test(amountTrimmed)) return null;
  const amount = parseFloat(amountTrimmed.replace(/[$,]/g, ""));
  if (!Number.isFinite(amount)) return null;

  return { cash, vendor, amount, unit, description };
}

// Parse structured fields from free-form entry text using shorthand syntax.
// Recognized tokens (any order):
//   $25, $25.50         → amount
//   @park, @"Home Depot" → location (vendor, park, city, restaurant — anywhere)
//   #tag                → tag (multiple #tag tokens supported)
//   w:Name, with:Name, w/Name → people
//   vs:Name, vs.Name    → people (also flags as opponent context)
//   1h, 1.5h, 90min, 30m → time spent (in minutes)
//   1d, 1w              → time spent (1d=1440min, 1w=10080min)
//   3mi, 5km            → distance (in miles, km converted)
//   500cal, cal:500     → calories
//   mood:happy, mood:"so happy" → mood
//   rating:9, rating:9/10, rate:8 → rating
//   6-2 6-3, 7-5 (set scores) → score (tennis-style)
//
// Returns { amount, location, tags, people, timeMinutes, distanceMiles,
//           calories, mood, rating, score, description }.
// `description` is the original text with all matched tokens removed. If a
// field isn't present in the text, it's null/empty.
function parseStructuredFields(text, opts) {
  // opts.knownNames — optional Set of lowercase names that the diary
  // already knows about (member tags, people on previous entries). Used
  // to disambiguate prose 'w/' from a real person trigger:
  //   "w/ Lucas" → captured (capitalized)
  //   "w/ smyan" → captured if 'smyan' is in knownNames
  //   "w/ a bag" → rejected (lowercase + not in knownNames)
  // When knownNames is not provided (e.g., legacy/email-ingest call sites),
  // the parser falls back to capitalization-only filtering.
  const knownNames = (opts && opts.knownNames instanceof Set) ? opts.knownNames : new Set();
  if (!text || typeof text !== "string") {
    return { amount: null, location: [], tags: [], people: [], timeMinutes: null, distanceMiles: null, calories: null, mood: [], rating: "", score: "", customFields: {}, libraryFields: {}, description: text || "" };
  }
  let remaining = text;
  const result = { amount: null, location: [], tags: [], people: [], timeMinutes: null, distanceMiles: null, calories: null, mood: [], rating: "", score: "", customFields: {}, libraryFields: {}, description: "" };

  // Helper: extract first match of a regex, return [matched, value]. Removes
  // the match from `remaining`. Pass a capture-group index for the value.
  const eat = (re, valIdx = 1) => {
    const m = remaining.match(re);
    if (!m) return null;
    remaining = (remaining.slice(0, m.index) + remaining.slice(m.index + m[0].length)).replace(/\s+/g, " ");
    return m[valIdx];
  };
  const eatAll = (re, valIdx = 1) => {
    const out = [];
    let m;
    while ((m = remaining.match(re))) {
      out.push(m[valIdx]);
      remaining = (remaining.slice(0, m.index) + remaining.slice(m.index + m[0].length)).replace(/\s+/g, " ");
    }
    return out;
  };

  // 1. Amount — $ followed by number. Two forms:
  //    - Comma-grouped: requires at least one comma group ($1,234 or $1,234.56)
  //    - Plain: any digits, optional decimal ($50, $1363, $13.63)
  // The comma branch needs the + (one or more comma groups), not * — with
  // *, $1363 would partially match as $136 and stop, since the regex
  // engine accepts the first valid alternation.
  const amt = eat(/\$([0-9]{1,3}(?:,[0-9]{3})+(?:\.[0-9]+)?|[0-9]+(?:\.[0-9]+)?)/);
  if (amt) {
    const n = parseFloat(amt.replace(/,/g, ""));
    if (Number.isFinite(n)) result.amount = n;
  }

  // 1b. If amount wasn't set via $, allow "@<price>" as amount but ONLY
  // when the number has a decimal (looks like a real price: "@25.50",
  // "@9.99"). Bare integers like @2425 or @1995 are too easily confused
  // with locations, addresses, room numbers, or years — they stay as
  // locations.
  if (result.amount === null) {
    const atAmt = eat(/@\s*([0-9]{1,3}(?:,[0-9]{3})+\.[0-9]+|[0-9]+\.[0-9]+)\b/);
    if (atAmt) {
      const n = parseFloat(atAmt.replace(/,/g, ""));
      if (Number.isFinite(n)) result.amount = n;
    }
  }

  // 2. Locations — @"quoted" or @bareword. Allows 1-2 word names (e.g. "@Mr Ma",
   // "@Home Depot"). Multiple locations supported (e.g. "lunch @toms then @smithys").
  // Stored as an array. Allows an optional space after the @ for natural typing.
  const locsQuoted = eatAll(/@\s*"([^"]+)"/);
  for (const l of locsQuoted) result.location.push(l.trim());
  // Bare: 1-2 word names. Cap at 2 words. Stop at lowercase verbs/connectors.
  // First char can be letter OR digit (so '@2425' for an address, '@408'
  // for a room number all capture). Period is NOT part of the name
  // character class — it terminates the location, so '@seqoia. He's'
  // correctly captures only 'seqoia' rather than spanning into the next
  // sentence as 'seqoia. He'.
  const LOC_STOP = new Set(["a", "an", "the", "and", "or", "but", "for", "to", "from", "in", "at", "on", "with", "of"]);
  const locsBare = eatAll(/@\s*([A-Za-z0-9][A-Za-z0-9_-]*(?:\s+[A-Z][A-Za-z0-9_-]*)?)/);
  for (const l of locsBare) {
    let tokens = l.trim().split(/\s+/);
    while (tokens.length && LOC_STOP.has(tokens[tokens.length - 1].toLowerCase())) tokens.pop();
    const cleaned = tokens.slice(0, 2).join(" ").replace(/[.,;:]+$/, "");
    if (cleaned) result.location.push(cleaned);
  }
  // Dedupe locations (case-insensitive)
  if (result.location.length > 1) {
    const seen = new Set();
    result.location = result.location.filter(l => {
      const lc = l.toLowerCase();
      if (seen.has(lc)) return false;
      seen.add(lc);
      return true;
    });
  }

  // 3. Moods — mood:value or mood:"quoted". Optional space after colon.
  // Multiple moods supported in two forms:
  //   - "mood:tired mood:satisfied" (separate prefixes)
  //   - "mood:tired, satisfied, hopeful" (comma-list after one prefix)
  // Stored as an array.
  const moodsQuoted = eatAll(/\bmood:\s*"([^"]+)"/i);
  for (const m of moodsQuoted) result.mood.push(m.trim());
  // Comma-list form: "mood:" followed by 1+ words optionally separated by commas
  const moodsListMatches = eatAll(/\bmood:\s*([A-Za-z][A-Za-z0-9_-]*(?:\s*,\s*[A-Za-z][A-Za-z0-9_-]*)*)/i);
  for (const m of moodsListMatches) {
    const items = m.split(/\s*,\s*/).map(s => s.trim()).filter(Boolean);
    result.mood.push(...items);
  }
  // Natural-language form: "Feeling X" or "Feeling: X" or "I feel X" or
  // "I'm feeling X". Also captures "feeling:awesome" with no space after
  // the colon (compact shorthand). Multiple captures supported via
  // separate occurrences in the text — comma-list within one trigger
  // also supported. Conservative: matches at sentence start or after
  // punctuation, and for 'feeling:' the trigger is unambiguous enough
  // that we accept it anywhere.
  const FEELING_PATTERNS = [
    // "feeling:awesome" or "feeling: awesome" — explicit colon form,
    // case-insensitive. Anchored to word boundary, no leading-punct
    // requirement (the colon is unambiguous).
    /\bfeeling:\s*([A-Za-z][A-Za-z'-]*(?:\s*,\s*[A-Za-z][A-Za-z'-]*){0,3})/gi,
    // "Feeling great" / "feeling exhausted" — no colon, case-insensitive.
    // The FEELING_STOP filter (below) handles prose false positives like
    // "we have a feeling we should leave" — the captured "we" word would
    // be filtered out, so no spurious mood capture happens.
    /\bfeeling\s+([A-Za-z][A-Za-z'-]*(?:\s*,\s*[A-Za-z][A-Za-z'-]*){0,3})/gi,
    // "I feel X" / "I'm feeling X"
    /(?:^|[.!?;]\s+)I(?:'m)?\s+feel(?:ing)?\s+([A-Za-z][A-Za-z'-]*(?:\s*,\s*[A-Za-z][A-Za-z'-]*){0,3})/gi,
  ];
  for (const re of FEELING_PATTERNS) {
    let m;
    while ((m = re.exec(remaining))) {
      const items = m[1].split(/\s*,\s*/).map(s => s.trim().toLowerCase()).filter(Boolean);
      // Stop-word filter — avoids capturing 'I feel that we should...' etc.
      const FEELING_STOP = new Set(["that", "like", "we", "they", "it", "this", "to", "the", "a", "an", "but", "and"]);
      const filtered = items.filter(s => !FEELING_STOP.has(s));
      if (filtered.length) result.mood.push(...filtered);
    }
  }
  // Strip the captured "Feeling X" patterns from `remaining` so they don't
  // re-appear in the leftover description text.
  remaining = remaining
    .replace(/\bfeeling:\s*[A-Za-z][A-Za-z'-]*(?:\s*,\s*[A-Za-z][A-Za-z'-]*){0,3}\.?/gi, " ")
    .replace(/\bfeeling\s+[A-Za-z][A-Za-z'-]*(?:\s*,\s*[A-Za-z][A-Za-z'-]*){0,3}\.?/gi, " ")
    .replace(/(?:^|[.!?;]\s+)I(?:'m)?\s+feel(?:ing)?\s+[A-Za-z][A-Za-z'-]*(?:\s*,\s*[A-Za-z][A-Za-z'-]*){0,3}\.?/gi, " ")
    .replace(/\s+/g, " ");
  // Dedupe moods
  if (result.mood.length > 1) {
    const seen = new Set();
    result.mood = result.mood.filter(m => {
      const lc = m.toLowerCase();
      if (seen.has(lc)) return false;
      seen.add(lc);
      return true;
    });
  }

  // 4. Rating — rating:val or rate:val or rat:val. Optional space after colon.
  const ratingQuoted = eat(/\b(?:rating|rate|rat):\s*"([^"]+)"/i);
  if (ratingQuoted) {
    result.rating = ratingQuoted.trim();
  } else {
    const ratingBare = eat(/\b(?:rating|rate|rat):\s*([0-9]+(?:\/[0-9]+)?|[A-Za-z][A-Za-z0-9_-]*)/i);
    if (ratingBare) result.rating = ratingBare;
  }

  // 5. Calories — 500cal or cal:500 or 500calories
  const calPrefix = eat(/\bcal(?:ories)?:([0-9]+)/i);
  if (calPrefix) {
    result.calories = parseInt(calPrefix, 10);
  } else {
    const calSuffix = eat(/\b([0-9]+)cal(?:ories)?\b/i);
    if (calSuffix) result.calories = parseInt(calSuffix, 10);
  }

  // 6. Distance — 3mi / 5km / "5 miles" / "10 km" / "1/2 mile". Accepts
  // optional space between the number and unit so natural prose ('he ran
  // 5 miles') is captured, not just compact shorthand ('5mi'). Fraction
  // form ("1/2 mile") is tried first because the integer pattern would
  // otherwise match just the "2 mile" part of "1/2 mile" and return 2.
  const dFrac = eat(/\b([0-9]+)\/([0-9]+)\s*mi(?:les?)?\b/i, 0);
  if (dFrac) {
    const fm = dFrac.match(/^([0-9]+)\/([0-9]+)/);
    if (fm && parseFloat(fm[2]) > 0) {
      result.distanceMiles = parseFloat(fm[1]) / parseFloat(fm[2]);
    }
  }
  if (result.distanceMiles == null) {
    const dMi = eat(/\b([0-9]+(?:\.[0-9]+)?)\s*mi(?:les?)?\b/i);
    if (dMi) {
      result.distanceMiles = parseFloat(dMi);
    } else {
      const dKmFrac = eat(/\b([0-9]+)\/([0-9]+)\s*km\b/i, 0);
      if (dKmFrac) {
        const fm = dKmFrac.match(/^([0-9]+)\/([0-9]+)/);
        if (fm && parseFloat(fm[2]) > 0) {
          result.distanceMiles = (parseFloat(fm[1]) / parseFloat(fm[2])) * 0.621371;
        }
      } else {
        const dKm = eat(/\b([0-9]+(?:\.[0-9]+)?)\s*km\b/i);
        if (dKm) result.distanceMiles = parseFloat(dKm) * 0.621371;
      }
    }
  }

  // 7. Time spent — accumulate. Match in order: hours, minutes, days, weeks.
  // Each pattern eats its own match. Multiple time tokens add up:
  // "1h 30min" → 90 minutes total.
  let totalMin = 0;
  let timeFound = false;
  // hours: 1h, 1.5h, 1hour, 1hr, 2hrs
  const hourMatches = eatAll(/\b([0-9]+(?:\.[0-9]+)?)\s*(?:h|hr|hour)s?\b/i);
  for (const h of hourMatches) {
    totalMin += parseFloat(h) * 60;
    timeFound = true;
  }
  // minutes: 30min, 30m, 90minutes. Bare 'm' is allowed as a minute
  // shorthand. Collisions with mid-typing of 'mi'/'miles' are mitigated
  // at the useEffect level (debounced parsing) — the parser only runs
  // ~600ms after the user stops typing, so transient 'm' states during
  // typing toward 'miles' don't fire prematurely.
  const minMatches = eatAll(/\b([0-9]+(?:\.[0-9]+)?)\s*(?:min|minutes?|m)\b/i);
  for (const mn of minMatches) {
    totalMin += parseFloat(mn);
    timeFound = true;
  }
  // days: 1d, 2days
  const dayMatches = eatAll(/\b([0-9]+(?:\.[0-9]+)?)\s*(?:d|days?)\b/i);
  for (const d of dayMatches) {
    totalMin += parseFloat(d) * 1440;
    timeFound = true;
  }
  // weeks: 1w, 2weeks
  const weekMatches = eatAll(/\b([0-9]+(?:\.[0-9]+)?)\s*(?:w|weeks?)\b/i);
  for (const w of weekMatches) {
    totalMin += parseFloat(w) * 10080;
    timeFound = true;
  }
  if (timeFound) result.timeMinutes = Math.round(totalMin);

  // 8. Score — tennis-style "6-2 6-3" or "7-6(3) 6-4". Tiebreaks must be
  // parenthesized or they get confused with the next set's score.
  // Guards against false positives:
  //   1. Negative lookarounds for `/` block date-range fragments
  //   2. Numbers limited to 1-2 digits
  //   3. Post-match validation:
  //        - Single pair: both numbers must be 0-7 (strict tennis range)
  //        - Multi-pair: each pair 0-12 (allows tiebreaks)
  //      Blocks date fragments like "26-10" from "4/27/26-10/27/26"
  //      while still catching real tennis scores.
  const sc = eat(/(?<![\d\/])((?:[0-9]{1,2}-[0-9]{1,2}(?:\([0-9]{1,2}\))?)(?:[\s,]+[0-9]{1,2}-[0-9]{1,2}(?:\([0-9]{1,2}\))?){0,4})(?![\d\/])/);
  if (sc) {
    const pairs = sc.trim().split(/[\s,]+/);
    const valid = (() => {
      const parsePair = (p) => {
        const m = p.match(/^([0-9]+)-([0-9]+)(?:\(([0-9]+)\))?$/);
        if (!m) return null;
        return [parseInt(m[1], 10), parseInt(m[2], 10)];
      };
      if (pairs.length === 1) {
        const pp = parsePair(pairs[0]);
        if (!pp) return false;
        return pp[0] <= 7 && pp[1] <= 7;
      }
      return pairs.every(p => {
        const pp = parsePair(p);
        if (!pp) return false;
        return pp[0] <= 12 && pp[1] <= 12;
      });
    })();
    if (valid) result.score = sc.trim();
  }

  // 9. People — w:Name, with:Name, w/Name, vs:Name, vs.Name. Names can be
  // hyphenated, dotted, lowercase or capitalized. Allows 1-2 word names
  // (e.g., "Johnny", "Coach Sylvano"). Multiple names separated by comma.
  // Conservative — capped at 2 words to avoid grabbing trailing prose.
  const peopleQuoted = eatAll(/\b(?:with:|w:|w\/|vs[:.])\s*"([^"]+)"/i);
  for (const p of peopleQuoted) result.people.push(p.trim());
  // Bare: 1-2 word names. Stop word list prevents grabbing "a friend yesterday"
  // and verbs like "won", "lost" (e.g. "vs. Bob won 6-2" should give "Bob" not "Bob won").
  // The trailing punctuation/period is excluded so "w/ johnny." → "johnny".
  const STOP_WORDS = new Set([
    "a", "an", "the", "my", "our", "their", "his", "her", "and", "or", "but",
    "for", "with", "to", "from", "in", "at", "on", "we", "they", "i", "you", "it",
    // Pronouns commonly following a name (e.g. "w/ McElroy. He adjusted")
    "he", "she", "him", "us", "them", "this", "that", "these", "those",
    // Result verbs commonly typed after "vs:" / "vs." — stop these from being captured as second word of name
    "won", "lost", "tied", "beat", "beats", "drew", "tied", "draw",
    // Common-noun referents that aren't names. People often write
    // "vs. rival" / "vs. everyone" / "with: nobody" — those should be
    // treated as descriptions, not captured as people.
    "rival", "rivals", "opponent", "opponents", "everyone", "everybody",
    "nobody", "noone", "myself", "yourself", "ourselves", "themselves",
    "team", "teammate", "teammates", "friend", "friends", "family",
    // Time words that often follow names ("w/ Bob today", "w/ Sue yesterday")
    "today", "yesterday", "tomorrow", "tonight", "morning", "afternoon",
    "evening", "now", "later", "soon",
  ]);
  // First pass — opponent shorthand (vs:, vs.) — captures people AND flags
  // them as opponents in result.peopleAsOpponents. Used downstream by the
  // title generator to render 'vs.' instead of 'w/'.
  // Regex: name = letter + (letter|digit|hyphen|apostrophe). Period
  // terminates a name (so "smyan. won" doesn't capture as one). Optional
  // second word for two-token names (e.g. "Yi Cheng"). Optional comma list
  // for multiple names.
  // looksLikeName: returns true if a name candidate is likely a real
  // person rather than a prose noun. Heuristic:
  //   - Capitalized first character (Lucas, Smyan, Yi Cheng) → yes
  //   - In knownNames (case-insensitive) → yes (handles user's lowercase
  //     names that were established via earlier entries or member tags)
  //   - Otherwise → no (rejects "w/ a bag", "w/ butter", etc.)
  // The capitalization check is on the FIRST token only — for two-word
  // names ("Yi Cheng"), the first word being capitalized is enough.
  const looksLikeName = (candidate) => {
    if (!candidate) return false;
    const firstToken = candidate.split(/\s+/)[0] || "";
    if (firstToken[0] && firstToken[0] === firstToken[0].toUpperCase() && /[A-Z]/.test(firstToken[0])) {
      return true; // capitalized
    }
    const lc = candidate.toLowerCase();
    if (knownNames.has(lc)) return true;
    // Also check first token alone — "smyan" might be in knownNames even
    // if "smyan won" wasn't (the "won" cleanup removes it later anyway,
    // but we want to accept the trigger here)
    if (knownNames.has(firstToken.toLowerCase())) return true;
    return false;
  };
  const NAME_TOKEN = "[A-Za-z][A-Za-z0-9'-]*";
  // Title prefix: Dr., Mr., Mrs., Ms., Prof., Sr., Jr. — followed by a
  // name token. The period is part of the title, not a terminator. Matches
  // both "Dr. Johnson" and "Dr Johnson". Captured as one logical name.
  const NAME_TITLE = "(?:Dr|Mr|Mrs|Ms|Prof|Sr|Jr)\\.?\\s+";
  const NAME_WITH_TITLE = `(?:${NAME_TITLE})?${NAME_TOKEN}`;
  const NAME_OPT_SECOND = `${NAME_WITH_TITLE}(?:\\s+${NAME_TOKEN})?`;
  const NAME_LIST = `${NAME_OPT_SECOND}(?:\\s*,\\s*${NAME_OPT_SECOND})*`;
  const opponentMatches = eatAll(new RegExp(`\\bvs[:.]\\s*(${NAME_LIST})`, "i"));
  for (const p of opponentMatches) {
    const cleaned = p.split(/\s*,\s*/).map(name => {
      let tokens = name.trim().split(/\s+/).map(t => t.replace(/[.,;:]+$/, ""));
      while (tokens.length && STOP_WORDS.has(tokens[0].toLowerCase())) tokens.shift();
      while (tokens.length && STOP_WORDS.has(tokens[tokens.length - 1].toLowerCase())) tokens.pop();
      return tokens.slice(0, 2).join(" ");
    }).filter(Boolean).filter(looksLikeName);
    if (cleaned.length) {
      result.people.push(...cleaned);
      result.peopleAsOpponents = true;
    }
  }
  // Second pass — companion shorthand (with:, w:, w/). Doesn't set the
  // opponent flag.
  const peopleMatches = eatAll(new RegExp(`\\b(?:with:|w:|w\\/)\\s*(${NAME_LIST})`, "i"));
  for (const p of peopleMatches) {
    const cleaned = p.split(/\s*,\s*/).map(name => {
      // Drop leading stop words from each name (e.g. "a friend" → "friend")
      // Also drop trailing stop words (e.g. "Bob won" → "Bob")
      // Strip trailing periods from each token (e.g. "McElroy." → "McElroy")
      let tokens = name.trim().split(/\s+/).map(t => t.replace(/[.,;:]+$/, ""));
      // Trim leading stops
      while (tokens.length && STOP_WORDS.has(tokens[0].toLowerCase())) tokens.shift();
      // Trim trailing stops
      while (tokens.length && STOP_WORDS.has(tokens[tokens.length - 1].toLowerCase())) tokens.pop();
      // Cap at 2 word tokens for safety
      return tokens.slice(0, 2).join(" ");
    }).filter(Boolean).filter(looksLikeName);
    result.people.push(...cleaned);
  }
  // Dedupe people — same name appearing multiple times (e.g., title and body
  // both contain "vs. Yi Cheng") should only result in one entry.
  if (result.people.length > 1) {
    const seen = new Set();
    result.people = result.people.filter(p => {
      const lc = p.toLowerCase();
      if (seen.has(lc)) return false;
      seen.add(lc);
      return true;
    });
  }

  // 10. Tags — #tag or #"two words". Tags can start with any alphanumeric
  // (including digits like #2427A or #1589sac for property unit numbers).
  // Multiple supported. Comma between is OK.
  const tagsQuoted = eatAll(/#"([^"]+)"/);
  result.tags.push(...tagsQuoted.map(t => t.trim()));
  const tagsBare = eatAll(/#([A-Za-z0-9][A-Za-z0-9_-]*)/);
  // Skip purely-numeric hashtags (#3, #7, #42) — those are almost always
  // position/unit/issue references, not real tags. Quoted form (#"3")
  // still works if a user really wants a numeric tag.
  result.tags.push(...tagsBare.filter(t => !/^\d+$/.test(t)));

  // 11. Custom fields & library fields — generic "word: value" pattern.
  // Excludes built-ins (mood/rating/etc., which were already eaten above)
  // and URL schemes (http, https, mailto, ftp, file, tel). Multi-value
  // supported via comma. Library prefixes (payment, body_part, etc.) get
  // routed to result.libraryFields keyed by canonical name. Everything else
  // goes to result.customFields. Caller filters customFields against the
  // diary's established list.
  const RESERVED_PREFIXES = new Set([
    // Built-ins (most already eaten, but defense-in-depth)
    "mood", "rating", "rate", "rat", "with", "vs", "cal", "calories", "time",
    // URL/scheme prefixes that look like word:value
    "http", "https", "ftp", "file", "tel", "mailto", "data", "ws", "wss",
    // Common false-positive starters
    "note", "p", "ps", "pps", "re", "fwd", "fw",
  ]);
  // Internal helper: route a (fieldName, value) pair to either libraryFields
  // or customFields depending on whether the prefix is library-claimed.
  // Used by both the quoted and bare-value passes below.
  const recordFieldValue = (fieldNameRaw, values) => {
    if (RESERVED_PREFIXES.has(fieldNameRaw)) return;
    if (fieldNameRaw.length < 2) return;
    if (!values.length) return;
    const libraryKey = LIBRARY_PREFIX_TO_KEY[fieldNameRaw];
    if (libraryKey) {
      const def = FIELD_LIBRARY[libraryKey];
      if (def.multi) {
        if (!result.libraryFields[libraryKey]) result.libraryFields[libraryKey] = [];
        result.libraryFields[libraryKey].push(...values);
      } else {
        if (!result.libraryFields[libraryKey]) result.libraryFields[libraryKey] = values[0];
      }
    } else {
      if (!result.customFields[fieldNameRaw]) result.customFields[fieldNameRaw] = [];
      result.customFields[fieldNameRaw].push(...values);
    }
  };
  // Quoted form first — captures multi-word values like
  // `item:"cleaning supplies for the back"` or `property:"2427 A"`.
  // Run BEFORE the bare pass so the quote markers are eaten and the bare
  // regex doesn't try to match the prefix again on whatever's left.
  const customQuoted = eatAll(/\b([A-Za-z][A-Za-z0-9_-]{0,15}):\s*"([^"]+)"/, 0);
  for (const fullMatch of customQuoted) {
    const colonIdx = fullMatch.indexOf(":");
    if (colonIdx < 0) continue;
    const fieldNameRaw = fullMatch.slice(0, colonIdx).toLowerCase();
    const m = fullMatch.match(/"([^"]+)"/);
    const value = m ? m[1].trim() : "";
    if (!value) continue;
    recordFieldValue(fieldNameRaw, [value]);
  }
  const customMatches = eatAll(/\b([A-Za-z][A-Za-z0-9_-]{0,15}):\s*([A-Za-z0-9][A-Za-z0-9_-]*(?:\s*,\s*[A-Za-z0-9][A-Za-z0-9_-]*)*)/, 0);
  for (const fullMatch of customMatches) {
    const colonIdx = fullMatch.indexOf(":");
    if (colonIdx < 0) continue;
    const fieldNameRaw = fullMatch.slice(0, colonIdx).toLowerCase();
    const valuePart = fullMatch.slice(colonIdx + 1).trim();
    const values = valuePart.split(/\s*,\s*/).map(v => v.trim()).filter(Boolean);
    recordFieldValue(fieldNameRaw, values);
  }
  // Dedupe each multi-value list
  for (const k of Object.keys(result.customFields)) {
    const seen = new Set();
    result.customFields[k] = result.customFields[k].filter(v => {
      const lc = v.toLowerCase();
      if (seen.has(lc)) return false;
      seen.add(lc);
      return true;
    });
  }
  for (const k of Object.keys(result.libraryFields)) {
    const v = result.libraryFields[k];
    if (Array.isArray(v)) {
      const seen = new Set();
      result.libraryFields[k] = v.filter(val => {
        const lc = val.toLowerCase();
        if (seen.has(lc)) return false;
        seen.add(lc);
        return true;
      });
    }
  }

  // Whatever's left, with stray punctuation trimmed, is the description.
  result.description = remaining.replace(/\s*,\s*$/, "").replace(/\s{2,}/g, " ").trim();
  return result;
}

// Auto-generate a readable title from a body + structured fields object.
// Format depends on entry type — detected by which fields are present.
// Used by the CSV import path (when subject is blank) AND the web composer
// (to show a suggestion or to fill the subject on save).
//
// Goal: clean prose-style line. No #tags (those show as chips elsewhere).
// No mood:/rating: prefixes — natural prepositions instead.
//
// IMPORTANT: when body is long (>80 chars or multi-sentence), we DON'T
// append structured-field tokens. The user's prose already says it all,
// and double-printing structured fields makes the title nonsensical.
// Instead, we use a truncated first sentence as the title.
function generateTitleFromStructured(body, structured) {
  if (!structured && !body) return "";
  const s = structured || {};

  // Coerce location/mood — they may be string (old entries) or array (new).
  // Returns comma-joined display string, or empty string if no values.
  const fmtList = (val) => {
    if (!val) return "";
    if (Array.isArray(val)) return val.length ? val.join(", ") : "";
    return val; // string
  };
  const locStr = fmtList(s.location);
  const moodStr = fmtList(s.mood);

  // Time formatting helper
  const fmtTime = (min) => {
    if (min == null) return "";
    if (min >= 60 && min % 60 === 0) return `${min / 60}h`;
    if (min >= 60 && min % 30 === 0) return `${(min / 60).toFixed(1)}h`;
    if (min >= 60) return `${Math.floor(min / 60)}h ${min % 60}min`;
    return `${min}min`;
  };

  // Strip shorthand tokens from the body so they don't double-print in the
  // title. Used for prose-path titles AND for canonical-format bodies.
  // Removes: $25, @location, #tag, w:Person, vs:Person, mood:X, rating:X,
  // 1.5h, 3mi, 500cal, score patterns. Leaves natural-language prose.
  const stripShorthand = (text) => {
    if (!text) return "";
    let out = text;
    out = out.replace(/\$[0-9]+(?:\.[0-9]+)?/g, "");
    out = out.replace(/@\s*"[^"]+"/g, "");
    out = out.replace(/@\s*[A-Za-z0-9][A-Za-z0-9_\-.]*(?:\s+[A-Z][A-Za-z0-9_\-.]*)?/g, "");
    out = out.replace(/#"([^"]+)"/g, " ");
    out = out.replace(/#([A-Za-z0-9][A-Za-z0-9_-]*)/g, " ");
    out = out.replace(/\b(?:with:|w:|w\/|vs[:.])\s*"[^"]+"/gi, "");
    out = out.replace(/\b(?:with:|w:|w\/|vs[:.])\s*[A-Za-z][A-Za-z0-9_.-]*(?:\s+[A-Za-z][A-Za-z0-9_.-]*)?/gi, "");
    out = out.replace(/\bmood:\s*"[^"]+"/gi, "");
    out = out.replace(/\bmood:\s*[A-Za-z][A-Za-z0-9_-]*(?:\s*,\s*[A-Za-z][A-Za-z0-9_-]*)*/gi, "");
    out = out.replace(/\b(?:rating|rate|rat):\s*[0-9A-Za-z\/]+/gi, "");
    out = out.replace(/\b[0-9]+(?:\.[0-9]+)?\s*(?:h|hr|hour|min|minutes?|m|d|days?|w|weeks?)\b/gi, "");
    out = out.replace(/\b[0-9]+(?:\.[0-9]+)?\s*(?:mi|miles?|km)\b/gi, "");
    out = out.replace(/\bcal(?:ories)?:[0-9]+|[0-9]+cal(?:ories)?\b/gi, "");
    // Strip tennis score patterns ("6-2 6-3", "7-6(3) 6-4")
    out = out.replace(/\b[0-9]+-[0-9]+(?:\([0-9]+\))?(?:[\s,]+[0-9]+-[0-9]+(?:\([0-9]+\))?){0,4}\b/g, "");
    // Strip library + custom field shorthand: any "word: value" pattern that
    // wasn't a built-in (mood/rating/etc.) — those have already been stripped
    // above. This includes library prefixes (cat:, pay:, body:, ...) and
    // any user-defined custom field name. Conservative: only strip when value
    // is a single token (not a multi-word phrase that could be real prose).
    out = out.replace(/\b[A-Za-z][A-Za-z0-9_-]{0,15}:\s*[A-Za-z][A-Za-z0-9_-]*(?:\s*,\s*[A-Za-z][A-Za-z0-9_-]*)*/g, "");
    out = out.replace(/\s{2,}/g, " ").replace(/\s+([.,!?])/g, "$1").trim();
    out = out.replace(/^[,.\s]+/, "").trim();
    return out;
  };

  // Take just the first sentence (or first ~80 chars) of a string. Used in
  // canonical-format titles where body becomes part of the title — full body
  // shows below the title in the UI, so the title only needs a quick
  // descriptor.
  const firstSentence = (text) => {
    if (!text) return "";
    // Match up to first sentence-terminating punctuation, NOT preceded by
    // a known abbreviation (Mr., Mrs., Dr., St., vs.). Any character may
    // follow — we don't require the next word to be capitalized, since
    // the body might continue lowercase ("played for. walked").
    const m = text.match(/^[\s\S]*?(?:(?<!\bvs)(?<!\bMr)(?<!\bMrs)(?<!\bDr)(?<!\bSt)[.!?])/);
    let s = m ? m[0].trim() : text.trim();
    s = s.replace(/\.{2,}$/, ".");
    if (s.length > 80) {
      const cutoff = s.lastIndexOf(" ", 77);
      s = (cutoff > 40 ? s.slice(0, cutoff) : s.slice(0, 77)) + "…";
    }
    return s;
  };

  // Strip the body upfront — same input for all formatters. Then take just
  // the first sentence so titles stay concise.
  const cleanBody = firstSentence(stripShorthand(body));

  // Build a natural-language tail for library + custom fields (so canonical
  // titles read like "$5 @home for supplies, plumbing, paid with cash" rather
  // than "$5 @home for supplies cat:plumbing pay:cash"). Conservative: only
  // includes fields known to be in FIELD_LIBRARY; custom fields are not tail-
  // appended (they're free-form, no natural phrasing — they appear as chips).
  const libraryTail = (() => {
    const lib = s.library || {};
    if (!lib || !Object.keys(lib).length) return "";
    const phrases = [];
    // Hand-tuned phrasings per field key. Falls back to "label: value" if not listed.
    const formatLib = (key, value) => {
      const v = Array.isArray(value) ? value.join(", ") : value;
      switch (key) {
        case "payment_type": return `paid with ${v}`;
        case "spend_category": return v; // bare — body usually has its own context word
        case "body_part": return v;
        case "medication": return `(${v})`;
        case "subject": return v;
        case "cuisine": return `(${v})`;
        case "project": return `[${v}]`;
        case "symptom": return v;
        default:
          const def = FIELD_LIBRARY[key];
          return def ? `${def.label.toLowerCase()}: ${v}` : `${key}: ${v}`;
      }
    };
    for (const [k, v] of Object.entries(lib)) {
      if (v == null || (Array.isArray(v) && !v.length)) continue;
      phrases.push(formatLib(k, v));
    }
    return phrases.join(", ");
  })();

  // Headline-fields heuristic: an entry is in STRUCTURED regime if it has
  // any of score / distanceMiles / amount / rating set. Those fields define
  // what the entry is "about" and earn title-billing. Other structured
  // fields (location, time, mood, calories, custom) appear as chips below
  // the title — putting them in the title too creates noise.
  //
  // Rating triggers a "review" pattern (e.g. restaurants, products, places).
  // The body is repurposed as the item descriptor for expense and review
  // entries (the body for those is usually a noun phrase like "Pizza Hut"
  // or "groceries"), and dropped for sport entries (where the body is
  // typically commentary like "tough match" that doesn't identify the
  // entry — the headline does).
  //
  // If none of the headline fields are set, fall through to the prose path
  // below — the body becomes the title (e.g. doctor visits, journal
  // entries, generic notes). Their structured fields all show as chips.
  const hasHeadline = s.score || s.distanceMiles != null || s.amount != null || s.rating;
  if (hasHeadline) {
    // Format people prefix consistently across headline types.
    // peopleAsOpponents (set when 'vs:'/'vs.' shorthand was used) → "vs.";
    // anywhere else (icon, w:, w/) → "w/" (companion framing).
    const peoplePart = (s.people && s.people.length)
      ? `${s.peopleAsOpponents ? "vs." : "w/"} ${s.people.join(", ")}`
      : "";

    // For expense and review entries, the body acts as the "item" — what
    // was bought / what was reviewed. Take the first few words as a tight
    // noun phrase. Strip shorthand and trailing punctuation.
    const itemFromBody = (() => {
      if (!cleanBody) return "";
      // Take up to ~6 words (~40 chars) as a compact item descriptor.
      let trimmed = cleanBody.replace(/[.!?]+$/, "").trim();
      let words = trimmed.split(/\s+/).slice(0, 6);
      // Strip trailing prepositions/articles/connectives that result from
      // shorthand removal. "played for 5m" → "played for." → "played for"
      // becomes just "played" since "for" is a hanging preposition.
      const TRAIL_STOP = new Set([
        "for", "in", "at", "on", "with", "to", "from", "of", "by", "the",
        "a", "an", "and", "or", "but", "is", "was", "were", "are",
      ]);
      while (words.length && TRAIL_STOP.has(words[words.length - 1].toLowerCase().replace(/[.,;:]+$/, ""))) {
        words.pop();
      }
      const result = words.join(" ");
      return result.length > 40 ? result.slice(0, 38).trimEnd() + "…" : result;
    })();

    // Body prefix: the words that appear in the body BEFORE the first
    // structured-shorthand token. Short prefixes (≤3 words) are kept as
    // context for the headline ("riley played w/ Cahan 6-2"). Longer
    // prefixes are dropped — they read too noisy as title fragments.
    // Detection: find the earliest position of any shorthand trigger.
    const bodyPrefix = (() => {
      if (!body) return "";
      // Patterns that mark the start of structured data in the body
      const triggers = [
        /\bw\//i, /\bwith:/i, /\bw:/i, /\bvs[:.]/i,
        /\$[0-9]/, /@\w/, /#\w/,
        /\b[0-9]+(?:\.[0-9]+)?\s*(?:mi|miles?|km|h|hr|hour|min|minutes?|cal|calories?)\b/i,
        /\b(?:mood|rating|rate|score|cal|cals|calories):/i,
      ];
      let earliest = body.length;
      for (const re of triggers) {
        const m = body.match(re);
        if (m && m.index !== undefined && m.index < earliest) earliest = m.index;
      }
      if (earliest === 0 || earliest === body.length) return "";
      // Take prefix, drop trailing punct/whitespace, collapse spaces
      let prefix = body.slice(0, earliest).trim().replace(/[\s,.;:]+$/, "").trim();
      if (!prefix) return "";
      // Word count cap — only short, headline-ish prefixes
      const words = prefix.split(/\s+/);
      if (words.length > 3) return "";
      return prefix;
    })();

    // Collect headline tokens in priority order. Score takes the strong
    // first slot (it's the resolution of a match); distance and amount
    // each get their own. People always pairs with whichever headline is
    // present. Each headline group is space-joined; multiple groups are
    // mid-dot separated.
    const headlineGroups = [];
    if (s.score) {
      // Match: people + score
      const grp = [];
      if (peoplePart) grp.push(peoplePart);
      grp.push(s.score.replace(/\s+/g, ", "));
      headlineGroups.push(grp.join(" "));
    } else if (s.distanceMiles != null) {
      // Run / activity: people + distance. We deliberately do NOT include
      // a body excerpt — when shorthand (5m, 1hr, 5miles) is stripped from
      // prose like "played for 1hr walked for 1mi", the leftover fragments
      // ("played for walked for") render awkwardly in titles. The body
      // shows in full below the title, so excerpting it again is redundant.
      const grp = [];
      if (peoplePart) grp.push(peoplePart);
      grp.push(`${s.distanceMiles}mi`);
      headlineGroups.push(grp.join(" "));
    } else if (s.amount != null) {
      // Expense: $amount + payment + @location + item (body) + people
      const lib = s.library || {};
      const payment = lib.payment_type;
      const category = lib.spend_category;
      const grp = [`$${s.amount}`];
      if (payment) grp.push(String(payment).toLowerCase());
      // Truncate long location names. Full vendor names like "Ace Plumbing
      // and Rooter Inc." are too verbose in a title. Take the first 2-3
      // words (up to ~20 chars) — keeps the title scannable while still
      // identifying the place. Full name remains in the Location chip.
      if (locStr) {
        const shortLoc = (() => {
          const words = locStr.split(/\s+/);
          if (words.length <= 2 && locStr.length <= 22) return locStr;
          // Take first 2 words; if that's still over 22 chars, truncate.
          let result = words.slice(0, 2).join(" ");
          if (result.length > 22) result = result.slice(0, 20).trimEnd() + "…";
          return result;
        })();
        grp.push(`@${shortLoc}`);
      }
      // Skip itemFromBody if bodyPrefix already covers the same content
      // (avoids "Paid May $1363 Paid May" duplication when amount sits
      // at the end of a short body).
      if (itemFromBody && !(bodyPrefix && bodyPrefix.toLowerCase().includes(itemFromBody.toLowerCase()))) {
        grp.push(itemFromBody);
      }
      if (peoplePart) grp.push(peoplePart);
      if (category) grp.push(String(category));
      headlineGroups.push(grp.join(" "));
    } else if (s.rating) {
      // Review: @location + ★ rating + item (body)
      const grp = [];
      if (locStr) grp.push(`@${locStr}`);
      grp.push(`★ ${s.rating}`);
      if (itemFromBody && !(bodyPrefix && bodyPrefix.toLowerCase().includes(itemFromBody.toLowerCase()))) {
        grp.push(itemFromBody);
      }
      headlineGroups.push(grp.join(" "));
    }

    // Secondary fields appended with mid-dot separators.
    const secondary = [];
    // For score entries, distance/time are secondary
    if (s.score) {
      if (s.distanceMiles != null) secondary.push(`${s.distanceMiles}mi`);
      if (s.timeMinutes != null) secondary.push(fmtTime(s.timeMinutes));
    }
    // For distance entries, time + location are secondary
    if (s.distanceMiles != null && !s.score) {
      if (s.timeMinutes != null) secondary.push(fmtTime(s.timeMinutes));
      if (locStr) secondary.push(`@${locStr}`);
      if (s.calories != null) secondary.push(`${s.calories}cal`);
    }
    // Amount as secondary — when score/distance/rating took primary but
    // an amount was also captured (e.g., a copay on a doctor visit, an
    // entry fee on a race). When amount itself is the headline, this skips.
    const amountIsPrimary = s.amount != null && !s.score && s.distanceMiles == null && !s.rating;
    if (s.amount != null && !amountIsPrimary) {
      secondary.push(`$${s.amount}`);
    }
    // Rating shown as secondary on score/distance entries (when not the headline)
    if ((s.score || s.distanceMiles != null) && s.rating) {
      secondary.push(`★ ${s.rating}`);
    }
    // Mood is universal — if the user took the time to enter it, they
    // wanted it visible. Appears on every structured title when set.
    if (moodStr) secondary.push(`Feeling ${moodStr}`);

    // Compose: [body prefix] · headline groups · secondary fields. The
    // bodyPrefix (≤3 words like "riley played" or "Bought") provides
    // short context that's otherwise lost when structured fields claim
    // the title. Empty when body has no short prefix or the structured
    // tokens start the body directly.
    const all = bodyPrefix
      ? [`${bodyPrefix} ${headlineGroups[0] || ""}`.trim(), ...headlineGroups.slice(1), ...secondary]
      : [...headlineGroups, ...secondary];
    return all.join(" · ");
  }

  // No structured-format type matched. Fall back to prose-style title.
  // Use ORIGINAL body (don't strip shorthand) and append mood/rating if not
  // already in body. The shorthand IS the natural-language version here.
  const wordCount = body ? body.trim().split(/\s+/).length : 0;
  const hasConnectors = body && /\b(for|at|with|w\/|vs\.?|to|from|on|in)\b/i.test(body);
  const isProse = body && (wordCount > 4 || hasConnectors);
  if (isProse) {
    // Strip helper for individual sentences. Removes hashtags + library/custom
    // shorthand AND leading orphan punctuation (so "Mood:tired." doesn't end
    // up as "." after the colon-shorthand is removed).
    const stripForTitle = (sent) => {
      if (!sent) return "";
      return sent
        .replace(/#"([^"]+)"/g, " ")
        .replace(/#([A-Za-z0-9][A-Za-z0-9_-]*)/g, " ")
        .replace(/\bmood:\s*"[^"]+"/gi, "")
        .replace(/\bmood:\s*[A-Za-z][A-Za-z0-9_-]*(?:\s*,\s*[A-Za-z][A-Za-z0-9_-]*)*/gi, "")
        .replace(/\b(?:rating|rate|rat):\s*[0-9A-Za-z\/]+/gi, "")
        .replace(/\b[A-Za-z][A-Za-z0-9_-]{0,15}:\s*[A-Za-z][A-Za-z0-9_-]*(?:\s*,\s*[A-Za-z][A-Za-z0-9_-]*)*/g, "")
        .replace(/\s{2,}/g, " ").replace(/\s+([.,!?])/g, "$1")
        .replace(/^[,.;:!?\s]+/, "").trim();
    };
    const isMeaningful = (str) => str && /[A-Za-z]/.test(str) && str.length >= 4;
    // Split body into rough sentences (terminator + space + capital).
    const splitSentences = (text) => !text ? []
      : text.split(/(?<=[.!?])\s*(?=[A-Z])/).map(t => t.trim()).filter(Boolean);

    // Anchor patterns identify sentences that mention an extracted structured
    // field. When the first sentence strips to nothing (e.g. body opens with
    // "Mood:tired."), we prefer a sentence containing one of these because it
    // carries the most context — that's the snippet the user most wants in
    // the title (e.g. "5.5h sleep" from a body mentioning sleep duration).
    const anchorPatterns = [];
    if (s.timeMinutes != null) anchorPatterns.push(/\b\d+(?:\.\d+)?\s*(?:h|hr|hours?|min|minutes?)\b/i);
    if (s.distanceMiles != null) anchorPatterns.push(/\b\d+(?:\.\d+)?\s*(?:mi|miles?|km)\b/i);
    if (s.calories != null) anchorPatterns.push(/\b\d+\s*cal\b/i);
    if (s.amount != null) anchorPatterns.push(/\$\s*\d+(?:\.\d+)?/);

    // Extract a tight noun-phrase around the anchor: anchor + up to 3 words
    // after it. Skip leading/trailing prepositions and articles, stop at
    // adverbial junk like "yesterday" or conjunctions. Yields phrases like
    // "5.5h sleep", "$56 groceries", "30min meditation".
    const fillerWords = new Set(['of','a','an','in','on','at','for','to','with','from','by','the']);
    const stopWords = new Set(['yesterday','today','tomorrow','and','but','or','so','then','because','while']);
    const extractAnchorPhrase = (sent) => {
      for (const re of anchorPatterns) {
        const grab = new RegExp(`(${re.source})\\s*([^.!?,;]{0,40})`, re.flags);
        const m = sent.match(grab);
        if (!m) continue;
        const anchor = m[1].trim();
        const after = (m[2] || "").trim();
        if (!after) return null;
        const tokens = after.split(/\s+/);
        const out = [];
        let started = false;
        for (const tok of tokens) {
          if (out.length >= 3) break;
          const clean = tok.replace(/[.,!?;:]+$/, '');
          if (!clean) continue;
          const lc = clean.toLowerCase();
          if (!started && fillerWords.has(lc)) continue;
          if (stopWords.has(lc)) break;
          out.push(clean);
          started = true;
        }
        while (out.length && fillerWords.has(out[out.length-1].toLowerCase())) out.pop();
        if (!out.length) return null;
        return `${anchor} ${out.join(' ')}`;
      }
      return null;
    };

    // Try existing first-sentence approach
    let title = body.trim();
    const sentenceMatch = title.match(/^[\s\S]*?(?:(?<!\bvs)(?<!\bMr)(?<!\bMrs)(?<!\bDr)(?<!\bSt)[.!?])(?=\s+[A-Z]|[A-Z]|\s*$)/);
    if (sentenceMatch && sentenceMatch[0].length < title.length) {
      title = sentenceMatch[0].trim();
    }
    title = stripForTitle(title);

    // If first sentence stripped to empty/punctuation, find a better sentence:
    // prefer one mentioning an extracted field (anchor phrase), else fall
    // back to the first meaningful sentence.
    let usedFallback = false;
    if (!isMeaningful(title)) {
      const sentences = splitSentences(body);
      for (const sent of sentences) {
        const phrase = extractAnchorPhrase(sent);
        if (phrase) { title = phrase; usedFallback = true; break; }
      }
      if (!isMeaningful(title)) {
        for (const sent of sentences) {
          const stripped = stripForTitle(sent);
          if (isMeaningful(stripped)) { title = stripped; usedFallback = true; break; }
        }
      }
    }

    if (title.length > 120) {
      const cutoff = title.lastIndexOf(" ", 117);
      title = (cutoff > 60 ? title.slice(0, cutoff) : title.slice(0, 117)) + "…";
    }
    const titleLc = title.toLowerCase();
    const containsAny = (vals) => {
      if (!vals) return false;
      const arr = Array.isArray(vals) ? vals : [vals];
      return arr.some(v => v && titleLc.includes(String(v).toLowerCase()));
    };
    const tail = [];
    if (moodStr && !containsAny(s.mood)) tail.push(`Feeling ${moodStr}`);
    if (s.rating && !titleLc.includes(String(s.rating).toLowerCase())) {
      tail.push(`rating ${s.rating}`);
    }
    // Format: when first-sentence was usable, append mood/rating tail at the
    // end (existing behavior). When we fell back to a non-first sentence
    // (anchor phrase or later sentence), the body's natural lead is gone, so
    // the mood/rating phrase becomes the headline — separated from the
    // body snippet with a middot for readability:
    //   "Feeling tired, excited · 5.5h sleep"
    if (tail.length) {
      if (usedFallback && title) {
        title = `${tail.join(" ")} · ${title}`;
      } else {
        title = title ? `${title} ${tail.join(" ")}` : tail.join(" ");
      }
    }
    if (libraryTail) {
      title = title.replace(/[.\s]+$/, "");
      title = `${title}, ${libraryTail}`;
    }
    return title;
  }

  // Generic / fallback: short body or empty body, no structured-format type.
  const parts = [];
  if (cleanBody) parts.push(cleanBody);
  if (locStr) parts.push(`@${locStr}`);
  if (s.people && s.people.length) parts.push(`w/ ${s.people.join(", ")}`);
  if (moodStr) parts.push(`Feeling ${moodStr}`);
  if (s.rating) parts.push(`rating ${s.rating}`);
  let title = parts.join(" ");
  if (libraryTail) {
    title = title.replace(/[.\s]+$/, "");
    title = title ? `${title}, ${libraryTail}` : libraryTail;
  }
  return title;
}

// Touch devices (phones, tablets) get the native share sheet — fast path for
// picking Mail/Messages/etc. with files pre-attached. Desktop browsers should
// always do a plain download instead, since their "share sheet" (e.g. Windows
// 11's) is for sharing-to-people, not saving-to-disk. Detection via pointer
// media query is reliable across browsers and doesn't rely on user-agent sniffing.
const isTouchDevice = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(pointer: coarse)").matches;

// iOS Safari has a known issue where calling navigator.share({ files }) with
// a PDF opens the file in a Quick Look overlay; when the user dismisses that
// overlay, Safari unwinds the history stack by one step — bouncing the user
// from /diary/<id> back to /dashboard. To avoid that, we treat iOS as
// download-only for file shares, the same as desktop. Android's share sheet
// doesn't have this problem, so it still gets the native share path.
const isIOS = () =>
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports as "MacIntel" but has touch — distinguish from desktop Mac.
    (navigator.userAgent.includes("Mac") && navigator.maxTouchPoints > 1));

// Returns true if we should use navigator.share({ files }) to attach a file.
// Touch device + canShare + NOT iOS (because of the history-unwind quirk).
const canShareFiles = (file) => {
  if (!isTouchDevice()) return false;
  if (isIOS()) return false;
  return navigator.canShare && navigator.canShare({ files: [file] });
};

const formatRelativeTime = (date) => {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMs < 60000) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? "" : "s"} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
  return "on " + d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// ── REAL-TIME TAG SUGGESTIONS ─────────────────────────────────────────────────

function TagSuggestions({ text, currentTags, onAdd }) {
  const [suggestions, setSuggestions] = useState([]);
  useEffect(() => {
    const inferred = inferTagsFromText(text).filter(t => !isFollowupTag(t));
    setSuggestions(inferred.filter(t => !currentTags.includes(t)));
  }, [text, currentTags]);
  if (!suggestions.length) return null;
  return (
    <div style={{ background: "rgba(200,56,3,0.04)", border: "1px solid rgba(200,56,3,0.15)", borderRadius: "8px", padding: "10px 14px", marginBottom: "14px" }}>
      <div style={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "7px", fontFamily: "-apple-system,sans-serif" }}>✦ We noticed</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {suggestions.map(tag => {
          const m = getTagMeta(tag);
          return (
            <button key={tag} onClick={() => onAdd(tag)}
              style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: m.bg, color: m.color, border: `1px solid ${m.border}`, borderRadius: "20px", padding: "4px 12px", fontSize: "13px", fontWeight: "600", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
              {m.emoji} {tag} <span style={{ fontSize: "11px", opacity: 0.6 }}>+ add</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── TAG BADGES ────────────────────────────────────────────────────────────────

function TagBadges({ tags, autoTagged = false, isRecordsMode = false }) {
  const displayTags = (tags || []).filter(t => !isFollowupTag(t));
  if (!displayTags.length) return null;
  const radius = isRecordsMode ? "4px" : "20px";
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: "6px" }}>
      {displayTags.map((tag, i) => {
        const m = getTagMeta(tag);
        return (
          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "3px", background: m.bg, color: m.color, border: `1px solid ${m.border}`, borderRadius: radius, padding: isRecordsMode ? "2px 8px" : "3px 10px", fontSize: "12px", fontWeight: "600", fontFamily: "-apple-system,sans-serif" }}>
            {m.emoji} {tag}
            {autoTagged && i === 0 && <span style={{ fontSize: "9px", opacity: 0.4, marginLeft: "1px" }}>✦</span>}
          </span>
        );
      })}
    </div>
  );
}

// ── TAG INPUT ─────────────────────────────────────────────────────────────────

function TagInput({ value, onChange, bodyText }) {
  const currentTags = value.split(",").map(t => t.trim().toLowerCase()).filter(Boolean);
  const handleAddSuggested = tag => {
    const existing = value.split(",").map(t => t.trim().toLowerCase()).filter(Boolean);
    if (!existing.includes(tag)) onChange(existing.length ? existing.join(", ") + ", " + tag : tag);
  };
  return (
    <>
      <label style={{ display: "block", fontSize: "11px", color: "#999", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
        Tags <span style={{ color: "#ccc", fontWeight: "400", textTransform: "none" }}>(optional — we'll detect automatically)</span>
      </label>
      <input type="text" value={value} onChange={e => onChange(e.target.value)}
        placeholder="tennis, food, family..."
        style={{ width: "100%", background: "#f8f8f8", border: "1px solid #ddd", borderRadius: "8px", padding: "10px 14px", color: "#333", fontSize: "14px", fontFamily: "-apple-system,sans-serif", outline: "none", marginBottom: "10px", boxSizing: "border-box" }} />
      {currentTags.filter(t => !isFollowupTag(t)).length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "12px" }}>
          {currentTags.filter(t => !isFollowupTag(t)).map((tag, i) => {
            const m = getTagMeta(tag);
            return <span key={i} style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}`, borderRadius: "12px", padding: "2px 9px", fontSize: "12px", fontWeight: "600", fontFamily: "-apple-system,sans-serif" }}>{m.emoji} {tag}</span>;
          })}
        </div>
      )}
      <TagSuggestions text={bodyText} currentTags={currentTags} onAdd={handleAddSuggested} />
    </>
  );
}

// ── STRUCTURED FIELDS ROW (icon shortcuts for amount/location/etc.) ─────────

// Shows a row of small icon buttons for each structured field. Tapping an
// icon opens an inline input where the user types the value. On enter or
// blur, the value is committed to the structured object and the input
// collapses back. Fields that already have values render as chips.
function StructuredFieldsRow({ structured, onChange, editing, setEditing, draft, setDraft, onTouch, hiddenBuiltins, enabledLibrary, allCustomFields }) {
  // Track whether we've already shown the first-time blink hint on the
  // structured-field icons. Stored in localStorage so it shows once, ever.
  // We read on mount and stamp the key on first interaction (any field
  // click or open) so the next render in the same session also stops
  // the blink — not just the next page load.
  const [hasSeenStructHint, setHasSeenStructHint] = useState(() => {
    try { return !!localStorage.getItem("ltSeenStructFieldHint"); }
    catch { return true; } // fail-safe — if storage is blocked, suppress the blink
  });
  const markSeenStructHint = () => {
    if (hasSeenStructHint) return;
    try { localStorage.setItem("ltSeenStructFieldHint", "1"); } catch {}
    setHasSeenStructHint(true);
  };

  // What each structured field is for, shown as a faded placeholder when
  // the input is open and as a fast hover tooltip when not. Mobile users
  // can't hover so the placeholder is the primary affordance for them.
  const FIELD_DESCRIPTIONS = {
    amount: "How much it cost (e.g. 25.50 — added as $25.50)",
    location: "Where (e.g. Lowes, home, the park)",
    people: "Who you were with (comma-separated)",
    timeMinutes: "How long (45 = 45 min, 1.5 = 90 min, or '1h 30m')",
    distanceMiles: "How far you went, in miles",
    calories: "Calories consumed or burned",
    mood: "How you felt (e.g. tired, energized, content)",
    rating: "Star rating or score (e.g. 4/5, 8/10, A+)",
    score: "Game / match score (e.g. 6-3, 6-2)",
  };
  const customDescription = (name) => `Tag this entry with a custom value for "${name}"`;
  const FIELD_KEY_TO_BUILTIN_DESC = (key) => {
    if (key.startsWith("custom.")) return customDescription(key.slice("custom.".length));
    if (FIELD_LIBRARY[key]) return FIELD_LIBRARY[key].description || `Custom value for ${FIELD_LIBRARY[key].label}`;
    return FIELD_DESCRIPTIONS[key] || "";
  };

  // Helper: get value at a possibly-nested key path (e.g., "custom.shot")
  const getAt = (obj, path) => {
    const parts = path.split(".");
    let cur = obj;
    for (const p of parts) {
      if (!cur || typeof cur !== "object") return undefined;
      cur = cur[p];
    }
    return cur;
  };
  // Helper: return new object with `value` set at `path`. If value is null/empty, deletes the key.
  const setAt = (obj, path, value) => {
    const parts = path.split(".");
    const next = { ...obj };
    if (parts.length === 1) {
      if (value === null || value === undefined || value === "") delete next[parts[0]];
      else next[parts[0]] = value;
      return next;
    }
    // 2-level (custom.x)
    const [root, leaf] = parts;
    const subObj = { ...(next[root] || {}) };
    if (value === null || value === undefined || value === "") delete subObj[leaf];
    else subObj[leaf] = value;
    if (Object.keys(subObj).length) next[root] = subObj;
    else delete next[root];
    return next;
  };

  // Built-in fields. Each has an optional `path` (defaults to `key`).
  const BUILT_IN_FIELDS = [
    { key: "amount", icon: "💰", label: "Amount", parse: v => { const n = parseFloat(v.replace(/[$,\s]/g, "")); return Number.isFinite(n) ? n : null; }, format: v => `$${v}` },
    { key: "location", icon: "📍", label: "Location", parse: v => { const list = v.split(/,/).map(s => s.trim()).filter(Boolean); return list.length ? list : null; }, format: v => `@${Array.isArray(v) ? v.join(", ") : v}` },
    // City and address — typically auto-populated from photo EXIF GPS by
    // the inboundEmail Cloud Function, but exposed here as editable fields
    // too so the user can correct a misattribution (e.g. Places picked the
    // wrong neighbor business) or fill them in for a non-photo entry.
    // Both are simple string fields, not comma-lists.
    { key: "city", icon: "🏙️", label: "City", parse: v => v.trim() || null, format: v => v },
    { key: "address", icon: "📮", label: "Address", parse: v => v.trim() || null, format: v => v },
    { key: "people", icon: "👥", label: "People", parse: v => { const list = v.split(/,/).map(s => s.trim()).filter(Boolean); return list.length ? list : null; }, format: v => `w/ ${v.join(", ")}` },
    { key: "timeMinutes", icon: "⏱️", label: "Time", parse: v => {
      const trimmed = v.trim();
      if (!trimmed) return null;
      if (/^\d+(\.\d+)?$/.test(trimmed)) {
        const n = parseFloat(trimmed);
        return Number.isFinite(n) ? Math.round(n) : null;
      }
      const ps = parseStructuredFields(trimmed);
      return ps.timeMinutes;
    }, format: v => {
      if (v >= 60 && v % 60 === 0) return `${v / 60}h`;
      if (v >= 60 && v % 30 === 0) return `${(v / 60).toFixed(1)}h`;
      if (v >= 60) return `${Math.floor(v / 60)}h ${v % 60}min`;
      return `${v}min`;
    } },
    { key: "distanceMiles", icon: "🏃", label: "Distance", parse: v => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; }, format: v => `${v}mi` },
    { key: "calories", icon: "🔥", label: "Calories", parse: v => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : null; }, format: v => `${v}cal` },
    { key: "mood", icon: "😊", label: "Mood", parse: v => { const list = v.split(/,/).map(s => s.trim()).filter(Boolean); return list.length ? list : null; }, format: v => Array.isArray(v) ? v.join(", ") : v },
    { key: "rating", icon: "⭐", label: "Rating", parse: v => v.trim() || null, format: v => `★ ${v}` },
    { key: "score", icon: "🏆", label: "Score", parse: v => v.trim() || null, format: v => v },
  ];

  // Custom-field def builder. Custom fields all share the 🏷️ icon and the
  // field name as label. Comma-list values.
  const customFieldDef = (name) => ({
    key: `custom.${name}`,
    icon: "🏷️",
    label: name,
    parse: v => { const list = v.split(/,/).map(s => s.trim()).filter(Boolean); return list.length ? list : null; },
    format: v => `${name}: ${Array.isArray(v) ? v.join(", ") : v}`,
  });

  // Establish list = diary's known custom fields + any currently set on this
  // entry (so just-added fields show even before they're "established")
  const currentCustomKeys = structured && structured.custom ? Object.keys(structured.custom) : [];
  const customNamesSet = new Set([...(allCustomFields || []), ...currentCustomKeys]);
  // Also include the currently-editing custom field — happens when user just
  // tapped + and entered a name. The field has no value yet so isn't in
  // currentCustomKeys, but we need it in the list so the value input renders.
  if (editing && editing.startsWith("custom.")) {
    customNamesSet.add(editing.slice("custom.".length));
  }
  const customFieldList = [...customNamesSet].sort().map(customFieldDef);

  // Library fields: proper icon and parse from FIELD_LIBRARY definitions.
  // The chip is stored at structured.library.<key> just like custom.
  const libraryFieldList = (enabledLibrary || [])
    .filter(k => FIELD_LIBRARY[k])
    .map(k => {
      const def = FIELD_LIBRARY[k];
      return {
        key: `library.${k}`,
        icon: def.icon,
        label: def.label,
        parse: def.parse,
        format: v => `${def.label}: ${def.format(v)}`,
      };
    });

  // Filter built-ins by diary's hidden set
  const visibleBuiltIns = BUILT_IN_FIELDS.filter(f => !(hiddenBuiltins && hiddenBuiltins.has(f.key.toLowerCase())));

  const FIELDS = [...visibleBuiltIns, ...libraryFieldList, ...customFieldList];

  // State for "+ Add custom field" name input
  const [addingCustom, setAddingCustom] = useState(false);
  const [customNameDraft, setCustomNameDraft] = useState("");

  const commitField = (fieldDef) => {
    const parsed = fieldDef.parse(draft);
    const next = setAt(structured || {}, fieldDef.key, parsed);
    onChange(next);
    if (onTouch) onTouch(fieldDef.key);
    setEditing(null);
    setDraft("");
  };

  const removeField = (key) => {
    const next = setAt(structured || {}, key, null);
    onChange(next);
    if (onTouch) onTouch(key);
  };

  // Add a new custom field by name. Opens its mini-input for value entry.
  const startCustomField = (name) => {
    const clean = name.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "");
    if (!clean) return;
    setAddingCustom(false);
    setCustomNameDraft("");
    setDraft("");
    setEditing(`custom.${clean}`);
    if (onTouch) onTouch(`custom.${clean}`);
  };

  return (
    <div style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px solid #f5f3ee" }}>
      {/* Subtle heading. Helps newcomers understand this row isn't required —
          everything here is optional structured data, separate from writing
          your thoughts (the body) and tagging the entry (the picker below). */}
      <div style={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px", fontFamily: "-apple-system,sans-serif" }}>
        Optional data fields for your entry
      </div>
      {/* Icon row — always visible. Tapping an icon opens its mini-input. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: structured && Object.keys(structured).length ? "8px" : "0" }}>
        {FIELDS.map((f, idx) => {
          const value = getAt(structured, f.key);
          const hasValue = value != null && (Array.isArray(value) ? value.length > 0 : true);
          const isEditing = editing === f.key;
          const description = FIELD_KEY_TO_BUILTIN_DESC(f.key);
          if (isEditing) {
            return (
              <div key={f.key} style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff8f0", border: `1px solid ${ORANGE}`, borderRadius: "16px", padding: "2px 4px 2px 8px" }}>
                <span style={{ fontSize: "13px" }}>{f.icon}</span>
                {f.key.startsWith("custom.") && <span style={{ fontSize: "11px", color: "#666", fontFamily: "-apple-system,sans-serif" }}>{f.label}:</span>}
                <input type="text" autoFocus value={draft} onChange={e => setDraft(e.target.value)}
                  placeholder={description}
                  onKeyDown={e => {
                    if (e.key === "Enter") { e.preventDefault(); commitField(f); }
                    else if (e.key === "Escape") { setEditing(null); setDraft(""); }
                  }}
                  onBlur={() => commitField(f)}
                  style={{ width: "200px", border: "none", outline: "none", fontSize: "12px", background: "transparent", fontFamily: "-apple-system,sans-serif" }} />
              </div>
            );
          }
          if (hasValue) {
            const formatted = f.format(value);
            return (
              <button key={f.key} onClick={() => { setDraft(String(Array.isArray(value) ? value.join(", ") : value)); setEditing(f.key); }}
                style={{ background: "#f0f0f0", color: "#333", border: "1px solid #ddd", borderRadius: "16px", padding: "3px 4px 3px 9px", fontSize: "12px", fontWeight: "600", cursor: "pointer", fontFamily: "-apple-system,sans-serif", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <span>{f.icon} {formatted}</span>
                <span onClick={e => { e.stopPropagation(); removeField(f.key); }}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "16px", height: "16px", borderRadius: "50%", background: "rgba(0,0,0,0.1)", color: "#555", fontSize: "10px", fontWeight: 700, cursor: "pointer" }}>×</span>
              </button>
            );
          }
          return (
            <button key={f.key} onClick={() => { setDraft(""); setEditing(f.key); markSeenStructHint(); }}
              data-tip={`${f.label} — ${description}`}
              className={(idx === 0 && !hasSeenStructHint) ? "first-struct-field-blink" : undefined}
              style={{ background: "transparent", color: "#888", border: "1px dashed rgba(0,0,0,0.15)", borderRadius: "16px", padding: "3px 9px", fontSize: "13px", cursor: "pointer", fontFamily: "-apple-system,sans-serif", lineHeight: 1.2 }}>
              {f.icon}{f.key.startsWith("custom.") && <span style={{ fontSize: "10px", marginLeft: "3px", color: "#888" }}>{f.label}</span>}
            </button>
          );
        })}
        {/* + Add custom field */}
        {addingCustom ? (
          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff8f0", border: `1px solid ${ORANGE}`, borderRadius: "16px", padding: "2px 4px 2px 8px" }}>
            <span style={{ fontSize: "11px", color: "#666", fontFamily: "-apple-system,sans-serif" }}>field:</span>
            <input type="text" autoFocus value={customNameDraft} onChange={e => setCustomNameDraft(e.target.value)}
              placeholder="shot, cuisine, etc."
              onKeyDown={e => {
                if (e.key === "Enter") { e.preventDefault(); startCustomField(customNameDraft); }
                else if (e.key === "Escape") { setAddingCustom(false); setCustomNameDraft(""); }
              }}
              onBlur={() => { if (customNameDraft.trim()) startCustomField(customNameDraft); else setAddingCustom(false); }}
              style={{ width: "120px", border: "none", outline: "none", fontSize: "12px", background: "transparent", fontFamily: "-apple-system,sans-serif" }} />
          </div>
        ) : (
          <button onClick={() => { setAddingCustom(true); setCustomNameDraft(""); markSeenStructHint(); }}
            data-tip="Add your own field — e.g. shot, cuisine, vendor — to track values that aren't in the built-in list"
            style={{ background: "transparent", color: "#888", border: "1px dashed rgba(0,0,0,0.15)", borderRadius: "16px", padding: "3px 9px", fontSize: "13px", cursor: "pointer", fontFamily: "-apple-system,sans-serif", lineHeight: 1.2 }}>
            +
          </button>
        )}
      </div>
    </div>
  );
}

// ── TAG PICKER (assigned bubbles + "+" to add more) ──────────────────────────

// TagPicker — manages the tag chip selection for a single entry. Optional
// bodyHtml/setBodyHtml props enable two-way sync with a contentEditable body:
//   - When the user clicks a chip in the picker (addTag), '#tag ' is appended
//     to the body if it isn't already present.
//   - When the user removes a chip via the × button (removeTag), any
//     occurrence of '#tag' in the body is replaced with the bare word (just
//     the '#' stripped — the word stays as prose so the sentence still reads).
// The body→picker direction is handled by a separate useEffect in the
// parent that watches body content for '#tag' patterns and adds them.
const TagPicker = React.forwardRef(function TagPicker({ value, onChange, defaultOpen = false, diaryTags = [], bodyHtml, setBodyHtml, onDateOffset }, ref) {
  const [customInput, setCustomInput] = useState("");
  const customInputRef = useRef("");
  const valueRef = useRef(value);
  useEffect(() => { customInputRef.current = customInput; }, [customInput]);
  useEffect(() => { valueRef.current = value; }, [value]);

  const assigned = value.split(",").map(t => t.trim().toLowerCase()).filter(Boolean);

  // Tag-input shorthand interpretation:
  //   "!" or "!!"       → add the follow-up tag instead of literal "!"
  //   "-1", "-1d", "-2w", "-3mo", "-1y" → backdate the entry's date by
  //     that amount via onDateOffset (no literal tag added)
  //   ISO date "-2026-04-01" OR "2026-04-01" → set the entry's date to that
  //     day (the leading "-" is optional for absolute ISO dates)
  // Mirrors the parsing in the inboundEmail Cloud Function (DATE_OFFSET_TOKEN_RE)
  // so the shorthand works identically across paths.
  const DATE_OFFSET_RE = /^(?:-?(\d{4}-\d{2}-\d{2})|-(\d+(?:d|w|mo|y)?))$/i;
  const applyDateOffset = (suffix) => {
    if (!onDateOffset) return false;
    // ISO absolute date.
    if (/^\d{4}-\d{2}-\d{2}$/.test(suffix)) {
      onDateOffset({ kind: "absolute", iso: suffix });
      return true;
    }
    const m = suffix.match(/^(\d+)(d|w|mo|y)?$/i);
    if (!m) return false;
    const n = parseInt(m[1], 10);
    if (!Number.isFinite(n)) return false;
    const unit = (m[2] || "d").toLowerCase();
    onDateOffset({ kind: "relative", amount: n, unit });
    return true;
  };

  const addTag = (tag) => {
    const normalized = tag.trim().toLowerCase();
    if (!normalized) return;
    // Shorthand: follow-up (! or !!).
    if (normalized === "!" || normalized === "!!") {
      if (!assigned.includes("follow-up")) {
        onChange([...assigned, "follow-up"].join(", "));
      }
      return;
    }
    // Shorthand: "$" → expense.
    if (MONEY_TAG_TOKEN_RE.test(normalized)) {
      if (!assigned.includes("expense")) {
        onChange([...assigned, "expense"].join(", "));
      }
      return;
    }
    // Shorthand: date offset (-1, -1d, -1w, -1mo, -1y, -YYYY-MM-DD, YYYY-MM-DD).
    const offMatch = normalized.match(DATE_OFFSET_RE);
    if (offMatch) {
      // Two capture groups (ISO vs relative) — pick whichever matched.
      if (applyDateOffset(offMatch[1] || offMatch[2])) return;
    }
    if (assigned.includes(normalized)) return;
    onChange([...assigned, normalized].join(", "));
    // Picker→body sync: append '#tag ' to the body if not already present.
    // Only when bodyHtml/setBodyHtml were provided by the parent. Plain-text
    // append because the body is contentEditable HTML — we add a trailing
    // space so the next character the user types isn't merged into the tag.
    if (typeof bodyHtml === "string" && typeof setBodyHtml === "function") {
      const re = new RegExp(`#${normalized.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}\\b`, "i");
      if (!re.test(bodyHtml)) {
        const sep = bodyHtml && !bodyHtml.endsWith(" ") && !bodyHtml.endsWith(">") ? " " : "";
        setBodyHtml(`${bodyHtml}${sep}#${normalized} `);
      }
    }
  };
  const removeTag = (tag) => {
    onChange(assigned.filter(t => t !== tag).join(", "));
    // Picker→body sync: strip the '#' from any occurrence of '#tag' in the
    // body so the word remains as prose. Word-boundary anchored to avoid
    // matching '#tag' inside larger tokens (e.g. URLs with anchors).
    if (typeof bodyHtml === "string" && typeof setBodyHtml === "function") {
      const escaped = tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp(`#(${escaped})\\b`, "gi");
      const next = bodyHtml.replace(re, "$1");
      if (next !== bodyHtml) setBodyHtml(next);
    }
  };

  const flushPending = () => {
    const text = (customInputRef.current || "").trim();
    const currentValue = valueRef.current || "";
    if (!text) return currentValue;
    const currentAssigned = currentValue.split(",").map(t => t.trim().toLowerCase()).filter(Boolean);
    // Split comma-separated input into individual tokens, then process
    // each through the same shorthand rules used by addTag:
    //   "!" / "!!"     → adds "follow-up" instead of literal
    //   "-1", "-1d", "-2w", "-1mo", "-1y", "-YYYY-MM-DD" → date offset
    //                    (consumed; not added as a literal tag)
    //   anything else  → added as a normal tag
    const rawTokens = text.split(",").map(t => t.trim().toLowerCase()).filter(Boolean);
    const merged = [...currentAssigned];
    for (const tok of rawTokens) {
      if (tok === "!" || tok === "!!") {
        if (!merged.includes("follow-up")) merged.push("follow-up");
        continue;
      }
      const offMatch = tok.match(DATE_OFFSET_RE);
      if (offMatch && onDateOffset) {
        if (applyDateOffset(offMatch[1] || offMatch[2])) continue;
      }
      if (!merged.includes(tok)) merged.push(tok);
    }
    const newValue = merged.join(", ");
    onChange(newValue);
    setCustomInput("");
    customInputRef.current = "";
    return newValue;
  };

  // Expose flush() to parent via ref so Save handlers can call it before saving
  React.useImperativeHandle(ref, () => ({ flush: flushPending }), []);

  const handleCustomKey = (e) => {
    if (e.key === "Enter" && customInput.trim()) {
      e.preventDefault();
      flushPending();
    }
  };

  // Tags this diary has actually used (saved on at least one entry, or
  // declared via diary.customTags) — passed in as `diaryTags` from the
  // parent. We additionally remember any tag that's been selected during
  // this composer session, so a tag the user just typed stays as a
  // suggestion in "Used in this diary" even if they remove it before save.
  // Without this, typing then unselecting a custom tag would lose it.
  const [sessionTags, setSessionTags] = useState(() =>
    new Set(value.split(",").map(t => t.trim().toLowerCase()).filter(Boolean))
  );
  useEffect(() => {
    const current = value.split(",").map(t => t.trim().toLowerCase()).filter(Boolean);
    if (!current.length) return;
    setSessionTags(prev => {
      let changed = false;
      const next = new Set(prev);
      for (const t of current) if (!next.has(t)) { next.add(t); changed = true; }
      return changed ? next : prev;
    });
  }, [value]);

  const usedInDiary = [...new Set([
    ...diaryTags.map(t => t.toLowerCase()),
    ...sessionTags,
  ])]
    .filter(t => t && t !== "follow-up" && !assigned.includes(t))
    .sort();
  // Search box appears when there are many used-in-diary tags so users can
  // narrow down quickly instead of scanning a dozen+ chips.
  const [tagSearch, setTagSearch] = useState("");
  const searchActive = tagSearch.trim().length > 0;
  const matchSearch = (t) => !searchActive || t.toLowerCase().includes(tagSearch.toLowerCase());
  const filteredUsedInDiary = usedInDiary.filter(matchSearch);
  const showTagSearch = usedInDiary.length >= 8;

  return (
    <div style={{ marginTop: "8px" }}>
      <div style={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px", fontFamily: "-apple-system,sans-serif" }}>Tag your entry</div>
      {assigned.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center", marginBottom: "10px" }}>
          {assigned.map(tag => {
            const m = getTagMeta(tag);
            return (
              <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: m.color, color: "white", borderRadius: "20px", padding: "4px 4px 4px 10px", fontSize: "12px", fontWeight: "600", fontFamily: "-apple-system,sans-serif" }}>
                {m.emoji} {tag}
                <button onClick={() => removeTag(tag)} aria-label={`remove ${tag}`}
                  style={{ background: "rgba(255,255,255,0.25)", border: "none", borderRadius: "50%", width: "16px", height: "16px", color: "white", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px", lineHeight: 1 }}>×</button>
              </span>
            );
          })}
        </div>
      )}

      <div style={{ padding: "10px 12px", background: "#fafafa", borderRadius: "10px", border: "1px solid #f0ede8" }}>
          {showTagSearch && (
            <input type="text" placeholder="Search tags…" value={tagSearch}
              onChange={e => setTagSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && tagSearch.trim()) {
                  e.preventDefault();
                  addTag(tagSearch.trim());
                  setTagSearch("");
                }
              }}
              style={{ width: "100%", border: "1px solid #ddd", borderRadius: "6px", padding: "6px 10px", fontSize: "12px", outline: "none", marginBottom: "10px", fontFamily: "-apple-system,sans-serif", boxSizing: "border-box" }} />
          )}
          {/* When the search is active and produces no matches, offer to
              create the typed text as a new tag. Saves the user from
              having to retype it into the lower custom-tag input. */}
          {searchActive && filteredUsedInDiary.length === 0 && tagSearch.trim().length > 0 && (
            <div style={{ marginBottom: "10px" }}>
              <button onClick={() => { addTag(tagSearch.trim()); setTagSearch(""); }}
                style={{ background: "#fff8f0", color: ORANGE, border: `1px dashed ${ORANGE}`, borderRadius: "20px", padding: "4px 12px", fontSize: "12px", fontWeight: "600", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                + Add "{tagSearch.trim()}"
              </button>
            </div>
          )}
          {filteredUsedInDiary.length > 0 && (
            <>
              <div style={{ fontSize: "10px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px", fontFamily: "-apple-system,sans-serif" }}>
                {searchActive ? `Used in this diary · ${filteredUsedInDiary.length} match${filteredUsedInDiary.length === 1 ? "" : "es"}` : "Used in this diary"}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "10px" }}>
                {filteredUsedInDiary.map(tag => {
                  // Follow-up is the only tag with a bright red/orange
                  // color treatment. That's intentional on actual entry
                  // chips (it's a status flag meant to grab attention),
                  // but in this "available tags" picker it's just one
                  // of many options to choose from — the red feels loud
                  // and inconsistent next to the neutral chips of every
                  // other tag. Render it with the neutral palette here.
                  // It'll regain its red treatment once selected and
                  // shown as an active tag on the entry.
                  const m = isFollowupTag(tag)
                    ? { emoji: "", color: "#444", bg: "#f0ede8", border: "#d8d4cc", type: "records" }
                    : getTagMeta(tag);
                  return (
                    <button key={tag} onClick={() => { addTag(tag); setTagSearch(""); }}
                      style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}`, borderRadius: "20px", padding: "4px 10px", fontSize: "12px", fontWeight: "600", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                      {m.emoji} {tag}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {usedInDiary.length === 0 && (
            <div style={{ fontSize: "12px", color: "#aaa", marginBottom: "8px", fontFamily: "-apple-system,sans-serif" }}>No tags yet — type one below to create the first.</div>
          )}
          <input type="text" value={customInput} onChange={e => setCustomInput(e.target.value)}
            onKeyDown={handleCustomKey}
            onBlur={flushPending}
            placeholder="type a custom tag, then press Enter or Save"
            style={{ border: "1px solid #e0dcd8", borderRadius: "6px", outline: "none", fontSize: "13px", color: "#555", fontFamily: "-apple-system,sans-serif", background: "white", width: "100%", padding: "6px 10px", boxSizing: "border-box" }} />
        </div>
    </div>
  );
});

// ── FOLLOW-UP TOGGLE ──────────────────────────────────────────────────────────

function FollowUpToggle({ value, onChange }) {
  return (
    <div onClick={() => onChange(!value)}
      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: value ? "#fff8f0" : "#f8f8f8", border: `1px solid ${value ? "rgba(200,56,3,0.3)" : "#ddd"}`, borderRadius: "8px", cursor: "pointer", marginBottom: "16px", transition: "all 0.15s" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "16px" }}>🚩</span>
        <div style={{ paddingBottom: "40px" }}>
          <div style={{ fontSize: "14px", fontWeight: "600", color: value ? "#7a1e00" : "#555", fontFamily: "-apple-system,sans-serif" }}>Needs follow-up</div>
          <div style={{ fontSize: "11px", color: "#aaa", marginTop: "1px" }}>Flag this entry for action</div>
        </div>
      </div>
      {/* Toggle pill */}
      <div style={{ width: "44px", height: "24px", borderRadius: "12px", background: value ? ORANGE : "#ddd", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
        <div style={{ position: "absolute", top: "2px", left: value ? "22px" : "2px", width: "20px", height: "20px", borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
      </div>
    </div>
  );
}

// ── RICH EDITOR ───────────────────────────────────────────────────────────────

function RichEditor({ value, onChange, placeholder }) {
  const r = useRef(null);
  const [fmt, setFmt] = useState({});
  // Initial mount — seed the contentEditable with the initial value.
  useEffect(() => { if (r.current) r.current.innerHTML = value || ""; }, []);
  // External value updates (e.g. tag-picker chip removal stripping '#'
  // from the body) need to be reflected in the DOM. We compare against
  // the live innerHTML so typing-driven echoes don't trigger an unwanted
  // DOM rewrite (which would lose the cursor). When the prop differs from
  // what the editor is showing, accept it as authoritative.
  useEffect(() => {
    if (!r.current) return;
    const live = r.current.innerHTML;
    const incoming = value || "";
    if (live === incoming) return;
    // Save & restore selection if the editor has focus, so cursor stays
    // sensibly placed (caret moves to end after rewrite, which is fine for
    // strip-from-body — user wasn't actively typing in that spot anyway).
    r.current.innerHTML = incoming;
  }, [value]);
  const exec = useCallback((cmd, val = null) => {
    r.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(r.current?.innerHTML || "");
    setFmt({ bold: document.queryCommandState("bold"), italic: document.queryCommandState("italic"), underline: document.queryCommandState("underline") });
  }, [onChange]);
  const upd = () => {
    onChange(r.current?.innerHTML || "");
    setFmt({ bold: document.queryCommandState("bold"), italic: document.queryCommandState("italic"), underline: document.queryCommandState("underline") });
  };
  return (
    <div style={{ marginBottom: "12px" }}>
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "8px", alignItems: "center" }}>
        {[["B", "bold", fmt.bold], ["I", "italic", fmt.italic], ["U", "underline", fmt.underline]].map(([l, c, a]) => (
          <button key={c} onClick={() => exec(c)} style={{ background: a ? "rgba(200,56,3,0.1)" : "transparent", border: `1px solid ${a ? "rgba(200,56,3,0.4)" : "#ddd"}`, color: a ? ORANGE : "#555", padding: "4px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "13px", fontWeight: l === "B" ? "700" : "400", fontStyle: l === "I" ? "italic" : "normal", textDecoration: l === "U" ? "underline" : "none" }}>{l}</button>
        ))}
        <button onClick={() => exec("removeFormat")} style={{ background: "transparent", border: "1px solid #ddd", color: "#888", padding: "4px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}>✕</button>
      </div>
      <div ref={r} contentEditable data-placeholder={placeholder} onInput={upd} onKeyUp={upd} onMouseUp={upd}
        style={{ outline: "none", minHeight: "120px", background: "#f8f8f8", border: "1px solid #ddd", borderRadius: "8px", padding: "12px 14px", color: "#333", fontSize: "15px", fontFamily: "-apple-system,sans-serif", lineHeight: 1.65 }} />
    </div>
  );
}

// ── PHOTOS ────────────────────────────────────────────────────────────────────

function PhotoGrid({ photos, small = false }) {
  if (!photos?.length) return null;
  const size = small ? "72px" : "100px";
  const h = small ? "72px" : "75px";
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
      {photos.map((p, i) => (
        <div key={i} onClick={e => { e.stopPropagation(); (window.__ltOpenLightbox ? window.__ltOpenLightbox(photos, i) : window.open(p.url, "_blank")); }}
          style={{ cursor: "pointer", border: "1px solid #ccc", padding: "2px", background: "white", boxShadow: "1px 2px 4px rgba(0,0,0,0.1)", borderRadius: "4px", flexShrink: 0 }}>
          <img src={p.url} alt="" style={{ width: size, height: h, objectFit: "cover", display: "block", borderRadius: "2px" }} />
        </div>
      ))}
    </div>
  );
}

// ── INLINE COMPOSER (records view) ────────────────────────────────────────────
// Compact quick-capture: collapsed to a single row when idle, expands to full
// feature parity with the modal (title, body, photos, date, tags, followup).

function InlineComposer({ onSave, allTags, saving }) {
  // Free-tier photo gating. When a free user has hit the lifetime photo cap,
  // the Attach control becomes an upgrade prompt instead of a file picker.
  const { canAddPhotos, isPro, usage, limits } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState("");
  const [html, setHtml] = useState("");
  const [photoFiles, setPhotoFiles] = useState([]);
  const [dateStr, setDateStr] = useState(todayInput());
  const [tagString, setTagString] = useState("");
  const [followup, setFollowup] = useState(false);
  const tagPickerRef = useRef(null);

  const reset = () => {
    setTitle(""); setHtml(""); setPhotoFiles([]);
    setDateStr(todayInput());
    setTagString(""); setFollowup(false);
    setExpanded(false);
  };

  const canSave = (title.trim() || (html.trim() && html !== "<br>")) && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    // Flush any unsaved text in the custom-tag input, then use its returned value
    const finalTagString = tagPickerRef.current?.flush ? tagPickerRef.current.flush() : tagString;
    await onSave({
      title,
      html,
      photoFiles,
      dateStr,
      tagString: finalTagString,
      followup,
    });
    reset();
  };

  if (!expanded) {
    return (
      <div onClick={() => setExpanded(true)}
        style={{ background: "white", margin: "12px 16px 0", padding: "10px 14px", borderRadius: "8px", border: "1px solid #e5e0d8", cursor: "text", display: "flex", alignItems: "center", gap: "10px", color: "#999", fontSize: "14px", fontFamily: "-apple-system,sans-serif" }}>
        <span style={{ color: ORANGE, fontSize: "18px", fontWeight: 300, lineHeight: 1 }}>+</span>
        <span>Add a memory...</span>
      </div>
    );
  }

  return (
    <div style={{ background: "white", margin: "12px 16px 0", padding: "14px 16px 12px", borderRadius: "8px", border: `1px solid ${ORANGE}`, boxShadow: "0 2px 10px rgba(200,56,3,0.08)" }}>
      {/* Date row */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
        <span style={{ fontSize: "13px" }}>📅</span>
        <input type="date" value={dateStr} onChange={e => setDateStr(e.target.value)}
          style={{ border: "none", outline: "none", background: "transparent", fontSize: "13px", color: "#555", fontFamily: "-apple-system,sans-serif", cursor: "pointer" }} />
      </div>
      {/* Title */}
      <input type="text" value={title} onChange={e => setTitle(e.target.value)}
        placeholder="Title (optional)"
        style={{ width: "100%", border: "none", outline: "none", fontSize: "17px", fontWeight: 600, color: "#1a1a1a", fontFamily: "-apple-system,sans-serif", marginBottom: "4px", padding: "0", boxSizing: "border-box", background: "transparent" }} />
      {/* Body */}
      <RichEditor value={html} onChange={setHtml} placeholder="Write your thoughts..." />
      {/* Photo thumbs */}
      {photoFiles.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "10px" }}>
          {photoFiles.map((f, i) => (
            <div key={i} style={{ position: "relative", width: "64px", height: "64px", borderRadius: "4px", overflow: "hidden", border: "1px solid #ddd" }}>
              <img src={URL.createObjectURL(f)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <button onClick={() => setPhotoFiles(arr => arr.filter((_, j) => j !== i))}
                style={{ position: "absolute", top: "2px", right: "2px", background: "rgba(0,0,0,0.65)", border: "none", borderRadius: "50%", width: "18px", height: "18px", color: "white", cursor: "pointer", fontSize: "12px", lineHeight: 1, padding: 0 }}>×</button>
            </div>
          ))}
        </div>
      )}
      {/* Tags */}
      <div style={{ marginTop: "8px" }}>
        <TagPicker ref={tagPickerRef} value={tagString} onChange={setTagString} diaryTags={allTags} />
      </div>
      {/* Followup toggle */}
      <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", marginTop: "10px", fontSize: "13px", color: "#555", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
        <input type="checkbox" checked={followup} onChange={e => setFollowup(e.target.checked)} />
        🚩 Follow-up
      </label>
      {/* Actions */}
      <div style={{ display: "flex", gap: "8px", marginTop: "12px", alignItems: "center", flexWrap: "wrap" }}>
        {canAddPhotos ? (
          <label style={{ background: "#f5f5f5", color: "#555", border: "1px solid #ddd", borderRadius: "6px", padding: "6px 12px", fontSize: "12px", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
            📎 Attach
            <input type="file" accept="image/*,video/*,audio/*" multiple onChange={e => {
              const files = [...(e.target.files || [])];
              setPhotoFiles(prev => [...prev, ...files]);
              e.target.value = "";
            }} style={{ display: "none" }} />
          </label>
        ) : (
          <button
            type="button"
            onClick={() => { if (window.__ltOpenUpgrade) window.__ltOpenUpgrade("photos"); }}
            title={`You've used all ${limits.photos} free attachments. Upgrade to Pro for unlimited.`}
            style={{ background: "#FFF4E5", color: ORANGE, border: `1px solid ${ORANGE}`, borderRadius: "6px", padding: "6px 12px", fontSize: "12px", cursor: "pointer", fontFamily: "-apple-system,sans-serif", fontWeight: 600 }}>
            🔒 Attach — upgrade for more
          </button>
        )}
        {/* Usage hint for free users approaching/at the cap. */}
        {!isPro && usage.photos >= limits.photos - 5 && (
          <span style={{ fontSize: "11px", color: usage.photos >= limits.photos ? ORANGE : "#999", fontFamily: "-apple-system,sans-serif" }}>
            {Math.min(usage.photos, limits.photos)}/{limits.photos} attachments
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={reset} disabled={saving}
          style={{ background: "transparent", color: "#888", border: "none", padding: "6px 12px", fontSize: "13px", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>Cancel</button>
        <button onClick={handleSave} disabled={!canSave}
          style={{ background: canSave ? ORANGE : "#ddd", color: "white", border: "none", borderRadius: "6px", padding: "7px 18px", fontSize: "13px", fontWeight: 700, cursor: canSave ? "pointer" : "default", fontFamily: "-apple-system,sans-serif" }}>
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

// ── MEDIA ROW — shared render for videos + audio in entry cards ───────────────
// Photos are rendered separately (each component has its own sizing); this
// helper handles videos (small thumbnail + play overlay, opens lightbox) and
// audio (inline <audio controls>).

function MediaRow({ entry, size = 76 }) {
  const videos = entry.videos || [];
  const audio = entry.audio || [];
  const docs = entry.docs || [];
  if (!videos.length && !audio.length && !docs.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
      {videos.map((v, i) => (
        <div key={`v-${i}`}
          onClick={e => { e.stopPropagation(); (window.__ltOpenLightbox ? window.__ltOpenLightbox([v], 0) : window.open(v.url, "_blank")); }}
          style={{ cursor: "pointer", border: "1px solid #ddd", borderRadius: "4px", overflow: "hidden", background: "#1a1a1a", flexShrink: 0, width: size, height: size, position: "relative" }}>
          <video src={v.url} muted playsInline preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.3)" }}>
            <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: 0, height: 0, borderLeft: "9px solid #111", borderTop: "5px solid transparent", borderBottom: "5px solid transparent", marginLeft: "3px" }} />
            </div>
          </div>
        </div>
      ))}
      {audio.map((a, i) => (
        <div key={`a-${i}`}
          onClick={e => e.stopPropagation()}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#f5f3ef", border: "1px solid #e0dcd8", borderRadius: "20px", padding: "4px 10px 4px 8px", height: "36px", fontFamily: "-apple-system,sans-serif" }}>
          <span style={{ fontSize: "14px" }}>🎙️</span>
          <audio src={a.url} controls style={{ height: "28px", maxWidth: "220px" }} />
        </div>
      ))}
      {docs.map((d, i) => (
        <a key={`d-${i}`}
          href={d.url} target="_blank" rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "white", border: "1px solid #e0dcd8", borderRadius: "8px", padding: "6px 12px 6px 8px", maxWidth: "240px", textDecoration: "none", fontFamily: "-apple-system,sans-serif" }}>
          <span style={{ fontSize: "18px", flexShrink: 0 }}>{docIcon(d)}</span>
          <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <span style={{ fontSize: "12px", color: NAVY, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "170px" }}>{d.name}</span>
            {d.size ? <span style={{ fontSize: "10px", color: "#aaa" }}>{fmtBytes(d.size)}</span> : null}
          </div>
        </a>
      ))}
    </div>
  );
}

// ── DOCUMENT ICON HELPER ──────────────────────────────────────────────────────
// Picks an emoji based on file extension/MIME type. No library, no images.

function docIcon(doc) {
  const n = (doc?.name || "").toLowerCase();
  const m = (doc?.contentType || "").toLowerCase();
  if (m === "application/pdf" || /\.pdf$/.test(n)) return "📕";
  if (/word|\.docx?$/.test(m + n)) return "📘";
  if (/excel|spreadsheet|\.xlsx?$|\.csv$/.test(m + n)) return "📗";
  if (/powerpoint|presentation|\.pptx?$/.test(m + n)) return "📙";
  if (/\.zip$/.test(n)) return "🗜️";
  if (/^text\/|\.(txt|rtf)$/.test(m + n)) return "📄";
  return "📎";
}

// Format a file size in bytes into a human-readable string.
function fmtBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

// ── ATTACHMENT PREVIEW STRIP ──────────────────────────────────────────────────
// Shows each newly-selected File with an image thumb / video thumb with play overlay /
// audio chip / doc chip, plus an × to remove. Uses URL.createObjectURL for preview.

function AttachmentPreviewStrip({ files, onRemove, classifyFile }) {
  // Keep object URLs alive while we're displaying, revoke on unmount.
  const urlsRef = useRef(new Map());
  useEffect(() => {
    // Revoke URLs for files that are no longer in the list
    const currentFiles = new Set(files);
    for (const [f, url] of urlsRef.current.entries()) {
      if (!currentFiles.has(f)) { URL.revokeObjectURL(url); urlsRef.current.delete(f); }
    }
    return () => {
      for (const url of urlsRef.current.values()) URL.revokeObjectURL(url);
      urlsRef.current.clear();
    };
  }, [files]);

  const urlFor = f => {
    if (!urlsRef.current.has(f)) urlsRef.current.set(f, URL.createObjectURL(f));
    return urlsRef.current.get(f);
  };

  return (
    <div style={{ padding: "8px 20px", display: "flex", gap: "8px", flexWrap: "wrap", borderBottom: "1px solid #f0ede8", background: "#fdfcf9" }}>
      {files.map((f, i) => {
        const kind = classifyFile(f);
        const remove = () => onRemove(i);
        const sizeLabel = fmtBytes(f.size);

        if (kind === "image") {
          return (
            <div key={i} style={{ position: "relative" }}>
              <img src={urlFor(f)} alt="" style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "8px", border: "1px solid #ddd" }} />
              <button onClick={remove} style={{ position: "absolute", top: "-5px", right: "-5px", width: "18px", height: "18px", borderRadius: "50%", background: ORANGE, border: "none", color: "white", fontSize: "11px", cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
          );
        }
        if (kind === "video") {
          return (
            <div key={i} style={{ position: "relative" }}>
              <div style={{ width: "60px", height: "60px", borderRadius: "8px", border: "1px solid #ddd", background: "#1a1a1a", position: "relative", overflow: "hidden" }}>
                <video src={urlFor(f)} muted playsInline preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.3)" }}>
                  <div style={{ width: "22px", height: "22px", borderRadius: "50%", background: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ width: 0, height: 0, borderLeft: "7px solid #111", borderTop: "4px solid transparent", borderBottom: "4px solid transparent", marginLeft: "2px" }} />
                  </div>
                </div>
                <div style={{ position: "absolute", bottom: "2px", right: "2px", background: "rgba(0,0,0,0.6)", color: "white", fontSize: "9px", padding: "1px 4px", borderRadius: "3px", fontFamily: "-apple-system,sans-serif" }}>{sizeLabel}</div>
              </div>
              <button onClick={remove} style={{ position: "absolute", top: "-5px", right: "-5px", width: "18px", height: "18px", borderRadius: "50%", background: ORANGE, border: "none", color: "white", fontSize: "11px", cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
          );
        }
        if (kind === "audio") {
          return (
            <div key={i} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: "6px", background: "white", border: "1px solid #e0dcd8", borderRadius: "8px", padding: "6px 12px 6px 8px", maxWidth: "260px" }}>
              <span style={{ fontSize: "16px" }}>🎙️</span>
              <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <span style={{ fontSize: "12px", color: NAVY, fontFamily: "-apple-system,sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "170px" }}>{f.name}</span>
                <span style={{ fontSize: "10px", color: "#aaa", fontFamily: "-apple-system,sans-serif" }}>{sizeLabel}</span>
              </div>
              <button onClick={remove} style={{ position: "absolute", top: "-5px", right: "-5px", width: "18px", height: "18px", borderRadius: "50%", background: ORANGE, border: "none", color: "white", fontSize: "11px", cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
          );
        }
        // document
        return (
          <div key={i} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: "6px", background: "white", border: "1px solid #e0dcd8", borderRadius: "8px", padding: "6px 12px 6px 8px", maxWidth: "260px" }}>
            <span style={{ fontSize: "18px", flexShrink: 0 }}>{docIcon(f)}</span>
            <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <span style={{ fontSize: "12px", color: NAVY, fontFamily: "-apple-system,sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "170px" }}>{f.name}</span>
              <span style={{ fontSize: "10px", color: "#aaa", fontFamily: "-apple-system,sans-serif" }}>{sizeLabel}</span>
            </div>
            <button onClick={remove} style={{ position: "absolute", top: "-5px", right: "-5px", width: "18px", height: "18px", borderRadius: "50%", background: ORANGE, border: "none", color: "white", fontSize: "11px", cursor: "pointer", lineHeight: 1 }}>×</button>
          </div>
        );
      })}
    </div>
  );
}

// ── UPLOAD PROGRESS OVERLAY ───────────────────────────────────────────────────
// Full-screen overlay shown while files are uploading. Shows current file, overall
// progress, and a friendly error if something went wrong.

function UploadProgressOverlay({ state, onDismiss }) {
  if (!state.active && !state.error) return null;
  const kindLabels = { image: "photo", video: "video", audio: "audio", document: "document", file: "file" };
  const label = kindLabels[state.currentKind] || "file";
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9500, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", fontFamily: "-apple-system,sans-serif" }}>
      <div style={{ background: "white", borderRadius: "14px", padding: "24px", maxWidth: "380px", width: "100%", boxShadow: "0 10px 40px rgba(0,0,0,0.3)", textAlign: "center" }}>
        {state.error ? (
          <>
            <div style={{ fontSize: "28px", marginBottom: "12px" }}>⚠️</div>
            <div style={{ fontSize: "15px", fontWeight: 700, color: "#1a1a1a", marginBottom: "8px" }}>Upload failed</div>
            <div style={{ fontSize: "13px", color: "#666", lineHeight: 1.5, marginBottom: "20px", wordBreak: "break-word" }}>{state.error}</div>
            <button onClick={onDismiss} style={{ background: ORANGE, color: "white", border: "none", borderRadius: "8px", padding: "8px 20px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>OK</button>
          </>
        ) : (
          <>
            <div style={{ fontSize: "14px", fontWeight: 600, color: NAVY, marginBottom: "6px" }}>
              Uploading {label} {(state.completed || 0) + 1} of {state.total}
            </div>
            <div style={{ fontSize: "12px", color: "#888", marginBottom: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {state.currentName}
            </div>
            <div style={{ width: "100%", height: "8px", background: "#f0ede8", borderRadius: "4px", overflow: "hidden", marginBottom: "8px" }}>
              <div style={{ width: `${state.currentProgress || 0}%`, height: "100%", background: ORANGE, transition: "width 0.15s" }} />
            </div>
            <div style={{ fontSize: "11px", color: "#aaa" }}>{state.currentProgress || 0}%</div>
          </>
        )}
      </div>
    </div>
  );
}

// ── STRUCTURED DATA TABLE — read-only view of captured fields ────────────────
// Replaces the editor's chip row in entry-view contexts. Two-column
// key/value layout with field icons, labels, and formatted values. Renders
// nothing if no fields are set on the entry.
//
// Field display formatters here are slightly different from the chip
// formatters (no "@" prefix on location, no "$" prefix on amount in the
// value column — labels carry that semantic). Order matches the editor's
// icon row: built-ins first, then library, then custom.
// Decide whether to render the structured-data table for an entry.
// Returns true/false based on diary's `tableMode` setting:
//   - "always": render whenever any data is set
//   - "never": never render
//   - "smart" (default): render only when 3+ fields are populated. Below
//     that threshold, the field chips in composer + auto-generated title
//     already convey the data; a 1-row table feels like noise.
function shouldShowStructuredTable(entry, diary) {
  const mode = (diary && diary.tableMode) || "smart";
  if (mode === "never") return false;
  const s = entry.structured || {};
  const lib = s.library || {};
  const custom = s.custom || {};
  // Count populated fields (each populated key = 1, regardless of array length)
  let count = 0;
  if (s.amount != null) count++;
  if (s.location && (Array.isArray(s.location) ? s.location.length : s.location)) count++;
  // City and address from photo EXIF — each counts as a field toward the
  // smart-mode threshold. A photo entry with location + city + address
  // hits the 3-field threshold on geo data alone, which is the right
  // behavior since the photo is itself a strong "data-rich" signal.
  if (s.city) count++;
  if (s.address) count++;
  if (s.people && s.people.length) count++;
  if (s.timeMinutes != null) count++;
  if (s.distanceMiles != null) count++;
  if (s.calories != null) count++;
  if (s.mood && (Array.isArray(s.mood) ? s.mood.length : s.mood)) count++;
  if (s.rating) count++;
  if (s.score) count++;
  for (const v of Object.values(lib)) {
    if (v != null && !(Array.isArray(v) && !v.length)) count++;
  }
  for (const v of Object.values(custom)) {
    if (v != null && v !== "" && !(Array.isArray(v) && !v.length)) count++;
  }
  if (mode === "always") return count > 0;
  // smart: normally require 3+ fields, but always show the table when
  // photo-geo data is present. "I was at X in Y city" gives an entry
  // strong context that's worth surfacing even when nothing else is
  // captured — particularly common for casual photo entries with no
  // shorthand in the body.
  if (s.city || s.address || s.geo) return count > 0;
  return count >= 3;
}

// Banner shown on an entry's detail when the server (backfillEntryGeo)
// detected 2+ receipts in the entry's photos. Lets the user split them into
// separate entries (one per receipt) or keep them as one. Detection &
// splitting both happen server-side; this component just surfaces the prompt
// and calls the splitEntryReceipts callable. The banner disappears once the
// user splits (entry's flag cleared by the callable) or dismisses (we write
// structured.multiReceiptDismissed so it won't show again).
function MultiReceiptBanner({ entry }) {
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  const s = entry.structured || {};
  const count = s.multiReceiptDetected || 0;
  if (hidden || count < 2 || s.multiReceiptDismissed) return null;

  const onSplit = async (e) => {
    if (e) e.stopPropagation();
    try {
      setBusy(true);
      const fn = httpsCallable(getFunctions(), "splitEntryReceipts");
      await fn({ entryId: entry.id });
      // The callable updated this entry (cleared the flag) and created the
      // extras. The entry listener will refresh the view; hide the banner now.
      setHidden(true);
    } catch (e2) {
      console.error("splitEntryReceipts failed", e2);
      setBusy(false);
      alert("Couldn't split the receipts. Please try again.");
    }
  };

  const onDismiss = async (e) => {
    if (e) e.stopPropagation();
    setHidden(true); // optimistic
    try {
      await updateDoc(doc(db, "entries", entry.id), {
        "structured.multiReceiptDismissed": true,
        "structured.multiReceiptDetected": deleteField(),
        "structured.multiReceiptStash": deleteField(),
      });
    } catch (e2) {
      console.error("Failed to dismiss multi-receipt banner", e2);
      // Banner stays hidden for this session regardless.
    }
  };

  return (
    // stopPropagation on the whole banner: it sits inside the entry card,
    // which has a click-to-edit handler. Without this, tapping anywhere in
    // the banner (including the buttons) bubbles up and opens the editor.
    <div onClick={e => e.stopPropagation()}
      style={{ marginTop: "10px", padding: "12px 14px", background: "#FFF4E5", border: "1px solid #F0C98A", borderRadius: "8px", fontSize: "14px", color: "#5A3A00" }}>
      <div style={{ fontWeight: 600, marginBottom: "6px" }}>
        We detected {count} receipts in this entry
      </div>
      <div style={{ marginBottom: "10px", lineHeight: 1.4 }}>
        Want to split them into {count} separate entries? Each receipt becomes its own entry with its own vendor and total. Your note stays on this entry.
      </div>
      <div style={{ display: "flex", gap: "8px" }}>
        <button onClick={onSplit} disabled={busy}
          style={{ padding: "7px 14px", background: "#C83803", color: "white", border: "none", borderRadius: "6px", fontWeight: 600, fontSize: "13px", cursor: busy ? "default" : "pointer" }}>
          {busy ? "Splitting…" : `Split into ${count}`}
        </button>
        <button onClick={onDismiss} disabled={busy}
          style={{ padding: "7px 14px", background: "transparent", color: "#5A3A00", border: "1px solid #D8B070", borderRadius: "6px", fontSize: "13px", cursor: busy ? "default" : "pointer" }}>
          Keep as one
        </button>
      </div>
    </div>
  );
}

// Banner shown when an entry's receipt OCR captured a transaction date that
// differs from the entry's own date. Offers a one-tap "update entry date to
// the receipt date" — useful because people often photograph/log a receipt
// days after the purchase, and for an expense journal you usually want the
// entry filed under the transaction date. We ask (button) rather than auto-
// apply, because receipt-date OCR is error-prone (ambiguous MM/DD vs DD/MM,
// smudged digits, multiple dates on one receipt) and silently relocating an
// entry in the timeline would be confusing/risky.
function ReceiptDateSync({ entry }) {
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  const s = entry.structured || {};
  const ocr = s.ocr || {};
  // Prefer the receipt date when present (more semantically meaningful for
  // expense entries); otherwise fall back to the photo's EXIF DateTimeOriginal
  // (stashed as structured.photoDate by backfillEntryGeo for non-receipt
  // entries). Only one banner shows at a time — receipt wins.
  const ocrDate = typeof ocr.date === "string" ? ocr.date : null;
  const photoDate = typeof s.photoDate === "string" ? s.photoDate : null;
  const candidateDate = ocrDate || photoDate; // YYYY-MM-DD
  const source = ocrDate ? "receipt" : (photoDate ? "photo" : null);

  if (hidden || s.receiptDateDismissed) return null;
  // Only when we have a valid YYYY-MM-DD candidate date.
  if (!candidateDate || !/^\d{4}-\d{2}-\d{2}$/.test(candidateDate)) return null;

  const entryYmd = toInput(entry.entryDate); // entry date as YYYY-MM-DD (local)
  if (entryYmd === candidateDate) return null;  // already matches — nothing to do

  // A candidate date in the FUTURE is impossible — it's a misread (OCR) or
  // a bad camera clock (EXIF). Don't offer to apply it; instead show a muted
  // note. (Far-off PAST dates are left as a normal proposal — those can be a
  // legitimately old photo/receipt, so we don't second-guess them.)
  const todayYmd = toInput(new Date());
  const candidateInFuture = candidateDate > todayYmd; // string compare valid for YYYY-MM-DD

  // Friendly display of the candidate date.
  const candidateDisplay = (() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(candidateDate);
    if (!m) return candidateDate;
    const d = new Date(+m[1], +m[2] - 1, +m[3], 12, 0, 0);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  })();

  const onSync = async (e) => {
    if (e) e.stopPropagation();
    try {
      setBusy(true);
      await updateDoc(doc(db, "entries", entry.id), {
        entryDate: buildEntryDate(candidateDate, entry.entryDate),
      });
      setHidden(true);
    } catch (err) {
      console.error("Failed to sync entry date", err);
      setBusy(false);
      alert("Couldn't update the date. Please try again.");
    }
  };

  const onDismiss = async (e) => {
    if (e) e.stopPropagation();
    setHidden(true);
    try {
      await updateDoc(doc(db, "entries", entry.id), {
        "structured.receiptDateDismissed": true,
      });
    } catch (err) {
      console.error("Failed to dismiss date banner", err);
    }
  };

  // Future date → muted note, no action button. Different copy for receipt
  // vs photo so the user knows why we can't propose a date.
  if (candidateInFuture) {
    return (
      <div onClick={e => e.stopPropagation()}
        style={{ marginTop: "10px", padding: "8px 14px", background: "#F4F2EE", border: "1px solid #E0DBD2", borderRadius: "8px", fontSize: "12px", color: "#8A8275", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
        <span style={{ lineHeight: 1.4 }}>
          {source === "receipt"
            ? "Couldn't read a reliable date from this receipt."
            : "The photo's date looks unreliable."}
        </span>
        <button onClick={onDismiss} disabled={busy}
          style={{ padding: "4px 10px", background: "transparent", color: "#8A8275", border: "1px solid #D8D2C8", borderRadius: "6px", fontSize: "11px", cursor: busy ? "default" : "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
          Dismiss
        </button>
      </div>
    );
  }

  // Normal proposal banner — copy depends on whether the candidate came from
  // a receipt's OCR or a photo's EXIF.
  const promptText = source === "receipt"
    ? <>This receipt is dated <b>{candidateDisplay}</b>, but the entry is dated <b>{fmtShort(entry.entryDate)}</b>. Update the entry date to match the receipt?</>
    : <>This photo was taken on <b>{candidateDisplay}</b>, but the entry is dated <b>{fmtShort(entry.entryDate)}</b>. Update the entry date to match the photo?</>;

  return (
    <div onClick={e => e.stopPropagation()}
      style={{ marginTop: "10px", padding: "10px 14px", background: "#EAF4FF", border: "1px solid #A9CCEF", borderRadius: "8px", fontSize: "13px", color: "#1B3A5B" }}>
      <div style={{ marginBottom: "8px", lineHeight: 1.4 }}>
        {promptText}
      </div>
      <div style={{ display: "flex", gap: "8px" }}>
        <button onClick={onSync} disabled={busy}
          style={{ padding: "6px 12px", background: "#1B6FC4", color: "white", border: "none", borderRadius: "6px", fontWeight: 600, fontSize: "12px", cursor: busy ? "default" : "pointer" }}>
          {busy ? "Updating…" : `Use ${candidateDisplay}`}
        </button>
        <button onClick={onDismiss} disabled={busy}
          style={{ padding: "6px 12px", background: "transparent", color: "#1B3A5B", border: "1px solid #A9CCEF", borderRadius: "6px", fontSize: "12px", cursor: busy ? "default" : "pointer" }}>
          Keep current date
        </button>
      </div>
    </div>
  );
}

function StructuredDataTable({ entry, diary }) {
  if (!shouldShowStructuredTable(entry, diary)) return null;
  const s = entry.structured || {};
  const lib = s.library || {};
  const custom = s.custom || {};

  // Build the rows. Each row = { icon, label, value }
  const rows = [];

  const fmtTime = (mins) => {
    if (mins == null) return "";
    if (mins >= 60 && mins % 60 === 0) return `${mins / 60}h`;
    if (mins >= 60 && mins % 30 === 0) return `${(mins / 60).toFixed(1)}h`;
    if (mins >= 60) return `${Math.floor(mins / 60)}h ${mins % 60}min`;
    return `${mins}min`;
  };

  // Built-ins in fixed order. Skip empty/null values.
  if (s.amount != null) rows.push({ icon: "💰", label: "Amount", value: `$${s.amount}` });
  if (s.location && (Array.isArray(s.location) ? s.location.length : s.location)) {
    rows.push({ icon: "📍", label: "Location", value: Array.isArray(s.location) ? s.location.join(", ") : s.location });
  }
  // City and address come from photo EXIF GPS via the inboundEmail Cloud
  // Function (resolveGeoSidecar). City is the broad pivot ("where was I?");
  // address is the precise resolved street. Both render only when present —
  // entries without photos, or photos without GPS, won't show these.
  if (s.city) rows.push({ icon: "🏙️", label: "City", value: s.city });
  if (s.address) {
    // Address row is clickable — taps open Google Maps with the address
    // as the search query. URL format works cross-platform: desktop
    // opens web Maps; iOS opens Apple Maps or Google Maps app; Android
    // opens the Google Maps app.
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address)}`;
    rows.push({ icon: "📮", label: "Address", value: s.address, link: mapsUrl });
  }
  if (s.people && s.people.length) {
    const prefix = s.peopleAsOpponents ? "vs. " : "";
    rows.push({ icon: "👥", label: "People", value: prefix + s.people.join(", ") });
  }
  if (s.timeMinutes != null) rows.push({ icon: "⏱️", label: "Time", value: fmtTime(s.timeMinutes) });
  if (s.distanceMiles != null) rows.push({ icon: "🏃", label: "Distance", value: `${s.distanceMiles} mi` });
  if (s.calories != null) rows.push({ icon: "🔥", label: "Calories", value: `${s.calories} cal` });
  if (s.mood && (Array.isArray(s.mood) ? s.mood.length : s.mood)) {
    rows.push({ icon: "😊", label: "Mood", value: Array.isArray(s.mood) ? s.mood.join(", ") : s.mood });
  }
  if (s.rating) rows.push({ icon: "⭐", label: "Rating", value: `★ ${s.rating}` });
  if (s.score) rows.push({ icon: "🏆", label: "Score", value: s.score });

  // Library fields — use FIELD_LIBRARY for icon/label
  for (const [key, value] of Object.entries(lib)) {
    if (value == null || (Array.isArray(value) && !value.length)) continue;
    const def = FIELD_LIBRARY[key];
    const icon = def?.icon || "🔖";
    const label = def?.label || key;
    const formatted = Array.isArray(value) ? value.join(", ") : String(value);
    rows.push({ icon, label, value: formatted });
  }

  // Custom fields
  for (const [key, value] of Object.entries(custom)) {
    if (value == null || (Array.isArray(value) && !value.length) || value === "") continue;
    const formatted = Array.isArray(value) ? value.join(", ") : String(value);
    rows.push({ icon: "🏷️", label: key, value: formatted });
  }

  if (!rows.length) return null;

  return (
    <div style={{
      marginTop: "10px",
      background: "rgba(0,0,0,0.025)",
      borderRadius: "6px",
      padding: "8px 12px",
      fontSize: "13px",
      fontFamily: "-apple-system,sans-serif",
    }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ padding: "3px 12px 3px 0", color: "#666", whiteSpace: "nowrap", verticalAlign: "top", width: "1%" }}>
                <span style={{ marginRight: "6px" }}>{r.icon}</span>{r.label}
              </td>
              <td style={{ padding: "3px 0", color: "#1a1a1a", fontWeight: 500, wordBreak: "break-word" }}>
                {r.link ? (
                  <a href={r.link} target="_blank" rel="noopener noreferrer"
                    onClick={(ev) => ev.stopPropagation()}
                    title="Open in Google Maps"
                    style={{ color: "#1a1a1a", textDecoration: "none", borderBottom: "1px dotted #8a7a65", cursor: "pointer" }}>
                    {r.value}
                  </a>
                ) : r.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── ENTRY CARD — always expanded, tap to edit ─────────────────────────────────

function EntryCard({ entry, canEdit, onEdit, isRecordsMode, selectMode, isSelected, onSelect }) {
  const date = fmtDate(entry.entryDate);
  const body = entry.body || "";
  const who = entry._who || "";
  const hasFollowup = (entry.tags || []).some(isFollowupTag);
  const isFavorite = !!entry.favorite;
  const firstPhoto = entry.photos?.[0];
  const hasMorePhotos = entry.photos?.length > 1;

  const handleEdit = e => { e.stopPropagation(); onEdit(entry); };
  const handleSelect = e => { e.stopPropagation(); onSelect(entry.id); };

  // Checkbox — always visible, clicking activates select mode
  const handleCheckboxClick = e => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(entry.id); // always call — toggleSelect handles selectMode activation
  };
  const Checkbox = () => (
    <div onClick={handleCheckboxClick}
      style={{ width: "18px", height: "18px", borderRadius: "3px", border: `1.5px solid ${isSelected ? ORANGE : "#c0bbb5"}`, background: isSelected ? ORANGE : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer", transition: "none", marginTop: "3px", opacity: 0.35 }}>
      {isSelected && <span style={{ color: "white", fontSize: "11px", fontWeight: "700", lineHeight: 1 }}>✓</span>}
    </div>
  );

  if (isRecordsMode) {
    return (
      <div
        onClick={selectMode ? handleSelect : canEdit ? handleEdit : undefined}
        style={{ borderBottom: "1px solid #ede9e0", background: isSelected ? "rgba(200,56,3,0.04)" : "white", padding: "10px 16px", display: "flex", gap: "10px", alignItems: "flex-start", cursor: canEdit || selectMode ? "pointer" : "default" }}>
        {canEdit && <Checkbox />}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Top row: tag chips + date + icons */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "4px" }}>
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", alignItems: "center", flex: 1 }}>
              {(entry.tags || []).filter(t => !isFollowupTag(t)).map((tag, i) => {
                const m = getTagMeta(tag);
                return <span key={i} style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}`, borderRadius: "4px", padding: "2px 8px", fontSize: "12px", fontWeight: "600", fontFamily: "-apple-system,sans-serif" }}>{m.emoji} {tag}</span>;
              })}
              {entry.autoTagged && <span style={{ fontSize: "10px", color: "#ccc", fontFamily: "-apple-system,sans-serif" }}>auto ✦</span>}
            </div>
            <div style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0, marginLeft: "8px" }}>
              {isFavorite && <span style={{ fontSize: "15px" }}>⭐</span>}
              {hasFollowup && <span style={{ fontSize: "15px" }}>🚩</span>}
              <span style={{ fontSize: "11px", color: "#888", fontFamily: "-apple-system,sans-serif", fontStyle: "italic", whiteSpace: "nowrap" }}>{fmtShort(entry.entryDate)}</span>

            </div>
          </div>
          {entry.title && <div style={{ fontSize: "15px", fontWeight: "600", color: "#1a1a1a", marginBottom: "4px", fontFamily: "-apple-system,sans-serif" }}>{entry.title}</div>}
          <div onClick={e => { if (e.target.tagName === "A") e.stopPropagation(); }}
            style={{ fontSize: "14px", color: "#444", lineHeight: 1.65, fontFamily: "-apple-system,sans-serif" }}>
            {entry.bodyHtml
              ? <div className="entry-body" dangerouslySetInnerHTML={{ __html: sanitize(entry.bodyHtml) }} />
              : <div className="entry-body" dangerouslySetInnerHTML={{ __html: textToLinkifiedHtml(body) }} />}
          </div>
          <StructuredDataTable entry={entry} diary={diary} />
          <MultiReceiptBanner entry={entry} />
          <ReceiptDateSync entry={entry} />
          {/* Footer line: city (from photo EXIF, if present) + author byline.
              Putting city right next to the name makes "where" instantly
              scannable in the feed without taking up the chip slot above. */}
          {(who || entry.structured?.city) && (
            <div style={{ fontSize: "11px", color: "#bbb", fontFamily: "-apple-system,sans-serif", fontStyle: "italic", marginTop: "3px" }}>
              {entry.structured?.city ? `${entry.structured.city}${who ? " — " + who : ""}` : who}
            </div>
          )}
          {entry.photos?.length > 0 && (
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "8px" }}>
              {entry.photos.map((p, i) => (
                <div key={i} onClick={e => { e.stopPropagation(); (window.__ltOpenLightbox ? window.__ltOpenLightbox(entry.photos, i) : window.open(p.url, "_blank")); }}
                  style={{ cursor: "pointer", borderRadius: "6px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.08)", flexShrink: 0 }}>
                  <img src={p.url} alt="" style={{ width: "80px", height: "60px", objectFit: "cover", display: "block" }} />
                </div>
              ))}
            </div>
          )}
          <MediaRow entry={entry} size={72} />
        </div>
      </div>
    );
  }

  // ── MEMORIES ENTRY ──
  // Checkbox in left gutter (before red line), content starts after
  return (
    <div style={{ borderBottom: "1px solid #ede9e0", background: isSelected ? "rgba(200,56,3,0.04)" : "transparent", display: "flex", alignItems: "flex-start", cursor: canEdit || selectMode ? "pointer" : "default" }}
      onClick={e => { if (selectMode) handleSelect(e); else if (canEdit) handleEdit(e); }}>
      {/* Left gutter — left of red margin line */}
      <div style={{ width: "36px", flexShrink: 0, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: "16px" }}>
        {canEdit && <Checkbox />}
      </div>
      {/* Content — right of red line */}
      <div style={{ flex: 1, minWidth: 0, padding: "14px 12px 14px 6px" }}>

          {/* Title row: title left, date · author + ⭐/🚩 right */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", marginBottom: "8px" }}>
            {entry.title ? (
              <div className="entry-title" style={{ fontSize: "16px", fontWeight: "700", color: "#0B1F4A", fontFamily: "-apple-system,sans-serif", lineHeight: 1.35, flex: 1, minWidth: 0 }}>
                {entry.title}
              </div>
            ) : <div style={{ flex: 1 }} />}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "2px", flexShrink: 0, paddingTop: "1px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span style={{ fontSize: "11px", color: "#b0a898", fontFamily: "-apple-system,sans-serif", fontStyle: "italic", whiteSpace: "nowrap" }}>{date}</span>
                {isFavorite && <span style={{ fontSize: "15px" }}>⭐</span>}
                {hasFollowup && <span style={{ fontSize: "15px" }}>🚩</span>}
              </div>
              {(who || entry.structured?.city) && (
                <span style={{ fontSize: "11px", color: "#b0a898", fontFamily: "-apple-system,sans-serif", fontStyle: "italic", whiteSpace: "nowrap" }}>
                  {entry.structured?.city ? `${entry.structured.city}${who ? " — " + who : ""}` : who}
                </span>
              )}
            </div>
          </div>

          {/* Body */}
          <div onClick={e => { if (e.target.tagName === "A") e.stopPropagation(); }}
            style={{ fontSize: "15px", color: "#3a3530", lineHeight: 1.65, fontFamily: "-apple-system,sans-serif" }}>
            {entry.bodyHtml
              ? <div className="entry-body" dangerouslySetInnerHTML={{ __html: sanitize(entry.bodyHtml) }} />
              : <div className="entry-body" dangerouslySetInnerHTML={{ __html: textToLinkifiedHtml(body) }} />}
          </div>

          {/* Structured data table — read-only summary of captured fields */}
          <StructuredDataTable entry={entry} diary={diary} />
          <MultiReceiptBanner entry={entry} />
          <ReceiptDateSync entry={entry} />

          {/* Tags */}
          <TagBadges tags={entry.tags} autoTagged={entry.autoTagged} isRecordsMode={isRecordsMode} />

          {/* All photos below tags */}
          {entry.photos?.length > 0 && (
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "10px" }}>
              {entry.photos.map((p, i) => (
                <div key={i} onClick={e => { e.stopPropagation(); (window.__ltOpenLightbox ? window.__ltOpenLightbox(entry.photos, i) : window.open(p.url, "_blank")); }}
                  style={{ cursor: "pointer", borderRadius: "8px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", flexShrink: 0 }}>
                  <img src={p.url} alt="" style={{ width: "90px", height: "68px", objectFit: "cover", display: "block" }} />
                </div>
              ))}
            </div>
          )}
          <MediaRow entry={entry} size={80} />
      </div>
    </div>
  );
}

// ── LIST VIEW ENTRY ─────────────────────────────────────────────────────────

// ── EMPTY DIARY COVER ─────────────────────────────────────────────────────────
// Warm welcome shown on a brand-new diary with 0 entries.
// Greeting, diary name, inbound email, and clickable suggestion bubbles.
// Each suggestion prefills the new-entry modal with a title and a tag.

const EMPTY_SUGGESTIONS = [
  { title: "A great meal", tag: "food" },
  { title: "A new baby",           tag: "family" },
  { title: "A big test",           tag: "school" },
  { title: "A great conversation", tag: "family" },
  { title: "A big win",            tag: "sports" },
  { title: "A doctor appointment", tag: "medical" },
  { title: "A car tune-up",        tag: "auto" },
  { title: "A birthday",           tag: "family" },
  { title: "A home purchase",      tag: "property" },
  { title: "A trip",               tag: "travel" },
  { title: "A workout",            tag: "fitness" },
  { title: "A new appliance",      tag: "property" },
];

// ── Diary passcode (privacy curtain) ──────────────────────────────────────
// A 4-digit passcode that gates opening the diary in the app. IMPORTANT: this
// is a privacy CURTAIN, not encryption. The diary data still loads from
// Firestore into the browser, so this protects against casual snooping (a
// picked-up unlocked phone, a shared computer) — NOT against anyone with the
// account login or direct database access. We never store the plaintext code;
// we store a salted SHA-256 hash. With only 10,000 possible 4-digit codes a
// hash isn't brute-force-proof, but it keeps the code from being readable at a
// glance in the database, which is the right bar for a curtain. Owner-only,
// remembered per browser session, gates only the owner's own view (a shared
// diary isn't private by definition). Reset is via owner email (stage two).

// Derive a hash of the passcode. Salted with the diary id so the same code on
// two diaries yields different hashes. Uses Web Crypto (available in any HTTPS
// context, which the PWA always is). Returns a hex string.
async function hashPasscode(code, diaryId) {
  const data = new TextEncoder().encode(`ltpc:${diaryId}:${code}`);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, "0")).join("");
}

// Session-unlock helpers. "Unlocked" is remembered only for the browser
// session (sessionStorage), so closing/reopening the app re-locks the diary.
// The stored VALUE is the uid that unlocked it, so a different user (or a
// re-authenticated session) in the same tab does not inherit the unlock — we
// only treat it as unlocked when the stored uid matches the current user.
// (Explicit sign-out also clears these flags via Dashboard, which is what
// makes a same-user logout/login re-prompt.)
const passcodeSessionKey = (diaryId) => `lt_unlocked_${diaryId}`;
function isUnlockedThisSession(diaryId, uid) {
  try { return !!uid && sessionStorage.getItem(passcodeSessionKey(diaryId)) === uid; } catch { return false; }
}
function markUnlockedThisSession(diaryId, uid) {
  try { if (uid) sessionStorage.setItem(passcodeSessionKey(diaryId), uid); } catch {}
}
function clearSessionUnlock(diaryId) {
  try { sessionStorage.removeItem(passcodeSessionKey(diaryId)); } catch {}
}

// Auto-lock: if a passcode-protected diary's app is backgrounded (phone sleep,
// app switch, screen off — all fire visibilitychange) and stays hidden for
// longer than this, re-lock on return. 5 minutes balances the "someone picked
// up my phone" threat against the annoyance of re-typing the code after a
// quick glance away. We can't hook the OS device-lock from a web app, so this
// background-return check is the closest equivalent — and it fires on exactly
// the sleep-then-reopen flow.
const PASSCODE_AUTOLOCK_MS = 5 * 60 * 1000;
const passcodeHiddenAtKey = (diaryId) => `lt_hiddenAt_${diaryId}`;
function recordHiddenNow(diaryId) {
  try { sessionStorage.setItem(passcodeHiddenAtKey(diaryId), String(Date.now())); } catch {}
}
// Returns true if the diary should re-lock based on how long it was hidden.
function shouldAutolockOnReturn(diaryId) {
  try {
    const t = parseInt(sessionStorage.getItem(passcodeHiddenAtKey(diaryId)) || "0", 10);
    return t > 0 && (Date.now() - t) > PASSCODE_AUTOLOCK_MS;
  } catch { return false; }
}
function clearHiddenAt(diaryId) {
  try { sessionStorage.removeItem(passcodeHiddenAtKey(diaryId)); } catch {}
}

// The lock screen shown before a passcode-protected diary renders for its
// owner. Calls onUnlock() once the entered code matches the stored hash.
function PasscodeGate({ diary, ownerEmail, uid, onUnlock, onForgot, forgotNote, onResetDirect, onEmailLink }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetMsg, setResetMsg] = useState("");      // success/info after an action
  const [confirmDirect, setConfirmDirect] = useState(false); // two-step confirm for direct clear

  const submit = async (value) => {
    const c = (value ?? code).trim();
    if (c.length !== 4 || !/^\d{4}$/.test(c)) { setError("Enter your 4-digit code."); return; }
    setChecking(true);
    try {
      const h = await hashPasscode(c, diary.id);
      if (h === diary.passcodeHash) {
        markUnlockedThisSession(diary.id, uid);
        onUnlock();
      } else {
        setError("Incorrect code.");
        setCode("");
      }
    } catch {
      setError("Couldn't verify — try again.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#0B1F4A", padding: "24px", fontFamily: "-apple-system,sans-serif" }}>
      <div style={{ fontSize: "40px", marginBottom: "8px" }}>🔒</div>
      <div style={{ color: "#fff", fontSize: "18px", fontWeight: 700, marginBottom: "4px" }}>{diary.name}</div>
      <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "13px", marginBottom: "24px" }}>Enter your passcode to open this diary</div>
      <input
        type="password"
        inputMode="numeric"
        autoComplete="off"
        autoFocus
        maxLength={4}
        value={code}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, "").slice(0, 4);
          setCode(v);
          setError("");
          if (v.length === 4) submit(v); // auto-submit on 4th digit
        }}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        style={{ width: "160px", textAlign: "center", letterSpacing: "12px", fontSize: "28px", padding: "12px", borderRadius: "10px", border: "none", outline: "none", background: "#fff", color: "#0B1F4A", marginBottom: "12px" }}
      />
      <div style={{ height: "18px", color: "#ffb4a0", fontSize: "13px", marginBottom: "12px" }}>{checking ? "Checking…" : error}</div>
      <button onClick={onForgot}
        style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.8)", textDecoration: "underline", fontSize: "13px", cursor: "pointer" }}>
        Forgot passcode?
      </button>
      {forgotNote && (
        <div style={{ marginTop: "16px", maxWidth: "320px", textAlign: "center" }}>
          {resetMsg ? (
            <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "13px", lineHeight: 1.6 }}>{resetMsg}</div>
          ) : (
            <>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "12px", lineHeight: 1.6, marginBottom: "14px" }}>
                You're signed in{ownerEmail ? ` as ${ownerEmail}` : ""}, so you can reset this
                passcode now and set a new one — or have a reset link emailed to you.
              </div>
              {/* Direct clear: two-step confirm to avoid an accidental tap
                  wiping the passcode. */}
              {!confirmDirect ? (
                <button onClick={() => setConfirmDirect(true)} disabled={resetBusy}
                  style={{ display: "block", width: "100%", background: "#fff", color: "#0B1F4A", border: "none", padding: "10px", borderRadius: "8px", fontSize: "14px", fontWeight: 700, cursor: "pointer", marginBottom: "10px" }}>
                  Reset passcode now
                </button>
              ) : (
                <div style={{ marginBottom: "10px" }}>
                  <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "12px", marginBottom: "8px" }}>
                    Remove the passcode from this diary?
                  </div>
                  <button
                    onClick={async () => {
                      setResetBusy(true); setError("");
                      try {
                        await onResetDirect();      // clears passcodeHash on the diary
                        markUnlockedThisSession(diary.id, uid);
                        onUnlock();                 // drop straight into the diary
                      } catch (e) {
                        setResetMsg("");
                        setError("Couldn't reset — try again.");
                        setResetBusy(false);
                        setConfirmDirect(false);
                      }
                    }}
                    disabled={resetBusy}
                    style={{ background: "#C83803", color: "#fff", border: "none", padding: "9px 14px", borderRadius: "8px", fontSize: "13px", fontWeight: 700, cursor: "pointer", marginRight: "8px" }}>
                    {resetBusy ? "Resetting…" : "Yes, remove it"}
                  </button>
                  <button onClick={() => setConfirmDirect(false)} disabled={resetBusy}
                    style={{ background: "transparent", color: "rgba(255,255,255,0.8)", border: "1px solid rgba(255,255,255,0.4)", padding: "9px 14px", borderRadius: "8px", fontSize: "13px", cursor: "pointer" }}>
                    Cancel
                  </button>
                </div>
              )}
              {/* Email link fallback. */}
              <button
                onClick={async () => {
                  setResetBusy(true); setError("");
                  try {
                    await onEmailLink();
                    setResetMsg(ownerEmail
                      ? `We've emailed a reset link to ${ownerEmail}. It expires in 30 minutes.`
                      : "We've emailed a reset link to your account address. It expires in 30 minutes.");
                  } catch (e) {
                    setError("Couldn't send the email — try again.");
                  } finally {
                    setResetBusy(false);
                  }
                }}
                disabled={resetBusy}
                style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.8)", textDecoration: "underline", fontSize: "12px", cursor: "pointer" }}>
                Email me a reset link instead
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Quick "set a passcode" modal, opened from the on-page lock icon when a diary
// has none yet. Enter + confirm a 4-digit code; onSet(hash) is called with the
// salted hash. Deliberately minimal — the full set/change/remove UI lives in
// Settings → Privacy; this is just the fast path to protect a diary in place.
function QuickSetPasscode({ diary, uid, onClose, onSet }) {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const onlyDigits = (s) => s.replace(/\D/g, "").slice(0, 4);

  const save = async () => {
    setError("");
    if (!/^\d{4}$/.test(next)) { setError("Passcode must be 4 digits."); return; }
    if (next !== confirm) { setError("The two codes don't match."); return; }
    setBusy(true);
    try {
      const h = await hashPasscode(next, diary.id);
      await onSet(h);
    } catch (e) {
      setError(e.message || "Couldn't set the passcode.");
      setBusy(false);
    }
  };

  const inputStyle = { width: "140px", textAlign: "center", letterSpacing: "10px", fontSize: "22px", padding: "10px", borderRadius: "8px", border: "1px solid #ccc", outline: "none", marginBottom: "10px" };

  return (
    <Modal onClose={onClose} maxWidth="380px">
      <div style={{ fontFamily: "-apple-system,sans-serif", textAlign: "center" }}>
        <div style={{ fontSize: "32px", marginBottom: "6px" }}>🔒</div>
        <h2 style={{ fontSize: "18px", fontWeight: 700, margin: "0 0 6px", color: NAVY }}>Set a passcode</h2>
        <p style={{ fontSize: "13px", color: "#666", lineHeight: 1.6, margin: "0 0 18px" }}>
          Choose a 4-digit code for <b>{diary.name}</b>. You'll enter it to open this diary.
          It's a privacy screen, not full encryption.
        </p>
        <div style={{ fontSize: "12px", color: "#888", marginBottom: "4px" }}>New passcode</div>
        <input type="password" inputMode="numeric" autoComplete="new-password" name="lt-new-passcode" autoFocus value={next}
          onChange={(e) => { setNext(onlyDigits(e.target.value)); setError(""); }}
          style={inputStyle} />
        <div style={{ fontSize: "12px", color: "#888", marginBottom: "4px" }}>Confirm passcode</div>
        <input type="password" inputMode="numeric" autoComplete="new-password" name="lt-confirm-passcode" value={confirm}
          onChange={(e) => { setConfirm(onlyDigits(e.target.value)); setError(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") save(); }}
          style={inputStyle} />
        {error && <div style={{ fontSize: "13px", color: "#c0392b", marginBottom: "10px" }}>{error}</div>}
        <div style={{ marginTop: "8px" }}>
          <button onClick={save} disabled={busy}
            style={{ background: ORANGE, color: "#fff", border: "none", padding: "9px 18px", borderRadius: "8px", cursor: busy ? "default" : "pointer", fontSize: "14px", fontWeight: 700, marginRight: "8px" }}>
            {busy ? "Setting…" : "Set & lock"}
          </button>
          <button onClick={onClose} disabled={busy}
            style={{ background: "transparent", color: "#555", border: "1px solid #ddd", padding: "9px 18px", borderRadius: "8px", cursor: "pointer", fontSize: "14px" }}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Settings UI (owner-only) to set, change, or remove the diary passcode.
// onSaved(patch) persists to the diary doc and updates local state.
function PasscodeSettings({ diary, uid, onSaved, onLockNow }) {
  const hasCode = !!diary.passcodeHash;
  const [mode, setMode] = useState(null); // null | "set" | "change" | "remove"
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  const reset = () => { setMode(null); setCurrent(""); setNext(""); setConfirm(""); setError(""); };

  const verifyCurrent = async () => {
    const h = await hashPasscode(current.trim(), diary.id);
    return h === diary.passcodeHash;
  };

  const onlyDigits = (s) => s.replace(/\D/g, "").slice(0, 4);

  const save = async () => {
    setError("");
    if (mode === "remove") {
      if (!(await verifyCurrent())) { setError("Current passcode is incorrect."); return; }
      setBusy(true);
      try {
        await onSaved({ passcodeHash: deleteField() });
        clearSessionUnlock(diary.id);
        setDone("Passcode removed.");
        reset();
      } catch (e) { setError(e.message || "Couldn't remove passcode."); }
      finally { setBusy(false); }
      return;
    }
    // set or change
    if (mode === "change" && !(await verifyCurrent())) { setError("Current passcode is incorrect."); return; }
    if (!/^\d{4}$/.test(next)) { setError("New passcode must be 4 digits."); return; }
    if (next !== confirm) { setError("New passcode and confirmation don't match."); return; }
    setBusy(true);
    try {
      const h = await hashPasscode(next, diary.id);
      await onSaved({ passcodeHash: h });
      markUnlockedThisSession(diary.id, uid); // owner just set it; don't lock them out immediately
      setDone(mode === "set" ? "Passcode set." : "Passcode changed.");
      reset();
    } catch (e) { setError(e.message || "Couldn't save passcode."); }
    finally { setBusy(false); }
  };

  const inputStyle = { width: "120px", textAlign: "center", letterSpacing: "8px", fontSize: "20px", padding: "8px", borderRadius: "8px", border: "1px solid #ccc", outline: "none", marginBottom: "10px" };
  const btn = (label, onClick, primary) => (
    <button onClick={onClick} disabled={busy}
      style={{ background: primary ? ORANGE : "transparent", color: primary ? "#fff" : "#555", border: primary ? "none" : "1px solid #ddd", padding: "8px 16px", borderRadius: "8px", cursor: busy ? "default" : "pointer", fontSize: "14px", fontWeight: 600, marginRight: "8px" }}>
      {label}
    </button>
  );

  return (
    <div style={{ fontFamily: "-apple-system,sans-serif", maxWidth: "440px" }}>
      <h2 style={{ fontSize: "20px", fontWeight: 700, margin: "0 0 8px", color: NAVY }}>🔒 Passcode</h2>
      <p style={{ fontSize: "13px", color: "#666", lineHeight: 1.6, marginBottom: "18px" }}>
        Add a 4-digit passcode to open this diary on this device. It's a privacy screen for casual snooping —
        it asks for the code when you open the diary, but it isn't full encryption, and anyone with your account
        login can still see your entries. You can reset a forgotten passcode by email.
      </p>

      {done && <div style={{ fontSize: "13px", color: "#1a7d3c", marginBottom: "14px" }}>{done}</div>}

      {!mode && (
        <div>
          <div style={{ fontSize: "14px", color: "#333", marginBottom: "14px" }}>
            Status: <b>{hasCode ? "Passcode is ON" : "No passcode set"}</b>
          </div>
          {!hasCode && btn("Set passcode", () => { setDone(""); setMode("set"); }, true)}
          {hasCode && btn("Change passcode", () => { setDone(""); setMode("change"); }, true)}
          {hasCode && btn("Remove passcode", () => { setDone(""); setMode("remove"); }, false)}
          {hasCode && onLockNow && (
            <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #eee" }}>
              <button onClick={onLockNow}
                style={{ background: NAVY, color: "#fff", border: "none", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 600 }}>
                🔒 Lock now
              </button>
              <div style={{ fontSize: "12px", color: "#888", lineHeight: 1.6, marginTop: "8px" }}>
                Locks this diary immediately (e.g. before handing your phone to someone).
                It also re-locks on its own if you leave the app for more than 5 minutes.
              </div>
            </div>
          )}
        </div>
      )}

      {mode && (
        <div>
          {(mode === "change" || mode === "remove") && (
            <div>
              <div style={{ fontSize: "13px", color: "#555", marginBottom: "4px" }}>Current passcode</div>
              <input type="password" inputMode="numeric" autoComplete="new-password" name="lt-current" value={current}
                onChange={(e) => { setCurrent(onlyDigits(e.target.value)); setError(""); }}
                style={inputStyle} />
            </div>
          )}
          {(mode === "set" || mode === "change") && (
            <>
              <div style={{ fontSize: "13px", color: "#555", marginBottom: "4px" }}>{mode === "set" ? "New passcode" : "New passcode"}</div>
              <input type="password" inputMode="numeric" autoComplete="new-password" name="lt-new" value={next}
                onChange={(e) => { setNext(onlyDigits(e.target.value)); setError(""); }}
                style={inputStyle} />
              <div style={{ fontSize: "13px", color: "#555", marginBottom: "4px" }}>Confirm new passcode</div>
              <input type="password" inputMode="numeric" autoComplete="new-password" name="lt-confirm" value={confirm}
                onChange={(e) => { setConfirm(onlyDigits(e.target.value)); setError(""); }}
                style={inputStyle} />
            </>
          )}
          {error && <div style={{ fontSize: "13px", color: "#c0392b", marginBottom: "10px" }}>{error}</div>}
          <div style={{ marginTop: "6px" }}>
            {btn(busy ? "Saving…" : (mode === "remove" ? "Remove" : "Save"), save, true)}
            {btn("Cancel", reset, false)}
          </div>
        </div>
      )}
    </div>
  );
}

// Displays the diary's inbound logging email as a tap-to-copy control, with
// an "Add to contacts" link that generates a vCard (.vcf) on the fly. We use
// a downloadable vCard because it's the only contacts mechanism that works
// across iOS, Android, macOS, and Windows without an API or permissions —
// opening a .vcf triggers the OS "add contact" sheet everywhere.
//
// Module-level (not nested in DiaryView) so it can be reused by both the main
// view and EmptyDiaryCover, which live in different component scopes. It keeps
// its own tiny inline "✓ copied" indicator so callers don't have to pass one
// in; an optional onCopied callback lets a parent that already has a toast
// system surface its own message instead.
//
// `style` is spread onto the email text so each call site keeps its existing
// typography (the cover uses italic Georgia; the settings row uses a code
// font, etc.).
function CopyEmail({ email, diaryName, style, accentColor = ORANGE, onCopied, compact = false }) {
  const [copied, setCopied] = useState(false);
  if (!email) return null;

  const copy = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(email);
      ok = true;
    } catch {
      // Clipboard API can be unavailable (older browsers, insecure context,
      // permission denied). Fall back to a hidden textarea + execCommand so
      // tap-to-copy still works rather than silently failing.
      try {
        const ta = document.createElement("textarea");
        ta.value = email;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    if (onCopied) onCopied(ok);
    else if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1800); }
  };

  // Open Google Contacts. There's no deep link to the Import screen (Import
  // is a button inside the left menu of contacts.google.com — confirmed via
  // Google's own support docs), and no supported URL prefills a new contact.
  // So the honest Google path is: download the .vcf, open Google Contacts,
  // then click Import and pick the file. This opens the base page; the toast
  // tells the user what to do next.
  const openGoogleContacts = (e) => {
    e.stopPropagation();
    window.open("https://contacts.google.com/", "_blank", "noopener");
  };

  // Build and download a vCard for Apple Contacts / Outlook / Android / Google
  // (via Import). Opening a .vcf triggers the OS "add contact" sheet on
  // Apple/Android; Google and Outlook take it through their Import flow.
  const downloadVcard = (e) => {
    e.stopPropagation(); // don't also fire the copy handler
    const name = (diaryName && diaryName.trim()) ? `${diaryName.trim()} (Lifetrackr)` : "Lifetrackr Diary";
    // Escape per vCard spec: backslash, newline, comma, semicolon.
    const esc = (s) => String(s).replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
    const vcard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${esc(name)}`,
      `N:${esc(name)};;;;`,
      `EMAIL;TYPE=INTERNET:${esc(email)}`,
      `NOTE:${esc("Send entries to this address to log them in your " + (diaryName || "Lifetrackr") + " diary.")}`,
      "END:VCARD",
      "",
    ].join("\r\n");
    const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(diaryName || "lifetrackr").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "lifetrackr"}-logging.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const linkStyle = {
    background: "transparent", border: "none", color: accentColor, cursor: "pointer",
    fontSize: "11px", fontFamily: "-apple-system,sans-serif", textDecoration: "underline",
    padding: 0, whiteSpace: "nowrap",
  };

  return (
    <span style={compact
      ? { display: "inline-flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }
      : { display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
      {/* The address itself is tap-to-copy. A dotted underline + a copy icon
          make that affordance obvious — previously it looked like plain text
          and users had no signal it was interactive. */}
      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
        <span
          role="button"
          tabIndex={0}
          onClick={copy}
          onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); copy(); } }}
          title="Tap to copy address"
          style={{ cursor: "pointer", textDecoration: "underline dotted", textUnderlineOffset: "3px", ...style }}
        >
          {email}
        </span>
        <button
          type="button"
          onClick={(ev) => { ev.stopPropagation(); copy(); }}
          title="Copy address"
          aria-label="Copy email address"
          style={{ background: "transparent", border: "none", cursor: "pointer", padding: "2px", lineHeight: 1, fontSize: "13px", color: accentColor, flexShrink: 0 }}
        >
          {copied ? "✓" : "📋"}
        </button>
        {copied && (
          <span style={{ fontSize: "11px", fontStyle: "normal", fontFamily: "-apple-system,sans-serif", color: accentColor, fontWeight: 700 }}>
            copied
          </span>
        )}
      </span>
      {/* Contacts options. The .vcf is the real cross-platform mechanism:
          on iPhone/Mac/Android, opening it adds the contact directly. Google
          and Outlook have no add-by-URL or import deep link, so for those the
          honest flow is download → open Contacts → Import. We say so plainly
          rather than dressing it up as one click. */}
      {!compact && (
        <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
            <button type="button" onClick={downloadVcard} title="Download a contact card. On iPhone, Mac, and Android, opening it adds the contact directly." style={linkStyle}>
              Save to contacts (.vcf)
            </button>
            <span style={{ color: accentColor, opacity: 0.4, fontSize: "10px" }}>·</span>
            <button type="button" onClick={openGoogleContacts} title="Opens Google Contacts. Click Import in the left menu and choose the downloaded .vcf." style={linkStyle}>
              Open Google Contacts
            </button>
          </span>
          <span style={{ fontSize: "10px", color: accentColor, opacity: 0.7, fontFamily: "-apple-system,sans-serif", fontStyle: "normal" }}>
            Phone/Mac: opens directly · Google: download, then Import
          </span>
        </span>
      )}
    </span>
  );
}

function EmptyDiaryCover({ diary, canEdit, onPickSuggestion, onAddEntry, onImport }) {
  return (
    <div style={{ padding: "48px 32px 56px", maxWidth: "640px", margin: "0 auto", textAlign: "center" }}>
      {/* Three-dot ornament */}
      <div style={{ fontSize: "11px", color: "rgba(139,58,10,0.4)", letterSpacing: "3px", marginBottom: "18px", fontFamily: "'Georgia', serif" }}>· · ·</div>

      {/* Diary name */}
      <div style={{ fontFamily: "'Georgia', serif", fontSize: "clamp(28px, 5vw, 40px)", color: NAVY, lineHeight: 1.1, marginBottom: "8px", letterSpacing: "-0.5px", fontWeight: 700 }}>
        {diary.name}
      </div>
      {/* Inbound email */}
      <div style={{ marginBottom: "24px" }}>
        <CopyEmail
          email={diary.inboundEmail}
          diaryName={diary.name}
          accentColor="#8B3A0A"
          style={{ fontFamily: "'Georgia', serif", fontStyle: "italic", fontSize: "13px", color: "#8B3A0A" }}
        />
      </div>

      <div style={{ width: "60px", height: "1px", background: "rgba(139,58,10,0.25)", margin: "0 auto 28px" }} />

      {/* Greeting */}
      <div style={{ fontFamily: "'Georgia', serif", fontStyle: "italic", fontSize: "17px", color: "#3a2a1a", lineHeight: 1.55, maxWidth: "440px", margin: "0 auto 8px" }}>
        Let's start writing your life's story, one moment at a time.
      </div>

      {canEdit ? (
        <>
          <p style={{ fontSize: "13px", color: "#8a7a65", fontFamily: "-apple-system,sans-serif", margin: "0 0 24px" }}>
            All is fair game. Tap a starter below, or write your own.
          </p>

          {/* Suggestion bubble grid */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center", marginBottom: "28px" }}>
            {EMPTY_SUGGESTIONS.map((s, i) => {
              const m = getTagMeta(s.tag);
              return (
                <button key={i}
                  onClick={() => onPickSuggestion(s.title, s.tag)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "6px",
                    background: m.bg, color: m.color, border: `1px solid ${m.border}`,
                    borderRadius: "24px", padding: "8px 16px",
                    fontSize: "13px", fontWeight: 600,
                    cursor: "pointer", fontFamily: "-apple-system,sans-serif",
                    transition: "transform 0.1s, box-shadow 0.1s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = `0 2px 8px ${m.border}`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                  <span>{m.emoji}</span>
                  <span>{s.title}</span>
                </button>
              );
            })}
          </div>

          {/* Start from scratch */}
          <button onClick={onAddEntry}
            style={{ background: ORANGE, color: "white", border: "none", padding: "11px 26px", borderRadius: "22px", fontSize: "14px", fontWeight: 700, cursor: "pointer", fontFamily: "-apple-system,sans-serif", boxShadow: "0 3px 12px rgba(200,56,3,0.25)" }}>
            + Write your own
          </button>

          {/* Subtle import-from-CSV link — only useful before the diary has any
              content, so it lives in the empty state (not on populated diaries). */}
          {onImport && (
            <div style={{ marginTop: "16px" }}>
              <button onClick={onImport}
                style={{ background: "transparent", border: "none", color: "#8a7a65", fontSize: "12px", cursor: "pointer", fontFamily: "-apple-system,sans-serif", textDecoration: "underline", textDecorationColor: "#cdb89e" }}>
                or import from a CSV
              </button>
            </div>
          )}
        </>
      ) : (
        <p style={{ fontSize: "13px", color: "#8a7a65", fontFamily: "-apple-system,sans-serif", margin: "20px 0 0", fontStyle: "italic" }}>
          Waiting for the first entry...
        </p>
      )}

      <div style={{ fontSize: "11px", color: "rgba(139,58,10,0.4)", letterSpacing: "3px", marginTop: "32px", fontFamily: "'Georgia', serif" }}>· · ·</div>
    </div>
  );
}

// ── TIMELINE DETAIL ENTRY ─────────────────────────────────────────────────────
// Entry rendered inside a timeline card — shows title, full body (HTML),
// inline photo thumbnails, and tags. No click-to-expand; always expanded.

function TimelineDetailEntry({ entry, diary, canEdit, onEdit, isSelected, selectMode, onSelect, onTagClick }) {
  const isFavorite = !!entry.favorite;
  const hasFollowup = (entry.tags || []).some(isFollowupTag);
  const displayTags = (entry.tags || []).filter(t => !isFollowupTag(t));
  const who = entry._who || "";

  const handleCheckboxClick = e => { e.stopPropagation(); e.preventDefault(); onSelect(entry.id); };

  const rowClick = e => {
    if (selectMode) { e.stopPropagation(); onSelect(entry.id); return; }
    if (canEdit && onEdit) onEdit(entry);
  };
  // Double-click is wired explicitly for parity with ListEntry's
  // double-click-to-edit shortcut. In detail mode, single-click already
  // opens edit, so dblclick is functionally redundant — but having
  // both behaviors registered prevents accidental side-effects from
  // the first click of a double-click pair in case of future changes
  // to single-click behavior. Stops propagation to be safe.
  const rowDoubleClick = e => {
    if (selectMode) return;
    if (canEdit && onEdit) { e.stopPropagation(); onEdit(entry); }
  };

  return (
    <div onClick={rowClick} onDoubleClick={rowDoubleClick}
      style={{ padding: "14px 16px", cursor: canEdit ? "pointer" : "default", background: isSelected ? "rgba(200,56,3,0.04)" : "transparent" }}>
      {/* Header row: title + time + icons */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: entry.title ? "6px" : "0" }}>
        {entry.title && (
          <div style={{ fontSize: "15px", fontWeight: "600", color: NAVY, fontFamily: "-apple-system,sans-serif", flex: 1, lineHeight: 1.35 }}>
            {entry.title}
          </div>
        )}
        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0, marginLeft: entry.title ? 0 : "auto" }}>
          {(() => {
            const d = entry.entryDate?.toDate ? entry.entryDate.toDate() : (entry.entryDate ? new Date(entry.entryDate) : null);
            if (!d) return null;
            const timeStr = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
            return <span style={{ fontSize: "11px", color: "#aaa", fontStyle: "italic", fontFamily: "-apple-system,sans-serif", whiteSpace: "nowrap" }}>{timeStr}</span>;
          })()}
          {isFavorite && <span style={{ fontSize: "13px" }}>⭐</span>}
          {hasFollowup && <span style={{ fontSize: "13px" }}>🚩</span>}
          {entry.private && <span style={{ fontSize: "13px" }} title="Private entry — excluded from share/export">🔒</span>}
        </div>
      </div>

      {/* Body */}
      {(entry.bodyHtml || entry.body) && (
        <div className="entry-body"
          onClick={e => { if (e.target.tagName === "A") e.stopPropagation(); }}
          style={{ fontSize: "13px", color: "#444", lineHeight: 1.6, fontFamily: "-apple-system,sans-serif", wordWrap: "break-word" }}>
          {entry.bodyHtml
            ? <div dangerouslySetInnerHTML={{ __html: sanitize(entry.bodyHtml) }} />
            : <div dangerouslySetInnerHTML={{ __html: textToLinkifiedHtml(entry.body) }} />}
        </div>
      )}

      {/* Structured data table — read-only summary of captured fields */}
      <StructuredDataTable entry={entry} diary={diary} />
          <MultiReceiptBanner entry={entry} />
          <ReceiptDateSync entry={entry} />

      {/* Photos */}
      {(entry.photos || []).length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "10px" }}>
          {entry.photos.map((p, i) => (
            <div key={i} onClick={e => { e.stopPropagation(); (window.__ltOpenLightbox ? window.__ltOpenLightbox(entry.photos, i) : window.open(p.url, "_blank")); }}
              style={{ cursor: "pointer", border: "1px solid #ddd", borderRadius: "4px", overflow: "hidden", background: "white", flexShrink: 0 }}>
              <img src={p.url} alt="" style={{ width: "76px", height: "76px", objectFit: "cover", display: "block" }} />
            </div>
          ))}
        </div>
      )}

      {/* Videos and audio */}
      <MediaRow entry={entry} size={76} />

      {/* Tags + author. Each tag chip is a clickable filter button —
          tapping it activates that tag as the diary's sole filter so the
          user can pivot to "show me everything with this tag." We
          stopPropagation so the click doesn't also trigger the entry's
          tap-to-edit (the outer rowClick handler). */}
      {(displayTags.length > 0 || who) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "10px", alignItems: "center" }}>
          {displayTags.map((tag, i) => {
            const m = getTagMeta(tag);
            const clickable = !!onTagClick && !selectMode;
            return (
              <button key={i}
                onClick={clickable ? (ev) => { ev.stopPropagation(); onTagClick(tag); } : undefined}
                disabled={!clickable}
                title={clickable ? `Filter diary by "${tag}"` : undefined}
                style={{
                  background: m.bg,
                  color: m.color,
                  border: `1px solid ${m.border}`,
                  borderRadius: "20px",
                  padding: "1px 8px",
                  fontSize: "11px",
                  fontWeight: "600",
                  fontFamily: "-apple-system,sans-serif",
                  cursor: clickable ? "pointer" : "default",
                }}>
                {m.emoji} {tag}
              </button>
            );
          })}
          {(who || entry.structured?.city) && (
            <span style={{ fontSize: "11px", color: "#aaa", fontFamily: "-apple-system,sans-serif", fontStyle: "italic", marginLeft: "auto" }}>
              — {entry.structured?.city ? `${entry.structured.city}${who ? ", " + who : ""}` : who}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function ListEntry({ entry, diary, canEdit, onEdit, isSelected, selectMode, onSelect, onTagClick, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const isFavorite = !!entry.favorite;
  const hasFollowup = (entry.tags || []).some(isFollowupTag);
  const who = entry._who || "";

  const handleCheckboxClick = e => { e.stopPropagation(); e.preventDefault(); onSelect(entry.id); };
  const Checkbox = () => (
    <div onClick={handleCheckboxClick}
      style={{ width: "18px", height: "18px", borderRadius: "3px", border: `1.5px solid ${isSelected ? ORANGE : "#c0bbb5"}`, background: isSelected ? ORANGE : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer", marginTop: "2px", opacity: 0.35 }}>
      {isSelected && <span style={{ color: "white", fontSize: "11px", fontWeight: "700", lineHeight: 1 }}>✓</span>}
    </div>
  );

  return (
    <div style={{ borderBottom: compact ? "none" : "1px solid #ede9e0", background: isSelected ? "rgba(200,56,3,0.04)" : "transparent" }}>
      {/* Collapsed row.
          Single-click toggles the inline-expand (peek view — shows
          full body, photos, etc. without opening the edit modal).
          Double-click opens the edit modal directly — power-user
          shortcut for users who want to skip the peek step. Mobile
          equivalent is double-tap (same dblclick event fires). */}
      <div style={{ display: "flex", alignItems: "flex-start", cursor: "pointer" }}
        onClick={e => { if (selectMode) { e.stopPropagation(); onSelect(entry.id); } else setExpanded(x => !x); }}
        onDoubleClick={e => {
          if (selectMode) return;
          if (canEdit && onEdit) {
            e.stopPropagation();
            // Collapse the row that the first click of this double-
            // click pair just expanded. Keeps the state consistent
            // when the user comes back from the edit modal.
            setExpanded(false);
            onEdit(entry);
          }
        }}>
        {/* Gutter — hidden in compact/timeline mode since the timeline provides its own left margin */}
        {!compact && (
          <div style={{ width: "36px", flexShrink: 0, display: "flex", justifyContent: "center", paddingTop: "14px" }}>
            {canEdit && <Checkbox />}
          </div>
        )}
        {/* Content */}
        <div style={{ flex: 1, padding: compact ? "10px 12px" : "10px 12px 10px 4px", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, minWidth: 0 }}>
              {/* Expand indicator */}
              <span style={{ fontSize: "10px", color: "#ccc", flexShrink: 0 }}>{expanded ? "▾" : "▸"}</span>
              <span style={{ fontSize: "14px", fontWeight: entry.title ? "600" : "400", color: entry.title ? "#0B1F4A" : "#555", fontFamily: "-apple-system,sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {entry.title || (entry.body || "").slice(0, 60) || "Untitled"}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
              {(() => {
                // "Needs attention" chips: surfaced even in compact mode so the
                // user knows there's a suggestion without expanding every row.
                // Tapping a chip expands the row (peek view), where the full
                // ReceiptDateSync / MultiReceiptBanner lets them confirm. We
                // don't act on tap here — the confirm step stays (matches the
                // prompt-don't-auto-apply design for dates and splits).
                const st = entry.structured || {};
                const ocr = st.ocr || {};
                const rDate = typeof ocr.date === "string" ? ocr.date : null;
                const pDate = typeof st.photoDate === "string" ? st.photoDate : null;
                // Same precedence as the expanded banner: receipt wins, photo
                // is the fallback. One chip covers either source.
                const candDate = rDate || pDate;
                const candValid = candDate && /^\d{4}-\d{2}-\d{2}$/.test(candDate);
                const candFuture = candValid && candDate > toInput(new Date());
                // Show the "date?" chip only for an actionable mismatch — a
                // valid, non-future candidate date that differs from the entry
                // date. Future dates are misreads (handled as a muted note in
                // the expanded view), so no chip — nothing to act on.
                const dateMismatch = !st.receiptDateDismissed
                  && candValid && !candFuture
                  && toInput(entry.entryDate) !== candDate;
                const needsSplit = !st.multiReceiptDismissed
                  && (st.multiReceiptDetected || 0) >= 2;
                if (!dateMismatch && !needsSplit) return null;
                const chip = (label, bg, fg) => (
                  <button
                    onClick={(ev) => { ev.stopPropagation(); setExpanded(true); }}
                    title="Tap to review"
                    style={{ fontSize: "10px", fontWeight: 600, padding: "1px 7px", borderRadius: "20px", background: bg, color: fg, border: "none", cursor: "pointer", whiteSpace: "nowrap", lineHeight: 1.6 }}>
                    {label}
                  </button>
                );
                return (
                  <>
                    {needsSplit && chip("split?", "#FFF4E5", "#C83803")}
                    {dateMismatch && chip("date?", "#EAF4FF", "#1B6FC4")}
                  </>
                );
              })()}
              {isFavorite && <span style={{ fontSize: "13px" }}>⭐</span>}
              {hasFollowup && <span style={{ fontSize: "13px" }}>🚩</span>}
              {entry.private && <span style={{ fontSize: "13px" }} title="Private — excluded from share/export">🔒</span>}
              {!compact && <span style={{ fontSize: "11px", color: "#aaa", fontFamily: "-apple-system,sans-serif", fontStyle: "italic", whiteSpace: "nowrap" }}>{fmtShort(entry.entryDate)}</span>}
            </div>
          </div>
          {/* Tags row — always visible. Each chip is a clickable filter:
              tapping a tag activates it as the sole filter for the diary,
              letting the user pivot to "show me everything with this tag"
              with one click. stopPropagation prevents the entry's own
              tap-to-edit behavior from also firing. */}
          {(entry.tags || []).filter(t => !isFollowupTag(t)).length > 0 && (
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", marginTop: "4px" }}>
              {(entry.tags || []).filter(t => !isFollowupTag(t)).map((tag, i) => {
                const m = getTagMeta(tag);
                const clickable = !!onTagClick && !selectMode;
                return (
                  <button key={i}
                    onClick={clickable ? (ev) => { ev.stopPropagation(); onTagClick(tag); } : undefined}
                    disabled={!clickable}
                    title={clickable ? `Filter diary by "${tag}"` : undefined}
                    style={{
                      background: m.bg,
                      color: m.color,
                      border: `1px solid ${m.border}`,
                      borderRadius: "20px",
                      padding: "1px 7px",
                      fontSize: "11px",
                      fontWeight: "600",
                      fontFamily: "-apple-system,sans-serif",
                      cursor: clickable ? "pointer" : "default",
                    }}>
                    {m.emoji} {tag}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: "0 12px 12px 40px" }}>
          {who && <div style={{ fontSize: "11px", color: "#aaa", fontStyle: "italic", marginBottom: "6px", fontFamily: "-apple-system,sans-serif" }}>{fmtDate(entry.entryDate)} · {who}</div>}
          <div onClick={e => { if (e.target.tagName === "A") e.stopPropagation(); }}
            style={{ fontSize: "14px", color: "#333", lineHeight: 1.65, fontFamily: "-apple-system,sans-serif", marginBottom: "8px" }}>
            {entry.bodyHtml
              ? <div className="entry-body" dangerouslySetInnerHTML={{ __html: sanitize(entry.bodyHtml) }} />
              : <div className="entry-body" dangerouslySetInnerHTML={{ __html: textToLinkifiedHtml(entry.body || "") }} />}
          </div>
          <StructuredDataTable entry={entry} diary={diary} />
          <MultiReceiptBanner entry={entry} />
          <ReceiptDateSync entry={entry} />
          {entry.photos?.length > 0 && (
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "8px" }}>
              {entry.photos.map((p, i) => (
                <div key={i} onClick={() => (window.__ltOpenLightbox ? window.__ltOpenLightbox(entry.photos, i) : window.open(p.url, "_blank"))}
                  style={{ cursor: "pointer", borderRadius: "6px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.07)" }}>
                  <img src={p.url} alt="" style={{ width: "80px", height: "60px", objectFit: "cover", display: "block" }} />
                </div>
              ))}
            </div>
          )}
          <MediaRow entry={entry} size={72} />
          {canEdit && !selectMode && (
            <button onClick={() => onEdit(entry)} style={{ background: "none", border: "none", color: ORANGE, fontSize: "13px", cursor: "pointer", fontFamily: "-apple-system,sans-serif", padding: 0 }}>Edit →</button>
          )}
        </div>
      )}
    </div>
  );
}

// ── MULTI-TAG LENS BAR ────────────────────────────────────────────────────────

function LensBar({ allTags, activeLenses, onToggle, isRecordsMode, lensMode, setLensMode, chipRowRef, excludedTags, tagAncestorGroups, activeGroups }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  // How many tag chips fit fully on the row given the available width.
  // Computed by JS measurement so partial chips never render.
  const [visibleCount, setVisibleCount] = useState(allTags.length);
  const scrollRowRef = useRef(null);
  const moreRef = useRef(null);
  const measureRef = useRef(null);
  if (!allTags.length) return null;
  const radius = isRecordsMode ? "4px" : "16px";
  const pad = isRecordsMode ? "3px 9px" : "3px 10px";

  // Tag state helpers. A tag can be in one of four visual states:
  //   - Active explicitly (user clicked it; activeLenses.has(tag))
  //   - Implicitly active (one of its ancestor groups is active and the
  //     tag isn't excluded)
  //   - Excluded (one of its ancestor groups is active but user struck
  //     it out)
  //   - Inactive (default)
  const safeExcludedTags = excludedTags || new Set();
  const safeAncestors = tagAncestorGroups || new Map();
  const safeActiveGroups = activeGroups || new Set();
  const tagState = (tag) => {
    if (activeLenses.has(tag)) return "active";
    // Excluded — check this BEFORE the ancestor lookup so it works
    // whether or not a parent group is active. Excluded-without-group
    // means "show everything EXCEPT this tag" (a base-state exclusion).
    if (safeExcludedTags.has(tag)) return "excluded";
    const ancestors = safeAncestors.get(tag);
    const hasActiveAncestor = ancestors && [...ancestors].some(gid => safeActiveGroups.has(gid));
    if (hasActiveAncestor) return "implicit";
    return "inactive";
  };

  // Measure how many chips fit. We render ALL chips into a hidden measure
  // row offscreen, read their widths, then compute how many fit in the
  // visible row's width minus the More chip's width. Re-runs on resize and
  // whenever the tag list changes.
  useLayoutEffect(() => {
    const row = scrollRowRef.current;
    const measureRow = measureRef.current;
    if (!row || !measureRow) return;
    const compute = () => {
      const rowWidth = row.clientWidth;
      const moreWidth = moreRef.current?.offsetWidth || 60;
      const gap = 5;
      const reservedForMore = allTags.length > 4 ? moreWidth + gap : 0;
      // Children of measure row: [All button, ...tag buttons]
      const kids = [...measureRow.children];
      let used = 0;
      let count = 0;
      // First child is the All button — always shown
      if (kids[0]) used += kids[0].offsetWidth + gap;
      // Walk through tag chips and accumulate
      for (let i = 1; i < kids.length; i++) {
        const w = kids[i].offsetWidth;
        if (used + w + reservedForMore <= rowWidth) {
          used += w + gap;
          count++;
        } else {
          break;
        }
      }
      setVisibleCount(count);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(row);
    return () => ro.disconnect();
  }, [allTags.length, allTags.join("|")]);

  // Filter tags inside the picker popover by search query
  const filteredPickerTags = pickerSearch.trim()
    ? allTags.filter(t => t.toLowerCase().includes(pickerSearch.toLowerCase()))
    : allTags;

  const showModeToggle = activeLenses.size >= 2 && setLensMode;
  const visibleTags = allTags.slice(0, visibleCount);
  const hiddenCount = allTags.length - visibleCount;

  // Render a single chip — used by both the visible row and the (hidden)
  // measure row so widths are identical.
  const renderTagChip = (tag, key) => {
    const m = getTagMeta(tag);
    const state = tagState(tag);
    // Visual styling per state:
    //   active   → filled with tag color, white text (user explicitly chose)
    //   implicit → filled with tag color, white text (covered by group, looks
    //              identical to active so the user clearly sees what's
    //              included). Clicking excludes.
    //   excluded → grey background, strikethrough text, dimmed
    //   inactive → default (light fill, normal text)
    let bg, color, border, textDecoration, opacity;
    if (state === "active" || state === "implicit") {
      bg = m.color; color = "white"; border = m.color; textDecoration = "none"; opacity = 1;
    } else if (state === "excluded") {
      bg = "#f0f0f0"; color = "#999"; border = "#d0d0d0"; textDecoration = "line-through"; opacity = 0.7;
    } else {
      bg = m.bg; color = m.color; border = m.border; textDecoration = "none"; opacity = 1;
    }
    const title = state === "implicit"
      ? `Included via group. Click to exclude.`
      : state === "excluded"
      ? `Excluded — entries with "${tag}" are hidden. Click to clear.`
      : state === "active"
      ? `Included. Click to exclude instead.`
      : `Click to include. Click again to exclude.`;
    return (
      <button key={key} onClick={() => onToggle(tag)}
        title={title}
        style={{ flexShrink: 0, background: bg, color, border: `1px solid ${border}`, borderRadius: radius, padding: pad, fontSize: "12px", fontWeight: "600", cursor: "pointer", fontFamily: "-apple-system,sans-serif", whiteSpace: "nowrap", lineHeight: 1.3, textDecoration, opacity }}>
        {m.emoji} {tag}
      </button>
    );
  };

  return (
    <div style={{ position: "relative" }}>
      {showModeToggle && (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 0 0", fontSize: "11px", color: "#666", fontFamily: "-apple-system,sans-serif" }}>
          <span>Show entries matching</span>
          <span style={{ display: "inline-flex", border: "1px solid rgba(0,0,0,0.15)", borderRadius: "12px", overflow: "hidden", background: "white" }}>
            <button onClick={() => setLensMode("any")}
              style={{ background: lensMode === "any" ? NAVY : "transparent", color: lensMode === "any" ? "white" : "#888", border: "none", padding: "3px 10px", fontSize: "11px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              any
            </button>
            <button onClick={() => setLensMode("all")}
              style={{ background: lensMode === "all" ? NAVY : "transparent", color: lensMode === "all" ? "white" : "#888", border: "none", padding: "3px 10px", fontSize: "11px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              all
            </button>
          </span>
          <span>selected tags</span>
        </div>
      )}

      {/* Visible chip row — only shows tags that fully fit. Partial chips
          never render. The "More ▾" chip is at the end with the same gap. */}
      <div ref={(el) => { scrollRowRef.current = el; if (chipRowRef) chipRowRef.current = el; }}
        style={{ display: "flex", flexWrap: "nowrap", gap: "5px", padding: "6px 0 0", overflow: "hidden" }}>
        <button onClick={() => onToggle(null)}
          style={{ flexShrink: 0, background: activeLenses.size === 0 ? "#555" : "#f0f0f0", color: activeLenses.size === 0 ? "white" : "#555", border: `1px solid ${activeLenses.size === 0 ? "#555" : "#ddd"}`, borderRadius: radius, padding: pad, fontSize: "12px", fontWeight: "600", cursor: "pointer", fontFamily: "-apple-system,sans-serif", whiteSpace: "nowrap", lineHeight: 1.3 }}>
          All
        </button>
        {visibleTags.map((tag, i) => renderTagChip(tag, `vis-${tag}`))}
        {(allTags.length > 4 || hiddenCount > 0) && (
          <button ref={moreRef} onClick={() => setPickerOpen(true)}
            style={{ flexShrink: 0, background: "#f0f0f0", color: "#555", border: "1px solid #ddd", borderRadius: radius, padding: pad, fontSize: "12px", fontWeight: "600", cursor: "pointer", fontFamily: "-apple-system,sans-serif", whiteSpace: "nowrap", lineHeight: 1.3, display: "inline-flex", alignItems: "center", gap: "3px" }}>
            More{hiddenCount > 0 ? ` (${hiddenCount})` : ""} ▾
          </button>
        )}
      </div>

      {/* Hidden measure row — rendered offscreen but lays out exactly like the
          visible row, so we can measure each chip's actual rendered width.
          Necessary because chip widths depend on text length and emoji. */}
      <div ref={measureRef}
        aria-hidden="true"
        style={{ position: "absolute", visibility: "hidden", pointerEvents: "none", display: "flex", gap: "5px", top: 0, left: 0, whiteSpace: "nowrap" }}>
        <button style={{ flexShrink: 0, padding: pad, fontSize: "12px", fontWeight: "600", fontFamily: "-apple-system,sans-serif", border: "1px solid transparent", borderRadius: radius }}>All</button>
        {allTags.map((tag, i) => {
          const m = getTagMeta(tag);
          return (
            <button key={`m-${tag}`} style={{ flexShrink: 0, padding: pad, fontSize: "12px", fontWeight: "600", fontFamily: "-apple-system,sans-serif", border: "1px solid transparent", borderRadius: radius }}>
              {m.emoji} {tag}
            </button>
          );
        })}
      </div>

      {/* Picker popover — scrollable list of all tags with search + select.
          Click outside to dismiss. Multi-select friendly: clicks toggle but
          the popover stays open. */}
      {pickerOpen && (
        <div onClick={() => setPickerOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "white", borderRadius: "12px", padding: "16px", maxWidth: "420px", width: "100%", maxHeight: "70vh", display: "flex", flexDirection: "column", boxShadow: "0 12px 40px rgba(0,0,0,0.3)", fontFamily: "-apple-system,sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
              <div style={{ fontSize: "14px", fontWeight: 700, color: NAVY, flex: 1 }}>
                {activeLenses.size > 0 ? `${activeLenses.size} of ${allTags.length} selected` : `All ${allTags.length} tags`}
              </div>
              <button onClick={() => setPickerOpen(false)}
                style={{ background: ORANGE, color: "white", border: "none", padding: "5px 14px", borderRadius: "6px", fontSize: "13px", fontWeight: 700, cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                Done
              </button>
            </div>
            <input type="text" autoFocus placeholder="Search tags…" value={pickerSearch}
              onChange={e => setPickerSearch(e.target.value)}
              style={{ border: "1px solid #ddd", borderRadius: "6px", padding: "7px 10px", fontSize: "13px", outline: "none", marginBottom: "10px", fontFamily: "-apple-system,sans-serif" }} />
            <div style={{ flex: 1, overflowY: "auto", borderTop: "1px solid #f0f0f0" }}>
              {filteredPickerTags.length === 0 ? (
                <div style={{ padding: "20px", textAlign: "center", color: "#999", fontSize: "13px" }}>No tags match "{pickerSearch}"</div>
              ) : (
                filteredPickerTags.map(tag => {
                  const m = getTagMeta(tag);
                  const state = tagState(tag);
                  // Mirror the chip-row semantics:
                  //   active   → checkbox filled (explicit user pick)
                  //   implicit → checkbox filled (included via active group)
                  //   excluded → checkbox empty + strikethrough on the tag
                  //   inactive → checkbox empty
                  const checked = state === "active" || state === "implicit";
                  const excluded = state === "excluded";
                  return (
                    <div key={tag} onClick={() => onToggle(tag)}
                      style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 4px", borderBottom: "1px solid #f5f5f5", cursor: "pointer", userSelect: "none", opacity: excluded ? 0.7 : 1 }}>
                      <div style={{ width: "18px", height: "18px", borderRadius: "4px", border: `1.5px solid ${checked ? NAVY : "#ccc"}`, background: checked ? NAVY : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {checked && <span style={{ color: "white", fontSize: "12px", lineHeight: 1, fontWeight: 700 }}>✓</span>}
                      </div>
                      <span style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}`, borderRadius: "12px", padding: "2px 8px", fontSize: "12px", fontWeight: 600, textDecoration: excluded ? "line-through" : "none" }}>
                        {m.emoji} {tag}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
            {activeLenses.size > 0 && (
              <button onClick={() => onToggle(null)}
                style={{ marginTop: "10px", background: "transparent", color: ORANGE, border: "1px solid rgba(200,56,3,0.3)", padding: "6px 12px", borderRadius: "6px", fontSize: "12px", fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                Clear all
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Field metadata for the field-filter pills. Maps internal field key to
// display icon + label. Built-ins are listed; library fields and custom
// fields are formatted on the fly.
const FIELD_FILTER_META = {
  amount: { icon: "💰", label: "$" },
  location: { icon: "📍", label: "Location" },
  city: { icon: "🏙️", label: "City" },
  address: { icon: "📮", label: "Address" },
  people: { icon: "👥", label: "People" },
  timeMinutes: { icon: "⏱️", label: "Time" },
  distanceMiles: { icon: "🏃", label: "Distance" },
  calories: { icon: "🔥", label: "Calories" },
  mood: { icon: "😊", label: "Mood" },
  rating: { icon: "⭐", label: "Rating" },
  score: { icon: "🏆", label: "Score" },
};

function getFieldDisplay(fieldKey) {
  if (FIELD_FILTER_META[fieldKey]) return FIELD_FILTER_META[fieldKey];
  if (fieldKey.startsWith("library.")) {
    const k = fieldKey.slice(8);
    const def = FIELD_LIBRARY[k];
    return { icon: def?.icon || "🔖", label: def?.label || k };
  }
  if (fieldKey.startsWith("custom.")) {
    const k = fieldKey.slice(7);
    return { icon: "🏷️", label: k.charAt(0).toUpperCase() + k.slice(1) };
  }
  return { icon: "🔖", label: fieldKey };
}

// ── INSIGHTS PANEL ────────────────────────────────────────────────────────────

function InsightsPanel({ entries, activeLenses }) {
  if (!entries.length) return null;
  const now = new Date();

  // Which tags to show insights for
  const targetTags = activeLenses.size > 0 ? [...activeLenses] : Object.keys(TAG_KEYWORDS);

  // Caller is expected to pass already-visibility-filtered entries.

  // Follow-up panel now lives above entries — skip here
  const followups = [];

  const insights = [];
  for (const tag of targetTags) {
    if (tag === "follow-up") continue;
    const list = entries.filter(e => (e.tags || []).includes(tag));
    if (!list.length) continue;
    const m = getTagMeta(tag);
    const sorted = list.sort((a, b) => getTs(b.entryDate) - getTs(a.entryDate));
    const latestDate = sorted[0].entryDate?.toDate ? sorted[0].entryDate.toDate() : new Date(sorted[0].entryDate);
    const daysAgo = Math.floor((now - latestDate) / 86400000);
    if (daysAgo === 0) insights.push({ emoji: m.emoji, text: `Last ${tag} entry: today` });
    else if (daysAgo === 1) insights.push({ emoji: m.emoji, text: `Last ${tag} entry: yesterday` });
    else if (daysAgo < 30) insights.push({ emoji: m.emoji, text: `Last ${tag} entry: ${daysAgo} days ago` });
    const thisMonth = list.filter(e => {
      const d = e.entryDate?.toDate ? e.entryDate.toDate() : new Date(e.entryDate);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    if (thisMonth > 1) {
      const noun = tag === "food" ? "restaurants" : tag === "auto" ? "car services" : tag === "medical" ? "appointments" : `${tag} entries`;
      insights.push({ emoji: m.emoji, text: `${thisMonth} ${noun} this month` });
    }
    if (activeLenses.size === 1) {
      const thisYear = list.filter(e => {
        const d = e.entryDate?.toDate ? e.entryDate.toDate() : new Date(e.entryDate);
        return d.getFullYear() === now.getFullYear();
      }).length;
      if (thisYear > 1) insights.push({ emoji: m.emoji, text: `${thisYear} ${tag} entries this year` });
    }
  }

  const totalPhotos = entries.filter(e => activeLenses.size === 0 || (e.tags || []).some(t => activeLenses.has(t))).reduce((a, e) => a + (e.photos?.length || 0), 0);
  if (totalPhotos > 0 && activeLenses.size === 0) insights.push({ emoji: "📸", text: `${totalPhotos} photo${totalPhotos !== 1 ? "s" : ""} captured` });

  if (!followups.length && !insights.length) return null;

  return (
    <div style={{ margin: "24px 0 0" }}>
      {/* Follow-ups */}
      {followups.length > 0 && (
        <div style={{ marginBottom: "12px", background: "#fff8f0", border: "2px solid rgba(200,56,3,0.3)", borderRadius: "10px", overflow: "hidden" }}>
          <div style={{ background: ORANGE, padding: "8px 16px", display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "14px" }}>🚩</span>
            <span style={{ fontSize: "12px", fontWeight: "700", color: "white", textTransform: "uppercase", letterSpacing: "0.8px", fontFamily: "-apple-system,sans-serif" }}>
              {followups.length} Follow-up{followups.length !== 1 ? "s" : ""} Needed
            </span>
          </div>
          {followups.slice(0, 5).map((e, i) => {
            const preview = (e.title || e.body || "").slice(0, 65);
            const otherTags = (e.tags || []).filter(t => !isFollowupTag(t));
            return (
              <div key={i} style={{ padding: "9px 16px", borderBottom: i < followups.length - 1 ? "1px solid rgba(200,56,3,0.1)" : "none" }}>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#3d0e00", fontFamily: "-apple-system,sans-serif", marginBottom: "2px" }}>
                  {preview}{preview.length >= 65 ? "…" : ""}
                </div>
                <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "11px", color: "#aaa", fontFamily: "-apple-system,sans-serif" }}>{fmtShort(e.entryDate)}</span>
                  {otherTags.map((tag, j) => {
                    const tm = getTagMeta(tag);
                    return <span key={j} style={{ background: tm.bg, color: tm.color, border: `1px solid ${tm.border}`, borderRadius: "3px", padding: "0 5px", fontSize: "10px", fontWeight: "700", fontFamily: "-apple-system,sans-serif" }}>{tm.emoji} {tag}</span>;
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* General insights */}
      {insights.length > 0 && (
        <div style={{ padding: "16px 18px", background: "rgba(200,56,3,0.04)", border: "1px solid rgba(200,56,3,0.12)", borderRadius: "10px" }}>
          <div style={{ fontSize: "11px", fontWeight: "700", color: ORANGE, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "12px", fontFamily: "-apple-system,sans-serif" }}>✦ {activeLenses.size > 0 ? [...activeLenses].join(" + ") + " insights" : "Diary Insights"}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
            {insights.slice(0, 5).map((ins, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "16px" }}>{ins.emoji}</span>
                <span style={{ fontSize: "14px", color: "#444", fontFamily: "-apple-system,sans-serif", fontStyle: "italic" }}>{ins.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── MODAL ─────────────────────────────────────────────────────────────────────

// ── LIGHTBOX — full-screen photo viewer ───────────────────────────────────────

function Lightbox({ photos, startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const hasMultiple = photos.length > 1;
  const current = photos[idx];

  // Keyboard navigation: arrows to move, Escape to close
  useEffect(() => {
    const handler = e => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && hasMultiple) setIdx(i => (i - 1 + photos.length) % photos.length);
      else if (e.key === "ArrowRight" && hasMultiple) setIdx(i => (i + 1) % photos.length);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [hasMultiple, photos.length, onClose]);

  // Push a history sentinel so mobile back-gesture closes the lightbox, not the page
  useEffect(() => {
    window.history.pushState({ __lifetrackrLightbox: true }, "");
    let popped = false;
    const handlePop = () => { popped = true; onClose(); };
    window.addEventListener("popstate", handlePop);
    return () => {
      window.removeEventListener("popstate", handlePop);
      if (!popped) window.history.back();
    };
  }, [onClose]);

  if (!current) return null;

  const prev = e => { e.stopPropagation(); setIdx(i => (i - 1 + photos.length) % photos.length); };
  const next = e => { e.stopPropagation(); setIdx(i => (i + 1) % photos.length); };

  return (
    <div onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)",
        zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "zoom-out",
      }}>
      {/* Close button — top right */}
      <button onClick={e => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        style={{
          position: "absolute", top: "max(12px, env(safe-area-inset-top, 12px))", right: "16px",
          width: "40px", height: "40px", borderRadius: "50%",
          background: "rgba(255,255,255,0.12)", border: "none",
          color: "white", fontSize: "22px", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 2,
        }}>×</button>

      {/* Counter — top center (only if multiple) */}
      {hasMultiple && (
        <div style={{
          position: "absolute", top: "max(20px, env(safe-area-inset-top, 20px))",
          left: "50%", transform: "translateX(-50%)",
          color: "rgba(255,255,255,0.7)", fontSize: "13px",
          fontFamily: "-apple-system,sans-serif", letterSpacing: "0.5px",
        }}>
          {idx + 1} / {photos.length}
        </div>
      )}

      {/* Prev arrow */}
      {hasMultiple && (
        <button onClick={prev}
          aria-label="Previous photo"
          style={{
            position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)",
            width: "44px", height: "44px", borderRadius: "50%",
            background: "rgba(255,255,255,0.12)", border: "none",
            color: "white", fontSize: "24px", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 2,
          }}>‹</button>
      )}

      {/* Media — image or video based on contentType */}
      {(() => {
        const ct = (current.contentType || "").toLowerCase();
        const isVideo = ct.startsWith("video/") || /\.(mp4|mov|webm|m4v|mkv|avi)$/i.test(current.name || current.url || "");
        if (isVideo) {
          return (
            <video src={current.url} controls autoPlay playsInline
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: "94vw", maxHeight: "88vh", boxShadow: "0 10px 40px rgba(0,0,0,0.5)", borderRadius: "4px", background: "#000" }} />
          );
        }
        return (
          <img src={current.url} alt={current.name || ""}
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: "94vw", maxHeight: "88vh", objectFit: "contain",
              boxShadow: "0 10px 40px rgba(0,0,0,0.5)", borderRadius: "4px",
              cursor: "default",
            }} />
        );
      })()}

      {/* Next arrow */}
      {hasMultiple && (
        <button onClick={next}
          aria-label="Next photo"
          style={{
            position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)",
            width: "44px", height: "44px", borderRadius: "50%",
            background: "rgba(255,255,255,0.12)", border: "none",
            color: "white", fontSize: "24px", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 2,
          }}>›</button>
      )}
    </div>
  );
}

function Modal({ onClose, children, maxWidth = "520px" }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300 }} onClick={onClose}>
      <div style={{ background: "white", borderRadius: "16px", padding: "32px", width: "100%", maxWidth, margin: "0 24px", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MemberAccessEditor — collapsible "Access" panel under each non-owner member
// row. Owner uses this to limit which tags a member can see, and whether they
// can see untagged entries. Saves on every change (no separate save button).
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// MediaGrid — gallery view of all photos, videos, and documents from entries.
// Tiles are ~200-260px on desktop (responsive grid), clicking opens the source
// entry. Each tile shows a small icon overlay indicating the media type.
// ─────────────────────────────────────────────────────────────────────────────
function MediaGrid({ entries, onOpenEntry, compact = false }) {
  // Flatten all media across entries, preserving the source entry reference.
  const items = [];
  for (const e of entries) {
    (e.photos || []).forEach(p => items.push({ kind: "photo", url: p.url, entry: e, ts: getTs(e.entryDate) }));
    (e.videos || []).forEach(v => items.push({ kind: "video", url: v.url, thumb: v.thumb, entry: e, ts: getTs(e.entryDate) }));
    (e.docs || []).forEach(d => items.push({ kind: "doc", url: d.url, name: d.name || "document", mime: d.mime, entry: e, ts: getTs(e.entryDate) }));
  }
  items.sort((a, b) => b.ts - a.ts);
  if (!items.length) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px", color: "#aaa", fontFamily: "-apple-system,sans-serif", fontSize: "14px" }}>
        No photos, videos, or documents yet.
      </div>
    );
  }
  // Pick a friendly file-type icon for documents based on MIME.
  const docIcon = (mime) => {
    const m = (mime || "").toLowerCase();
    if (m.includes("pdf")) return "📄";
    if (m.includes("word") || m.includes("document")) return "📝";
    if (m.includes("sheet") || m.includes("excel")) return "📊";
    if (m.includes("presentation") || m.includes("powerpoint")) return "📽";
    if (m.startsWith("audio")) return "🎵";
    return "📎";
  };
  // Compact ≈ half-size tiles (twice as many per row).
  const minTileSize = compact ? 100 : 200;
  const gap = compact ? "5px" : "8px";
  const playSize = compact ? 28 : 44;
  const playFontSize = compact ? 13 : 20;
  const docIconSize = compact ? 28 : 44;
  const docNameSize = compact ? 10 : 12;
  const dateSize = compact ? 9 : 10;
  const datePad = compact ? "1px 4px" : "2px 6px";
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fill, minmax(${minTileSize}px, 1fr))`,
      gap,
      padding: "0 16px 40px",
    }}>
      {items.map((it, i) => (
        <div key={i} onClick={() => onOpenEntry(it.entry)}
          style={{ position: "relative", aspectRatio: "1 / 1", cursor: "pointer", borderRadius: compact ? "5px" : "8px", overflow: "hidden", background: "#1a1208", border: "1px solid rgba(255,255,255,0.06)", transition: "transform 0.12s" }}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.02)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
          {it.kind === "photo" && (
            <img src={it.url} alt="" loading="lazy"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          )}
          {it.kind === "video" && (
            <>
              {it.thumb ? (
                <img src={it.thumb} alt="" loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              ) : (
                <div style={{ width: "100%", height: "100%", background: "#2a1f1a" }} />
              )}
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: `${playSize}px`, height: `${playSize}px`, borderRadius: "50%", background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: `${playFontSize}px`, paddingLeft: "3px" }}>▶</div>
              </div>
            </>
          )}
          {it.kind === "doc" && (
            <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: compact ? "6px" : "12px", textAlign: "center", background: "linear-gradient(180deg, #f5f1e8, #e8dfc8)", color: "#444", fontFamily: "-apple-system,sans-serif" }}>
              <div style={{ fontSize: `${docIconSize}px`, lineHeight: 1 }}>{docIcon(it.mime)}</div>
              <div style={{ fontSize: `${docNameSize}px`, fontWeight: 600, marginTop: compact ? "5px" : "10px", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", wordBreak: "break-word" }}>
                {it.name}
              </div>
            </div>
          )}
          {/* Date in bottom corner */}
          <div style={{ position: "absolute", bottom: compact ? "4px" : "6px", right: compact ? "4px" : "6px", background: "rgba(0,0,0,0.55)", color: "white", fontSize: `${dateSize}px`, fontFamily: "-apple-system,sans-serif", padding: datePad, borderRadius: "4px", letterSpacing: "0.3px" }}>
            {(() => {
              const d = it.entry.entryDate?.toDate ? it.entry.entryDate.toDate() : new Date(it.entry.entryDate);
              return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
            })()}
          </div>
        </div>
      ))}
    </div>
  );
}


function MemberAccessEditor({ uid, access, allTags, entries = [], lastVisitedMs = null, onChange }) {
  const [open, setOpen] = useState(false);
  // Local state so toggling individual tags feels instant; we sync with parent
  // via onChange. The parent owns the canonical state from Firestore.
  const isUnrestricted = access.tags === null;
  const allowedSet = new Set(access.tags || []);
  // Build summary: list the actually-shared tags rather than just a count.
  let summary;
  if (isUnrestricted) {
    summary = "All tags" + (access.includeUntagged === false ? " (untagged hidden)" : "");
  } else if (allowedSet.size === 0) {
    summary = access.includeUntagged ? "Untagged only" : "Nothing";
  } else {
    summary = Array.from(allowedSet).join(", ") + (access.includeUntagged ? " + untagged" : "");
  }

  // Count entries that this member would see under current settings. Uses the
  // strict rule: every tag on the entry must be allowed, plus untagged-eligible.
  const matchCount = (() => {
    if (isUnrestricted) return entries.length;
    return entries.filter(e => {
      const t = e.tags || [];
      if (t.length === 0) return access.includeUntagged !== false;
      return t.every(tag => allowedSet.has(tag));
    }).length;
  })();

  const toggleTag = (tag) => {
    const nextTags = isUnrestricted
      // First time restricting: start with everything except this tag
      ? allTags.filter(t => t !== tag)
      : allowedSet.has(tag)
        ? Array.from(allowedSet).filter(t => t !== tag)
        : [...Array.from(allowedSet), tag];
    onChange({ tags: nextTags, includeUntagged: access.includeUntagged });
  };

  const allowAll = () => onChange({ tags: null, includeUntagged: true });
  const allowNone = () => onChange({ tags: [], includeUntagged: false });
  const toggleUntagged = () => onChange({
    tags: isUnrestricted ? null : Array.from(allowedSet),
    includeUntagged: !access.includeUntagged,
  });

  return (
    <div style={{ marginTop: "8px", paddingLeft: "48px" }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ background: "transparent", border: "none", color: "#888", fontSize: "12px", cursor: "pointer", padding: "2px 0", fontFamily: "-apple-system,sans-serif", display: "flex", alignItems: "center", gap: "6px", textAlign: "left", flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600, color: "#666" }}>Sharing:</span>
        <span style={{ color: "#888", fontWeight: 400 }}>{summary}</span>
        <svg width="11" height="11" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, opacity: 0.55, marginLeft: "1px" }} aria-hidden="true">
          <path d="M9.5 1.8 L12.2 4.5 L4.5 12.2 L1.5 12.5 L1.8 9.5 Z" stroke="#666" strokeWidth="1.2" strokeLinejoin="round" fill="none"/>
          <path d="M8.6 2.7 L11.3 5.4" stroke="#666" strokeWidth="1.2"/>
        </svg>
      </button>
      {/* Count of matching entries — separate line so pencil clearly refers to tags */}
      <div style={{ fontSize: "12px", color: "#aaa", fontFamily: "-apple-system,sans-serif", paddingTop: "2px" }}>
        {matchCount} of {entries.length} {entries.length === 1 ? "entry" : "entries"}
      </div>
      {/* Last-visited line (small, only when known) */}
      {lastVisitedMs ? (
        <div style={{ fontSize: "12px", color: "#aaa", fontFamily: "-apple-system,sans-serif", paddingTop: "2px" }}>
          Last viewed {formatRelativeTime(lastVisitedMs)}
        </div>
      ) : (
        <div style={{ fontSize: "12px", color: "#aaa", fontStyle: "italic", fontFamily: "-apple-system,sans-serif", paddingTop: "2px" }}>
          Hasn't opened this diary yet
        </div>
      )}
      {open && (
        <div style={{ marginTop: "8px", padding: "10px 12px", background: "#fafaf7", borderRadius: "6px", border: "1px solid #ece8e0" }}>
          <div style={{ display: "flex", gap: "6px", marginBottom: "10px" }}>
            <button onClick={allowAll}
              style={{ background: isUnrestricted ? ORANGE : "transparent", color: isUnrestricted ? "white" : "#666", border: `1px solid ${isUnrestricted ? ORANGE : "#ddd"}`, borderRadius: "12px", padding: "4px 12px", fontSize: "12px", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>All tags</button>
            <button onClick={allowNone}
              style={{ background: !isUnrestricted && allowedSet.size === 0 ? ORANGE : "transparent", color: !isUnrestricted && allowedSet.size === 0 ? "white" : "#666", border: `1px solid ${!isUnrestricted && allowedSet.size === 0 ? ORANGE : "#ddd"}`, borderRadius: "12px", padding: "4px 12px", fontSize: "12px", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>No tags</button>
          </div>
          <div style={{ fontSize: "10px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px", fontWeight: 600 }}>Visible tags</div>
          {allTags.length === 0 ? (
            <div style={{ fontSize: "12px", color: "#aaa", fontStyle: "italic" }}>This diary has no tags yet.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {allTags.map(tag => {
                const meta = getTagMeta(tag);
                const allowed = isUnrestricted || allowedSet.has(tag);
                return (
                  <button key={tag} onClick={() => toggleTag(tag)}
                    style={{ background: allowed ? meta.bg : "white", color: allowed ? meta.color : "#aaa", border: `1px solid ${allowed ? meta.border : "#e0dcd8"}`, borderRadius: "12px", padding: "4px 10px", fontSize: "12px", fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,sans-serif", opacity: allowed ? 1 : 0.7, transition: "all 0.1s" }}>
                    {meta.emoji} {tag}
                  </button>
                );
              })}
            </div>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "12px", cursor: "pointer", fontSize: "12px", color: "#555", fontFamily: "-apple-system,sans-serif" }}>
            <input type="checkbox" checked={access.includeUntagged} onChange={toggleUntagged}
              style={{ cursor: "pointer", width: "14px", height: "14px" }} />
            Include entries with no tags
          </label>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ShareModal — share an entry to social platforms with minimal user effort.
// Strategy: pre-download photo, copy edited text to clipboard, then open the
// target platform's compose flow. User taps Post to finalize.
// Facebook + Instagram block third-party automated posting, so the best we can
// do is reduce the manual steps to ~3-5 taps.
// ─────────────────────────────────────────────────────────────────────────────
function ShareModal({ entry, onClose }) {
  if (typeof window !== "undefined" && entry && !window.__shareModalSeen) {
    window.__shareModalSeen = true;
    alert("ShareModal received entry: " + JSON.stringify({ title: entry.title, photos: entry.photos?.length }));
  }
  // Strip HTML to plain text. Done with a DOM parse so encoded entities,
  // <br>, and inline tags become reasonable text.
  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    // Replace <br> and block tags with newlines
    tmp.querySelectorAll("br").forEach(b => b.replaceWith("\n"));
    tmp.querySelectorAll("p,div,li").forEach(el => { el.append("\n"); });
    return (tmp.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
  };

  const initialText = entry
    ? ((entry.title ? entry.title + "\n\n" : "") + stripHtml(entry.bodyHtml || entry.body || "")).trim()
    : "";
  const [text, setText] = useState(initialText);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [toast, setToast] = useState("");
  // Reset when the entry changes
  useEffect(() => {
    if (!entry) return;
    const t = ((entry.title ? entry.title + "\n\n" : "") + stripHtml(entry.bodyHtml || entry.body || "")).trim();
    setText(t);
    setPhotoIndex(0);
  }, [entry]);

  const photos = entry?.photos || [];
  const selectedPhoto = photos[photoIndex];

  const showToast = (msg, ms = 3500) => {
    setToast(msg);
    setTimeout(() => setToast(""), ms);
  };

  // Download the photo via fetch + blob so the browser saves it. Cross-origin
  // requires the storage URL to allow CORS (Firebase Storage does by default).
  const downloadPhoto = async () => {
    if (!selectedPhoto) return false;
    try {
      const res = await fetch(selectedPhoto.url);
      const blob = await res.blob();
      const ext = (blob.type.split("/")[1] || "jpg").split("+")[0];
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lifetrackr-${(entry.title || "photo").replace(/[^a-z0-9]+/gi, "-").slice(0, 40)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return true;
    } catch (e) {
      console.error("Photo download failed", e);
      return false;
    }
  };

  const copyText = async () => {
    try { await navigator.clipboard.writeText(text); return true; }
    catch { return false; }
  };

  // Per-platform handlers
  const shareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
  };
  const shareThreads = async () => {
    await copyText();
    if (selectedPhoto) await downloadPhoto();
    const url = `https://www.threads.net/intent/post?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
    showToast(selectedPhoto ? "Text copied + photo saved. Attach photo in Threads." : "Text copied to clipboard.");
  };
  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
  };
  const shareEmail = () => {
    const subject = entry?.title || "Sharing from LifeTrackr";
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
  };
  const shareFacebook = async () => {
    await copyText();
    if (selectedPhoto) await downloadPhoto();
    // Facebook composer doesn't accept text via URL. Open FB and instruct user.
    window.open("https://www.facebook.com/", "_blank", "noopener");
    showToast(selectedPhoto
      ? "Text copied + photo saved. In Facebook: New Post → upload photo → paste text."
      : "Text copied. In Facebook: New Post → paste text.");
  };
  const shareInstagram = async () => {
    await copyText();
    if (selectedPhoto) await downloadPhoto();
    // No reliable web link to IG composer; just open IG and instruct.
    window.open("https://www.instagram.com/", "_blank", "noopener");
    showToast(selectedPhoto
      ? "Text copied + photo saved. In Instagram: + → Post → pick photo → paste caption."
      : "Text copied. (Instagram requires a photo for new posts.)");
  };
  const shareNative = async () => {
    if (!navigator.share) {
      showToast("Native share isn't available on this device.");
      return;
    }
    try {
      const data = { text };
      if (selectedPhoto) {
        try {
          const res = await fetch(selectedPhoto.url);
          const blob = await res.blob();
          const file = new File([blob], "photo.jpg", { type: blob.type });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            data.files = [file];
          }
        } catch {}
      }
      await navigator.share(data);
    } catch (e) { /* user cancelled */ }
  };
  const justCopy = async () => {
    const ok = await copyText();
    showToast(ok ? "Copied to clipboard." : "Couldn't copy. Select and copy manually.");
  };

  // PDF share: builds a PDF from the entry, then either passes it to the
  // native share sheet (mobile) so the user can pick Mail/Gmail/etc. with the
  // attachment pre-attached, or downloads it + opens Gmail with text prefilled
  // (desktop).
  const sharePdf = async () => {
    showToast("Building PDF…", 60000);
    let blob;
    try {
      blob = await buildEntryPdf([entry]);
    } catch (e) {
      console.error("PDF build failed", e);
      showToast("Couldn't build PDF. See console for details.");
      return;
    }
    const safeName = (entry?.title || "entry").replace(/[^a-z0-9]+/gi, "-").slice(0, 40) || "entry";
    const file = new File([blob], `${safeName}.pdf`, { type: "application/pdf" });

    // Phones/tablets: native share sheet with the PDF attached. Lets the
    // user pick Mail / Messages / Gmail / etc. with the PDF pre-attached.
    if (canShareFiles(file)) {
      try {
        await navigator.share({ files: [file], text, title: entry?.title || "Shared entry" });
        showToast("");
        return;
      } catch (e) {
        if (e.name === "AbortError") { showToast(""); return; }
        // Fall through to download on other errors.
      }
    }

    // Desktop and iOS: open the PDF blob in a separate context.
    // - On desktop, this triggers a download via the `download` attribute.
    // - On iOS Safari, the `download` attribute is honored as "Open in new
    //   tab" (Safari doesn't actually save files via this attribute). To
    //   prevent the new-tab close from unwinding our history stack and
    //   bouncing the user to /dashboard, we use window.open() instead of
    //   an <a target="_blank"> click. window.open creates a fresh window
    //   reference; closing it never pops the opener's history.
    const url = URL.createObjectURL(blob);
    if (isIOS()) {
      const w = window.open(url, "_blank");
      // If popups are blocked, fall back to <a> click without target so it
      // navigates the current tab. User uses Safari's back button to return.
      if (!w) {
        const a = document.createElement("a");
        a.href = url; a.download = file.name;
        document.body.appendChild(a); a.click(); a.remove();
      }
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    showToast("PDF downloaded.");
  };

  // Word doc download — single entry. Same download mechanics as PDF (iOS uses
  // window.open since Safari ignores `download` attribute on blob URLs).
  const shareDocx = async () => {
    showToast("Building Word doc…", 60000);
    let blob;
    try {
      blob = await buildEntryDocx([entry]);
    } catch (e) {
      console.error("Docx build failed", e);
      showToast("Couldn't build Word doc. See console for details.");
      return;
    }
    const safeName = (entry?.title || "entry").replace(/[^a-z0-9]+/gi, "-").slice(0, 40) || "entry";
    const filename = `${safeName}.docx`;
    const url = URL.createObjectURL(blob);
    if (isIOS()) {
      const w = window.open(url, "_blank");
      if (!w) {
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
      }
    } else {
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      a.target = "_blank"; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    showToast("Word doc downloaded.");
  };

  // Email-with-PDF: build the PDF, then either pass it to the OS share sheet
  // (mobile, so the user can pick Mail / Gmail / etc. with the file pre-attached)
  // or download it + open Gmail compose with subject prefilled (desktop).
  const emailPdf = async () => {
    showToast("Building PDF…", 60000);
    let blob;
    try {
      blob = await buildEntryPdf([entry]);
    } catch (e) {
      console.error("PDF build failed", e);
      showToast("Couldn't build PDF. See console for details.");
      return;
    }
    const safeName = (entry?.title || "entry").replace(/[^a-z0-9]+/gi, "-").slice(0, 40) || "entry";
    const filename = `${safeName}.pdf`;
    const file = new File([blob], filename, { type: "application/pdf" });
    const subject = entry?.title || "Sharing from LifeTrackr";

    // Mobile path: native share sheet with the PDF attached + subject/text.
    if (canShareFiles(file)) {
      try {
        await navigator.share({ files: [file], text, title: subject });
        showToast("");
        return;
      } catch (e) {
        if (e.name === "AbortError") { showToast(""); return; }
        // fall through to desktop path
      }
    }

    // Desktop path: download the PDF, then open Gmail compose so the user
    // can drag the file in. Gmail's compose URL doesn't accept attachments.
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    const body = `${text}\n\n(Attached: ${filename})`;
    window.open(
      `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      "_blank",
      "noopener"
    );
    showToast("PDF saved to Downloads — drag it into the Gmail draft.");
  };

  if (!entry) return null;
  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 400, padding: "20px" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", borderRadius: "14px", maxWidth: "440px", width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 10px 40px rgba(0,0,0,0.3)", fontFamily: "-apple-system,sans-serif", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: "16px", fontWeight: 700, color: "#222" }}>Share entry</div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#aaa", fontSize: "22px", cursor: "pointer", padding: "0", lineHeight: 1 }}>×</button>
        </div>
        {/* Body */}
        <div style={{ padding: "16px 18px", overflowY: "auto" }}>
          {/* Photo selector */}
          {photos.length > 0 && (
            <div style={{ marginBottom: "14px" }}>
              <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px", fontWeight: 600 }}>
                {photos.length === 1 ? "Photo" : `Photo (${photoIndex + 1} of ${photos.length})`}
              </div>
              <div style={{ display: "flex", gap: "6px", overflowX: "auto", paddingBottom: "4px" }}>
                {photos.map((p, i) => (
                  <img key={i} src={p.url} alt=""
                    onClick={() => setPhotoIndex(i)}
                    style={{ width: "70px", height: "70px", objectFit: "cover", borderRadius: "6px", cursor: "pointer", flexShrink: 0, border: i === photoIndex ? `2px solid ${ORANGE}` : "2px solid transparent", opacity: i === photoIndex ? 1 : 0.7 }} />
                ))}
              </div>
            </div>
          )}
          {/* Editable text */}
          <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px", fontWeight: 600 }}>Caption</div>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={5}
            style={{ width: "100%", border: "1px solid #ddd", borderRadius: "8px", padding: "10px", fontSize: "13px", fontFamily: "-apple-system,sans-serif", resize: "vertical", color: "#333", boxSizing: "border-box" }} />
          <div style={{ fontSize: "11px", color: "#aaa", marginTop: "4px" }}>
            {text.length} character{text.length === 1 ? "" : "s"} · edit before sharing
          </div>

          {/* Native share (mobile) */}
          {typeof navigator !== "undefined" && navigator.share && (
            <button onClick={shareNative}
              style={{ width: "100%", marginTop: "16px", padding: "12px", borderRadius: "10px", border: "none", background: ORANGE, color: "white", fontSize: "14px", fontWeight: 700, cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
              📲 Share via... (mobile)
            </button>
          )}

          {/* Per-platform grid */}
          <div style={{ marginTop: "16px", fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px", fontWeight: 600 }}>Or pick a platform</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
            <PlatformBtn label="Facebook" icon="📘" onClick={shareFacebook} />
            <PlatformBtn label="Instagram" icon="📷" onClick={shareInstagram} />
            <PlatformBtn label="Twitter/X" icon="𝕏" onClick={shareTwitter} />
            <PlatformBtn label="Threads" icon="🧵" onClick={shareThreads} />
            <PlatformBtn label="WhatsApp" icon="💬" onClick={shareWhatsApp} />
            <PlatformBtn label="Email" icon="✉" onClick={shareEmail} />
            <PlatformBtn label="PDF" icon="📄" onClick={sharePdf} />
            <PlatformBtn label="Word" icon="📝" onClick={shareDocx} />
            <PlatformBtn label="Email + PDF" icon="📎" onClick={emailPdf} />
            <PlatformBtn label="Copy" icon="📋" onClick={justCopy} />
          </div>

          {selectedPhoto && (
            <div style={{ marginTop: "14px", padding: "10px 12px", background: "#f8f5ed", borderRadius: "8px", fontSize: "11px", color: "#7a5a1a", lineHeight: 1.5 }}>
              <strong>How it works:</strong> Tapping Facebook or Instagram saves the photo to your device and copies the caption. Then in the app: New Post → pick the saved photo → paste caption → post.
            </div>
          )}
        </div>
        {/* Toast */}
        {toast && (
          <div style={{ padding: "12px 18px", borderTop: "1px solid #eee", background: "#fffbf3", color: "#7a5a1a", fontSize: "12px", lineHeight: 1.4 }}>
            {toast}
          </div>
        )}
      </div>
    </div>
  );
}

function PlatformBtn({ label, icon, onClick }) {
  return (
    <button onClick={onClick}
      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", padding: "10px 4px", borderRadius: "10px", border: "1px solid #e8e3d8", background: "white", cursor: "pointer", fontFamily: "-apple-system,sans-serif", fontSize: "11px", color: "#444", transition: "background 0.1s" }}
      onMouseEnter={e => e.currentTarget.style.background = "#fafaf5"}
      onMouseLeave={e => e.currentTarget.style.background = "white"}>
      <span style={{ fontSize: "22px" }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────────────

export default function DiaryView() {
  const { id } = useParams();
  const { user, canAddPhotos } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [diary, setDiary] = useState(null);
  const [entries, setEntries] = useState([]);
  // Custom structured fields that have been used in this diary. Once a
  // field appears on any entry's structured.custom (via the + button or
  // imported), it's "established" — future entries can use it via body
  // shorthand and it shows up as a chip in the icon row.
  const establishedCustomFields = (() => {
    const set = new Set();
    for (const e of entries) {
      const custom = e.structured?.custom;
      if (!custom || typeof custom !== "object") continue;
      for (const k of Object.keys(custom)) set.add(k.toLowerCase());
    }
    return [...set].sort();
  })();
  // Per-diary configuration — which built-ins to hide, which library fields
  // to enable, which custom fields are declared. All read from diary doc.
  // Built-ins default to ALL ON (user opts to hide). Library defaults to OFF
  // (user opts in via settings). Custom is declared in settings + auto-
  // discovered from existing entries.
  const hiddenBuiltins = new Set((diary?.hiddenBuiltins || []).map(k => k.toLowerCase()));
  const enabledLibrary = (diary?.enabledLibrary || []).filter(k => FIELD_LIBRARY[k]);
  // Declared custom fields, normalized to lowercase-able string NAMES.
  // diary.customFields is stored as a list of { name, multi } records (the
  // FieldsManagement settings tab and the CSV/Excel importer's auto-declare
  // both write that shape), but every consumer below treats entries as
  // strings via .map(s => s.toLowerCase()). Before this normalization, a
  // diary whose customFields had been populated — e.g. by importing a file
  // with custom columns — would crash the entire render with
  // "s.toLowerCase is not a function" (a white screen), because the objects
  // have no toLowerCase. We coerce here so all downstream uses are safe, and
  // we still accept the legacy bare-string form for older diaries.
  const declaredCustom = (diary?.customFields || [])
    .map(c => (typeof c === "string" ? c : (c && c.name) || ""))
    .filter(Boolean);
  // Final list of custom field names that should appear in the chip row:
  // explicitly declared in settings + auto-discovered from entries.
  const allCustomFields = [...new Set([...declaredCustom, ...establishedCustomFields].map(s => s.toLowerCase()))].sort();
  const [loading, setLoading] = useState(true);
  const [names, setNames] = useState({});
  // Snapshot of the user's lastVisited timestamp for THIS diary, captured once on
  // mount so the "new entries" badge stays stable while the user is viewing the
  // diary. We then write a fresh timestamp after a short delay so the next time
  // they visit, "new" resets.
  const [lastVisitedSnapshot, setLastVisitedSnapshot] = useState(null);
  const [lastVisitedLoaded, setLastVisitedLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(!!(location.state?.openDiary));
  const addEditorRef = useRef(null);
  const editEditorRef = useRef(null);
  // Ref to the edit modal's date input. Used to programmatically open the
  // native picker via showPicker() when the user clicks the 📅 icon or the
  // input itself — see comment on the date row JSX for why.
  const editDateInputRef = useRef(null);
  // Debounce timers for contentEditable onInput. Bulk operations like select-all-then-delete
  // can fire many input events in quick succession with very large innerHTML strings.
  // Without debouncing, each event triggers a React re-render, which can lock up the
  // main thread enough that subsequent clicks (Save/Cancel) feel unresponsive.
  const editInputTimerRef = useRef(null);
  const newInputTimerRef = useRef(null);
  const newTagPickerRef = useRef(null);
  const editTagPickerRef = useRef(null);

  const [search, setSearch] = useState("");
  const [activeLenses, setActiveLenses] = useState(new Set());
  // Active tag-group filters. When a group is active, its tags (recursively)
  // contribute to the entry filter. Stored as a Set of group ids. See
  // expandedGroupTags below for the resolved tag set per active group.
  const [activeGroups, setActiveGroups] = useState(new Set());
  // Tags explicitly excluded while a parent group is active. Lets users
  // pick a group like "PV LLC" then drop specific units they don't want.
  // Excluded entries remain marked so they can be quickly re-included.
  const [excludedTags, setExcludedTags] = useState(new Set());
  // How multiple selected tags combine: "all" (AND, intersection) or "any" (OR, union).
  // Default to "all" since multi-tag selection usually means "narrow further".
  const [lensMode, setLensMode] = useState("all");
  // Ref to the lens bar / search row container. We scroll-anchor here when a
  // filter is applied so the page doesn't visibly jump as content shrinks
  // (which happens when stats bar / inline composer / suggestions disappear
  // because they're gated on "no filter active").
  const filterRowRef = useRef(null);
  // Y-position captured at the moment of a filter change, used to restore
  // scroll after React re-renders. Set in click handlers (synchronously
  // before state updates) and consumed by a useLayoutEffect after render.
  const pendingAnchorOffsetRef = useRef(null);
  const [sort, setSort] = useState("newest");
  const [showFilter, setShowFilter] = useState(false);
  // Independent collapsible state for the groups row and the tags row.
  // Defaults: both collapsed. Each auto-expands when filters relevant to
  // it are active (a group active → groups row expanded; a tag active →
  // tags row expanded). Lets users open just the section they care about
  // without the full chrome of both rows.
  const [showGroupsRow, setShowGroupsRow] = useState(false);
  const [showTagsLensRow, setShowTagsLensRow] = useState(false);
  const [selectMode, setSelectMode] = useState(false);

  // First-visit timeline hint. We pulse the most recent timeline dot with a
  // "Tap to select" label so users discover that selecting an entry is what
  // unlocks the bulk actions. Dismissed once they enter select mode at least
  // once OR after they manually X it. State persists in localStorage so it
  // doesn't keep nagging on every visit.
  const HINT_KEY = "lt:timelineHintDismissed";

  // Banner expansion state — followup and favorites banners can be tapped to
  // expand into a list of all matching entries. Tapping an entry in the list
  // navigates to it (scrolls or opens for edit, depending on what makes sense).
  const [followupExpanded, setFollowupExpanded] = useState(false);
  const [favoritesExpanded, setFavoritesExpanded] = useState(false);

  const [showTimelineHint, setShowTimelineHint] = useState(() => {
    try { return !localStorage.getItem(HINT_KEY); } catch { return false; }
  });
  const dismissTimelineHint = () => {
    try { localStorage.setItem(HINT_KEY, "1"); } catch {}
    setShowTimelineHint(false);
  };
  // Auto-dismiss the hint when the user actually enters select mode — they've
  // discovered the feature, no need to keep showing it.
  useEffect(() => {
    if (selectMode && showTimelineHint) dismissTimelineHint();
  }, [selectMode, showTimelineHint]);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showAssignTag, setShowAssignTag] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [filterFavorites, setFilterFavorites] = useState(false);
  const [filterFollowup, setFilterFollowup] = useState(false);
  const [filterPhotos, setFilterPhotos] = useState(false);
  const [filterContributor, setFilterContributor] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  // Field-level filters. Each key is a field name (e.g. "distanceMiles",
  // "mood"); each value is an object: { has, min, max, values }.
  //   has: true       → entry must have this field set (non-null)
  //   min: number     → entry's numeric value must be ≥ min
  //   max: number     → entry's numeric value must be ≤ max
  //   values: array   → entry's value must include one of these (OR)
  // Multiple criteria on one field combine AND. Across fields, AND.
  const [fieldFilters, setFieldFilters] = useState({});
  // Combination mode for active field filters: "all" (AND, default) means
  // an entry must match every active field's criteria; "any" (OR) means
  // matching one is enough. Mirrors the tag-lens AND/OR toggle.
  const [fieldFilterMode, setFieldFilterMode] = useState("all");
  // The entry currently being shared via the ShareModal (null = closed).
  const [shareEntry, setShareEntry] = useState(null);

  const [page, setPage] = useState(0);
  // Media view mode: "timeline" (default) or "media" (gallery grid). Per-diary.
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem(`lt_viewMode_${id}`) || "timeline";
    } catch { return "timeline"; }
  });
  useEffect(() => {
    try { localStorage.setItem(`lt_viewMode_${id}`, viewMode); } catch {}
  }, [id, viewMode]);

  // View mode: "detail" (full entries with photos in timeline) | "compact" (titles only)
  const [viewModeSetting, setViewModeSetting] = useState("detail");
  const [savingViewMode, setSavingViewMode] = useState(false);

  // Lightbox — { photos: [{url, name}], index }. Shared across modals/lists so any
  // photo thumbnail in the app can open a full-size overlay.
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = (photos, index = 0) => setLightbox({ photos, index });
  const closeLightbox = () => setLightbox(null);
  // Expose globally so entry sub-components can invoke without prop drilling
  useEffect(() => {
    window.__ltOpenLightbox = openLightbox;
    return () => { if (window.__ltOpenLightbox === openLightbox) delete window.__ltOpenLightbox; };
  }, []);

  // Upgrade prompt — opened from gated controls (e.g. the Attach button when a
  // free user has hit the photo cap). `reason` ("photos" | "ocr") tailors the
  // copy. Wired globally like the lightbox so deep sub-components can open it.
  const [upgradeReason, setUpgradeReason] = useState(null);
  const [upgradeBusy, setUpgradeBusy] = useState(false);
  useEffect(() => {
    window.__ltOpenUpgrade = (reason) => setUpgradeReason(reason || "general");
    return () => { if (window.__ltOpenUpgrade) delete window.__ltOpenUpgrade; };
  }, []);

  const [showAdd, setShowAdd] = useState(false);
  const [autoOpenedAdd, setAutoOpenedAdd] = useState(false);
  const [newBody, setNewBody] = useState("");
  // Structured fields collected via the icon row in the composer. Each can
  // be empty; we only persist non-empty values. Replaced wholesale on
  // composer reset.
  const [newStructured, setNewStructured] = useState({});
  // While "Drop pin" is requesting geolocation + reverse-geocoding, the
  // button shows a "Locating…" label and is disabled. Shared between new
  // and edit modals; only one can be active at a time so a single flag
  // is fine.
  const [droppingPin, setDroppingPin] = useState(false);
  // Tracks which structured fields the user has explicitly touched (set or
  // cleared via icon-row interaction). Body live-parsing fills only the
  // UN-touched fields, so the user's explicit choices stick.
  const [touchedStructFields, setTouchedStructFields] = useState(new Set());
  // Which structured field is currently being edited inline (null = none).
  // Tapping an icon sets this; the input renders, then user hits enter or
  // tabs out to commit + collapse to a chip.
  const [editingStructField, setEditingStructField] = useState(null);
  const [structFieldDraft, setStructFieldDraft] = useState("");
  const [newHtml, setNewHtml] = useState("");
  const [newDate, setNewDate] = useState(todayInput());
  const [newPhotos, setNewPhotos] = useState([]);
  const [newTags, setNewTags] = useState("");
  const [newTitle, setNewTitle] = useState("");
  // Track whether user has manually edited the title input. Once true, we
  // stop auto-generating the title from body/structured fields so we don't
  // overwrite their explicit choice.
  const [titleManuallyTouched, setTitleManuallyTouched] = useState(false);
  const [newFollowup, setNewFollowup] = useState(false);
  const [newFavorite, setNewFavorite] = useState(false);
  // Private entries get a 🔒 indicator, are excluded from share + export,
  // and (for shared diaries) hidden from collaborators. Visible to the
  // owner with a visual flag — no PIN gate, no extra friction. Intent
  // is "don't accidentally leak this when sharing," not "hide from a
  // determined attacker." See PRIVATE_ENTRY_NOTES in this file.
  const [newPrivate, setNewPrivate] = useState(false);
  const [saving, setSaving] = useState(false);

  // Live shorthand detection: when the user types in title or body, parse
  // for shorthand tokens ($25, @location, w:Person, 1.5h, etc.) and auto-
  // fill the structured-fields chips for fields they haven't explicitly
  // touched yet. Lets users write naturally without thinking about chips.
  useEffect(() => {
    if (!showAdd) return;
    // Body is canonical. Include title when the user has typed in it
    // (titleManuallyTouched). When title is auto-generated, parsing it
    // creates a feedback loop — skip in that case.
    const body = (newBody || "").trim();
    const titlePart = titleManuallyTouched ? (newTitle || "").trim() : "";
    const text = [titlePart, body].filter(Boolean).join(". ");
    if (!text) return;
    // Debounce to 600ms so mid-typing transitional states (e.g. typing
    // '5 m' on the way to '5 miles', which would fire time then distance
    // in succession) don't flicker chips. Only the settled form parses.
    const timer = setTimeout(() => {
      const ps = parseStructuredFields(text, { knownNames });
    setNewStructured(prev => {
      const next = { ...prev };
      let changed = false;
      // tryFill: set chip to parser value if different, OR clear chip if parser
      // returns nothing (and user didn't touch the chip).
      const tryFill = (key, value) => {
        if (touchedStructFields.has(key)) return; // user has explicit control
        const isEmpty = value == null || value === "" || (Array.isArray(value) && !value.length);
        if (isEmpty) {
          // Clear the chip if it was previously set (auto-filled from earlier body state)
          if (next[key] != null && (Array.isArray(next[key]) ? next[key].length : true)) {
            delete next[key];
            changed = true;
          }
          return;
        }
        if (next[key] === value || JSON.stringify(next[key]) === JSON.stringify(value)) return;
        next[key] = value;
        changed = true;
      };
      tryFill("amount", ps.amount);
      tryFill("location", ps.location);
      tryFill("people", ps.people && ps.people.length ? ps.people : null);
      // peopleAsOpponents tracks whether the people typed via 'vs:'/'vs.'
      // shorthand were flagged as opponents. Title generator uses this to
      // decide between 'vs.' (opponent) and 'w/' (companion) framing.
      if (!touchedStructFields.has("people")) {
        if (ps.peopleAsOpponents && ps.people && ps.people.length) {
          if (!next.peopleAsOpponents) { next.peopleAsOpponents = true; changed = true; }
        } else if (next.peopleAsOpponents) {
          delete next.peopleAsOpponents;
          changed = true;
        }
      }
      tryFill("timeMinutes", ps.timeMinutes);
      tryFill("distanceMiles", ps.distanceMiles);
      tryFill("calories", ps.calories);
      tryFill("mood", ps.mood);
      tryFill("rating", ps.rating);
      tryFill("score", ps.score);
      // Custom fields — auto-fill ones declared in settings or established
      if (ps.customFields) {
        const declaredSet = new Set([...(declaredCustom || []), ...(establishedCustomFields || [])].map(s => s.toLowerCase()));
        for (const [k, v] of Object.entries(ps.customFields)) {
          if (!declaredSet.has(k.toLowerCase())) continue;
          const customKey = `custom.${k.toLowerCase()}`;
          if (touchedStructFields.has(customKey)) continue;
          const cur = (next.custom || {})[k.toLowerCase()];
          if (JSON.stringify(cur) === JSON.stringify(v)) continue;
          next.custom = { ...(next.custom || {}), [k.toLowerCase()]: v };
          changed = true;
        }
      }
      // Library fields — auto-fill ones enabled for this diary, AND clear
      // un-touched ones that the body no longer mentions (so backspacing the
      // value removes the chip).
      const enabledLibSet = new Set(enabledLibrary || []);
      const detectedLibKeys = new Set(Object.keys(ps.libraryFields || {}));
      if (next.library) {
        for (const k of Object.keys(next.library)) {
          const libKey = `library.${k}`;
          if (touchedStructFields.has(libKey)) continue; // user controls
          if (!detectedLibKeys.has(k)) {
            // Body no longer has this field — clear the chip
            const updated = { ...next.library };
            delete updated[k];
            next.library = Object.keys(updated).length ? updated : undefined;
            if (!Object.keys(updated).length) delete next.library;
            changed = true;
          }
        }
      }
      if (ps.libraryFields) {
        for (const [k, v] of Object.entries(ps.libraryFields)) {
          if (!enabledLibSet.has(k)) continue;
          const libKey = `library.${k}`;
          if (touchedStructFields.has(libKey)) continue;
          const cur = (next.library || {})[k];
          if (JSON.stringify(cur) === JSON.stringify(v)) continue;
          next.library = { ...(next.library || {}), [k]: v };
          changed = true;
        }
      }
      // Custom fields — same clear-when-gone pass
      const detectedCustomKeys = new Set(Object.keys(ps.customFields || {}).map(k => k.toLowerCase()));
      if (next.custom) {
        for (const k of Object.keys(next.custom)) {
          const cKey = `custom.${k}`;
          if (touchedStructFields.has(cKey)) continue;
          if (!detectedCustomKeys.has(k.toLowerCase())) {
            const updated = { ...next.custom };
            delete updated[k];
            if (Object.keys(updated).length) next.custom = updated; else delete next.custom;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
    }, 600);
    return () => clearTimeout(timer);
  }, [newBody, newTitle, titleManuallyTouched, showAdd, establishedCustomFields, declaredCustom, enabledLibrary]);

  // Live title generation. As the user types in body and fills structured
  // fields, the title field auto-fills with a generated subject. If the
  // user manually edits the title (titleManuallyTouched=true), we stop
  // auto-updating so we don't overwrite their explicit input.
  useEffect(() => {
    if (!showAdd) return;
    if (titleManuallyTouched) return;
    const generated = generateTitleFromStructured(newBody, newStructured);
    if (generated && generated !== newTitle) {
      setNewTitle(generated);
    } else if (!generated && newTitle) {
      // If the body got cleared and there are no structured fields, clear title too
      setNewTitle("");
    }
  }, [newBody, newStructured, showAdd, titleManuallyTouched]);

  const [editEntry, setEditEntry] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [editHtml, setEditHtml] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editPhotos, setEditPhotos] = useState([]); // newly picked File objects (all media types — sorted at save time)
  const [editExisting, setEditExisting] = useState([]); // existing photos from the entry
  const [editExistingVideos, setEditExistingVideos] = useState([]); // existing videos
  const [editExistingAudio, setEditExistingAudio] = useState([]); // existing audio
  const [editExistingDocs, setEditExistingDocs] = useState([]); // existing documents
  const [editTags, setEditTags] = useState("");
  // Structured fields for edit modal
  const [editStructured, setEditStructured] = useState({});
  const [editingEditStructField, setEditingEditStructField] = useState(null);
  const [editStructFieldDraft, setEditStructFieldDraft] = useState("");
  const [editTouchedStructFields, setEditTouchedStructFields] = useState(new Set());
  const [editTitle, setEditTitle] = useState("");
  // Same as titleManuallyTouched but for edit modal. Starts true (treat saved
  // title as user-authored) unless the entry has the titleAutoGenerated flag,
  // in which case starts false (allow further auto-enhancement on edit).
  const [editTitleManuallyTouched, setEditTitleManuallyTouched] = useState(true);
  const [editFollowup, setEditFollowup] = useState(false);
  const [editFavorite, setEditFavorite] = useState(false);
  const [editPrivate, setEditPrivate] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Note on body↔picker sync: we used to live-watch the body for '#tag'
  // patterns and auto-select matching chips, but the UX was confusing
  // (mid-typing partial tags, backspace creating spurious chips) so it
  // was removed. The existing parser still handles '#tags' correctly on
  // save — typed tags become tag chips at that point. Picker→body sync
  // (clicking a chip appends '#tag' to body, removing a chip strips
  // the '#') stays since it's user-initiated and unambiguous.

  // Live shorthand detection for the edit modal — same logic as for the
  // create modal but operates on editTitle/editBody. Auto-fills chips for
  // un-touched structured fields based on shorthand the user types.
  useEffect(() => {
    if (!editEntry) return;
    // Body is canonical prose. When the title is user-authored (typed by
    // the user, not auto-generated from structured fields), include it so
    // shorthand in titles like "Paid May $1363" parses too. When the
    // title is auto-generated, parsing it would create a feedback loop —
    // the parser would re-derive chip values from values it just generated
    // — so we skip the title in that case.
    const body = (editBody || "").trim();
    const titlePart = editTitleManuallyTouched ? (editTitle || "").trim() : "";
    // Combine with a separator so adjacent tokens across the boundary
    // don't accidentally fuse (e.g. "$50" in title and "with" in body
    // shouldn't read as "$50with").
    const text = [titlePart, body].filter(Boolean).join(". ");
    if (!text) return;
    // Debounce — same rationale as new-entry composer above
    const timer = setTimeout(() => {
      const ps = parseStructuredFields(text, { knownNames });
    setEditStructured(prev => {
      const next = { ...prev };
      let changed = false;
      const tryFill = (key, value) => {
        if (editTouchedStructFields.has(key)) return;
        const isEmpty = value == null || value === "" || (Array.isArray(value) && !value.length);
        if (isEmpty) {
          if (next[key] != null && (Array.isArray(next[key]) ? next[key].length : true)) {
            delete next[key];
            changed = true;
          }
          return;
        }
        if (next[key] === value || JSON.stringify(next[key]) === JSON.stringify(value)) return;
        next[key] = value;
        changed = true;
      };
      tryFill("amount", ps.amount);
      tryFill("location", ps.location);
      tryFill("people", ps.people && ps.people.length ? ps.people : null);
      // peopleAsOpponents — see comment in new-entry composer above
      if (!editTouchedStructFields.has("people")) {
        if (ps.peopleAsOpponents && ps.people && ps.people.length) {
          if (!next.peopleAsOpponents) { next.peopleAsOpponents = true; changed = true; }
        } else if (next.peopleAsOpponents) {
          delete next.peopleAsOpponents;
          changed = true;
        }
      }
      tryFill("timeMinutes", ps.timeMinutes);
      tryFill("distanceMiles", ps.distanceMiles);
      tryFill("calories", ps.calories);
      tryFill("mood", ps.mood);
      tryFill("rating", ps.rating);
      tryFill("score", ps.score);
      // Custom fields — auto-fill ones declared in settings or established
      if (ps.customFields) {
        const declaredSet = new Set([...(declaredCustom || []), ...(establishedCustomFields || [])].map(s => s.toLowerCase()));
        for (const [k, v] of Object.entries(ps.customFields)) {
          if (!declaredSet.has(k.toLowerCase())) continue;
          const customKey = `custom.${k.toLowerCase()}`;
          if (editTouchedStructFields.has(customKey)) continue;
          const cur = (next.custom || {})[k.toLowerCase()];
          if (JSON.stringify(cur) === JSON.stringify(v)) continue;
          next.custom = { ...(next.custom || {}), [k.toLowerCase()]: v };
          changed = true;
        }
      }
      // Library fields — auto-fill ones enabled for this diary
      // Library fields — auto-fill ones enabled for this diary, AND clear
      // un-touched ones that the body no longer mentions.
      const enabledLibSet = new Set(enabledLibrary || []);
      const detectedLibKeys = new Set(Object.keys(ps.libraryFields || {}));
      if (next.library) {
        for (const k of Object.keys(next.library)) {
          const libKey = `library.${k}`;
          if (editTouchedStructFields.has(libKey)) continue;
          if (!detectedLibKeys.has(k)) {
            const updated = { ...next.library };
            delete updated[k];
            if (Object.keys(updated).length) next.library = updated; else delete next.library;
            changed = true;
          }
        }
      }
      if (ps.libraryFields) {
        for (const [k, v] of Object.entries(ps.libraryFields)) {
          if (!enabledLibSet.has(k)) continue;
          const libKey = `library.${k}`;
          if (editTouchedStructFields.has(libKey)) continue;
          const cur = (next.library || {})[k];
          if (JSON.stringify(cur) === JSON.stringify(v)) continue;
          next.library = { ...(next.library || {}), [k]: v };
          changed = true;
        }
      }
      // Custom fields — same clear-when-gone pass
      const detectedCustomKeys = new Set(Object.keys(ps.customFields || {}).map(k => k.toLowerCase()));
      if (next.custom) {
        for (const k of Object.keys(next.custom)) {
          const cKey = `custom.${k}`;
          if (editTouchedStructFields.has(cKey)) continue;
          if (!detectedCustomKeys.has(k.toLowerCase())) {
            const updated = { ...next.custom };
            delete updated[k];
            if (Object.keys(updated).length) next.custom = updated; else delete next.custom;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
    }, 600);
    return () => clearTimeout(timer);
  }, [editBody, editTitle, editTitleManuallyTouched, editEntry, establishedCustomFields, declaredCustom, enabledLibrary]);

  // Live title generation for edit modal. If the title was originally
  // auto-generated (editTitleManuallyTouched=false), continue updating it
  // as the user adds more body text or fills more chips. Once user types
  // in title field, we flip editTitleManuallyTouched=true and stop.
  useEffect(() => {
    if (!editEntry) return;
    if (editTitleManuallyTouched) return;
    // For OCR-imported expense entries, the body may be empty but the
    // OCR item descriptor lives on expenseData.description. Use that as
    // a body fallback so the regenerated title keeps the "what was
    // bought" piece (e.g. "$5.75 @DOLLAR TREE retail merchandise" not
    // just "$5.75 @DOLLAR TREE").
    const effectiveBody = editBody || editEntry?.expenseData?.description || "";
    const generated = generateTitleFromStructured(effectiveBody, editStructured);
    if (generated && generated !== editTitle) {
      setEditTitle(generated);
    }
  }, [editBody, editStructured, editEntry, editTitleManuallyTouched]);

  const [showSettings, setShowSettings] = useState(!!(location.state?.openSettings));
  const [settingsTab, setSettingsTab] = useState("info");
  // Passcode gate session state (see the gate logic before the main return).
  // `unlocked` is seeded from sessionStorage so a within-session navigation
  // doesn't re-prompt; `forgotNote` toggles the reset hint on the lock screen.
  const [unlocked, setUnlocked] = useState(() => isUnlockedThisSession(id, user?.uid));
  const [forgotNote, setForgotNote] = useState(false);
  const [showSetPasscode, setShowSetPasscode] = useState(false); // quick set-passcode prompt from the on-page lock icon

  // Auto-lock on background return. When the app is hidden (sleep, app-switch,
  // screen-off) we stamp the time; when it becomes visible again, if it was
  // hidden longer than PASSCODE_AUTOLOCK_MS we clear the session unlock and
  // flip `unlocked` to false so the gate reappears. Only armed for a
  // passcode-protected diary owned by the current user — otherwise there's
  // nothing to lock. Listener is cleaned up on unmount / dependency change.
  useEffect(() => {
    if (!diary?.passcodeHash || !id) return;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        recordHiddenNow(id);
      } else if (document.visibilityState === "visible") {
        if (shouldAutolockOnReturn(id)) {
          clearSessionUnlock(id);
          setUnlocked(false);
        }
        clearHiddenAt(id);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [diary?.passcodeHash, id]);

  // Tracks whether the viewport is narrow enough (mobile/iPhone) that the
  // settings tab strip should collapse non-selected tabs to emoji-only.
  // 500px catches all iPhone widths in portrait with margin. Initializer is
  // a function so window.innerWidth is read only once on mount.
  const [isNarrowViewport, setIsNarrowViewport] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 500
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => setIsNarrowViewport(window.innerWidth < 500);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Cover tab state
  const [coverNameDraft, setCoverNameDraft] = useState("");
  const [savingCoverName, setSavingCoverName] = useState(false);
  const [savingCoverTheme, setSavingCoverTheme] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  // Delete diary state
  const [deleteDiaryConfirm, setDeleteDiaryConfirm] = useState("");
  const [deletingDiary, setDeletingDiary] = useState(false);

  // Members tab state
  const [memberProfiles, setMemberProfiles] = useState({}); // uid -> {name, email, photoURL}
  const [memberLastVisited, setMemberLastVisited] = useState({}); // uid -> ms timestamp
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("contributor");
  // Per-invite tag access. null = all tags (default). Otherwise array of tag names.
  const [inviteTags, setInviteTags] = useState(null);
  // Default OFF: untagged entries are private to author + owner. New members
  // need explicit grant to see them. The owner can opt in via the access
  // editor checkbox below the tag chips.
  const [inviteIncludeUntagged, setInviteIncludeUntagged] = useState(false);
  const [inviteAccessOpen, setInviteAccessOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null); // {type: "ok"|"error", text}
  const [leaving, setLeaving] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [extraEmailInput, setExtraEmailInput] = useState("");
  const [savingExtra, setSavingExtra] = useState(false);
  const [extraMsg, setExtraMsg] = useState(null);
  // Ownership transfer state. transferTarget is the uid of the member the
  // owner picked from the member list to transfer to (null means modal closed).
  // For new-email transfers we instead use the existing invite form with
  // inviteRole === "owner" — no separate state needed.
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferring, setTransferring] = useState(false);
  // Resend-with-edit flow. When the owner clicks "Resend" on a pending
  // invitation, we open an inline editor showing the current tag-access
  // settings; the owner can adjust tags / includeUntagged before triggering
  // the resend. resendingInviteId holds the in-flight id, resendDraftTags
  // and resendDraftUntagged hold the editable copies of the invite's
  // current settings. Save & Resend persists to the pendingInvites doc
  // and bumps resendRequestedAt so the email cloud function re-fires.
  const [resendingInviteId, setResendingInviteId] = useState(null);
  const [resendDraftTags, setResendDraftTags] = useState(null);
  const [resendDraftUntagged, setResendDraftUntagged] = useState(false);
  const [resendSaving, setResendSaving] = useState(false);

  useEffect(() => {
    getDoc(doc(db, "diaries", id)).then(s => {
      if (s.exists()) {
        const d = s.data();
        setDiary({ id: s.id, ...d });
        if (d.defaultView) {
          if (d.defaultView === "list") setViewModeSetting("compact");
          else if (d.defaultView === "detail" || d.defaultView === "compact") setViewModeSetting(d.defaultView);
          else setViewModeSetting("detail");
        }
        setCoverNameDraft(d.name || "");
      }
    });
  }, [id]);

  useEffect(() => {
    if (!id || !user?.uid) return;
    // Private entries are enforced at the Firestore-rules layer: a member may
    // read an entry only if it's non-private OR they authored it. Firestore
    // rules don't filter queries — a query that COULD return an unreadable doc
    // fails entirely — so we must load with two queries whose results we're
    // allowed to read, then union them by id:
    //   (1) every non-private entry in this diary
    //   (2) my own entries in this diary (includes my private ones)
    // Both require `private` to be an explicit boolean on every entry (handled
    // by the write paths + the one-time backfill migration).
    const qPublic = query(
      collection(db, "entries"),
      where("diaryId", "==", id),
      where("private", "==", false),
    );
    const qMine = query(
      collection(db, "entries"),
      where("diaryId", "==", id),
      where("authorId", "==", user.uid),
    );

    // Merge the two snapshots. We keep the latest docs from each query in its
    // own map, then combine + sort by entryDate desc (we dropped the orderBy
    // from the queries to avoid composite-index requirements on the compound
    // filters; sorting client-side over a few hundred entries is trivial).
    let publicDocs = new Map();
    let mineDocs = new Map();
    let gotPublic = false;
    let gotMine = false;

    const emit = () => {
      if (!gotPublic && !gotMine) return;
      const merged = new Map(publicDocs);
      for (const [k, v] of mineDocs) merged.set(k, v); // mine overrides dup ids
      const data = [...merged.values()].sort((a, b) => {
        const ta = a.entryDate?.toMillis ? a.entryDate.toMillis() : (a.entryDate ? new Date(a.entryDate).getTime() : 0);
        const tb = b.entryDate?.toMillis ? b.entryDate.toMillis() : (b.entryDate ? new Date(b.entryDate).getTime() : 0);
        return tb - ta;
      });
      setEntries(data);
      setLoading(false);
      [...new Set(data.map(e => e.authorId).filter(Boolean))].forEach(async aid => {
        try {
          const s = await getDoc(doc(db, "users", aid));
          const n = s.exists() ? (s.data().name || s.data().email?.split("@")[0] || aid) : (aid.includes("@") ? aid.split("@")[0] : aid);
          setNames(p => ({ ...p, [aid]: n }));
        } catch { setNames(p => ({ ...p, [aid]: aid.includes("@") ? aid.split("@")[0] : aid })); }
      });
    };

    const unsubPublic = onSnapshot(qPublic, snap => {
      publicDocs = new Map(snap.docs.map(d => [d.id, { id: d.id, ...d.data() }]));
      gotPublic = true;
      emit();
    });
    const unsubMine = onSnapshot(qMine, snap => {
      mineDocs = new Map(snap.docs.map(d => [d.id, { id: d.id, ...d.data() }]));
      gotMine = true;
      emit();
    });
    return () => { unsubPublic(); unsubMine(); };
  }, [id, user?.uid]);

  // Track when the user last visited this diary, so we can show a "X new" badge
  // on subsequent visits. Read once on mount (snapshot stays stable while viewing),
  // then write a fresh timestamp after a short grace period so the count resets
  // for the next visit. Stored at users/{uid}/diaryViews/{diaryId}.
  useEffect(() => {
    if (!user?.uid || !id) return;
    let cancelled = false;
    const ref = doc(db, "users", user.uid, "diaryViews", id);
    (async () => {
      try {
        const s = await getDoc(ref);
        if (cancelled) return;
        const lv = s.exists() ? s.data()?.lastVisited : null;
        const ms = lv?.toMillis ? lv.toMillis() : (lv ? new Date(lv).getTime() : 0);
        setLastVisitedSnapshot(ms);
        setLastVisitedLoaded(true);
      } catch (e) {
        if (!cancelled) {
          setLastVisitedSnapshot(0);
          setLastVisitedLoaded(true);
        }
      }
    })();
    // Write a fresh "visited now" timestamp after 5s so the next visit has a
    // current baseline. We don't want to write on first render (would always
    // produce 0 new entries on the next visit).
    const writeTimer = setTimeout(() => {
      setDoc(ref, { lastVisited: serverTimestamp() }, { merge: true }).catch(() => {});
    }, 5000);
    return () => { cancelled = true; clearTimeout(writeTimer); };
  }, [id, user?.uid]);

  // Intercept the hardware back button on Android: when a modal is open,
  // back should close the modal instead of navigating away from the diary.
  //
  // We deliberately scope this to Android only. iOS has no hardware back
  // button — the left-edge swipe is a navigation gesture, not a modal
  // dismiss gesture, and iOS users close modals via the X / Cancel button.
  // More importantly, iOS Safari fires spurious popstate events when the
  // user returns from share sheets, file pickers, and PDF previews, which
  // would incorrectly close our modals AND trigger an underlying back
  // navigation — bouncing the user all the way to /dashboard.
  const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
  const modalSentinelRef = useRef(false);
  useEffect(() => {
    if (!isAndroid) return;
    const anyModalOpen = showAdd || !!editEntry || showSettings || showAssignTag;
    if (!anyModalOpen) return;

    // Push a sentinel history entry so the back gesture has something to pop.
    window.history.pushState({ __lifetrackrModal: true }, "");
    modalSentinelRef.current = true;

    const handlePop = () => {
      modalSentinelRef.current = false;
      if (showAssignTag) { setShowAssignTag(false); clearSelect(); return; }
      if (showSettings) { setShowSettings(false); return; }
      if (editEntry) { setEditEntry(null); return; }
      if (showAdd) { setShowAdd(false); return; }
    };
    window.addEventListener("popstate", handlePop);

    return () => {
      window.removeEventListener("popstate", handlePop);
      // If modal was closed by app (Cancel/Save/X button), pop the sentinel from history
      // so the browser back stack stays clean.
      if (modalSentinelRef.current) {
        modalSentinelRef.current = false;
        window.history.back();
      }
    };
  }, [showAdd, editEntry, showSettings, showAssignTag, isAndroid]);

  // Determine view mode
  // View derived from diary setting
  const isCompactMode = viewModeSetting === "compact";
  const isDetailMode = !isCompactMode;
  const isRecordsMode = false;
  const isListMode = isCompactMode;

  const allTags = [...new Set([...entries.flatMap(e => e.tags || []), ...(diary?.customTags || [])])].sort();

  // ── TAG GROUP RESOLUTION ────────────────────────────────────────────────
  // Tag groups are defined on the diary doc as a flat array with parentId
  // pointers. For filtering, we need the recursive tag membership: "all
  // tags reachable from group X by walking its descendants." We compute
  // this once per render via useMemo and reuse below.
  const tagGroups = Array.isArray(diary?.tagGroups) ? diary.tagGroups : [];

  // Children index: groupId → array of direct child groups.
  const groupChildren = useMemo(() => {
    const m = new Map();
    for (const g of tagGroups) {
      const key = g.parentId || "__root__";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(g);
    }
    return m;
  }, [tagGroups]);

  // Walk a group's full descendant tag set: own tags + tags of all nested
  // subgroups. Returns a Set.
  const expandGroupTags = useCallback((groupId) => {
    const out = new Set();
    const visit = (id) => {
      const g = tagGroups.find(x => x.id === id);
      if (!g) return;
      for (const t of (g.tags || [])) out.add(t);
      const kids = groupChildren.get(id) || [];
      for (const c of kids) visit(c.id);
    };
    visit(groupId);
    return out;
  }, [tagGroups, groupChildren]);

  // Tag → set of group ids it's a member of (including ancestor groups).
  // Used in LensBar to decide if clicking a tag chip should toggle it as
  // an exclusion (when at least one of its ancestor groups is active)
  // versus the normal "include this tag" behavior.
  const tagAncestorGroups = useMemo(() => {
    const m = new Map();
    // For each group, walk its full descendant set and mark each contained
    // tag as having this group as an ancestor.
    for (const g of tagGroups) {
      const tagSet = (() => {
        const out = new Set();
        const visit = (id) => {
          const gg = tagGroups.find(x => x.id === id);
          if (!gg) return;
          for (const t of (gg.tags || [])) out.add(t);
          const kids = groupChildren.get(id) || [];
          for (const c of kids) visit(c.id);
        };
        visit(g.id);
        return out;
      })();
      for (const t of tagSet) {
        if (!m.has(t)) m.set(t, new Set());
        m.get(t).add(g.id);
      }
    }
    return m;
  }, [tagGroups, groupChildren]);
  // Names this diary recognizes — combines tag names (member-tag convention)
  // with people previously captured on entries. Used by the structured-
  // fields parser to decide whether 'w/ smyan' is a real person trigger
  // (lowercase but recognized) or just prose ('w/ a bag' — unrecognized).
  // Lowercased for case-insensitive matching.
  const knownNames = new Set([
    ...allTags,
    ...entries.flatMap(e => (e.structured?.people || [])),
  ].map(s => String(s).toLowerCase()));
  // Fields actually used in 2+ entries — drives which filter pills appear.
  // Returns Map<fieldKey, count>. Field keys are flat: built-ins are bare
  // names ("amount", "distanceMiles"); library = "library.<name>";
  // custom = "custom.<name>". Counted per-entry, not per-value.
  const usedFields = (() => {
    const counts = new Map();
    const bump = k => counts.set(k, (counts.get(k) || 0) + 1);
    for (const e of entries) {
      const s = e.structured;
      if (!s) continue;
      // Built-ins
      const builtins = ["amount","location","city","address","people","timeMinutes","distanceMiles","calories","mood","rating","score"];
      for (const k of builtins) {
        const v = s[k];
        if (v == null || v === "" || (Array.isArray(v) && !v.length)) continue;
        bump(k);
      }
      // Library
      if (s.library) {
        for (const [k, v] of Object.entries(s.library)) {
          if (v == null || v === "" || (Array.isArray(v) && !v.length)) continue;
          bump(`library.${k}`);
        }
      }
      // Custom
      if (s.custom) {
        for (const [k, v] of Object.entries(s.custom)) {
          if (v == null || v === "" || (Array.isArray(v) && !v.length)) continue;
          bump(`custom.${k}`);
        }
      }
    }
    // Filter to 2+ count
    return new Map([...counts].filter(([, c]) => c >= 2));
  })();
  // Tags actually used on at least one entry — narrower than `allTags` which
  // also includes diary.customTags (declared but possibly unused tags). Used
  // by the access editor / invite UI so we don't offer tags as "visible to
  // this member" that don't exist anywhere yet — those would just be empty
  // chips with nothing to grant access to.
  // We DO include diary.customTags here even though they may be unused on
  // entries: those are tags the owner explicitly declared/named and likely
  // wants to grant access to before any entry uses them (e.g. setting up
  // visibility for a new player before their first entry). The exclusion
  // is for ad-hoc tags that have never been touched.
  const usedTags = [...new Set([
    ...entries.flatMap(e => e.tags || []),
    ...(diary?.customTags || []),
  ])].filter(t => t !== "follow-up").sort();

  // Tags to display in the lens bar — respects diary.tagOrder and diary.hiddenTags
  // Viewer's tag access — applied as a hard filter above all other filters. Owner
  // sees everything; legacy string members see everything; restricted members see
  // only entries whose tags are ALL in their allowed set (strict rule), and only
  // see untagged entries if their includeUntagged flag is on.
  const myAccessEarly = getMemberAccess(diary?.members?.[user?.uid]);

  const lensTags = (() => {
    const hidden = new Set((diary?.hiddenTags || []).map(t => t.toLowerCase()));
    let visible = allTags.filter(t => t !== "follow-up" && !hidden.has(t));
    // Restricted viewers only see lens chips for tags they're allowed to see.
    if (myAccessEarly && myAccessEarly.tags !== null) {
      const allowed = new Set(myAccessEarly.tags);
      visible = visible.filter(t => allowed.has(t));
    }
    const order = diary?.tagOrder || [];
    const orderedSet = new Set(order);
    const ordered = order.filter(t => visible.includes(t));
    const rest = visible.filter(t => !orderedSet.has(t));
    let result = [...ordered, ...rest];

    // When any group is active, surface its tags first so users can see
    // (and exclude) what the group includes without scrolling or opening
    // the picker. Tags that aren't in any active group fall to the back.
    //
    // Ordering within the active set is hierarchical: tags are grouped
    // by their most-specific subgroup, and subgroups are visited in a
    // depth-first order from the active group. Each subgroup's name is
    // used as a sort key (alphanumeric), so for a hierarchy like:
    //   2425 LLC → [2425building, 2427building, ..., 55parkbuilding]
    // the user sees tags from each building together: all 2425 tags,
    // then all 2427 tags, etc. — instead of "2425, 2427, ..., 55park"
    // interleaving across buildings.
    //
    // Within a single subgroup, tags are sorted alphanumerically so
    // "2425 < 2425a < 2425b" (Intl.Collator numeric mode).
    if (activeGroups.size > 0) {
      const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
      // Walk each active group's tree in order. For each tag we encounter,
      // record its first-seen position so we can sort by it.
      const tagOrderMap = new Map();  // tag → ordinal
      let nextOrdinal = 0;
      const walk = (groupId) => {
        const g = tagGroups.find(x => x.id === groupId);
        if (!g) return;
        // Tags directly in this subgroup first (sorted alphanumerically).
        const ownTags = [...(g.tags || [])].sort((a, b) => collator.compare(a, b));
        for (const t of ownTags) {
          if (!tagOrderMap.has(t)) tagOrderMap.set(t, nextOrdinal++);
        }
        // Then recurse into children, sorted by their name (alphanumeric).
        const kids = [...(groupChildren.get(groupId) || [])]
          .sort((a, b) => collator.compare(a.name, b.name));
        for (const c of kids) walk(c.id);
      };
      // Process active groups in alphanumeric name order for stability.
      const activeGroupObjs = [...activeGroups]
        .map(gid => tagGroups.find(g => g.id === gid))
        .filter(Boolean)
        .sort((a, b) => collator.compare(a.name, b.name));
      for (const g of activeGroupObjs) walk(g.id);

      const groupTags = result.filter(t => tagOrderMap.has(t))
        .sort((a, b) => tagOrderMap.get(a) - tagOrderMap.get(b));
      const otherTags = result.filter(t => !tagOrderMap.has(t));
      result = [...groupTags, ...otherTags];
    }
    // Final pass: surface active + excluded tags at the very front so the
    // user sees what's actively filtering without scrolling or opening
    // the picker. Especially important after clicking a tag inside an
    // entry (jumpToTag) — the just-clicked tag should appear first.
    // Preserves relative order within each bucket.
    if (activeLenses.size > 0 || excludedTags.size > 0) {
      const isHot = (t) => activeLenses.has(t) || excludedTags.has(t);
      const hot = result.filter(isHot);
      const cold = result.filter(t => !isHot(t));
      result = [...hot, ...cold];
    }
    return result;
  })();

  // Multi-tag lens toggle — null clears all
  const toggleLens = tag => {
    // Capture lens-bar viewport position BEFORE state change so we can restore
    // it after React re-renders. This prevents the page-jump that happens
    // when filtered content changes the layout above the lens bar.
    if (filterRowRef.current) {
      pendingAnchorOffsetRef.current = filterRowRef.current.getBoundingClientRect().top;
    }
    if (tag === null) {
      // Clearing all also clears groups + exclusions, since they're all
      // part of one mental "active filter."
      setActiveLenses(new Set());
      setActiveGroups(new Set());
      setExcludedTags(new Set());
      return;
    }
    // If any of this tag's ancestor groups is active, the tag is already
    // "implicitly included" via the group. Clicking it should EXCLUDE it
    // (mark struck-through). Clicking again should un-exclude.
    const ancestors = tagAncestorGroups.get(tag);
    const hasActiveAncestor = ancestors && [...ancestors].some(gid => activeGroups.has(gid));
    if (hasActiveAncestor) {
      setExcludedTags(prev => {
        const next = new Set(prev);
        next.has(tag) ? next.delete(tag) : next.add(tag);
        return next;
      });
      return;
    }
    // Normal click-cycle on a tag chip outside any active group:
    //   inactive → included → excluded → inactive → ...
    // Two clicks lets the user say "show me everything EXCEPT this tag"
    // without needing a separate exclude UI. Third click clears it.
    setActiveLenses(prev => {
      const nextLenses = new Set(prev);
      setExcludedTags(prevEx => {
        const nextEx = new Set(prevEx);
        if (nextLenses.has(tag)) {
          // Was included → move to excluded.
          nextLenses.delete(tag);
          nextEx.add(tag);
        } else if (nextEx.has(tag)) {
          // Was excluded → clear entirely.
          nextEx.delete(tag);
        } else {
          // Not in either → include.
          nextLenses.add(tag);
        }
        return nextEx;
      });
      return nextLenses;
    });
  };

  // Click a tag chip displayed inside an entry to filter the whole diary
  // by that tag. Replaces (not adds to) the current filter state so the
  // user gets a clean "show me everything with this tag" view. If the
  // user clicks the same tag they're already filtering by, this toggles
  // it off (consistent with clicking the chip in the Tags pill row).
  // Clears groups + excludes too so the resulting view is unambiguous.
  // Scrolls the diary to the top so the user sees results from the top.
  const jumpToTag = (tag) => {
    if (filterRowRef.current) {
      pendingAnchorOffsetRef.current = filterRowRef.current.getBoundingClientRect().top;
    }
    const alreadyOnlyThis = activeLenses.size === 1
      && activeLenses.has(tag)
      && activeGroups.size === 0
      && excludedTags.size === 0;
    if (alreadyOnlyThis) {
      setActiveLenses(new Set());
    } else {
      setActiveLenses(new Set([tag]));
      setActiveGroups(new Set());
      setExcludedTags(new Set());
      // Expand the Tags pill row so the active filter is visible at the
      // top of the diary. Avoids hiding what's filtering the view.
      setShowTagsLensRow(true);
    }
  };

  // Group chip toggle. Activating a group implicitly includes all its
  // descendant tags. Deactivating clears any exclusions inside that group
  // too — otherwise stale exclusions would silently affect future filters.
  const toggleGroup = (groupId) => {
    if (filterRowRef.current) {
      pendingAnchorOffsetRef.current = filterRowRef.current.getBoundingClientRect().top;
    }
    setActiveGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
        // Clear exclusions that belonged exclusively to this group's tag set.
        // A tag stays excluded only if it's still under another active group.
        const removedTags = expandGroupTags(groupId);
        setExcludedTags(prevEx => {
          const ex = new Set(prevEx);
          for (const t of ex) {
            if (!removedTags.has(t)) continue;
            // Still under any other active group?
            const ancestors = tagAncestorGroups.get(t);
            const stillCovered = ancestors && [...ancestors].some(g => g !== groupId && next.has(g));
            if (!stillCovered) ex.delete(t);
          }
          return ex;
        });
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const myAccess = myAccessEarly;
  const isEntryVisible = (entry) => {
    // Private entries are visible only to the author. Everyone else
    // (collaborators, even diary owners reading another user's entries)
    // can't see them. This is a CLIENT-SIDE filter for the MVP — a
    // determined attacker with database access could still see them.
    // A future iteration would also add a Firestore security rule so
    // private entries aren't readable at the data layer for non-authors.
    // See PRIVATE_ENTRY_NOTES in this file for the full security model.
    if (entry.private && entry.authorId && entry.authorId !== user?.uid) {
      return false;
    }
    if (!myAccess || myAccess.tags === null) return true;
    const entryTags = entry.tags || [];
    if (entryTags.length === 0) return myAccess.includeUntagged !== false;
    const allowed = new Set(myAccess.tags);
    return entryTags.every(t => allowed.has(t));
  };
  // Tag list filtered to what THIS user is allowed to use. For unrestricted users
  // (owner, legacy member, no filter) this is the same as allTags. For restricted
  // contributors, we hide tags they can't see — so they can't even pick them when
  // creating entries.
  const myDiaryTags = (myAccess && myAccess.tags !== null)
    ? allTags.filter(t => myAccess.tags.includes(t))
    : allTags;
  // Pre-filtered list of entries the current user is allowed to see. Used by
  // InsightsPanel, photo counts, authors filter — anything that surfaces info
  // about the diary's contents at a summary level.
  const visibleEntries = entries.filter(isEntryVisible);
  // How many of the visible entries are "new" since the user's last visit?
  // We use createdAt (when the entry was first written) rather than entryDate
  // (which is the user-set date and could be in the past). Counts only entries
  // by other people — your own entries shouldn't show up as "new" to you.
  const newEntriesCount = (() => {
    if (!lastVisitedLoaded || !lastVisitedSnapshot) return 0;
    return visibleEntries.filter(e => {
      if (e.authorId && e.authorId === user?.uid) return false;
      const ts = e.createdAt?.toMillis ? e.createdAt.toMillis() : (e.createdAt ? new Date(e.createdAt).getTime() : 0);
      return ts > lastVisitedSnapshot;
    }).length;
  })();
  // Defense-in-depth: strip any tags a restricted user shouldn't be using.
  // Allows "follow-up" through (universal control tag, not a content tag).
  const sanitizeMyTags = (tags) => {
    if (!myAccess || myAccess.tags === null) return tags;
    const allowed = new Set([...myAccess.tags, "follow-up"]);
    return (tags || []).filter(t => allowed.has(t));
  };
  // Whether this user is required to tag every entry (owner allows untagged?
  // Otherwise we enforce at least one allowed tag).
  const requiresTag = !!(myAccess && myAccess.tags !== null && myAccess.includeUntagged === false);

  const filtered = entries
    .filter(isEntryVisible)
    .filter(e => {
      // Tag/group filter. Combines:
      //   - activeLenses: explicitly-clicked tag chips (include)
      //   - activeGroups: explicitly-clicked group chips (expand to all
      //     tags reachable from each group's subtree)
      //   - excludedTags: tags the user struck-through. When at least one
      //     include is active, exclusion subtracts from the effective set.
      //     When NO include is active but there are exclusions, the
      //     baseline is "all entries except those tagged with any
      //     excluded tag" — letting users say "show me everything
      //     except expense and receipt" with two clicks.
      //
      // The effective tag set is (activeLenses ∪ ⋃ expanded(groups)) \ excludedTags.
      // Entry passes if it has at least one tag in the effective set
      // (OR semantics — natural for groups; you wouldn't AND across an
      // LLC and a building). lensMode "all" only applies to manually-
      // clicked tag chips when no groups are active.
      const tags = e.tags || [];
      const hasIncludeFilter = activeLenses.size > 0 || activeGroups.size > 0;
      const hasExcludeOnly = !hasIncludeFilter && excludedTags.size > 0;

      if (hasExcludeOnly) {
        // Pure-exclude mode: entry passes if NONE of its tags are excluded.
        if (tags.some(t => excludedTags.has(t))) return false;
      } else if (hasIncludeFilter) {
        const expandedFromGroups = new Set();
        for (const gid of activeGroups) {
          for (const t of expandGroupTags(gid)) expandedFromGroups.add(t);
        }
        // Build effective: groups expand to OR, plus any extra direct lens
        // tags. Excluded tags are removed from the effective set.
        const effective = new Set([...activeLenses, ...expandedFromGroups]);
        for (const ex of excludedTags) effective.delete(ex);

        if (activeGroups.size > 0) {
          // OR semantics when any group is active: entry matches if it
          // has ANY tag in the effective set, AND none of its tags are
          // excluded (so explicit exclusions stick even if the entry
          // would have matched via group expansion).
          const matchesAny = tags.some(t => effective.has(t));
          if (!matchesAny) return false;
          // Honor explicit exclusions: entry's tags that are excluded
          // disqualify the entry IF no non-excluded tag of the entry
          // matched. We already checked matchesAny above considering the
          // excluded set, so this naturally handles it.
        } else {
          // No groups active — preserve original AND/OR lens behavior.
          const matches = lensMode === "all"
            ? [...activeLenses].every(t => tags.includes(t))
            : tags.some(t => activeLenses.has(t));
          if (!matches) return false;
        }
      }
      if (filterFavorites && !e.favorite) return false;
      if (filterFollowup && !(e.tags || []).some(isFollowupTag)) return false;
      if (filterPhotos && !(e.photos?.length > 0 || e.videos?.length > 0 || e.audio?.length > 0 || e.docs?.length > 0)) return false;
      if (filterContributor !== "all" && e.authorId !== filterContributor) return false;
      if (filterDateFrom) {
        const d = e.entryDate?.toDate ? e.entryDate.toDate() : new Date(e.entryDate);
        if (d < new Date(filterDateFrom + "T00:00:00")) return false;
      }
      if (filterDateTo) {
        const d = e.entryDate?.toDate ? e.entryDate.toDate() : new Date(e.entryDate);
        if (d > new Date(filterDateTo + "T23:59:59")) return false;
      }
      // Field filters — apply per-field criteria. fieldFilterMode controls
      // whether all active criteria must match (AND, default) or just any
      // one (OR). Within a single field's value list, members combine OR.
      // Within a single field's min/max/values combo, criteria combine AND.
      const activeFieldEntries = Object.entries(fieldFilters).filter(([, c]) =>
        c && (c.has || c.min != null || c.max != null || c.values?.length)
      );
      if (activeFieldEntries.length) {
        const matchesField = (field, criteria) => {
          const s = e.structured || {};
          let val;
          if (field.startsWith("library.")) val = (s.library || {})[field.slice(8)];
          else if (field.startsWith("custom.")) val = (s.custom || {})[field.slice(7)];
          else val = s[field];
          const isEmpty = val == null || val === "" || (Array.isArray(val) && !val.length);
          if (criteria.has && isEmpty) return false;
          if (criteria.min != null || criteria.max != null) {
            const n = Number(val);
            if (!Number.isFinite(n)) return false;
            if (criteria.min != null && n < criteria.min) return false;
            if (criteria.max != null && n > criteria.max) return false;
          }
          if (criteria.values?.length) {
            if (isEmpty) return false;
            const valueArr = Array.isArray(val)
              ? val.map(v => String(v).toLowerCase())
              : [String(val).toLowerCase()];
            const wantSet = new Set(criteria.values.map(v => String(v).toLowerCase()));
            if (!valueArr.some(v => wantSet.has(v))) return false;
          }
          return true;
        };
        const passes = fieldFilterMode === "any"
          ? activeFieldEntries.some(([f, c]) => matchesField(f, c))
          : activeFieldEntries.every(([f, c]) => matchesField(f, c));
        if (!passes) return false;
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        // Search across:
        //   - title, body (plain text + bodyHtml)
        //   - tags
        //   - structured fields: location (vendor name), city, address,
        //     payment_type, spend_category, body_part, medication, etc.
        //     This lets users find entries by what's ON the receipt
        //     rather than what was typed — e.g. searching "Best Buy"
        //     finds an entry whose vendor was OCR-extracted, even if
        //     "Best Buy" never appeared in the body or title.
        //   - OCR-derived description (the 1-line summary of what
        //     was bought/provided, when receipt OCR ran)
        //   - Custom field values (declared per-diary)
        const s = e.structured || {};
        const ocr = s.ocr || {};
        const structuredHaystack = [
          // Builtins. Some are arrays (location, with), some scalars.
          ...(Array.isArray(s.location) ? s.location : []),
          ...(Array.isArray(s.with) ? s.with : []),
          s.city || "",
          s.address || "",
          // Library fields — only some diaries enable these. Search
          // them unconditionally; if not set, they're empty strings.
          s.payment_type || "",
          s.spend_category || "",
          s.body_part || "",
          s.medication || "",
          s.subject || "",
          s.cuisine || "",
          s.project || "",
          s.symptom || "",
          s.property || "",
          // OCR-derived description (1-line summary of what was on the
          // receipt). Catches things like "Bathroom fill valve" even
          // when the user didn't write that in the body.
          ocr.description || "",
          // OCR itemDescs (when present — currently a 1-element array
          // mirroring description; future line-items work would expand).
          ...(Array.isArray(ocr.itemDescs) ? ocr.itemDescs : []),
          // Custom fields — flatten any string/array values.
          ...Object.entries(s.custom || {}).flatMap(([_, v]) =>
            Array.isArray(v) ? v : (typeof v === "string" ? [v] : [])
          ),
        ].join(" ").toLowerCase();
        const baseHaystack = (
          (e.body || "") + " " +
          toText(e.bodyHtml || "") + " " +
          (e.title || "") + " " +
          (e.tags || []).join(" ")
        ).toLowerCase();
        if (!(baseHaystack + " " + structuredHaystack).includes(q)) return false;
      }
      return true;
    })
    .sort((a, b) => sort === "newest" ? getTs(b.entryDate) - getTs(a.entryDate) : getTs(a.entryDate) - getTs(b.entryDate))
    .map(e => ({ ...e, _who: e.authorId ? names[e.authorId] : "" }));

  useEffect(() => { setPage(0); }, [search, activeLenses, sort, filterFavorites, filterFollowup, filterPhotos, filterContributor, filterDateFrom, filterDateTo, fieldFilters, fieldFilterMode]);

  // Scroll restoration: when the user taps a tag, applies a search, etc.,
  // the click handler captures the lens-bar's viewport Y into
  // pendingAnchorOffsetRef BEFORE state updates. This effect runs after the
  // re-render and restores the lens-bar to that exact Y position so the
  // page doesn't visibly jump.
  useLayoutEffect(() => {
    if (pendingAnchorOffsetRef.current == null) return;
    const targetTop = pendingAnchorOffsetRef.current;
    pendingAnchorOffsetRef.current = null;
    if (!filterRowRef.current) return;
    const newTop = filterRowRef.current.getBoundingClientRect().top;
    const delta = newTop - targetTop;
    if (Math.abs(delta) > 1) {
      window.scrollBy({ top: delta, behavior: "auto" });
    }
  });

  const contributors = [...new Set(entries.map(e => e.authorId).filter(Boolean))];
  const activeFilterCount = [filterFavorites, filterFollowup, filterPhotos, filterContributor !== "all", filterDateFrom, filterDateTo].filter(Boolean).length + Object.keys(fieldFilters).length;

  const clearAllFilters = () => {
    setActiveLenses(new Set()); setActiveGroups(new Set()); setExcludedTags(new Set());
    setSearch(""); setFilterFavorites(false);
    setFilterFollowup(false); setFilterPhotos(false); setFilterContributor("all");
    setFilterDateFrom(""); setFilterDateTo(""); setSort("newest");
  };

  // Download the currently-filtered entries as a CSV. If the title follows
  // the "vendor / amount / unit / description" shape, those parts get split
  // into separate columns. Otherwise they're left blank — non-destructive.
  // Strip private entries from any list bound for export, share, or
  // sharing-with-collaborators. Used by CSV/Excel exports, share-modal,
  // and the snapshot-image generator. Owner sees private entries in
  // their own timeline (with a 🔒 indicator), but they never leak via
  // any export or share surface. If a user wants to share a private
  // entry, they have to flip its Private toggle off first.
  const stripPrivate = (entries) => (entries || []).filter(e => !e.private);

  const exportCsv = () => {
    const exportable = stripPrivate(filtered);
    if (!exportable.length) return;
    const { csv } = buildEntriesCsv(exportable, diary);
    // Leading BOM so Excel detects UTF-8 (otherwise non-ASCII characters
    // like emoji and accented vendor names render as mojibake).
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeName = (diary?.name || "diary").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40) || "diary";
    a.href = url;
    a.download = `${safeName}-entries-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setToolbarDownloadOpen(false);
  };

  // Excel-formatted variant. Same data as exportCsv but as an XLSX file
  // with column widths, frozen header, and no text wrap. Lazily loads
  // the xlsx library so users who only use CSV don't pay the ~400KB
  // bundle cost.
  const exportXlsx = async () => {
    const exportable = stripPrivate(filtered);
    if (!exportable.length) return;
    try {
      const XLSX = await import("xlsx");
      const wb = buildEntriesXlsx(exportable, XLSX, diary);
      // cellStyles: true preserves the bold-header + wrap-off styling
      // we attach to cells. Without it, the data exports but styles drop.
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = (diary?.name || "diary").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40) || "diary";
      a.href = url;
      a.download = `${safeName}-entries-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert("Excel export failed. Please try CSV instead.\n\n" + (e.message || ""));
    }
    setToolbarDownloadOpen(false);
  };

  // Variant of exportCsv that uses the focused expense schema. Skips entries
  // whose title doesn't have "/" — those aren't expenses.
  const exportExpensesCsv = () => {
    const exportable = stripPrivate(filtered);
    if (!exportable.length) return;
    const { csv, rowCount } = buildExpensesCsv(exportable);
    if (rowCount === 0) {
      alert("None of the visible entries match the expense format (vendor / amount / unit / description). Use Download CSV for the general format.");
      setToolbarDownloadOpen(false);
      return;
    }
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeName = (diary?.name || "diary").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40) || "diary";
    a.href = url;
    a.download = `${safeName}-expenses-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setToolbarDownloadOpen(false);
  };

  // Excel variant of the expense report.
  const exportExpensesXlsx = async () => {
    if (!filtered.length) return;
    try {
      const XLSX = await import("xlsx");
      const { wb, rowCount } = buildExpensesXlsx(filtered, XLSX);
      if (rowCount === 0) {
        alert("None of the visible entries match the expense format (vendor / amount / unit / description). Use Download Excel for the general format.");
        setToolbarDownloadOpen(false);
        return;
      }
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = (diary?.name || "diary").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40) || "diary";
      a.href = url;
      a.download = `${safeName}-expenses-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert("Excel export failed. Please try CSV instead.\n\n" + (e.message || ""));
    }
    setToolbarDownloadOpen(false);
  };

  const [toolbarDownloadOpen, setToolbarDownloadOpen] = useState(false);
  const [toolbarBuilding, setToolbarBuilding] = useState(false);

  // Filter-scoped versions of PDF/Email/Copy. These mirror the bulk-select
  // handlers but use `filtered` (the currently-visible list) instead of
  // `selectedIds`. Same UX whether the user got their list via filter or via
  // hand-picking, just different sources.
  const buildToolbarFilename = (count, ext) => {
    const safe = (diary?.name || "diary").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40) || "diary";
    const date = new Date().toISOString().slice(0, 10);
    return `${safe}-${date}-${count}-entries.${ext}`;
  };

  const exportPdf = async () => {
    if (!filtered.length) return;
    setToolbarBuilding(true);
    try {
      const blob = await buildEntryPdf(filtered);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildToolbarFilename(filtered.length, "pdf");
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error("PDF build failed", e);
      alert("Couldn't build PDF — see console for details.");
    } finally {
      setToolbarBuilding(false);
      setToolbarDownloadOpen(false);
    }
  };

  // Word doc for filter view — same as exportPdf, just .docx output.
  const exportDocx = async () => {
    if (!filtered.length) return;
    setToolbarBuilding(true);
    try {
      const blob = await buildEntryDocx(filtered);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildToolbarFilename(filtered.length, "docx");
      a.target = "_blank"; a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error("Docx build failed", e);
      alert("Couldn't build Word doc — see console for details.");
    } finally {
      setToolbarBuilding(false);
      setToolbarDownloadOpen(false);
    }
  };

  const exportEmailPdf = async () => {
    if (!filtered.length) return;
    setToolbarBuilding(true);
    try {
      const blob = await buildEntryPdf(filtered);
      const filename = buildToolbarFilename(filtered.length, "pdf");
      const file = new File([blob], filename, { type: "application/pdf" });
      const subject = `${filtered.length} ${filtered.length === 1 ? "entry" : "entries"} from ${diary?.name || "LifeTrackr"}`;

      if (canShareFiles(file)) {
        try {
          await navigator.share({ files: [file], title: subject });
          return;
        } catch (e) {
          if (e.name === "AbortError") return;
          // fall through
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      a.target = "_blank"; a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      const body = `Attached: ${filename}\n\n(${filtered.length} ${filtered.length === 1 ? "entry" : "entries"} from your ${diary?.name || ""} diary.)`;
      window.open(
        `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
        "_blank", "noopener"
      );
      alert("PDF saved to Downloads — drag it into the Gmail draft.");
    } catch (e) {
      console.error("Email PDF failed", e);
      alert("Couldn't build PDF — see console for details.");
    } finally {
      setToolbarBuilding(false);
      setToolbarDownloadOpen(false);
    }
  };

  const exportCopyText = async () => {
    if (!filtered.length) return;
    const text = filtered.map(e => {
      const titleLine = e.title ? e.title + "\n" : "";
      const body = (e.bodyHtml ? toText(e.bodyHtml) : (e.body || "")).trim();
      return titleLine + body;
    }).join("\n\n---\n\n");
    try {
      await navigator.clipboard.writeText(text);
      alert(`Copied ${filtered.length} ${filtered.length === 1 ? "entry" : "entries"} to clipboard.`);
    } catch {
      alert("Couldn't copy. Try again or download instead.");
    }
    setToolbarDownloadOpen(false);
  };

  // Group all filtered entries by month-year (for headers)
  const groupedEntries = (() => {
    const groups = [];
    let currentKey = null;
    filtered.forEach(e => {
      const d = e.entryDate?.toDate ? e.entryDate.toDate() : new Date(e.entryDate);
      const key = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
      if (key !== currentKey) { groups.push({ key, entries: [] }); currentKey = key; }
      groups[groups.length - 1].entries.push(e);
    });
    return groups;
  })();

  const totalPages = Math.max(1, Math.ceil(filtered.length / ENTRIES_PER_PAGE));
  const pageEntries = filtered.slice(page * ENTRIES_PER_PAGE, (page + 1) * ENTRIES_PER_PAGE);

  // Classify a file's MIME type into one of our attachment buckets.
  // Documents: PDFs, Word, Excel, PowerPoint, plain text, CSV, etc.
  // Pull any image files out of a clipboard paste event. Returns an array
  // of File objects, possibly empty. Used by the body editors' onPaste
  // handlers to support "paste a screenshot to attach it" — Ctrl+V on
  // desktop, Cmd+V on Mac, and (where supported) long-press paste on
  // mobile. Images become attachments, not inline body content.
  //
  // Notes:
  //  - clipboardData.items iterates all items; we filter to type/image.
  //  - getAsFile() returns null for non-file items, so we filter those.
  //  - Pasted images typically arrive as "image/png" with name "image.png"
  //    (browsers normalize them). They have no EXIF GPS, so the geo
  //    pipeline downstream will simply produce no location — that's fine.
  //  - HEIC pastes from macOS/iOS are decoded to PNG by the browser, so
  //    no special-case needed.
  const extractClipboardImages = (clipboardData) => {
    if (!clipboardData || !clipboardData.items) return [];
    const out = [];
    for (const item of clipboardData.items) {
      if (!item.type || !item.type.startsWith("image/")) continue;
      const file = item.getAsFile();
      if (file) out.push(file);
    }
    return out;
  };

  const classifyFile = file => {
    const m = (file.type || "").toLowerCase();
    const n = (file.name || "").toLowerCase();
    if (m.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(n)) return "image";
    if (m.startsWith("video/") || /\.(mp4|mov|m4v|webm|avi|mkv|3gp|wmv)$/i.test(n)) return "video";
    if (m.startsWith("audio/") || /\.(mp3|m4a|wav|ogg|aac|flac|opus|amr)$/i.test(n)) return "audio";
    // Document types
    if (
      m === "application/pdf" || /\.pdf$/.test(n) ||
      m.includes("msword") || m.includes("wordprocessingml") || /\.docx?$/.test(n) ||
      m.includes("ms-excel") || m.includes("spreadsheetml") || /\.xlsx?$/.test(n) ||
      m.includes("ms-powerpoint") || m.includes("presentationml") || /\.pptx?$/.test(n) ||
      m === "text/plain" || /\.(txt|rtf)$/.test(n) ||
      m === "text/csv" || /\.csv$/.test(n) ||
      m === "application/zip" || /\.zip$/.test(n)
    ) return "document";
    // Unknown → treat as document so we at least surface it to the user instead of silently losing it
    return "document";
  };

  // Friendly label for a file's kind — shown in progress UI and error messages
  const kindLabel = kind => ({ image: "photo", video: "video", audio: "audio", document: "document" }[kind] || "file");

  // Enforce the free-tier ATTACHMENT cap at attach time. When the user can't
  // add more (free tier, lifetime cap of any-kind attachments hit), block the
  // entire selection — photos, videos, audio, and docs all count toward the
  // cap, so all are gated together. Returns the kept files (none, when capped)
  // and how many were blocked, so the caller can open the upgrade prompt. Pro
  // users (canAddPhotos === true) pass everything through.
  const filterForPhotoCap = (files) => {
    if (canAddPhotos) return { kept: files, blockedPhotos: 0 };
    return { kept: [], blockedPhotos: files.length };
  };

  // Upload progress state: { active: bool, total: N, completed: N, currentName, currentProgress (0-100), error }
  const [uploadState, setUploadState] = useState({ active: false });

  // Upload every file with per-file progress events, return enriched objects grouped by kind.
  // On any failure, reject so the caller can surface the error (instead of silently dropping attachments).
  const uploadAndSort = async (files, onProgress) => {
    const photos = [];
    const videos = [];
    const audio = [];
    const docs = [];
    const total = files.length;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const kind = classifyFile(f);
      // Guard: cap individual file at 500 MB (Firebase Storage allows more, but uploads from mobile
      // beyond this often fail on flaky connections)
      if (f.size > 500 * 1024 * 1024) {
        throw new Error(`"${f.name}" is ${(f.size / 1024 / 1024).toFixed(0)} MB — too large. Please trim the video or use a shorter clip under 500 MB.`);
      }
      const safeName = (f.name || `${kind}_${Date.now()}`).replace(/[^\w.\-]+/g, "_");
      const r = ref(storage, `diaries/${id}/${Date.now()}_${safeName}`);

      // Use resumable upload so we get progress events AND connection-drop recovery
      await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(r, f, { contentType: f.type || "application/octet-stream" });
        task.on("state_changed",
          snap => {
            const pct = snap.totalBytes > 0 ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100) : 0;
            if (onProgress) onProgress({ index: i, total, name: f.name, kind, pct });
          },
          err => {
            console.error("Upload failed", f.name, err);
            reject(new Error(`Upload of "${f.name}" failed: ${err.code || err.message || "unknown error"}`));
          },
          () => resolve()
        );
      });

      const url = await getDownloadURL(r);
      // Save storagePath too — backfillEntryGeo (Cloud Function) needs the
      // bucket-relative path to download the original photo bytes and read
      // EXIF GPS. Without this, geo resolution silently no-ops on in-app
      // uploads because the function only has the URL, which it can't use
      // with bucket.file().download().
      const item = { url, storagePath: r.fullPath, name: f.name, contentType: f.type || "", size: f.size };
      if (kind === "video") videos.push(item);
      else if (kind === "audio") audio.push(item);
      else if (kind === "document") docs.push(item);
      else photos.push(item);
    }
    return { photos, videos, audio, docs };
  };

  const parsTagInput = str => str.split(",").map(t => t.trim().toLowerCase()).filter(Boolean);

  const buildTags = (tagStr, followup) => {
    const tags = parsTagInput(tagStr);
    if (followup && !tags.some(isFollowupTag)) tags.push("follow-up");
    if (!followup) return tags.filter(t => !isFollowupTag(t));
    return tags;
  };

  // Live extraction of the email-style "(tag1, tag2) " or "!!" shorthand from
  // a title input. Wired to the title inputs' onBlur so users see immediate
  // visual feedback (tags pop into the picker, prefix disappears from the
  // title) when they tab/click away.
  //
  // Save-time logic in handleAdd/handleUpdate also runs the same extraction
  // as a fallback — covers the case where a user clicks Save while still
  // focused on the title input, which on some browsers fires the click
  // before our blur handler has finished its state update.
  //
  // We promote "!!" to the followup checkbox rather than leaving it as a
  // transient string, so the user's intent is visibly captured in UI state.
  // Also applies any date offset shorthand from inside the (...) prefix
  // (e.g. "(-1)", "(-1d)", "(-2w)") to the date state — otherwise the
  // offset would be silently dropped on blur, since the blur strips the
  // entire prefix from the title before save sees it.
  const applyTitlePrefixToTags = (titleValue, setTitle, tagsValue, setTags, setFollowup, currentDate, setDate, setPrivate) => {
    if (!titleValue || typeof titleValue !== "string") return;
    const { tags: prefixTags, cleanedTitle, dateOffset, isPrivate: prefixPrivate } = extractLeadingTagPrefix(titleValue);
    const hasAnything = prefixTags.length > 0 || !!dateOffset || prefixPrivate;
    if (!hasAnything) return;
    if (prefixTags.some(isFollowupTag)) setFollowup(true);
    // Reflect a "(private)"/"(priv)" marker in the 🔒 toggle so the user gets
    // immediate visual confirmation (mirrors how follow-up flips on above).
    if (prefixPrivate && setPrivate) setPrivate(true);
    if (dateOffset && setDate && currentDate) {
      const off = parseOffsetString(dateOffset);
      const newYmd = applyOffsetToYmd(currentDate, off);
      if (newYmd && newYmd !== currentDate) setDate(newYmd);
    }
    const existing = parsTagInput(tagsValue);
    const existingSet = new Set(existing.map(t => t.toLowerCase()));
    const merged = [...existing];
    for (const t of prefixTags) {
      if (isFollowupTag(t)) continue; // handled via setFollowup
      const lc = t.toLowerCase();
      if (existingSet.has(lc)) continue;
      merged.push(t);
      existingSet.add(lc);
    }
    if (cleanedTitle !== titleValue) setTitle(cleanedTitle);
    if (merged.length !== existing.length) setTags(merged.join(", "));
  };

  const addingRef = useRef(false);
  const handleAdd = async () => {
    if (addingRef.current) return; // Guard against double-submit (e.g., fast double-tap on mobile Save)
    const hasBody = hasHtmlContent(newHtml);
    const hasTitle = newTitle.trim().length > 0;
    const hasAttachments = newPhotos.length > 0;
    if (!hasTitle && !hasBody && !hasAttachments) return;
    addingRef.current = true;
    setSaving(true);
    try {
      // Flush any un-confirmed custom tag text from the picker before saving
      const finalTagsInput = newTagPickerRef.current?.flush?.() ?? newTags;
      if (newPhotos.length > 0) {
        setUploadState({ active: true, total: newPhotos.length, completed: 0, currentName: newPhotos[0]?.name || "", currentKind: classifyFile(newPhotos[0]), currentProgress: 0 });
      }
      const { photos, videos, audio, docs } = await uploadAndSort(newPhotos, p => {
        setUploadState({ active: true, total: p.total, completed: p.index, currentName: p.name, currentKind: p.kind, currentProgress: p.pct });
      });
      setUploadState({ active: false });
      // Extract any "(tag1, tag2) " or "!!" shorthand from the title, mirroring
      // the inbound-email convention so the same syntax works in both places.
      // Tags found in the title prefix get merged with the picker's tags;
      // the prefix is stripped from the saved title. A date offset token
      // inside the parens (e.g. "(-1d)") is captured and applied to the
      // entry date below.
      const { tags: titlePrefixTags, cleanedTitle: cleanedNewTitle, dateOffset: newDateOffset, isPrivate: titlePrefixPrivate } =
        extractLeadingTagPrefix(newTitle.trim());
      // Apply title-prefix date offset to newDate. If the user also typed
      // a shorthand into the tag picker, both apply (compose). State update
      // is asynchronous but we read the resulting YMD into a local var for
      // use in buildEntryDate below.
      const effectiveDateYmd = newDateOffset
        ? applyOffsetToYmd(newDate, parseOffsetString(newDateOffset))
        : newDate;
      if (newDateOffset && effectiveDateYmd !== newDate) {
        setNewDate(effectiveDateYmd);
      }
      const effectiveFollowup = newFollowup || titlePrefixTags.includes("follow-up");
      const rawTags = buildTags(finalTagsInput, effectiveFollowup);
      // Merge in non-followup prefix tags (followup is already handled above
      // via effectiveFollowup so buildTags doesn't strip it back out).
      for (const t of titlePrefixTags) {
        if (isFollowupTag(t)) continue;
        if (!rawTags.includes(t)) rawTags.push(t);
      }
      const tags = sanitizeMyTags(rawTags);
      // If this user can't see untagged entries, refuse to create one
      const contentTags = tags.filter(t => !isFollowupTag(t));
      if (requiresTag && contentTags.length === 0) {
        setUploadState({ active: false, error: "Please pick at least one tag for this entry." });
        addingRef.current = false;
        setSaving(false);
        return;
      }
      const entryDoc = {
        diaryId: id, authorId: user.uid,
        title: cleanedNewTitle,
        body: toText(newHtml), bodyHtml: sanitize(newHtml),
        entryDate: buildEntryDate(effectiveDateYmd),
        createdAt: serverTimestamp(), photos, tags, favorite: newFavorite, source: "web",
      };
      // Always persist `private` explicitly (true OR false). It must never be
      // absent: the diary's entry list loads non-private entries via
      // where("private","==",false), and a missing field would exclude the
      // entry from that query (making it invisible to everyone). The flag can
      // come from the 🔒 toggle OR a "(private)"/"(priv)" title prefix.
      entryDoc.private = !!(newPrivate || titlePrefixPrivate);
      // If subject is empty but we have body and/or composer-entered structured
      // fields, auto-generate a readable subject. Same logic as CSV import.
      if (!entryDoc.title) {
        const generated = generateTitleFromStructured(entryDoc.body, newStructured);
        if (generated) entryDoc.title = generated;
      }
      // Mark whether the title was auto-generated (vs. user-typed). On edit,
      // auto-generated titles can be re-enhanced as user adds more details;
      // user-typed titles are preserved as-is.
      entryDoc.titleAutoGenerated = !titleManuallyTouched;
      if (videos.length) entryDoc.videos = videos;
      if (audio.length) entryDoc.audio = audio;
      if (docs.length) entryDoc.docs = docs;
      // If this entry is tagged "expense" and the title looks parseable,
      // attach structured expenseData so reports/CSV exports don't have to
      // re-parse the title later. Title-only is still the source of truth —
      // expenseData is purely a derived cache.
      if (tags.includes("expense")) {
        const ed = parseExpenseTitle(entryDoc.title);
        if (ed) entryDoc.expenseData = ed;
      }
      // Run the structured-fields parser on the BODY (not title — see
      // comment in the live-parse useEffect above for the rationale).
      // Pulls out shorthand tokens like $25, @vendor, w:Person, 1.5h, 3mi,
      // mood:happy. Stored on entry.structured for future use (reports,
      // charts, filters). Any #tag tokens found here are also merged into
      // entry.tags so they show as normal tags in the UI.
      // Parse shorthand from body, plus title when user-authored. When
      // the title is auto-generated, including it would feedback-loop
      // (parser re-derives chips from values it just generated).
      const bodyText = (entryDoc.body || "").trim();
      const titleText = titleManuallyTouched ? (entryDoc.title || "").trim() : "";
      const combinedText = [titleText, bodyText].filter(Boolean).join(". ") || (entryDoc.title || "").trim();
      const structured = parseStructuredFields(combinedText, { knownNames });
      if (structured.tags.length) {
        // Case-insensitive dedupe — same tag in different cases (e.g.
        // "277shipuB" and "277shipub") should appear only once.
        const existingTagSet = new Set(tags.map(t => t.toLowerCase()));
        for (const t of structured.tags) {
          if (!existingTagSet.has(t.toLowerCase())) {
            tags.push(t);
            existingTagSet.add(t.toLowerCase());
          }
        }
        entryDoc.tags = tags;
      }
      // Auto-tag from natural language: two passes —
      //   1) Literal tag-name match: if body/title mentions the tag name itself
      //      (e.g., "tennis"), add the tag. Includes diary-used + built-in tags.
      //   2) Keyword/synonym match via inferTagsFromText: matches body against
      //      TAG_KEYWORDS dictionary (e.g., "forehand" → tennis, "doctor" → medical).
      // Word-boundary match, case-insensitive, skips short generic words.
      if (combinedText) {
        const lc = combinedText.toLowerCase();
        const existingTagSet = new Set(tags.map(t => t.toLowerCase()));
        // Pass 1: literal tag name. Only considers tags this diary uses
        // or declares — global TAG_KEYWORDS keys (auto, medical, etc.)
        // are not auto-applied unless the diary actually has them.
        const candidateTags = new Set((myDiaryTags || []).map(t => t.toLowerCase()));
        for (const tag of candidateTags) {
          if (!tag || tag.length <= 3) continue;
          if (isFollowupTag(tag)) continue;
          if (existingTagSet.has(tag)) continue;
          const escaped = tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const re = new RegExp(`\\b${escaped}\\b`, "i");
          if (re.test(lc)) {
            tags.push(tag);
            existingTagSet.add(tag);
          }
        }
        // Pass 2: keyword-based synonym match (forehand → tennis, etc.)
        // Restricted to tags this diary actually uses or declares — no
        // phantom suggestions like 'auto' for a tennis-team diary.
        const inferred = inferTagsFromText(combinedText, allTags, structured.amount != null);
        for (const tag of inferred) {
          const tagLc = tag.toLowerCase();
          if (existingTagSet.has(tagLc)) continue;
          if (isFollowupTag(tag)) continue;
          tags.push(tag);
          existingTagSet.add(tagLc);
        }
        entryDoc.tags = tags;
      }
      // Merge the composer's newStructured into the parsed result. Parser
      // values represent the CURRENT body state — they should win for any
      // chip the user didn't explicitly touch. Chips override the parser ONLY
      // when the user clicked a chip and explicitly set a value (touched).
      // This prevents stale chip values from earlier typing stages (e.g.,
      // "$5 @hom" before user typed more) from leaking into the saved entry.
      const isTouched = (key) => touchedStructFields.has(key);
      const pickField = (key, parserVal) => {
        if (isTouched(key) && newStructured[key] != null) return newStructured[key];
        // Parser value if present; else fall back to chip value (could be set
        // for fields like "score" via chip when body has no shorthand for it)
        return parserVal != null && (Array.isArray(parserVal) ? parserVal.length : true)
          ? parserVal
          : newStructured[key];
      };
      const merged = {
        amount: pickField("amount", structured.amount),
        location: pickField("location", structured.location && structured.location.length ? structured.location : null),
        people: pickField("people", structured.people && structured.people.length ? structured.people : null),
        timeMinutes: pickField("timeMinutes", structured.timeMinutes),
        distanceMiles: pickField("distanceMiles", structured.distanceMiles),
        calories: pickField("calories", structured.calories),
        mood: pickField("mood", structured.mood && structured.mood.length ? structured.mood : null),
        rating: pickField("rating", structured.rating || ""),
        score: pickField("score", structured.score || ""),
      };
      // Only persist structured if at least one field was extracted
      const hasAnyStructured = (
        merged.amount != null ||
        (merged.location && (Array.isArray(merged.location) ? merged.location.length : merged.location)) ||
        (merged.people && merged.people.length) ||
        merged.timeMinutes != null || merged.distanceMiles != null ||
        merged.calories != null ||
        (merged.mood && (Array.isArray(merged.mood) ? merged.mood.length : merged.mood)) ||
        merged.rating || merged.score
      );
      if (hasAnyStructured) {
        // Save only the populated fields; skip empty arrays/null for cleanliness
        const s = {};
        if (merged.amount != null) s.amount = merged.amount;
        if (merged.location && (Array.isArray(merged.location) ? merged.location.length : merged.location)) {
          // Always store as array. Coerce string → [string] for back-compat.
          s.location = Array.isArray(merged.location) ? merged.location : [merged.location];
        }
        if (merged.people && merged.people.length) {
          s.people = merged.people;
          // Carry the opponent flag if it's set on the working state.
          // Only meaningful when there are people; cleared otherwise.
          if (newStructured.peopleAsOpponents) s.peopleAsOpponents = true;
        }
        if (merged.timeMinutes != null) s.timeMinutes = merged.timeMinutes;
        if (merged.distanceMiles != null) s.distanceMiles = merged.distanceMiles;
        if (merged.calories != null) s.calories = merged.calories;
        if (merged.mood && (Array.isArray(merged.mood) ? merged.mood.length : merged.mood)) {
          s.mood = Array.isArray(merged.mood) ? merged.mood : [merged.mood];
        }
        if (merged.rating) s.rating = merged.rating;
        if (merged.score) s.score = merged.score;
        entryDoc.structured = s;
      }
      // Custom fields. Merge: chip-set values (from newStructured.custom) take
      // priority; parser-detected fields (structured.customFields) only fill
      // in if they're already established in this diary OR if the user has
      // also set them via the chip.
      const customMerged = {};
      const declaredCustomSet = new Set([...(declaredCustom || []), ...(establishedCustomFields || [])].map(s => s.toLowerCase()));
      // Parser-detected, only if established (avoids "Note: bring water" false positives)
      if (structured.customFields) {
        for (const [k, v] of Object.entries(structured.customFields)) {
          if (declaredCustomSet.has(k.toLowerCase())) {
            customMerged[k.toLowerCase()] = Array.isArray(v) ? v : [v];
          }
        }
      }
      // Chip-set values override only when user explicitly touched the field.
      // Otherwise parser wins (so a stale chip from earlier typing doesn't leak).
      if (newStructured.custom) {
        for (const [k, v] of Object.entries(newStructured.custom)) {
          if (v != null && (Array.isArray(v) ? v.length : true)) {
            const customKey = `custom.${k.toLowerCase()}`;
            const isTouched = touchedStructFields.has(customKey);
            // If user explicitly set this chip → use it. Otherwise only use it
            // if parser didn't already provide a value.
            if (isTouched || customMerged[k.toLowerCase()] == null) {
              customMerged[k.toLowerCase()] = Array.isArray(v) ? v : [v];
            }
          }
        }
      }
      if (Object.keys(customMerged).length) {
        if (!entryDoc.structured) entryDoc.structured = {};
        entryDoc.structured.custom = customMerged;
      }
      // Library fields. Only persist values for fields that are actually
      // enabled in this diary's settings (so a parser hit on "payment:"
      // when the diary doesn't use payment_type doesn't accidentally save).
      const libraryMerged = {};
      const enabledLibrarySet = new Set(enabledLibrary || []);
      if (structured.libraryFields) {
        for (const [k, v] of Object.entries(structured.libraryFields)) {
          if (enabledLibrarySet.has(k)) libraryMerged[k] = v;
        }
      }
      // Chip-set library overrides parser only when user touched
      if (newStructured.library) {
        for (const [k, v] of Object.entries(newStructured.library)) {
          if (v != null && (Array.isArray(v) ? v.length : true)) {
            const libKey = `library.${k}`;
            const isTouched = touchedStructFields.has(libKey);
            if (isTouched || libraryMerged[k] == null) {
              libraryMerged[k] = v;
            }
          }
        }
      }
      if (Object.keys(libraryMerged).length) {
        if (!entryDoc.structured) entryDoc.structured = {};
        entryDoc.structured.library = libraryMerged;
      }
      // Dual-purpose tags: a tag like #happy or #cash also populates the
      // mood/payment_type field. Only kicks in when:
      //   - The relevant field hasn't been touched by user (chip-set wins)
      //   - For payment_type, the diary has the library field enabled
      // This is additive — the tag stays as a tag, AND the field gets set.
      {
        const finalTags = entryDoc.tags || [];
        const finalTagsLc = finalTags.map(t => t.toLowerCase());
        // Mood — built-in, always considered. Existing mood values preserved.
        if (!touchedStructFields.has("mood") && !hiddenBuiltins.has("mood")) {
          const moodMatches = finalTagsLc.filter(t => MOOD_TAGS.has(t));
          if (moodMatches.length) {
            if (!entryDoc.structured) entryDoc.structured = {};
            const existing = Array.isArray(entryDoc.structured.mood) ? entryDoc.structured.mood : (entryDoc.structured.mood ? [entryDoc.structured.mood] : []);
            const existingLc = new Set(existing.map(m => m.toLowerCase()));
            for (const m of moodMatches) {
              if (!existingLc.has(m)) { existing.push(m); existingLc.add(m); }
            }
            entryDoc.structured.mood = existing;
          }
        }
        // Payment — only when diary has payment_type library enabled
        const enabledLibSet = new Set(enabledLibrary || []);
        if (enabledLibSet.has("payment_type") && !touchedStructFields.has("library.payment_type")) {
          const payMatch = finalTagsLc.find(t => PAYMENT_TAGS.has(t));
          if (payMatch) {
            if (!entryDoc.structured) entryDoc.structured = {};
            if (!entryDoc.structured.library) entryDoc.structured.library = {};
            // Don't overwrite if already set
            if (!entryDoc.structured.library.payment_type) {
              entryDoc.structured.library.payment_type = payMatch;
            }
          }
        }
      }
      await addDoc(collection(db, "entries"), entryDoc);
      setNewHtml(""); setNewBody(""); setNewPhotos([]);
      setNewDate(todayInput());
      setNewTitle(""); setNewTags(""); setNewFollowup(false); setNewFavorite(false); setNewPrivate(false); setShowAdd(false);
      setNewStructured({}); setEditingStructField(null); setStructFieldDraft(""); setTouchedStructFields(new Set());
      setTitleManuallyTouched(false);
    } catch (e) {
      console.error(e);
      setUploadState({ active: false, error: e.message || "Save failed. Please try again." });
    }
    setSaving(false);
    addingRef.current = false;
  };

  // Initialize edit editor content
  useEffect(() => {
    if (editEntry && editEditorRef.current) {
      editEditorRef.current.innerHTML = editEntry.bodyHtml || editEntry.body || "";
    }
  }, [editEntry]);

  // Initialize add editor when modal opens
  useEffect(() => {
    if (showAdd && addEditorRef.current) {
      addEditorRef.current.innerHTML = "";
    }
  }, [showAdd]);

  const toggleSelect = id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    // Activate select mode when first item selected
    setSelectMode(true);
  };

  const selectAll = () => setSelectedIds(new Set(filtered.map(e => e.id)));
  const clearSelect = () => { setSelectedIds(new Set()); setSelectMode(false); };

  // Auto-deactivate select mode when all items deselected
  useEffect(() => {
    if (selectMode && selectedIds.size === 0) setSelectMode(false);
  }, [selectedIds, selectMode]);

  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    setBulkDeleting(true);
    try {
      await Promise.all([...selectedIds].map(id => deleteDoc(doc(db, "entries", id))));
      clearSelect();
    } catch (e) {
      console.error("Bulk delete failed", e);
      // Surface the failure to the user instead of silently swallowing it.
      // Permission errors are the most common cause — Firestore rules usually
      // require the user to be the entry's author or a diary admin.
      alert(`Couldn't delete: ${e?.message || "unknown error"}. Check console for details.`);
    }
    setBulkDeleting(false);
    setConfirmBulkDelete(false);
  };

  // Bulk-add tags: union the picked tags with each entry's existing tags
  // rather than replacing. "Assign tag" sounds like an additive action — and
  // replacing existing tags would silently destroy auto-tags or follow-up
  // markers the user may have set per-entry.
  const handleBulkAssignTag = async tags => {
    if (!selectedIds.size || !tags.length) return;
    try {
      // Build per-entry updates so each one keeps its own existing tags merged
      // with the picked ones. Skip writes when nothing actually changed.
      const updates = [];
      for (const eid of selectedIds) {
        const existing = entries.find(e => e.id === eid);
        const before = Array.isArray(existing?.tags) ? existing.tags : [];
        const merged = [...new Set([...before, ...tags])];
        if (merged.length === before.length && merged.every(t => before.includes(t))) continue;
        updates.push({ eid, tags: merged });
      }
      await Promise.all(updates.map(u =>
        updateDoc(doc(db, "entries", u.eid), { tags: u.tags, autoTagged: false })
      ));
    } catch (e) { console.error(e); }
  };

  // Bulk share/export menu — anchored under the Share icon in the select toolbar.
  const [bulkShareMenuOpen, setBulkShareMenuOpen] = useState(false);
  const [bulkBuilding, setBulkBuilding] = useState(false);

  // Resolve selectedIds → actual entry objects, preserving the diary's sort
  // order (so the resulting CSV/PDF reads top-down the way the user sees it).
  const getSelectedEntries = () => {
    const set = new Set(selectedIds);
    return filtered.filter(e => set.has(e.id));
  };

  // Common filename base, e.g. "property-2026-04-26-14-entries"
  const buildBulkFilename = (count, ext) => {
    const safe = (diary?.name || "diary").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40) || "diary";
    const date = new Date().toISOString().slice(0, 10);
    return `${safe}-${date}-${count}-entries.${ext}`;
  };

  const bulkCopyText = async () => {
    const sel = stripPrivate(getSelectedEntries());
    if (!sel.length) return;
    const text = sel.map(e => {
      const titleLine = e.title ? e.title + "\n" : "";
      const body = (e.bodyHtml ? toText(e.bodyHtml) : (e.body || "")).trim();
      return titleLine + body;
    }).join("\n\n---\n\n");
    try {
      await navigator.clipboard.writeText(text);
      alert(`Copied ${sel.length} ${sel.length === 1 ? "entry" : "entries"} to clipboard.`);
    } catch {
      alert("Couldn't copy. Try again or download instead.");
    }
    setBulkShareMenuOpen(false);
  };

  const bulkDownloadCsv = () => {
    const sel = stripPrivate(getSelectedEntries());
    if (!sel.length) return;
    const { csv } = buildEntriesCsv(sel, diary);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = buildBulkFilename(sel.length, "csv");
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setBulkShareMenuOpen(false);
  };

  // Excel variant of bulkDownloadCsv — see exportXlsx for design notes.
  const bulkDownloadXlsx = async () => {
    const sel = stripPrivate(getSelectedEntries());
    if (!sel.length) return;
    try {
      const XLSX = await import("xlsx");
      const wb = buildEntriesXlsx(sel, XLSX, diary);
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildBulkFilename(sel.length, "xlsx");
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert("Excel export failed. Please try CSV instead.\n\n" + (e.message || ""));
    }
    setBulkShareMenuOpen(false);
  };

  // Variant of bulkDownloadCsv that uses the focused expense schema
  // (Cash, Date, Vendor, Amount, Unit, Description). Skips entries whose
  // title doesn't have "/" — those aren't expenses.
  const bulkDownloadExpensesCsv = () => {
    const sel = stripPrivate(getSelectedEntries());
    if (!sel.length) return;
    const { csv, rowCount } = buildExpensesCsv(sel);
    if (rowCount === 0) {
      alert("None of the selected entries match the expense format (vendor / amount / unit / description). Use Download CSV for the general format.");
      setBulkShareMenuOpen(false);
      return;
    }
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = (diary?.name || "diary").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40) || "diary";
    a.href = url;
    a.download = `${safe}-expenses-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setBulkShareMenuOpen(false);
  };

  // Excel variant of bulkDownloadExpensesCsv.
  const bulkDownloadExpensesXlsx = async () => {
    const sel = stripPrivate(getSelectedEntries());
    if (!sel.length) return;
    try {
      const XLSX = await import("xlsx");
      const { wb, rowCount } = buildExpensesXlsx(sel, XLSX);
      if (rowCount === 0) {
        alert("None of the selected entries match the expense format (vendor / amount / unit / description). Use Download Excel for the general format.");
        setBulkShareMenuOpen(false);
        return;
      }
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (diary?.name || "diary").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40) || "diary";
      a.href = url;
      a.download = `${safe}-expenses-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert("Excel export failed. Please try CSV instead.\n\n" + (e.message || ""));
    }
    setBulkShareMenuOpen(false);
  };

  const bulkDownloadPdf = async () => {
    const sel = stripPrivate(getSelectedEntries());
    if (!sel.length) return;
    setBulkBuilding(true);
    try {
      const blob = await buildEntryPdf(sel);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildBulkFilename(sel.length, "pdf");
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error("Bulk PDF failed", e);
      alert("Couldn't build PDF — see console for details.");
    } finally {
      setBulkBuilding(false);
      setBulkShareMenuOpen(false);
    }
  };

  // Word doc for bulk-select — same pattern as bulkDownloadPdf.
  const bulkDownloadDocx = async () => {
    const sel = stripPrivate(getSelectedEntries());
    if (!sel.length) return;
    setBulkBuilding(true);
    try {
      const blob = await buildEntryDocx(sel);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildBulkFilename(sel.length, "docx");
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error("Bulk Docx failed", e);
      alert("Couldn't build Word doc — see console for details.");
    } finally {
      setBulkBuilding(false);
      setBulkShareMenuOpen(false);
    }
  };

  // Email: mobile gets a native share sheet with the PDF attached (user picks
  // Gmail / Mail / etc.); desktop downloads the PDF and opens Gmail compose
  // with subject prefilled, expecting the user to drag the PDF in.
  const bulkEmailPdf = async () => {
    const sel = stripPrivate(getSelectedEntries());
    if (!sel.length) return;
    setBulkBuilding(true);
    try {
      const blob = await buildEntryPdf(sel);
      const filename = buildBulkFilename(sel.length, "pdf");
      const file = new File([blob], filename, { type: "application/pdf" });
      const subject = `${sel.length} ${sel.length === 1 ? "entry" : "entries"} from ${diary?.name || "LifeTrackr"}`;

      if (canShareFiles(file)) {
        try {
          await navigator.share({ files: [file], title: subject });
          return;
        } catch (e) {
          if (e.name === "AbortError") return;
          // Fall through to desktop path on other errors.
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      const body = `Attached: ${filename}\n\n(${sel.length} ${sel.length === 1 ? "entry" : "entries"} from your ${diary?.name || ""} diary.)`;
      window.open(
        `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
        "_blank", "noopener"
      );
      alert("PDF saved to Downloads — drag it into the Gmail draft.");
    } catch (e) {
      console.error("Bulk email failed", e);
      alert("Couldn't build PDF — see console for details.");
    } finally {
      setBulkBuilding(false);
      setBulkShareMenuOpen(false);
    }
  };

  const openEdit = e => {
    const hasFollowup = (e.tags || []).some(isFollowupTag);
    setEditEntry(e);
    setEditHtml(e.bodyHtml || e.body || "");
    setEditBody(e.body || "");
    setEditDate(toInput(e.entryDate));
    setEditExisting(e.photos || []);
    setEditExistingVideos(e.videos || []);
    setEditExistingAudio(e.audio || []);
    setEditExistingDocs(e.docs || []);
    setEditPhotos([]);
    setEditTags((e.tags || []).filter(t => !isFollowupTag(t)).join(", "));
    setEditTitle(e.title || "");
    setEditFollowup(hasFollowup);
    setEditFavorite(!!(e.favorite));
    setEditPrivate(!!(e.private));
    // Initialize structured fields from saved entry. We mark each field as
    // "touched" (locked from auto-update) ONLY if it was NOT derivable from
    // the body. If the body shorthand parses to the same value as the saved
    // chip, the chip is body-derived — leave it un-touched so editing the
    // body keeps the chip in sync.
    const saved = e.structured || {};
    setEditStructured({ ...saved });
    // Parse the BODY ONLY for touched-detection. We deliberately exclude
    // the title here — auto-generated titles can contain rendered chip
    // values (e.g. "vs. s, sm, smy, smyan 6-2"), and parsing them back
    // would regenerate the same bad chip values, defeating the recovery
    // heuristic that's supposed to clean them up. The body is the user's
    // actual prose; the chips should be validated against it alone.
    const bodyParsed = parseStructuredFields(e.body || "", { knownNames });
    const touchedFields = new Set();
    const eq = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
    // Subset check for array fields: returns true if every value in `saved`
    // still appears in `parsed` (case-insensitive). When saved ⊂ parsed, the
    // chip is treated as body-derived (not user-touched) so the live auto-
    // fill can catch up on items the parser found that weren't saved
    // previously — e.g. a legacy entry where mood:circumspect was captured
    // but a later-added "Mood:proud" in the body was not. Body is the
    // source of truth: to remove a parsed value from the chip, edit the
    // shorthand out of the body.
    const isArraySubset = (savedArr, parsedArr) => {
      if (!Array.isArray(savedArr) || !Array.isArray(parsedArr)) return false;
      if (!savedArr.length) return false; // empty saved isn't a meaningful subset
      const parsedSet = new Set(parsedArr.map(v => String(v).toLowerCase()));
      return savedArr.every(v => parsedSet.has(String(v).toLowerCase()));
    };
    // Inverse subset / artifact-cleanup check: when the saved value has
    // items the parser DIDN'T find, but those extras are all typing
    // artifacts (prefixes of items the parser DID find), treat the chip
    // as body-derived and let the parser overwrite. Recovery path for
    // legacy entries that were saved with bad data when the parser was
    // running per-keystroke and accumulating partial matches.
    //
    // Conservative: only fires when EVERY saved-extra is an artifact. If
    // any saved-extra is unrelated to parsed values, it's probably an
    // intentional manual chip edit (e.g. user added a name that isn't
    // mentioned in the body) and we leave it alone.
    const isArtifactCleanup = (savedArr, parsedArr) => {
      if (!Array.isArray(savedArr) || !Array.isArray(parsedArr)) return false;
      if (!savedArr.length || !parsedArr.length) return false;
      const parsedLc = parsedArr.map(v => String(v).toLowerCase());
      const parsedSet = new Set(parsedLc);
      const extras = savedArr.filter(v => !parsedSet.has(String(v).toLowerCase()));
      if (!extras.length) return false;
      // An extra is a typing artifact if some parsed value has the same
      // length or longer AND shares 2+ leading characters (or the whole
      // extra if shorter). Catches both progressive prefixes (s, sm, smy
      // of "smyan") and same-length typos (smyab of "smyan"). Permissive
      // enough to recover legacy bad data; still rejects clearly different
      // names like "Alice" vs "Bob".
      return extras.every(extra => {
        const ex = String(extra).toLowerCase();
        if (!ex) return false;
        return parsedLc.some(p => {
          if (!p || ex.length > p.length) return false;
          let i = 0;
          while (i < ex.length && i < p.length && ex[i] === p[i]) i++;
          return i >= Math.min(2, ex.length);
        });
      });
    };
    // Built-ins
    const checks = [
      ["amount", saved.amount, bodyParsed.amount],
      ["location", saved.location, bodyParsed.location && bodyParsed.location.length ? bodyParsed.location : null],
      ["people", saved.people, bodyParsed.people && bodyParsed.people.length ? bodyParsed.people : null],
      ["timeMinutes", saved.timeMinutes, bodyParsed.timeMinutes],
      ["distanceMiles", saved.distanceMiles, bodyParsed.distanceMiles],
      ["calories", saved.calories, bodyParsed.calories],
      ["mood", saved.mood, bodyParsed.mood && bodyParsed.mood.length ? bodyParsed.mood : null],
      ["rating", saved.rating, bodyParsed.rating || null],
      ["score", saved.score, bodyParsed.score || null],
    ];
    for (const [k, savedVal, bodyVal] of checks) {
      if (savedVal != null && (Array.isArray(savedVal) ? savedVal.length : true) && !eq(savedVal, bodyVal)) {
        // Subset escape: saved ⊂ parsed → parser is catching up, body wins
        if (Array.isArray(savedVal) && Array.isArray(bodyVal) && isArraySubset(savedVal, bodyVal)) continue;
        // Artifact cleanup: saved has typing-artifact extras → body wins
        if (Array.isArray(savedVal) && Array.isArray(bodyVal) && isArtifactCleanup(savedVal, bodyVal)) continue;
        touchedFields.add(k);
      }
    }
    // Library — touched only if saved value differs from body-parsed value
    // (with subset escape for array library values)
    if (saved.library) {
      for (const [k, v] of Object.entries(saved.library)) {
        const bodyV = (bodyParsed.libraryFields || {})[k];
        if (eq(v, bodyV)) continue;
        if (Array.isArray(v) && Array.isArray(bodyV) && isArraySubset(v, bodyV)) continue;
        if (Array.isArray(v) && Array.isArray(bodyV) && isArtifactCleanup(v, bodyV)) continue;
        touchedFields.add(`library.${k}`);
      }
    }
    // Custom — same
    if (saved.custom) {
      for (const [k, v] of Object.entries(saved.custom)) {
        const bodyV = (bodyParsed.customFields || {})[k.toLowerCase()];
        if (eq(v, bodyV)) continue;
        if (Array.isArray(v) && Array.isArray(bodyV) && isArraySubset(v, bodyV)) continue;
        if (Array.isArray(v) && Array.isArray(bodyV) && isArtifactCleanup(v, bodyV)) continue;
        touchedFields.add(`custom.${k}`);
      }
    }
    setEditTouchedStructFields(touchedFields);
    setEditingEditStructField(null);
    setEditStructFieldDraft("");
    // If the saved title was auto-generated, treat as un-touched so live
    // regeneration can keep enhancing it as the user adds more details.
    // For legacy entries (no flag set), treat empty titles as auto-eligible.
    let manuallyTouched;
    if (e.titleAutoGenerated === true) {
      manuallyTouched = false;
    } else if (e.titleAutoGenerated === false) {
      manuallyTouched = true;
    } else {
      // Legacy entry — no flag. If the title is empty, auto-fill is OK.
      // If they had a title, preserve it (treat as user-authored).
      manuallyTouched = !!(e.title && e.title.trim());
    }
    setEditTitleManuallyTouched(manuallyTouched);
    setConfirmDel(false);
  };

  const updatingRef = useRef(false);
  const handleUpdate = async () => {
    if (!editEntry) return;
    if (updatingRef.current) return;
    // Flush any pending debounced editor input so we save the latest content,
    // not stale state from before the user's last keystrokes/deletions.
    if (editInputTimerRef.current) {
      clearTimeout(editInputTimerRef.current);
      editInputTimerRef.current = null;
    }
    const liveEl = editEditorRef.current;
    const liveHtml = liveEl ? liveEl.innerHTML : editHtml;
    const liveText = liveEl ? (liveEl.innerText || liveEl.textContent || "") : editBody;
    updatingRef.current = true;
    setUpdating(true);
    try {
      if (editPhotos.length > 0) {
        setUploadState({ active: true, total: editPhotos.length, completed: 0, currentName: editPhotos[0]?.name || "", currentKind: classifyFile(editPhotos[0]), currentProgress: 0 });
      }
      const { photos: newPhotosUp, videos: newVideosUp, audio: newAudioUp, docs: newDocsUp } = await uploadAndSort(editPhotos, p => {
        setUploadState({ active: true, total: p.total, completed: p.index, currentName: p.name, currentKind: p.kind, currentProgress: p.pct });
      });
      setUploadState({ active: false });
      const finalEditTagsInput = editTagPickerRef.current?.flush?.() ?? editTags;
      // Extract any "(tag1, tag2) " or "!!" shorthand from the title — same
      // contract as the create path and the inbound-email handler. Date
      // offset inside the parens (e.g. "(-1d)") is captured and applied
      // to editDate below.
      const { tags: editTitlePrefixTags, cleanedTitle: cleanedEditTitle, dateOffset: editDateOffsetStr, isPrivate: editTitlePrefixPrivate } =
        extractLeadingTagPrefix(editTitle.trim());
      const effectiveEditDateYmd = editDateOffsetStr
        ? applyOffsetToYmd(editDate, parseOffsetString(editDateOffsetStr))
        : editDate;
      if (editDateOffsetStr && effectiveEditDateYmd !== editDate) {
        setEditDate(effectiveEditDateYmd);
      }
      const effectiveEditFollowup = editFollowup || editTitlePrefixTags.includes("follow-up");
      const rawTags = buildTags(finalEditTagsInput, effectiveEditFollowup);
      for (const t of editTitlePrefixTags) {
        if (isFollowupTag(t)) continue;
        if (!rawTags.includes(t)) rawTags.push(t);
      }
      const tags = sanitizeMyTags(rawTags);
      const contentTags = tags.filter(t => !isFollowupTag(t));
      if (requiresTag && contentTags.length === 0) {
        setUploadState({ active: false, error: "Please pick at least one tag for this entry." });
        updatingRef.current = false;
        setUpdating(false);
        return;
      }
      const mergedPhotos = [...editExisting, ...newPhotosUp];
      const mergedVideos = [...editExistingVideos, ...newVideosUp];
      const mergedAudio = [...editExistingAudio, ...newAudioUp];
      const mergedDocs = [...editExistingDocs, ...newDocsUp];
      // Only touch entryDate if the user actually changed the date in the picker
      // (or applied a date offset via "(-1d)" shorthand in the title).
      // Plain edits (text, tags, photos, etc.) leave the original timestamp intact.
      const originalDateStr = toInput(editEntry.entryDate);
      const dateChanged = effectiveEditDateYmd !== originalDateStr;
      const entryUpdate = {
        title: cleanedEditTitle,
        body: toText(liveHtml), bodyHtml: sanitize(liveHtml),
        photos: mergedPhotos,
        videos: mergedVideos,
        audio: mergedAudio,
        docs: mergedDocs,
        tags, favorite: editFavorite,
        // Private flag — always explicit true/false, never absent (the
        // non-private entry query, where("private","==",false), would miss a
        // doc lacking the field). A "(private)"/"(priv)" title prefix forces it
        // on even if the toggle state hasn't caught up (e.g. saved without the
        // title field blurring).
        private: !!(editPrivate || editTitlePrefixPrivate),
        updatedAt: serverTimestamp(), source: "web",
        // Track whether title is auto-generated. Flips to false the moment
        // user types in the title field on edit; allows re-enhancement on
        // future edits otherwise.
        titleAutoGenerated: !editTitleManuallyTouched,
      };
      // If user cleared the title (or it was already empty), auto-generate
      // from body + structured fields. Same logic as create-save.
      // For OCR-imported entries with empty body, expenseData.description
      // (set by the Cloud Function) provides the item descriptor — without
      // this fallback, the regen would drop the item from the title.
      if (!entryUpdate.title) {
        const effectiveBody = entryUpdate.body || editEntry?.expenseData?.description || "";
        const generated = generateTitleFromStructured(effectiveBody, editStructured);
        if (generated) {
          entryUpdate.title = generated;
          entryUpdate.titleAutoGenerated = true;
        }
      }
      if (dateChanged) {
        entryUpdate.entryDate = buildEntryDate(effectiveEditDateYmd, editEntry.entryDate);
      }
      // Refresh expenseData on each edit. Three cases:
      //   - has expense tag + parseable title → write structured expenseData
      //   - has expense tag + unparseable title → leave existing expenseData
      //     in place (don't destroy data the user might have manually entered
      //     elsewhere or had migrated)
      //   - no expense tag → leave field alone (the field hides, but the data
      //     is preserved in case the user re-adds the tag later)
      if (tags.includes("expense")) {
        const ed = parseExpenseTitle(entryUpdate.title);
        if (ed) entryUpdate.expenseData = ed;
      }
      // Parse shorthand from body, plus title when user-authored.
      // Auto-generated titles are excluded to prevent feedback loops.
      const bodyText = (entryUpdate.body || "").trim();
      const titleText = editTitleManuallyTouched ? (entryUpdate.title || "").trim() : "";
      const editText = [titleText, bodyText].filter(Boolean).join(". ") || (entryUpdate.title || "").trim();
      const editStruct = parseStructuredFields(editText, { knownNames });
      if (editStruct.tags.length) {
        const existingTagSet = new Set(tags.map(t => t.toLowerCase()));
        for (const t of editStruct.tags) {
          if (!existingTagSet.has(t.toLowerCase())) {
            tags.push(t);
            existingTagSet.add(t.toLowerCase());
          }
        }
        entryUpdate.tags = tags;
      }
      // Auto-tag from natural language — same logic as create
      if (editText) {
        const lc = editText.toLowerCase();
        const existingTagSet = new Set(tags.map(t => t.toLowerCase()));
        const candidateTags = new Set((myDiaryTags || []).map(t => t.toLowerCase()));
        for (const tag of candidateTags) {
          if (!tag || tag.length <= 3) continue;
          if (isFollowupTag(tag)) continue;
          if (existingTagSet.has(tag)) continue;
          const escaped = tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const re = new RegExp(`\\b${escaped}\\b`, "i");
          if (re.test(lc)) {
            tags.push(tag);
            existingTagSet.add(tag);
          }
        }
        // Pass 2: keyword-based synonym match (forehand → tennis, etc.)
        // Restricted to tags this diary actually uses or declares.
        const inferred = inferTagsFromText(editText, allTags, (editStruct.amount != null) || (editStructured?.amount != null));
        for (const tag of inferred) {
          const tagLc = tag.toLowerCase();
          if (existingTagSet.has(tagLc)) continue;
          if (isFollowupTag(tag)) continue;
          tags.push(tag);
          existingTagSet.add(tagLc);
        }
        entryUpdate.tags = tags;
      }
      // Parser values are source of truth for the CURRENT body; chips override
      // ONLY when user explicitly touched them. See create-merge for rationale.
      const editIsTouched = (key) => editTouchedStructFields.has(key);
      const editPickField = (key, parserVal) => {
        if (editIsTouched(key) && editStructured[key] != null) return editStructured[key];
        return parserVal != null && (Array.isArray(parserVal) ? parserVal.length : true)
          ? parserVal
          : editStructured[key];
      };
      const editMerged = {
        amount: editPickField("amount", editStruct.amount),
        location: editPickField("location", editStruct.location && editStruct.location.length ? editStruct.location : null),
        people: editPickField("people", editStruct.people && editStruct.people.length ? editStruct.people : null),
        timeMinutes: editPickField("timeMinutes", editStruct.timeMinutes),
        distanceMiles: editPickField("distanceMiles", editStruct.distanceMiles),
        calories: editPickField("calories", editStruct.calories),
        mood: editPickField("mood", editStruct.mood && editStruct.mood.length ? editStruct.mood : null),
        rating: editPickField("rating", editStruct.rating || ""),
        score: editPickField("score", editStruct.score || ""),
      };
      const editHasAny = (
        editMerged.amount != null ||
        (editMerged.location && (Array.isArray(editMerged.location) ? editMerged.location.length : editMerged.location)) ||
        (editMerged.people && editMerged.people.length) ||
        editMerged.timeMinutes != null || editMerged.distanceMiles != null ||
        editMerged.calories != null ||
        (editMerged.mood && (Array.isArray(editMerged.mood) ? editMerged.mood.length : editMerged.mood)) ||
        editMerged.rating || editMerged.score
      );
      if (editHasAny) {
        const s = {};
        if (editMerged.amount != null) s.amount = editMerged.amount;
        if (editMerged.location && (Array.isArray(editMerged.location) ? editMerged.location.length : editMerged.location)) {
          s.location = Array.isArray(editMerged.location) ? editMerged.location : [editMerged.location];
        }
        if (editMerged.people && editMerged.people.length) {
          s.people = editMerged.people;
          if (editStructured.peopleAsOpponents) s.peopleAsOpponents = true;
        }
        if (editMerged.timeMinutes != null) s.timeMinutes = editMerged.timeMinutes;
        if (editMerged.distanceMiles != null) s.distanceMiles = editMerged.distanceMiles;
        if (editMerged.calories != null) s.calories = editMerged.calories;
        if (editMerged.mood && (Array.isArray(editMerged.mood) ? editMerged.mood.length : editMerged.mood)) {
          s.mood = Array.isArray(editMerged.mood) ? editMerged.mood : [editMerged.mood];
        }
        if (editMerged.rating) s.rating = editMerged.rating;
        if (editMerged.score) s.score = editMerged.score;
        entryUpdate.structured = s;
      } else {
        // If no structured tokens AND no chip values, clear the field
        entryUpdate.structured = null;
      }
      // Custom fields — same merge logic as create
      const editCustomMerged = {};
      const editDeclaredSet = new Set([...(declaredCustom || []), ...(establishedCustomFields || [])].map(s => s.toLowerCase()));
      if (editStruct.customFields) {
        for (const [k, v] of Object.entries(editStruct.customFields)) {
          if (editDeclaredSet.has(k.toLowerCase())) {
            editCustomMerged[k.toLowerCase()] = Array.isArray(v) ? v : [v];
          }
        }
      }
      if (editStructured.custom) {
        for (const [k, v] of Object.entries(editStructured.custom)) {
          if (v != null && (Array.isArray(v) ? v.length : true)) {
            const customKey = `custom.${k.toLowerCase()}`;
            const isTouched = editTouchedStructFields.has(customKey);
            if (isTouched || editCustomMerged[k.toLowerCase()] == null) {
              editCustomMerged[k.toLowerCase()] = Array.isArray(v) ? v : [v];
            }
          }
        }
      }
      if (Object.keys(editCustomMerged).length) {
        if (!entryUpdate.structured) entryUpdate.structured = {};
        entryUpdate.structured.custom = editCustomMerged;
      }
      // Library fields — same logic as create
      const editLibraryMerged = {};
      const editEnabledLibrarySet = new Set(enabledLibrary || []);
      if (editStruct.libraryFields) {
        for (const [k, v] of Object.entries(editStruct.libraryFields)) {
          if (editEnabledLibrarySet.has(k)) editLibraryMerged[k] = v;
        }
      }
      if (editStructured.library) {
        for (const [k, v] of Object.entries(editStructured.library)) {
          if (v != null && (Array.isArray(v) ? v.length : true)) {
            const libKey = `library.${k}`;
            const isTouched = editTouchedStructFields.has(libKey);
            if (isTouched || editLibraryMerged[k] == null) {
              editLibraryMerged[k] = v;
            }
          }
        }
      }
      if (Object.keys(editLibraryMerged).length) {
        if (!entryUpdate.structured) entryUpdate.structured = {};
        entryUpdate.structured.library = editLibraryMerged;
      }
      // Dual-purpose tags — same logic as create-save
      {
        const finalTags = entryUpdate.tags || [];
        const finalTagsLc = finalTags.map(t => t.toLowerCase());
        if (!editTouchedStructFields.has("mood") && !hiddenBuiltins.has("mood")) {
          const moodMatches = finalTagsLc.filter(t => MOOD_TAGS.has(t));
          if (moodMatches.length) {
            if (!entryUpdate.structured) entryUpdate.structured = {};
            const existing = Array.isArray(entryUpdate.structured.mood) ? entryUpdate.structured.mood : (entryUpdate.structured.mood ? [entryUpdate.structured.mood] : []);
            const existingLc = new Set(existing.map(m => m.toLowerCase()));
            for (const m of moodMatches) {
              if (!existingLc.has(m)) { existing.push(m); existingLc.add(m); }
            }
            entryUpdate.structured.mood = existing;
          }
        }
        const enabledLibSet = new Set(enabledLibrary || []);
        if (enabledLibSet.has("payment_type") && !editTouchedStructFields.has("library.payment_type")) {
          const payMatch = finalTagsLc.find(t => PAYMENT_TAGS.has(t));
          if (payMatch) {
            if (!entryUpdate.structured) entryUpdate.structured = {};
            if (!entryUpdate.structured.library) entryUpdate.structured.library = {};
            if (!entryUpdate.structured.library.payment_type) {
              entryUpdate.structured.library.payment_type = payMatch;
            }
          }
        }
      }

      // === Geo metadata preservation ===
      //
      // The Cloud Function backfill (functions/index.js:backfillEntryGeo)
      // auto-populates city/address/location from photo EXIF GPS. If we
      // just drop those fields on every save here, backfill silently
      // re-adds them on the next onWrite — looking to the user like
      // "I deleted it but it came back" (real bug).
      //
      // For each geo-derived field:
      //   - If user explicitly cleared it (touched + now empty) → add to
      //     `geoSuppressed` so backfill skips it.
      //   - If user didn't touch it → carry forward from the existing
      //     entry so the round-trip is lossless.
      //   - If user provided a new value → use it, clear suppression.
      //
      // The `geo` sidecar (raw lat/lng + resolved Place metadata) is
      // always carried forward — it's not user-visible as a chip, just
      // a record of what auto-resolution found.
      {
        const prevStructured = editEntry.structured || {};
        const newSuppressed = new Set(prevStructured.geoSuppressed || []);
        if (entryUpdate.structured == null) entryUpdate.structured = {};

        // Always preserve the raw geo sidecar.
        if (prevStructured.geo && !entryUpdate.structured.geo) {
          entryUpdate.structured.geo = prevStructured.geo;
        }

        // city + address: simple chip fields not in editMerged.
        for (const key of ["city", "address"]) {
          const userValue = editStructured[key];
          const userTouched = editIsTouched(key);
          const userCleared = userTouched && (userValue == null || userValue === "");
          if (userCleared) {
            newSuppressed.add(key);
            // already absent from entryUpdate.structured; leave it that way
          } else if (userValue) {
            entryUpdate.structured[key] = userValue;
            newSuppressed.delete(key);
          } else if (prevStructured[key]) {
            // User didn't touch — carry forward the existing value.
            entryUpdate.structured[key] = prevStructured[key];
          }
        }

        // location: handled by editMerged above, but we still need to
        // record suppression intent. If user touched it and the final
        // structured has no location, mark suppressed so backfill can't
        // restore it from geo.place.
        if (editIsTouched("location")) {
          const hasFinalLocation = entryUpdate.structured.location
            && entryUpdate.structured.location.length;
          if (!hasFinalLocation) {
            newSuppressed.add("location");
          } else {
            newSuppressed.delete("location");
          }
        }

        if (newSuppressed.size > 0) {
          entryUpdate.structured.geoSuppressed = Array.from(newSuppressed);
        } else if (entryUpdate.structured.geoSuppressed) {
          delete entryUpdate.structured.geoSuppressed;
        }

        // If after all this, structured is genuinely empty, clear it
        // back to null to match the prior contract.
        if (Object.keys(entryUpdate.structured).length === 0) {
          entryUpdate.structured = null;
        }
      }

      await updateDoc(doc(db, "entries", editEntry.id), entryUpdate);
      // Optimistically update local state so the timeline reflects the save
      // immediately. The Firestore listener will reconcile shortly with the
      // server-confirmed copy. Without this, there's a brief render window where
      // the timeline still shows the pre-edit version (e.g., stale attachments).
      setEntries(prev => prev.map(e => e.id === editEntry.id ? { ...e, ...entryUpdate } : e));
      setEditEntry(null);
    } catch (e) {
      console.error(e);
      setUploadState({ active: false, error: e.message || "Save failed. Please try again." });
    }
    setUpdating(false);
    updatingRef.current = false;
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await deleteDoc(doc(db, "entries", editEntry.id)); setEditEntry(null); setConfirmDel(false); } catch (e) { console.error(e); }
    setDeleting(false);
  };

  const saveViewMode = async mode => {
    setSavingViewMode(true);
    setViewModeSetting(mode);
    await updateDoc(doc(db, "diaries", id), { defaultView: mode });
    setSavingViewMode(false);
  };

  const saveCoverName = async () => {
    const name = coverNameDraft.trim();
    if (!name || name === diary.name) return;
    setSavingCoverName(true);
    try {
      await updateDoc(doc(db, "diaries", id), { name });
      setDiary(prev => ({ ...prev, name }));
    } catch (e) { console.error(e); }
    setSavingCoverName(false);
  };

  const saveCoverTheme = async themeId => {
    setSavingCoverTheme(true);
    try {
      await updateDoc(doc(db, "diaries", id), { themeId });
      setDiary(prev => ({ ...prev, themeId }));
    } catch (e) { console.error(e); }
    setSavingCoverTheme(false);
  };

  // Save one or more custom cover colors. Keys: customSpine, customCover,
  // customNameColor. Each is a hex string like "#8B3A0A" or null to clear
  // (falls back to theme default).
  const saveCoverColors = async (patch) => {
    setSavingCoverTheme(true);
    try {
      await updateDoc(doc(db, "diaries", id), patch);
      setDiary(prev => ({ ...prev, ...patch }));
    } catch (e) { console.error(e); }
    setSavingCoverTheme(false);
  };

  const uploadCoverImage = async file => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const r = ref(storage, `diaries/${id}/cover_${Date.now()}_${file.name}`);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      await updateDoc(doc(db, "diaries", id), { coverImageUrl: url });
      setDiary(prev => ({ ...prev, coverImageUrl: url }));
    } catch (e) { console.error(e); }
    setUploadingCover(false);
  };

  const removeCoverImage = async () => {
    setUploadingCover(true);
    try {
      await updateDoc(doc(db, "diaries", id), { coverImageUrl: deleteField() });
      setDiary(prev => { const next = { ...prev }; delete next.coverImageUrl; return next; });
    } catch (e) { console.error(e); }
    setUploadingCover(false);
  };

  const handleDeleteDiary = async () => {
    if (!diary || deleteDiaryConfirm.trim() !== diary.name) return;
    setDeletingDiary(true);
    try {
      // Delete all entries belonging to this diary
      const snap = await getDocs(query(collection(db, "entries"), where("diaryId", "==", id)));
      await Promise.all(snap.docs.map(d => deleteDoc(doc(db, "entries", d.id))));
      // Delete the diary itself
      await deleteDoc(doc(db, "diaries", id));
      navigate("/dashboard");
    } catch (e) {
      console.error(e);
      setDeletingDiary(false);
    }
  };

  // Fetch member profile docs whenever the member list changes
  useEffect(() => {
    if (!diary?.members) return;
    const uids = Object.keys(diary.members);
    const missing = uids.filter(u => !memberProfiles[u]);
    if (missing.length === 0) return;
    missing.forEach(async uid => {
      try {
        const s = await getDoc(doc(db, "users", uid));
        if (s.exists()) {
          const d = s.data();
          setMemberProfiles(p => ({ ...p, [uid]: { name: d.name || d.email?.split("@")[0] || uid, email: d.email || "", photoURL: d.photoURL || "" } }));
        } else {
          setMemberProfiles(p => ({ ...p, [uid]: { name: uid, email: "", photoURL: "" } }));
        }
      } catch {
        setMemberProfiles(p => ({ ...p, [uid]: { name: uid, email: "", photoURL: "" } }));
      }
    });
  }, [diary?.members, memberProfiles]);

  // Fetch last-visited timestamps for each member (only the owner sees this UI;
  // useful to gauge whether shared members have engaged with the diary).
  useEffect(() => {
    if (!diary?.members || !id) return;
    const uids = Object.keys(diary.members);
    uids.forEach(async uid => {
      try {
        const s = await getDoc(doc(db, "users", uid, "diaryViews", id));
        if (s.exists()) {
          const lv = s.data()?.lastVisited;
          const ms = lv?.toMillis ? lv.toMillis() : (lv ? new Date(lv).getTime() : 0);
          setMemberLastVisited(p => ({ ...p, [uid]: ms }));
        } else {
          setMemberLastVisited(p => ({ ...p, [uid]: 0 }));
        }
      } catch {
        setMemberLastVisited(p => ({ ...p, [uid]: 0 }));
      }
    });
  }, [diary?.members, id]);

  const handleInviteMember = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setInviting(true);
    setInviteMsg(null);
    // Resolve member value: legacy string if no filter, object otherwise.
    const noFilter = inviteTags === null && inviteIncludeUntagged !== false;
    const memberValue = noFilter
      ? inviteRole
      : { role: inviteRole, tags: inviteTags, includeUntagged: inviteIncludeUntagged };
    try {
      // Find the user by email
      const snap = await getDocs(query(collection(db, "users"), where("email", "==", email), limit(1)));

      // Owner-transfer path. If they already have a LifeTrackr account and
      // are already a member of the diary, jump straight to the transfer
      // confirmation modal. If they're an existing user but not yet a
      // member, add them as a member first then initiate the transfer.
      // If they have no account, fall through to the pendingInvites path
      // below — we tag the invite role=owner so the inbound-account
      // creation flow knows to create a transfer request, not a regular
      // membership.
      if (inviteRole === "owner" && !snap.empty) {
        const userDoc = snap.docs[0];
        const uid = userDoc.id;
        if (!diary.members?.[uid]) {
          // Add as Editor first, then trigger transfer modal
          await updateDoc(doc(db, "diaries", id), {
            [`members.${uid}`]: "contributor",
            memberUids: arrayUnion(uid), // flat index for membership queries/rules
            authorizedEmails: arrayUnion(email),
          });
          setDiary(prev => ({
            ...prev,
            members: { ...(prev.members || {}), [uid]: "contributor" },
            memberUids: [...new Set([...(prev.memberUids || []), uid])],
            authorizedEmails: [...new Set([...(prev.authorizedEmails || []), email])],
          }));
          setMemberProfiles(prev => ({ ...prev, [uid]: { name: userDoc.data().name || email, email, photoURL: userDoc.data().photoURL || "" } }));
        }
        setInviteEmail("");
        setInviteRole("contributor");
        setInviteAccessOpen(false);
        setInviting(false);
        setTransferTarget(uid);
        return;
      }

      if (!snap.empty) {
        // Existing user — add them directly
        const userDoc = snap.docs[0];
        const uid = userDoc.id;
        if (diary.members?.[uid]) {
          setInviteMsg({ type: "error", text: "That person already has access to this diary." });
          setInviting(false);
          return;
        }
        await updateDoc(doc(db, "diaries", id), {
          [`members.${uid}`]: memberValue,
          memberUids: arrayUnion(uid), // flat index for membership queries/rules
          authorizedEmails: arrayUnion(email),
        });
        setDiary(prev => ({
          ...prev,
          members: { ...(prev.members || {}), [uid]: memberValue },
          memberUids: [...new Set([...(prev.memberUids || []), uid])],
          authorizedEmails: [...new Set([...(prev.authorizedEmails || []), email])],
        }));
        setInviteEmail("");
        setInviteTags(null);
        setInviteIncludeUntagged(false);
        setInviteAccessOpen(false);
        setInviteMsg({ type: "ok", text: `Invited ${userDoc.data().name || email}.` });
      } else {
        // No LifeTrackr account yet — check for an existing pending invite, then create one
        const pendingKey = `${email}__${id}`;
        const existing = await getDoc(doc(db, "pendingInvites", pendingKey));
        if (existing.exists()) {
          setInviteMsg({ type: "error", text: "An invite to this email is already pending." });
          setInviting(false);
          return;
        }
        // Use a deterministic id so repeat clicks can't create dupes.
        // For role="owner" (transfer path), the recipient is added as a
        // contributor first and then a pendingOwnerTransfer is created — so
        // they get a Dashboard banner and choose to accept ownership. The
        // server-side handler that processes pendingInvites on signup is
        // responsible for honoring `role: "owner"` and routing accordingly.
        await setDoc(doc(db, "pendingInvites", pendingKey), {
          email,
          diaryId: id,
          diaryName: diary.name,
          role: inviteRole,
          // Persist access settings on the pending invite so they're applied
          // when the recipient creates their account
          tags: inviteTags,
          includeUntagged: inviteIncludeUntagged,
          invitedBy: user.uid,
          invitedByName: user.displayName || user.email || "",
          invitedByEmail: user.email || "",
          createdAt: serverTimestamp(),
          status: "pending",
        });
        setInviteEmail("");
        setInviteTags(null);
        setInviteIncludeUntagged(false);
        setInviteAccessOpen(false);
        const transferNote = inviteRole === "owner" ? " They'll be asked to accept ownership after they sign up." : "";
        setInviteMsg({ type: "ok", text: `Invitation sent to ${email}. They'll join automatically when they create a LifeTrackr account.${transferNote}` });
      }
    } catch (e) {
      console.error("Invite failed:", e);
      const detail = e?.code ? ` (${e.code})` : (e?.message ? ` (${String(e.message).slice(0,80)})` : "");
      setInviteMsg({ type: "error", text: `Something went wrong${detail}. Please try again.` });
    }
    setInviting(false);
  };

  const handleRemoveMember = async (uid) => {
    const profile = memberProfiles[uid];
    if (!profile) return;
    if (!window.confirm(`Remove ${profile.name} from this diary?`)) return;
    try {
      const updates = { [`members.${uid}`]: deleteField(), memberUids: arrayRemove(uid) };
      if (profile.email) updates.authorizedEmails = arrayRemove(profile.email);
      await updateDoc(doc(db, "diaries", id), updates);
      setDiary(prev => {
        const nextMembers = { ...(prev.members || {}) };
        delete nextMembers[uid];
        return {
          ...prev,
          members: nextMembers,
          memberUids: (prev.memberUids || []).filter(u => u !== uid),
          authorizedEmails: (prev.authorizedEmails || []).filter(e => e !== profile.email),
        };
      });
    } catch (e) { console.error(e); }
  };

  const handleChangeRole = async (uid, newRole) => {
    try {
      // Preserve any existing access settings (tags, includeUntagged) when changing role.
      // We store as an object only if there are non-default access settings; otherwise
      // we use the legacy string form for cleaner data.
      const current = diary?.members?.[uid];
      const access = getMemberAccess(current);
      const hasFilter = access.tags !== null || access.includeUntagged === false;
      const value = hasFilter
        ? { role: newRole, tags: access.tags, includeUntagged: access.includeUntagged }
        : newRole;
      await updateDoc(doc(db, "diaries", id), { [`members.${uid}`]: value });
      setDiary(prev => ({ ...prev, members: { ...(prev.members || {}), [uid]: value } }));
    } catch (e) { console.error(e); }
  };

  // Save tag access for a member. tags=null means "all tags". tags=[] means "no tags".
  const handleChangeMemberAccess = async (uid, { tags, includeUntagged }) => {
    try {
      const current = diary?.members?.[uid];
      const currentRole = getMemberRole(current);
      if (!currentRole || currentRole === "owner") return;
      // If both filters are at their defaults (no tag filter, untagged ON), collapse
      // to legacy string form. Otherwise store as object.
      const noFilter = tags === null && includeUntagged !== false;
      const value = noFilter
        ? currentRole
        : { role: currentRole, tags, includeUntagged };
      await updateDoc(doc(db, "diaries", id), { [`members.${uid}`]: value });
      setDiary(prev => ({ ...prev, members: { ...(prev.members || {}), [uid]: value } }));
    } catch (e) { console.error(e); }
  };

  const handleLeaveDiary = async () => {
    if (!user || !diary) return;
    if (!window.confirm(`Leave "${diary.name}"? You'll lose access, but the diary and its entries stay for everyone else.`)) return;
    setLeaving(true);
    try {
      const updates = { [`members.${user.uid}`]: deleteField(), memberUids: arrayRemove(user.uid) };
      if (user.email) updates.authorizedEmails = arrayRemove(user.email);
      await updateDoc(doc(db, "diaries", id), updates);
      navigate("/dashboard");
    } catch (e) {
      console.error(e);
      setLeaving(false);
    }
  };

  // Subscribe to pending invites for this diary (owner only)
  useEffect(() => {
    if (!diary || !user) return;
    if (diary.members?.[user.uid] !== "owner") return;
    if (settingsTab !== "members") return;
    const q = query(collection(db, "pendingInvites"), where("diaryId", "==", id), where("status", "==", "pending"));
    return onSnapshot(q, snap => {
      setPendingInvites(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [diary, user, settingsTab, id]);

  const handleCancelPendingInvite = async (inviteId) => {
    if (!window.confirm("Cancel this pending invitation?")) return;
    try {
      await deleteDoc(doc(db, "pendingInvites", inviteId));
    } catch (e) { console.error(e); }
  };

  // Initiate ownership transfer to an existing member. Owner selects a
  // member from the list, this writes a `pendingOwnerTransfer` field to the
  // diary doc which the recipient sees on Dashboard. Recipient must accept
  // before the swap actually happens — owner stays owner until then. Owner
  // can cancel from the same place at any time before acceptance.
  //
  // Why store on the diary doc rather than a separate collection: a single
  // diary fetch tells the recipient whether there's a pending transfer for
  // them (no second query on Dashboard scan), and Firestore rules can
  // validate the eventual swap as a single-document write.
  const handleInitiateTransfer = async (toUid) => {
    if (!user || !diary) return;
    const targetProfile = memberProfiles[toUid];
    if (!targetProfile) return;
    setTransferring(true);
    try {
      await updateDoc(doc(db, "diaries", id), {
        pendingOwnerTransfer: {
          toUid,
          toEmail: targetProfile.email || null,
          fromUid: user.uid,
          fromName: user.displayName || user.email || "Owner",
          diaryName: diary.name || "this diary",
          requestedAt: serverTimestamp(),
        },
      });
      setDiary(prev => ({
        ...prev,
        pendingOwnerTransfer: {
          toUid,
          toEmail: targetProfile.email || null,
          fromUid: user.uid,
          fromName: user.displayName || user.email || "Owner",
          diaryName: prev.name || "this diary",
        },
      }));
      setTransferTarget(null);
    } catch (e) { console.error(e); }
    setTransferring(false);
  };

  // Cancel a pending ownership transfer (owner side, before recipient
  // accepts). Just clears the `pendingOwnerTransfer` field.
  const handleCancelTransfer = async () => {
    if (!diary?.pendingOwnerTransfer) return;
    if (!window.confirm("Cancel the pending ownership transfer?")) return;
    try {
      await updateDoc(doc(db, "diaries", id), {
        pendingOwnerTransfer: deleteField(),
      });
      setDiary(prev => {
        const next = { ...prev };
        delete next.pendingOwnerTransfer;
        return next;
      });
    } catch (e) { console.error(e); }
  };

  const handleAddExtraEmail = async () => {
    const email = extraEmailInput.trim().toLowerCase();
    if (!email) return;
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setExtraMsg({ type: "error", text: "That doesn't look like a valid email address." });
      return;
    }
    if ((diary.authorizedEmails || []).includes(email)) {
      setExtraMsg({ type: "error", text: "That email is already authorized." });
      return;
    }
    setSavingExtra(true);
    setExtraMsg(null);
    try {
      await updateDoc(doc(db, "diaries", id), {
        authorizedEmails: arrayUnion(email),
      });
      setDiary(prev => ({
        ...prev,
        authorizedEmails: [...new Set([...(prev.authorizedEmails || []), email])],
      }));
      setExtraEmailInput("");
      setExtraMsg({ type: "ok", text: `${email} can now email entries to this diary.` });
    } catch (e) {
      console.error(e);
      setExtraMsg({ type: "error", text: "Couldn't add email. Please try again." });
    }
    setSavingExtra(false);
  };

  const handleRemoveExtraEmail = async (email) => {
    if (!window.confirm(`Remove ${email} from authorized senders? They won't be able to email entries to this diary anymore.`)) return;
    try {
      await updateDoc(doc(db, "diaries", id), {
        authorizedEmails: arrayRemove(email),
      });
      setDiary(prev => ({
        ...prev,
        authorizedEmails: (prev.authorizedEmails || []).filter(e => e !== email),
      }));
    } catch (e) { console.error(e); }
  };

  // Step 1 of resend-with-edit: open the editor for this invite. Loads
  // the invite's current tag access into the draft state. The user can
  // adjust chips, then commit via saveAndResend, or back out via
  // closeResendEditor (which reverts to whatever was saved on the doc).
  const openResendEditor = (inv) => {
    setResendingInviteId(inv.id);
    // Normalize: tags can be null (unrestricted), an array, or undefined.
    setResendDraftTags(Array.isArray(inv.tags) ? [...inv.tags] : (inv.tags === null ? null : []));
    setResendDraftUntagged(inv.includeUntagged !== false);
  };
  const closeResendEditor = () => {
    setResendingInviteId(null);
    setResendDraftTags(null);
    setResendDraftUntagged(false);
  };

  // Step 2 of resend-with-edit: persist the edited access settings to
  // the pendingInvite doc AND clear the emailSent flag + bump the resend
  // timestamp so the cloud function re-fires the invitation email.
  // Single document update so rules see one consistent change.
  const saveAndResend = async () => {
    if (!resendingInviteId) return;
    setResendSaving(true);
    try {
      await updateDoc(doc(db, "pendingInvites", resendingInviteId), {
        tags: resendDraftTags,
        includeUntagged: resendDraftUntagged,
        emailSent: false,
        emailError: deleteField(),
        emailFailedAt: deleteField(),
        resendRequestedAt: serverTimestamp(),
      });
      closeResendEditor();
    } catch (e) {
      console.error("saveAndResend failed:", e);
      alert("Couldn't save and resend. Please try again.");
    }
    setResendSaving(false);
  };

  // Legacy: simple resend without opening the editor. Kept in case we
  // want to wire it to a "quick resend" affordance later, but the UI
  // currently always opens the editor first.
  const handleResendInvite = async (inviteId) => {
    try {
      await updateDoc(doc(db, "pendingInvites", inviteId), {
        emailSent: false,
        emailError: deleteField(),
        emailFailedAt: deleteField(),
        resendRequestedAt: serverTimestamp(),
      });
    } catch (e) { console.error(e); }
  };

  if (!diary) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "-apple-system,sans-serif", color: "#666", background: "#1a1208" }}>Loading...</div>;

  // Resolve role from the members map. Falls back to ownerId at the top
  // level for diaries whose members map doesn't include the owner's UID —
  // happens with older diaries created before the members map became the
  // canonical source of role, or with diaries migrated through data
  // structure changes. Without this fallback the owner of such a diary
  // appears as a non-member and loses Fields/Import tabs.
  const role = getMemberRole(diary.members?.[user?.uid])
    || (diary?.ownerId && diary.ownerId === user?.uid ? "owner" : null);
  const canEdit = role === "owner" || role === "contributor";
  // True when current viewer is not the owner — used for "shared with you" UI.
  const isShared = role && role !== "owner";
  // Find the owner's UID + display name for "shared by" attribution.
  const ownerUid = (() => {
    const entries = Object.entries(diary.members || {});
    const found = entries.find(([, v]) => getMemberRole(v) === "owner");
    return found ? found[0] : diary.ownerId || null;
  })();
  const ownerName = ownerUid ? (memberProfiles[ownerUid]?.name || "") : "";

  // ── Passcode gate ──
  // If this diary has a passcode AND the current viewer is its owner AND they
  // haven't unlocked it this browser session, show the lock screen instead of
  // the diary. We gate ONLY the owner (a shared diary isn't private by
  // definition, and only the owner can reset the code by email). The
  // `unlocked` / `forgotNote` state is declared at the top of the component
  // with the other hooks (React requires unconditional hook order).
  // Lock the diary right now (e.g. before handing the phone to someone) and
  // return to the diaries list — locking then leaving you staring at this
  // diary's own passcode screen would be odd; the natural destination is the
  // dashboard. The unlock is cleared in sessionStorage, so re-opening this
  // diary later will correctly prompt for the passcode again.
  const lockNow = () => {
    clearSessionUnlock(id);
    setUnlocked(false);
    navigate("/dashboard");
  };
  // The on-page lock icon has two modes:
  //   • diary HAS a passcode + unlocked → solid icon, taps lock immediately.
  //   • diary has NO passcode (owner only) → muted icon, taps open a quick
  //     "set a passcode" prompt; on success it sets, locks, and leaves.
  // Two distinct looks so "lock now" and "set a passcode" never get confused.
  const hasPasscode = !!diary.passcodeHash;
  const showLockIcon = role === "owner" && (hasPasscode ? unlocked : true);
  const lockIconMode = hasPasscode ? "lock" : "setup";
  const onLockIconClick = () => {
    if (hasPasscode) lockNow();
    else setShowSetPasscode(true);
  };

  const passcodeApplies = !!diary.passcodeHash && role === "owner";
  if (passcodeApplies && !unlocked) {
    return (
      <PasscodeGate
        diary={diary}
        ownerEmail={user?.email || ""}
        uid={user?.uid}
        onUnlock={() => setUnlocked(true)}
        onForgot={() => setForgotNote(true)}
        forgotNote={forgotNote}
        onResetDirect={async () => {
          // Owner is signed in — clear the passcode directly.
          await updateDoc(doc(db, "diaries", id), { passcodeHash: deleteField() });
          setDiary(prev => { const n = { ...prev }; delete n.passcodeHash; return n; });
        }}
        onEmailLink={async () => {
          // Create a reset-request doc; a Cloud Function emails the owner a
          // link. Token is random; expiry is enforced both in the doc and at
          // redemption time. We DON'T put the diary's secrets in the doc —
          // just enough for the function to email the owner and for the
          // landing page to clear the hash after validating ownership.
          const token = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
          await setDoc(doc(db, "passcodeResets", token), {
            token,
            diaryId: id,
            diaryName: diary.name || "your diary",
            ownerUid: user.uid,
            ownerEmail: user.email || "",
            createdAt: serverTimestamp(),
            expiresAt: Date.now() + 30 * 60 * 1000, // 30 minutes
            used: false,
          });
        }}
      />
    );
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: THEME.deskBg }}>
      <style>{`
        .entry-body p { margin: 0; padding: 0; }
        .entry-body p:empty { display: none; }
        .entry-body a { color: #0B5FA5; text-decoration: underline; word-break: break-word; }
        .entry-body a:hover { color: #C83803; }
        .entry-title { color: #0B1F4A !important; }
        ::-webkit-scrollbar { display: none; }
        [contenteditable]:empty:before { content: attr(data-placeholder); color: #bbb; pointer-events: none; }
        .modal-sheet { max-height: calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 20px) - 60px) !important; }
        .modal-bottom { padding-bottom: max(12px, env(safe-area-inset-bottom, 12px)) !important; }
        @supports not (height: 100svh) {
          .modal-inner { max-height: calc(100vh - 80px) !important; }
        }
        @media (max-width: 600px) {
          .diary-spine { display: none !important; }
          .diary-page { border-radius: 3px !important; }
          .diary-page-edge { display: none !important; }
          /* Timeline: shift everything left to free up space for entry cards */
          .timeline-wrap { padding-left: 60px !important; padding-right: 8px !important; }
          .timeline-wrap .timeline-line { left: 44px !important; }
          .timeline-wrap .timeline-dot { left: -20px !important; }
          .timeline-wrap .timeline-date { left: -56px !important; width: 32px !important; font-size: 9px !important; }
          .timeline-wrap .timeline-month { left: -60px !important; width: 36px !important; font-size: 8px !important; letter-spacing: 0.5px !important; }
          .entry-count { padding-left: 60px !important; }
        }
        /* First-visit timeline hint: previously pulsed the most recent
           dot to draw attention. Animation removed because an animated
           box-shadow can't be GPU-accelerated — Chromium repaints the
           timeline composition layer 60 times per second while the
           animation runs, visible as a constant green flash in paint-
           flashing diagnostics and as flicker during scroll. The blue
           tooltip "Tap any dot to select an entry" still does the
           attention-pull job. */
        .timeline-dot-pulse {
          /* No-op: kept as a class so the existing JSX doesn't break. */
        }
        /* First-visit attention pull on the first structured-field icon — same
           gentle pulse used elsewhere, applied to the dashed-bordered icon
           button. Only added on the first time a user opens the entry editor;
           the wrapper component strips the class after the entry is saved
           or the modal closes (via the localStorage flag). */
        @keyframes ltFieldPulse {
          0%   { box-shadow: 0 0 0 0 rgba(200,56,3,0.45); transform: scale(1); }
          50%  { box-shadow: 0 0 0 8px rgba(200,56,3,0);  transform: scale(1.04); }
          100% { box-shadow: 0 0 0 0 rgba(200,56,3,0);    transform: scale(1); }
        }
        .first-struct-field-blink {
          animation: ltFieldPulse 1.8s ease-out infinite;
          border-color: rgba(200,56,3,0.45) !important;
        }
        /* Custom fast-hover tooltips. Browsers delay native title tooltips
           ~700ms which feels sluggish for icon-rich UI. We use a CSS-only
           tooltip that appears at 200ms, anchored above the trigger.
           Use 'data-tip="text"' on any element to get one. */
        [data-tip] {
          position: relative;
        }
        [data-tip]::after {
          content: attr(data-tip);
          position: absolute;
          bottom: calc(100% + 6px);
          left: 50%;
          transform: translateX(-50%);
          background: #1a1208;
          color: white;
          font-size: 11px;
          font-weight: 500;
          font-family: -apple-system, sans-serif;
          padding: 4px 8px;
          border-radius: 4px;
          white-space: nowrap;
          max-width: 240px;
          overflow: hidden;
          text-overflow: ellipsis;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.1s ease 0.2s;
          z-index: 100;
        }
        [data-tip]:hover::after {
          opacity: 1;
        }
      `}</style>

      {/* NAV */}
      <nav style={{ background: ORANGE, borderBottom: `3px solid ${ORANGE_DARK}`, padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, zIndex: 10 }}>
        <button onClick={() => navigate("/dashboard")} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.4)", color: "white", padding: "6px 14px", borderRadius: "20px", fontSize: "13px", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>← Diaries</button>
        <span onClick={() => navigate("/")} style={{ fontSize: "22px", fontWeight: "900", color: "white", cursor: "pointer", fontFamily: "Georgia,serif" }}>Life<span style={{ color: NAVY }}>Trackr</span></span>
        {/* Invisible spacer to keep the centered logo balanced. The gear that was here moved to the diary page corner so it's clearly per-diary. */}
        <span style={{ width: "34px", height: "34px", visibility: "hidden" }} aria-hidden="true" />
      </nav>

      {/* DESK */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "20px 12px 40px", position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: "80%", height: "200px", background: `radial-gradient(ellipse,${THEME.ambientColor} 0%,transparent 70%)`, pointerEvents: "none" }} />

        {/* Toolbar */}
        {isOpen && (
          <div style={{ display: "flex", gap: "8px", marginBottom: "10px", alignItems: "center", zIndex: 5, flexWrap: "wrap", justifyContent: "center" }}>
            {!selectMode && isOpen && (<>



              <button onClick={() => setIsOpen(false)} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.4)", padding: "7px 12px", borderRadius: "20px", fontSize: "12px", cursor: "pointer" }}>Close</button>
            </>)}
          </div>
        )}

        {/* Notebook shadow — REMOVED.
            We used to render a separate absolute-positioned <div> with
            "filter: blur(28px)" + "height: calc(100% - 160px)" as a fake
            drop shadow. The blur filter is GPU-expensive, and on a long
            element (the journal is thousands of px tall when there are
            many entries) Edge re-rasterizes it in chunks during scroll
            — that's the rectangular flash of the parent background.
            The notebook already has its own box-shadow ("0 20px 60px
            rgba(0,0,0,0.7)" on line 8350) which is GPU-friendly and
            covers the same visual purpose. */}

        {/* NOTEBOOK
            Box-shadow with a large spread on the full-height notebook
            container caused visible scroll flicker on Chromium-based
            browsers — the shadow's bounding box gets re-rasterized in
            tiles as scroll exposes new content. The spine + page-edge
            shadows below give plenty of visual depth on their own. */}
        <div style={{ width: "min(780px,97vw)", flex: "1 0 auto", display: "flex", borderRadius: "4px", position: "relative", zIndex: 2 }}>
          {/* Spine — box-shadow (both inset and outer) removed.
              Same reason as the notebook and page-edge: this element
              spans the full journal height, and shadows on tall layers
              force tile re-rasterization during scroll on Chromium-
              based browsers, producing visible flicker. */}
          <div className="diary-spine" style={{ width: "36px", flexShrink: 0, background: isRecordsMode ? "#2a1f1a" : (diary.pageSpineColor || THEME.spineBg), borderRadius: "3px 0 0 3px", position: "relative", zIndex: 5, display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
            <div style={{ position: "absolute", inset: 0, backgroundImage: THEME.spineTexture, borderRadius: "3px 0 0 3px", opacity: 0.7 }} />
            {/* Rotated diary name on the spine. We don't make this sticky
                anymore — sticky + transform: rotate + writing-mode in Edge
                forces full-window repaints during scroll, showing a brief
                flash of the parent background across the viewport. The
                spine just sits at the top of the page now. */}
            <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: "16px", fontWeight: "900", color: diary.pageSpineTextColor || "rgba(255,220,180,0.96)", letterSpacing: "4px", textTransform: "uppercase", fontFamily: "-apple-system,'Helvetica Neue',sans-serif", marginTop: "20px", zIndex: 1 }}>
              {diary.name}
            </div>
          </div>

          {/* PAGE */}
            <div className="diary-page" style={{ flex: 1, background: diary.pageBgColor || "#f5f4f0", borderRadius: "0 3px 3px 0", position: "relative", overflow: "hidden", minHeight: "600px" }}>
            {/* Page edge — gives the right side of the journal its
                "page thickness" look. Box-shadow removed because this
                element spans the full journal height and the shadow
                forces tile re-rasterization during scroll. */}
            <div className="diary-page-edge" style={{ position: "absolute", top: "2px", right: "-13px", bottom: "2px", width: "15px", background: THEME.pageEdgeBg, borderRadius: "0 3px 3px 0" }} />

            {/* Mobile-only: settings gear lives at top-right of the diary
                page. On desktop it's inlined into the search row (so it
                doesn't overlap the "+" add-entry button). On mobile the
                search row is too tight to fit the gear too, so absolute
                positioning is the cleanest option there. */}
            {isNarrowViewport && showLockIcon && (
              <button onClick={onLockIconClick}
                title={lockIconMode === "lock" ? "Lock diary now" : "Set a passcode to lock this diary"}
                aria-label={lockIconMode === "lock" ? "Lock diary now" : "Set a passcode for this diary"}
                style={{ position: "absolute", top: "10px", right: "56px", width: "32px", height: "32px", borderRadius: "50%", border: "1px solid #ddd", background: "white", color: lockIconMode === "lock" ? NAVY : "#bbb", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, zIndex: 6, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", fontSize: "15px" }}>
                {lockIconMode === "lock" ? "🔒" : "🔓"}
              </button>
            )}
            {isNarrowViewport && (
              <button onClick={() => { setShowSettings(true); setSettingsTab("info"); }}
                title="Diary settings"
                style={{ position: "absolute", top: "10px", right: "16px", width: "32px", height: "32px", borderRadius: "50%", border: "1px solid #ddd", background: "white", color: "#666", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, zIndex: 6, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
            )}

            <div style={{ padding: "0", minHeight: "100%" }}>

              {/* (Compact diary-name header was here, but removed — the diary
                  name on the spine is the persistent identity, no need to
                  duplicate it at the top of the content area.) */}

              {/* Records view: thin stats bar + inline composer (page 1 unfiltered only) */}
              {isOpen && isRecordsMode && page === 0 && activeLenses.size === 0 && !search.trim() && activeFilterCount === 0 && entries.length > 0 && (() => {
                const authorCount = new Set(entries.map(e => e.authorId).filter(Boolean)).size;
                const daysSpan = (() => {
                  if (entries.length < 2) return 1;
                  const dates = entries.map(e => e.entryDate?.toDate ? e.entryDate.toDate() : (e.entryDate ? new Date(e.entryDate) : null)).filter(Boolean);
                  if (dates.length < 2) return 1;
                  const min = new Date(Math.min(...dates.map(d => d.getTime())));
                  const max = new Date(Math.max(...dates.map(d => d.getTime())));
                  return Math.max(1, Math.round((max - min) / 86400000) + 1);
                })();
                const firstDate = (() => {
                  const dates = entries.map(e => e.entryDate?.toDate ? e.entryDate.toDate() : (e.entryDate ? new Date(e.entryDate) : null)).filter(Boolean);
                  if (!dates.length) return null;
                  return new Date(Math.min(...dates.map(d => d.getTime())));
                })();
                const statParts = [
                  `${entries.length} ${entries.length === 1 ? "entry" : "entries"}`,
                  `${daysSpan} ${daysSpan === 1 ? "day" : "days"}`,
                ];
                if (authorCount > 1) statParts.push(`${authorCount} authors`);
                if (firstDate) statParts.push(`begun ${firstDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`);
                return (
                  <div style={{ background: "#2a1f1a", color: "rgba(255,220,180,0.75)", padding: "8px 16px", fontSize: "12px", fontFamily: "-apple-system,sans-serif", borderBottom: "1px solid rgba(255,220,180,0.12)" }}>
                    {statParts.join(" · ")}
                  </div>
                );
              })()}

              {/* Records view: inline composer (replaces modal click) */}
              {isOpen && isRecordsMode && canEdit && page === 0 && activeLenses.size === 0 && !search.trim() && activeFilterCount === 0 && (
                <InlineComposer
                  onSave={async ({ title, html, photoFiles, dateStr, tagString, followup }) => {
                    setSaving(true);
                    try {
                      if (photoFiles && photoFiles.length > 0) {
                        setUploadState({ active: true, total: photoFiles.length, completed: 0, currentName: photoFiles[0]?.name || "", currentKind: classifyFile(photoFiles[0]), currentProgress: 0 });
                      }
                      const { photos: photosUp, videos: videosUp, audio: audioUp, docs: docsUp } = photoFiles && photoFiles.length
                        ? await uploadAndSort(photoFiles, p => setUploadState({ active: true, total: p.total, completed: p.index, currentName: p.name, currentKind: p.kind, currentProgress: p.pct }))
                        : { photos: [], videos: [], audio: [], docs: [] };
                      setUploadState({ active: false });
                      const rawTagsArr = buildTags(tagString, followup);
                      const tagsArr = sanitizeMyTags(rawTagsArr);
                      const contentTags = tagsArr.filter(t => !isFollowupTag(t));
                      if (requiresTag && contentTags.length === 0) {
                        setUploadState({ active: false, error: "Please pick at least one tag for this entry." });
                        setSaving(false);
                        return;
                      }
                      const entryDate = buildEntryDate(dateStr);
                      const entryDoc = {
                        diaryId: id,
                        authorId: user.uid,
                        title: (title || "").trim() || null,
                        body: toText(html),
                        bodyHtml: sanitize(html),
                        tags: tagsArr,
                        autoTagged: false,
                        entryDate,
                        createdAt: serverTimestamp(),
                        photos: photosUp,
                        source: "web",
                        private: false, // explicit so the non-private query matches it
                        ...(followup ? { favorite: false } : {}),
                      };
                      if (videosUp.length) entryDoc.videos = videosUp;
                      if (audioUp.length) entryDoc.audio = audioUp;
                      if (docsUp.length) entryDoc.docs = docsUp;
                      if (tagsArr.includes("expense") && entryDoc.title) {
                        const ed = parseExpenseTitle(entryDoc.title);
                        if (ed) entryDoc.expenseData = ed;
                      }
                      await addDoc(collection(db, "entries"), entryDoc);
                    } catch (e) {
                      console.error(e);
                      setUploadState({ active: false, error: e.message || "Save failed. Please try again." });
                    }
                    setSaving(false);
                  }}
                  allTags={myDiaryTags}
                  saving={saving}
                />
              )}

              {/* Ceremonial intro — page 1, unfiltered, and fewer than 10 entries (diary doesn't yet fill the page on its own) */}
              {isOpen && !isRecordsMode && page === 0 && activeLenses.size === 0 && !search.trim() && activeFilterCount === 0 && visibleEntries.length > 0 && visibleEntries.length < 10 && (() => {
                const authorCount = new Set(visibleEntries.map(e => e.authorId).filter(Boolean)).size;
                const daysSpan = (() => {
                  if (visibleEntries.length < 2) return 1;
                  const dates = visibleEntries.map(e => {
                    const d = e.entryDate?.toDate ? e.entryDate.toDate() : (e.entryDate ? new Date(e.entryDate) : null);
                    return d;
                  }).filter(Boolean);
                  if (dates.length < 2) return 1;
                  const min = new Date(Math.min(...dates.map(d => d.getTime())));
                  const max = new Date(Math.max(...dates.map(d => d.getTime())));
                  return Math.max(1, Math.round((max - min) / 86400000) + 1);
                })();
                const firstDate = (() => {
                  const dates = visibleEntries.map(e => {
                    const d = e.entryDate?.toDate ? e.entryDate.toDate() : (e.entryDate ? new Date(e.entryDate) : null);
                    return d;
                  }).filter(Boolean);
                  if (!dates.length) return null;
                  return new Date(Math.min(...dates.map(d => d.getTime())));
                })();

                const introTextColor = isRecordsMode ? "rgba(255,255,255,0.88)" : NAVY;
                const introAccent = isRecordsMode ? "rgba(255,220,180,0.6)" : "rgba(139,58,10,0.6)";
                const introStatColor = isRecordsMode ? "#ff7a4d" : ORANGE;
                const introDivider = isRecordsMode ? "rgba(255,220,180,0.2)" : "rgba(139,58,10,0.25)";
                const introBg = isRecordsMode ? "#2a1f1a" : "transparent";

                return (
                  <div style={{ padding: (isRecordsMode || isListMode) ? "52px 24px 44px" : "60px 40px 48px 60px", textAlign: "center", background: introBg, borderBottom: `1px dashed ${introDivider}`, position: "relative" }}>
                    {/* Settings cog in top right of intro */}
                    <div style={{ position: "absolute", top: "14px", right: "16px", fontSize: "12px", color: introAccent, fontFamily: "'Georgia', serif", letterSpacing: "2px" }}>· · ·</div>

                    <div style={{ fontSize: "11px", color: introAccent, letterSpacing: "3px", marginBottom: "18px", fontFamily: "'Georgia', serif" }}>· · ·</div>

                    <div style={{ fontFamily: "'Georgia', serif", fontSize: "clamp(32px, 5vw, 42px)", color: introTextColor, lineHeight: 1.1, marginBottom: "10px", letterSpacing: "-0.5px", fontWeight: 700 }}>
                      {diary.name}
                    </div>
                    {/* Shared-with-you attribution + new-entries badge */}
                    {isShared && (
                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px", marginBottom: "10px", flexWrap: "wrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "11px", color: introAccent, fontFamily: "-apple-system,sans-serif", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600 }}>
                          <span style={{ background: "rgba(200,56,3,0.12)", color: ORANGE, padding: "3px 8px", borderRadius: "10px", letterSpacing: "0.6px" }}>Shared</span>
                          {ownerName && <span style={{ textTransform: "none", letterSpacing: "0", color: introAccent, fontStyle: "italic", fontFamily: "'Georgia', serif", fontSize: "13px" }}>by {ownerName}</span>}
                        </span>
                        {newEntriesCount > 0 && (
                          <span style={{ background: ORANGE, color: "white", padding: "3px 9px", borderRadius: "10px", fontSize: "11px", fontWeight: 700, letterSpacing: "0.4px", fontFamily: "-apple-system,sans-serif" }}>
                            {newEntriesCount} new
                          </span>
                        )}
                      </div>
                    )}
                    <div style={{ marginBottom: "28px" }}>
                      <CopyEmail
                        email={diary.inboundEmail}
                        diaryName={diary.name}
                        accentColor={isRecordsMode ? "rgba(255,220,180,0.9)" : "#8B3A0A"}
                        style={{ fontFamily: "'Georgia', serif", fontStyle: "italic", fontSize: "13px", color: isRecordsMode ? "rgba(255,220,180,0.7)" : "#8B3A0A" }}
                        onCopied={(ok) => showToast(ok ? "Logging address copied." : "Couldn't copy — select and copy manually.")}
                      />
                    </div>

                    <div style={{ width: "60px", height: "1px", background: introDivider, margin: "0 auto 24px" }} />

                    <div style={{ display: "flex", justifyContent: "center", gap: "32px", marginBottom: "22px", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontFamily: "'Georgia', serif", fontSize: "22px", color: introStatColor, fontWeight: 700 }}>{visibleEntries.length}</div>
                        <div style={{ fontSize: "10px", color: isRecordsMode ? "rgba(255,220,180,0.6)" : "#8B3A0A", letterSpacing: "0.8px", textTransform: "uppercase" }}>
                          {visibleEntries.length === 1 ? "Entry" : "Entries"}
                        </div>
                      </div>
                      {/* Second stat: "New" for shared/restricted viewers, "Days" for owner.
                          Restricted viewers can have a tiny effective date range, so days
                          isn't very informative; freshness is more useful. */}
                      {isShared ? (
                        <div>
                          <div style={{ fontFamily: "'Georgia', serif", fontSize: "22px", color: introStatColor, fontWeight: 700 }}>{newEntriesCount}</div>
                          <div style={{ fontSize: "10px", color: isRecordsMode ? "rgba(255,220,180,0.6)" : "#8B3A0A", letterSpacing: "0.8px", textTransform: "uppercase" }}>
                            New
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontFamily: "'Georgia', serif", fontSize: "22px", color: introStatColor, fontWeight: 700 }}>{daysSpan}</div>
                          <div style={{ fontSize: "10px", color: isRecordsMode ? "rgba(255,220,180,0.6)" : "#8B3A0A", letterSpacing: "0.8px", textTransform: "uppercase" }}>
                            {daysSpan === 1 ? "Day" : "Days"}
                          </div>
                        </div>
                      )}
                      {authorCount > 1 && (
                        <div>
                          <div style={{ fontFamily: "'Georgia', serif", fontSize: "22px", color: introStatColor, fontWeight: 700 }}>{authorCount}</div>
                          <div style={{ fontSize: "10px", color: isRecordsMode ? "rgba(255,220,180,0.6)" : "#8B3A0A", letterSpacing: "0.8px", textTransform: "uppercase" }}>Authors</div>
                        </div>
                      )}
                    </div>

                    {firstDate && (
                      <div style={{ fontFamily: "'Georgia', serif", fontStyle: "italic", fontSize: "12px", color: isRecordsMode ? "rgba(255,220,180,0.7)" : "#8B3A0A" }}>
                        Begun {firstDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                      </div>
                    )}

                    <div style={{ fontSize: "11px", color: introAccent, letterSpacing: "3px", marginTop: "22px", fontFamily: "'Georgia', serif" }}>· · ·</div>
                  </div>
                );
              })()}

              {/* Follow-up + Favorites banners — render above all filtering UI
                  so the most actionable items always appear first, regardless
                  of how the diary is filtered. */}
              {isOpen && entries.length > 0 && !selectMode && (
                <div style={{ padding: isRecordsMode ? "16px 16px 0" : "14px 56px 0 40px" }}>
                  {(() => {
                    const fups = entries
                      .filter(e => (e.tags || []).some(isFollowupTag) && (activeLenses.size === 0 || (e.tags || []).some(t => activeLenses.has(t))))
                      .sort((a, b) => getTs(b.entryDate) - getTs(a.entryDate));
                    if (!fups.length) return null;
                    return (
                      <div style={{ background: "#fff8f0", border: "1px solid rgba(200,56,3,0.2)", borderRadius: "8px", padding: "8px 12px", marginBottom: "6px" }}>
                        <div onClick={() => setFollowupExpanded(x => !x)}
                          style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", userSelect: "none" }}>
                          <span style={{ fontSize: "13px" }}>🚩</span>
                          <span style={{ fontSize: "13px", fontWeight: "600", color: "#7a1e00", fontFamily: "-apple-system,sans-serif", flex: 1 }}>
                            {fups.length} follow-up{fups.length !== 1 ? "s" : ""} pending
                          </span>
                          <span style={{ fontSize: "11px", color: "#7a1e00", fontFamily: "-apple-system,sans-serif", opacity: 0.7 }}>
                            {followupExpanded ? "▴ hide" : "▾ show"}
                          </span>
                        </div>
                        {followupExpanded && (
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "8px" }}>
                            {fups.map(fe => {
                              const fePrev = (fe.title || fe.body || "").slice(0, 65);
                              const feTags = (fe.tags || []).filter(t => !isFollowupTag(t));
                              return (
                                <div key={fe.id}
                                  onClick={() => canEdit ? openEdit(fe) : null}
                                  style={{ background: "white", border: "1px solid rgba(200,56,3,0.15)", borderRadius: "6px", padding: "7px 10px", cursor: canEdit ? "pointer" : "default", display: "flex", flexDirection: "column", gap: "3px" }}>
                                  <div style={{ fontSize: "13px", color: "#3d0e00", fontFamily: "-apple-system,sans-serif", fontWeight: 500 }}>
                                    {fePrev}{fePrev.length >= 65 ? "…" : ""}
                                  </div>
                                  <div style={{ display: "flex", gap: "5px", alignItems: "center", flexWrap: "wrap" }}>
                                    <span style={{ fontSize: "11px", color: "#aaa", fontFamily: "-apple-system,sans-serif", fontStyle: "italic" }}>{fmtShort(fe.entryDate)}</span>
                                    {feTags.map((tag, i) => {
                                      const m = getTagMeta(tag);
                                      return <span key={i} style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}`, borderRadius: "10px", padding: "1px 7px", fontSize: "10px", fontWeight: "600", fontFamily: "-apple-system,sans-serif" }}>{m.emoji} {tag}</span>;
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {(() => {
                    const favs = entries
                      .filter(e => e.favorite && (activeLenses.size === 0 || (e.tags || []).some(t => activeLenses.has(t))))
                      .sort((a, b) => getTs(b.entryDate) - getTs(a.entryDate));
                    if (!favs.length) return null;
                    return (
                      <div style={{ background: "#fffaeb", border: "1px solid rgba(218,165,32,0.3)", borderRadius: "8px", padding: "8px 12px", marginBottom: "6px" }}>
                        <div onClick={() => setFavoritesExpanded(x => !x)}
                          style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", userSelect: "none" }}>
                          <span style={{ fontSize: "13px" }}>⭐</span>
                          <span style={{ fontSize: "13px", fontWeight: "600", color: "#7a5a00", fontFamily: "-apple-system,sans-serif", flex: 1 }}>
                            {favs.length} favorite{favs.length !== 1 ? "s" : ""}
                          </span>
                          <span style={{ fontSize: "11px", color: "#7a5a00", fontFamily: "-apple-system,sans-serif", opacity: 0.7 }}>
                            {favoritesExpanded ? "▴ hide" : "▾ show"}
                          </span>
                        </div>
                        {favoritesExpanded && (
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "8px" }}>
                            {favs.map(fe => {
                              const fePrev = (fe.title || fe.body || "").slice(0, 65);
                              const feTags = (fe.tags || []).filter(t => !isFollowupTag(t));
                              return (
                                <div key={fe.id}
                                  onClick={() => canEdit ? openEdit(fe) : null}
                                  style={{ background: "white", border: "1px solid rgba(218,165,32,0.2)", borderRadius: "6px", padding: "7px 10px", cursor: canEdit ? "pointer" : "default", display: "flex", flexDirection: "column", gap: "3px" }}>
                                  <div style={{ fontSize: "13px", color: "#5a4000", fontFamily: "-apple-system,sans-serif", fontWeight: 500 }}>
                                    {fePrev}{fePrev.length >= 65 ? "…" : ""}
                                  </div>
                                  <div style={{ display: "flex", gap: "5px", alignItems: "center", flexWrap: "wrap" }}>
                                    <span style={{ fontSize: "11px", color: "#aaa", fontFamily: "-apple-system,sans-serif", fontStyle: "italic" }}>{fmtShort(fe.entryDate)}</span>
                                    {feTags.map((tag, i) => {
                                      const m = getTagMeta(tag);
                                      return <span key={i} style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}`, borderRadius: "10px", padding: "1px 7px", fontSize: "10px", fontWeight: "600", fontFamily: "-apple-system,sans-serif" }}>{m.emoji} {tag}</span>;
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Lens bar + search + filter icon — only when there are enough entries to need filtering */}
              {isOpen && entries.length >= 3 && (
                <div style={{ padding: isRecordsMode ? "8px 16px" : "8px 12px 0 40px", background: isRecordsMode ? "white" : "transparent", borderBottom: isRecordsMode ? "1px solid #e0dcd8" : "none", borderTop: isRecordsMode ? "none" : `1px solid ${THEME.headerBorderColor}` }}>
                  {/* Build the two filter pills (Groups, Tags). On mobile,
                      they get rendered as their own row above the search.
                      On desktop, they're inlined inside the search row
                      to save vertical space. Each pill expands its own
                      chip row below and auto-expands when its filters
                      are active. */}
                  {(() => {
                    const hasGroupFilters = activeGroups.size > 0;
                    const hasTagFilters = activeLenses.size > 0 || excludedTags.size > 0;
                    const groupsExpanded = showGroupsRow || hasGroupFilters;
                    const tagsExpanded = showTagsLensRow || hasTagFilters || hasGroupFilters;
                    // Build pills only when their underlying data exists.
                    // Render them inline above the chip rows on mobile;
                    // on desktop, do nothing here — they'll be injected
                    // into the search row below via filterPills.
                    if (!tagGroups.length && !lensTags.length) return null;
                    if (!isNarrowViewport) return null;  // desktop renders elsewhere
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: (groupsExpanded || tagsExpanded) ? "6px" : "4px" }}>
                        {tagGroups.length > 0 && (
                          <button onClick={() => setShowGroupsRow(s => !s)}
                            title={groupsExpanded ? "Hide groups" : "Show groups"}
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", background: "transparent", color: hasGroupFilters ? NAVY : "#666", border: `1px solid ${hasGroupFilters ? NAVY : "#d8d4cc"}`, borderRadius: "16px", fontSize: "11px", fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                            <span style={{ fontSize: 10 }}>{groupsExpanded ? "▾" : "▸"}</span>
                            Groups
                            {hasGroupFilters && <span style={{ background: NAVY, color: "white", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8, marginLeft: 2 }}>{activeGroups.size}</span>}
                          </button>
                        )}
                        {lensTags.length > 0 && (
                          <button onClick={() => setShowTagsLensRow(s => !s)}
                            title={tagsExpanded ? "Hide tags" : "Show tags"}
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", background: "transparent", color: hasTagFilters ? NAVY : "#666", border: `1px solid ${hasTagFilters ? NAVY : "#d8d4cc"}`, borderRadius: "16px", fontSize: "11px", fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                            <span style={{ fontSize: 10 }}>{tagsExpanded ? "▾" : "▸"}</span>
                            Tags
                            {hasTagFilters && <span style={{ background: NAVY, color: "white", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8, marginLeft: 2 }}>{activeLenses.size + excludedTags.size}</span>}
                          </button>
                        )}
                      </div>
                    );
                  })()}

                  {/* ROW ORDERING NOTE
                      We render in this order: (1) search row with inline
                      Groups/Tags pills, (2) Group chips when expanded,
                      (3) Tag chips when expanded. Search is the visual
                      anchor — filters expand BELOW it as a refinement,
                      reading like "filter bar, then filter detail." This
                      replaced an earlier order where chip rows were
                      above the search row, which made the filter affordance
                      feel disconnected from what it was modifying. */}

                  {/* Row 1: search (left), view toggle, filter (right).
                      On desktop, Groups/Tags pills are injected here at
                      the start so they share the search row's vertical
                      space instead of taking their own row. On mobile,
                      they're already rendered above as their own row. */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                    {/* Desktop-inline filter pills */}
                    {!isNarrowViewport && (tagGroups.length > 0 || lensTags.length > 0) && (() => {
                      const hasGroupFilters = activeGroups.size > 0;
                      const hasTagFilters = activeLenses.size > 0 || excludedTags.size > 0;
                      const groupsExpanded = showGroupsRow || hasGroupFilters;
                      const tagsExpanded = showTagsLensRow || hasTagFilters || hasGroupFilters;
                      return (
                        <>
                          {tagGroups.length > 0 && (
                            <button onClick={() => setShowGroupsRow(s => !s)}
                              title={groupsExpanded ? "Hide groups" : "Show groups"}
                              style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", background: "transparent", color: hasGroupFilters ? NAVY : "#666", border: `1px solid ${hasGroupFilters ? NAVY : "#d8d4cc"}`, borderRadius: "16px", fontSize: "11px", fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                              <span style={{ fontSize: 10 }}>{groupsExpanded ? "▾" : "▸"}</span>
                              Groups
                              {hasGroupFilters && <span style={{ background: NAVY, color: "white", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8, marginLeft: 2 }}>{activeGroups.size}</span>}
                            </button>
                          )}
                          {lensTags.length > 0 && (
                            <button onClick={() => setShowTagsLensRow(s => !s)}
                              title={tagsExpanded ? "Hide tags" : "Show tags"}
                              style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", background: "transparent", color: hasTagFilters ? NAVY : "#666", border: `1px solid ${hasTagFilters ? NAVY : "#d8d4cc"}`, borderRadius: "16px", fontSize: "11px", fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                              <span style={{ fontSize: 10 }}>{tagsExpanded ? "▾" : "▸"}</span>
                              Tags
                              {hasTagFilters && <span style={{ background: NAVY, color: "white", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8, marginLeft: 2 }}>{activeLenses.size + excludedTags.size}</span>}
                            </button>
                          )}
                        </>
                      );
                    })()}
                    {/* Narrow search — left-aligned, 220px on desktop */}
                    <div className="search-wrap" style={{ width: "220px", maxWidth: "60%", background: "rgba(0,0,0,0.04)", border: "1px solid #e0dcd4", borderRadius: "8px", padding: "3px 10px", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ color: "#bbb", fontSize: "13px" }}>🔍</span>
                      <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
                        name="diary-search" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                        style={{ background: "transparent", border: "none", color: "#333", fontSize: "13px", outline: "none", fontFamily: "-apple-system,sans-serif", width: "100%", flex: 1, minWidth: 0 }} />
                      {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", color: "#bbb", cursor: "pointer", fontSize: "13px", padding: 0 }}>✕</button>}
                    </div>
                    {/* "Mine" quick-filter used to live here. Removed in favor
                        of the Contributor toggle inside the filter panel
                        (showFilter), which handles the same case more
                        flexibly: it auto-hides on solo diaries (contributors
                        <= 1, where the filter is meaningless), and on
                        multi-contributor diaries it lets the user pick any
                        author rather than just themselves. Frees up row space
                        on iPhone where the search bar was getting squeezed. */}
                    {/* View-mode toggle — line-spacing metaphor (like Word) */}
                    <button onClick={() => saveViewMode(isCompactMode ? "detail" : "compact")}
                      title={isCompactMode ? "Switch to expanded view" : "Switch to compact view"}
                      style={{ flexShrink: 0, width: "28px", height: "28px", borderRadius: "6px", border: "1px solid #e0dcd4", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        {isCompactMode ? (
                          /* Compact mode: tap to EXPAND — bars FAR APART
                             at top/bottom edges, arrows BETWEEN them
                             pointing OUTWARD (away from each other) each
                             toward its bar. */
                          <>
                            <line x1="3" y1="3.5" x2="15" y2="3.5" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
                            <line x1="3" y1="14.5" x2="15" y2="14.5" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
                            {/* Top arrow: shaft from y=9 (center) to y=6, head pointing UP at y=6 (near top bar) */}
                            <line x1="9" y1="9" x2="9" y2="6" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M7 7.5 L9 5.5 L11 7.5" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            {/* Bottom arrow: shaft from y=9 (center) to y=12, head pointing DOWN at y=12 (near bottom bar) */}
                            <line x1="9" y1="9" x2="9" y2="12" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M7 10.5 L9 12.5 L11 10.5" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                          </>
                        ) : (
                          /* Detail mode: tap to COMPRESS — bars CLOSE to
                             center, arrows OUTSIDE the bars (at top/bottom
                             of icon) pointing INWARD (toward each other,
                             each pointing at its bar). */
                          <>
                            <line x1="3" y1="7" x2="15" y2="7" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
                            <line x1="3" y1="11" x2="15" y2="11" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
                            {/* Top arrow at top of icon, pointing DOWN toward top bar at y=7 */}
                            <line x1="9" y1="2" x2="9" y2="5" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M7 3.5 L9 5.5 L11 3.5" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            {/* Bottom arrow at bottom of icon, pointing UP toward bottom bar at y=11 */}
                            <line x1="9" y1="16" x2="9" y2="13" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M7 14.5 L9 12.5 L11 14.5" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                          </>
                        )}
                      </svg>
                    </button>
                    {/* Media view toggle — picture-frame icon (mountain + sun) */}
                    <button onClick={() => setViewMode(viewMode === "media" ? "timeline" : "media")}
                      title={viewMode === "media" ? "Switch to timeline view" : "Switch to media view (gallery)"}
                      style={{ flexShrink: 0, width: "28px", height: "28px", borderRadius: "6px", border: `1px solid ${viewMode === "media" ? ORANGE : "#e0dcd4"}`, background: viewMode === "media" ? "rgba(200,56,3,0.08)" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <rect x="2.5" y="3.5" width="13" height="11" rx="1.4" stroke={viewMode === "media" ? ORANGE : "#666"} strokeWidth="1.3" fill="none"/>
                        <circle cx="6" cy="7" r="1.2" stroke={viewMode === "media" ? ORANGE : "#666"} strokeWidth="1.1" fill="none"/>
                        <path d="M3.2 13 L7 9.5 L9.5 11.5 L12 9 L14.8 12.5" stroke={viewMode === "media" ? ORANGE : "#666"} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                    {/* Filter icon */}
                    <button onClick={() => setShowFilter(f => !f)}
                      style={{ flexShrink: 0, width: "28px", height: "28px", borderRadius: "6px", border: `1px solid ${activeFilterCount > 0 ? ORANGE : "#e0dcd4"}`, background: activeFilterCount > 0 ? "rgba(200,56,3,0.08)" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        {/* Three horizontal slider tracks with circular knobs at varying positions */}
                        <line x1="2" y1="4" x2="14" y2="4" stroke={activeFilterCount > 0 ? ORANGE : "#888"} strokeWidth="1.5" strokeLinecap="round"/>
                        <line x1="2" y1="8" x2="14" y2="8" stroke={activeFilterCount > 0 ? ORANGE : "#888"} strokeWidth="1.5" strokeLinecap="round"/>
                        <line x1="2" y1="12" x2="14" y2="12" stroke={activeFilterCount > 0 ? ORANGE : "#888"} strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="10" cy="4" r="1.8" fill="white" stroke={activeFilterCount > 0 ? ORANGE : "#888"} strokeWidth="1.5"/>
                        <circle cx="5" cy="8" r="1.8" fill="white" stroke={activeFilterCount > 0 ? ORANGE : "#888"} strokeWidth="1.5"/>
                        <circle cx="11" cy="12" r="1.8" fill="white" stroke={activeFilterCount > 0 ? ORANGE : "#888"} strokeWidth="1.5"/>
                      </svg>
                      {activeFilterCount > 0 && (
                        <div style={{ position: "absolute", top: "-4px", right: "-4px", width: "14px", height: "14px", borderRadius: "50%", background: ORANGE, color: "white", fontSize: "9px", fontWeight: "700", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "-apple-system,sans-serif" }}>{activeFilterCount}</div>
                      )}
                    </button>
                    {/* Sort by date — toggle between newest-first and
                        oldest-first. Icon shows three stacked lines (long
                        on top to short on bottom for newest-first, reversed
                        for oldest-first) with an arrow on the side. */}
                    <button onClick={() => setSort(s => s === "newest" ? "oldest" : "newest")}
                      title={sort === "newest" ? "Sort: newest first (tap for oldest)" : "Sort: oldest first (tap for newest)"}
                      style={{ flexShrink: 0, width: "28px", height: "28px", borderRadius: "6px", border: "1px solid #e0dcd4", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        {sort === "newest" ? (
                          /* Newest first: lines decreasing top to bottom + down arrow on right */
                          <>
                            <line x1="2" y1="4" x2="9" y2="4" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
                            <line x1="2" y1="8" x2="7" y2="8" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
                            <line x1="2" y1="12" x2="5" y2="12" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
                            <line x1="13" y1="3" x2="13" y2="13" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M11 11 L13 13.5 L15 11" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                          </>
                        ) : (
                          /* Oldest first: lines increasing top to bottom + up arrow on right */
                          <>
                            <line x1="2" y1="4" x2="5" y2="4" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
                            <line x1="2" y1="8" x2="7" y2="8" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
                            <line x1="2" y1="12" x2="9" y2="12" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
                            <line x1="13" y1="3" x2="13" y2="13" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M11 5 L13 2.5 L15 5" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                          </>
                        )}
                      </svg>
                    </button>
                    {/* Import CSV — quick access to bulk import. Owner/editor
                        only. Opens Settings directly on the Import tab. */}
                    {canEdit && (
                      <button onClick={() => { setSettingsTab("import"); setShowSettings(true); }}
                        title="Import entries from CSV"
                        style={{ flexShrink: 0, width: "28px", height: "28px", borderRadius: "6px", border: "1px solid #e0dcd4", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          {/* Up arrow into a tray — mirror of the download icon */}
                          <path d="M8 11 L8 2 M5 5 L8 2 L11 5" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M3 13 L13 13" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </button>
                    )}
                    {/* Download CSV — dropdown with general + expenses variants */}
                    {filtered.length > 0 && (
                      <div style={{ position: "relative" }}>
                        <button onClick={() => setToolbarDownloadOpen(o => !o)}
                          title={`Download ${filtered.length} ${filtered.length === 1 ? "entry" : "entries"}`}
                          style={{ flexShrink: 0, width: "28px", height: "28px", borderRadius: "6px", border: "1px solid #e0dcd4", background: toolbarDownloadOpen ? "#f5f3ef" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 2 L8 11 M5 8 L8 11 L11 8" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M3 13 L13 13" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                        </button>
                        {toolbarDownloadOpen && (
                          <>
                            <div onClick={() => setToolbarDownloadOpen(false)}
                              style={{ position: "fixed", inset: 0, zIndex: 60 }} />
                            <div style={{ position: "absolute", top: "40px", right: 0, zIndex: 61, background: "white", border: "1px solid #e0dcd8", borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", minWidth: "230px", overflow: "hidden", fontFamily: "-apple-system,sans-serif" }}>
                              {[
                                { label: "Download Excel", icon: "📗", onClick: exportXlsx, hint: `${filtered.length} ${filtered.length === 1 ? "entry" : "entries"}` },
                                { label: "Download Expenses Excel", icon: "📗", onClick: exportExpensesXlsx, hint: "parsed format" },
                                { label: "Download CSV", icon: "📊", onClick: exportCsv, hint: `${filtered.length} ${filtered.length === 1 ? "entry" : "entries"}` },
                                { label: "Download Expenses CSV", icon: "💰", onClick: exportExpensesCsv, hint: "parsed format" },
                                { label: "Download PDF", icon: "📄", onClick: exportPdf, hint: toolbarBuilding ? "Building…" : null },
                                { label: "Download Word", icon: "📝", onClick: exportDocx, hint: toolbarBuilding ? "Building…" : null },
                                { label: "Email (PDF attached)", icon: "✉️", onClick: exportEmailPdf, hint: toolbarBuilding ? "Building…" : null },
                                { label: "Copy as text", icon: "📋", onClick: exportCopyText },
                              ].map((item, i, arr) => (
                                <button key={i} onClick={item.onClick} disabled={toolbarBuilding}
                                  style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", background: "transparent", border: "none", cursor: toolbarBuilding ? "default" : "pointer", textAlign: "left", borderBottom: i < arr.length - 1 ? "1px solid #f0ede8" : "none", color: "#222", fontSize: "13px", fontFamily: "-apple-system,sans-serif" }}
                                  onMouseEnter={e => { if(!toolbarBuilding) e.currentTarget.style.background = "#faf8f3"; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                                  <span style={{ fontSize: "15px" }}>{item.icon}</span>
                                  <span style={{ flex: 1 }}>{item.label}</span>
                                  {item.hint && <span style={{ fontSize: "11px", color: "#aaa", fontStyle: "italic" }}>{item.hint}</span>}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {/* Desktop-only inline "+" add-entry button. Saves the
                        vertical real estate of the standalone +-row below
                        when there's nothing else to put on that row. On
                        mobile this stays in the standalone row (search row
                        is already tight). */}
                    {!isNarrowViewport && canEdit && !selectMode && viewMode !== "media" && (
                      <button onClick={() => setShowAdd(true)}
                        title="Add new entry"
                        style={{ flexShrink: 0, marginLeft: "auto", width: "32px", height: "32px", borderRadius: "50%", background: "white", border: "1px solid #ddd", color: "#666", fontSize: "20px", fontWeight: 300, lineHeight: 1, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                        +
                      </button>
                    )}
                    {/* Per-diary settings gear — moved from absolute-positioned
                        top-right into the search row so it sits next to the
                        "+" add-entry button. Styled identically to read as a
                        sibling action. On mobile, the diary-page settings
                        access lives elsewhere (the diary header) so we
                        only render this on desktop. */}
                    {!isNarrowViewport && showLockIcon && (
                      <button onClick={onLockIconClick}
                        title={lockIconMode === "lock" ? "Lock diary now" : "Set a passcode to lock this diary"}
                        aria-label={lockIconMode === "lock" ? "Lock diary now" : "Set a passcode for this diary"}
                        style={{ flexShrink: 0, marginLeft: canEdit && !selectMode && viewMode !== "media" ? "0" : "auto", width: "32px", height: "32px", borderRadius: "50%", border: "1px solid #ddd", background: "white", color: lockIconMode === "lock" ? NAVY : "#bbb", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", fontSize: "15px", marginRight: "6px" }}>
                        {lockIconMode === "lock" ? "🔒" : "🔓"}
                      </button>
                    )}
                    {!isNarrowViewport && (
                      <button onClick={() => { setShowSettings(true); setSettingsTab("info"); }}
                        title="Diary settings"
                        style={{ flexShrink: 0, marginLeft: showLockIcon ? "0" : (canEdit && !selectMode && viewMode !== "media" ? "0" : "auto"), width: "32px", height: "32px", borderRadius: "50%", border: "1px solid #ddd", background: "white", color: "#666", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <circle cx="12" cy="12" r="3" />
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {/* Row 2 (below search): group chips. Only when groups
                      pill is expanded OR group filters are active.
                      We sort active groups to the front so the user can
                      see which groups are currently filtering at a
                      glance — the rest follow in alphabetical order. */}
                  {tagGroups.length > 0 && (showGroupsRow || activeGroups.size > 0) && (() => {
                    const sortedGroups = [...tagGroups].sort((a, b) => {
                      // Active groups first, then alpha by name.
                      const aActive = activeGroups.has(a.id);
                      const bActive = activeGroups.has(b.id);
                      if (aActive !== bActive) return aActive ? -1 : 1;
                      return a.name.localeCompare(b.name);
                    });
                    if (!sortedGroups.length) return null;
                    return (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: "4px" }}>
                        {sortedGroups.map(g => {
                          const active = activeGroups.has(g.id);
                          const tagCount = expandGroupTags(g.id).size;
                          return (
                            <button key={g.id}
                              onClick={() => toggleGroup(g.id)}
                              title={active
                                ? `Active. Includes ${tagCount} tag${tagCount === 1 ? "" : "s"}. Click to deactivate.`
                                : `Filter by group "${g.name}" (${tagCount} tag${tagCount === 1 ? "" : "s"})`}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: isRecordsMode ? "3px 9px" : "3px 10px",
                                background: active ? NAVY : "transparent",
                                color: active ? "white" : NAVY,
                                border: `1px solid ${active ? NAVY : "#d8d4cc"}`,
                                borderRadius: isRecordsMode ? "4px" : "16px",
                                fontSize: "12px",
                                fontWeight: 600,
                                cursor: "pointer",
                                fontFamily: "-apple-system,sans-serif",
                                lineHeight: 1.3,
                              }}>
                              {g.name}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {/* Row 3 (below search): tag lens bar. Only when tags
                      pill is expanded, or any tag/group filter is active
                      (group activates implicit tag chips that need to be
                      visible so user can exclude them). LensBar sorts
                      active/excluded tags to the front internally so a
                      just-clicked tag is immediately visible. */}
                  {lensTags.length > 0 && (showTagsLensRow || activeLenses.size > 0 || excludedTags.size > 0 || activeGroups.size > 0) && (
                    <div style={{ overflow: "hidden", marginBottom: "2px" }}>
                      <LensBar allTags={lensTags} activeLenses={activeLenses} onToggle={toggleLens} isRecordsMode={isRecordsMode} lensMode={lensMode} setLensMode={setLensMode} chipRowRef={filterRowRef} excludedTags={excludedTags} tagAncestorGroups={tagAncestorGroups} activeGroups={activeGroups} />
                    </div>
                  )}
                  {/* Active filter summary */}
                  {(activeFilterCount > 0 || activeLenses.size > 0) && (
                    <div style={{ fontSize: "12px", color: "#aaa", fontFamily: "-apple-system,sans-serif", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
                      <button onClick={clearAllFilters} style={{ background: "none", border: "none", color: ORANGE, cursor: "pointer", fontSize: "12px", padding: 0, fontFamily: "-apple-system,sans-serif" }}>Clear all</button>
                    </div>
                  )}
                  {/* Filter panel */}
                  {showFilter && (
                    <div style={{ background: "white", border: "1px solid #e8e4dc", borderRadius: "12px", padding: "16px", marginBottom: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                        <span style={{ fontSize: "14px", fontWeight: "600", color: "#1a1a1a", fontFamily: "-apple-system,sans-serif" }}>Filters</span>
                        <button onClick={() => setShowFilter(false)} style={{ background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: "18px", padding: 0, lineHeight: 1 }}>×</button>
                      </div>
                      {/* Toggles — Show only */}
                      <div>
                        <div style={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px", fontFamily: "-apple-system,sans-serif" }}>Show only</div>
                        {/* Date range — toggle-row pattern, FIRST in the
                            list since it's the most-used filter. Active
                            when any date is set; toggling off clears both. */}
                        {(() => {
                          const dateActive = !!(filterDateFrom || filterDateTo);
                          return (
                            <div style={{ padding: "8px 0", borderBottom: "1px solid #f5f3ef" }}>
                              <div onClick={() => {
                                  if (dateActive) { setFilterDateFrom(""); setFilterDateTo(""); }
                                  else {
                                    // Default to "this month" when activating
                                    const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                                    const today = new Date();
                                    setFilterDateFrom(fmt(new Date(today.getFullYear(), today.getMonth(), 1)));
                                    setFilterDateTo(fmt(today));
                                  }
                                }}
                                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                                <span style={{ fontSize: "14px", color: "#333", fontFamily: "-apple-system,sans-serif" }}>📅 Date range</span>
                                <div style={{ width: "38px", height: "22px", borderRadius: "11px", background: dateActive ? ORANGE : "#ddd", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                                  <div style={{ position: "absolute", top: "2px", left: dateActive ? "18px" : "2px", width: "18px", height: "18px", borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
                                </div>
                              </div>
                              {dateActive && (
                                <div style={{ marginTop: "8px", paddingLeft: "20px" }}>
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginBottom: "8px" }}>
                                    {(() => {
                                      const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                                      const today = new Date();
                                      const y = today.getFullYear();
                                      const m = today.getMonth();
                                      const presets = [
                                        { label: "This month", from: new Date(y, m, 1), to: today },
                                        { label: "Last month", from: new Date(y, m - 1, 1), to: new Date(y, m, 0) },
                                        { label: "Last 30 days", from: new Date(today.getTime() - 30 * 86400000), to: today },
                                        { label: "YTD", from: new Date(y, 0, 1), to: today },
                                        { label: "Last year", from: new Date(y - 1, 0, 1), to: new Date(y - 1, 11, 31) },
                                      ];
                                      return presets.map(p => {
                                        const isActive = filterDateFrom === fmt(p.from) && filterDateTo === fmt(p.to);
                                        return (
                                          <button key={p.label}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setFilterDateFrom(fmt(p.from));
                                              setFilterDateTo(fmt(p.to));
                                            }}
                                            style={{ background: isActive ? NAVY : "white", color: isActive ? "white" : "#555", border: `1px solid ${isActive ? NAVY : "#ddd"}`, borderRadius: "14px", padding: "4px 11px", fontSize: "11px", fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,sans-serif", whiteSpace: "nowrap" }}>
                                            {p.label}
                                          </button>
                                        );
                                      });
                                    })()}
                                  </div>
                                  <div style={{ display: "flex", gap: "8px" }}>
                                    <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
                                      onClick={e => e.stopPropagation()}
                                      style={{ flex: 1, border: "1px solid #e0dcd4", borderRadius: "8px", padding: "7px 10px", fontSize: "13px", outline: "none", fontFamily: "-apple-system,sans-serif", color: "#333" }} />
                                    <span style={{ alignSelf: "center", color: "#aaa", fontSize: "13px" }}>to</span>
                                    <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
                                      onClick={e => e.stopPropagation()}
                                      style={{ flex: 1, border: "1px solid #e0dcd4", borderRadius: "8px", padding: "7px 10px", fontSize: "13px", outline: "none", fontFamily: "-apple-system,sans-serif", color: "#333" }} />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {[
                          ["⭐ Favorites", filterFavorites, setFilterFavorites],
                          ["🚩 Needs follow-up", filterFollowup, setFilterFollowup],
                          ["📎 Has media", filterPhotos, setFilterPhotos],
                        ].map(([label, val, setter]) => (
                          <div key={label} onClick={() => setter(v => !v)}
                            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f5f3ef", cursor: "pointer" }}>
                            <span style={{ fontSize: "14px", color: "#333", fontFamily: "-apple-system,sans-serif" }}>{label}</span>
                            <div style={{ width: "38px", height: "22px", borderRadius: "11px", background: val ? ORANGE : "#ddd", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                              <div style={{ position: "absolute", top: "2px", left: val ? "18px" : "2px", width: "18px", height: "18px", borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
                            </div>
                          </div>
                        ))}

                        {/* Contributor — toggle-row pattern. Only shown if
                            this diary has multiple contributors. Active
                            when not "all". */}
                        {contributors.length > 1 && (() => {
                          const contribActive = filterContributor !== "all";
                          return (
                            <div style={{ padding: "8px 0", borderBottom: "1px solid #f5f3ef" }}>
                              <div onClick={() => {
                                  if (contribActive) setFilterContributor("all");
                                  else if (user?.uid) setFilterContributor(user.uid);
                                  else setFilterContributor(contributors[0]);
                                }}
                                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                                <span style={{ fontSize: "14px", color: "#333", fontFamily: "-apple-system,sans-serif" }}>👤 Contributor</span>
                                <div style={{ width: "38px", height: "22px", borderRadius: "11px", background: contribActive ? ORANGE : "#ddd", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                                  <div style={{ position: "absolute", top: "2px", left: contribActive ? "18px" : "2px", width: "18px", height: "18px", borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
                                </div>
                              </div>
                              {contribActive && (
                                <div style={{ marginTop: "8px", paddingLeft: "20px" }}>
                                  <select value={filterContributor} onChange={e => setFilterContributor(e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                    style={{ width: "100%", border: "1px solid #e0dcd4", borderRadius: "8px", padding: "8px 10px", fontSize: "14px", outline: "none", fontFamily: "-apple-system,sans-serif", color: "#333", background: "white" }}>
                                    {contributors.map(c => <option key={c} value={c}>{names[c] || c}</option>)}
                                  </select>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Field filters — only shown when there are fields used
                          in 2+ entries. Per-field row: name + has toggle + min/max
                          for numeric or value chips for categorical. */}
                      {usedFields.size > 0 && (() => {
                        const NUMERIC = new Set(["amount","distanceMiles","timeMinutes","calories"]);
                        const order = ["amount","distanceMiles","timeMinutes","score","rating","mood","people","location","city","address","calories"];
                        const fields = [...usedFields.keys()].sort((a, b) => {
                          const ai = order.indexOf(a); const bi = order.indexOf(b);
                          if (ai !== -1 && bi !== -1) return ai - bi;
                          if (ai !== -1) return -1;
                          if (bi !== -1) return 1;
                          return a.localeCompare(b);
                        });
                        // Distinct values per categorical field
                        const valuesByField = new Map();
                        for (const field of fields) {
                          if (NUMERIC.has(field)) continue;
                          const set = new Set();
                          for (const e of entries) {
                            const s = e.structured;
                            if (!s) continue;
                            let v;
                            if (field.startsWith("library.")) v = (s.library || {})[field.slice(8)];
                            else if (field.startsWith("custom.")) v = (s.custom || {})[field.slice(7)];
                            else v = s[field];
                            if (v == null || v === "" || (Array.isArray(v) && !v.length)) continue;
                            if (Array.isArray(v)) v.forEach(x => set.add(String(x)));
                            else set.add(String(v));
                          }
                          if (set.size) valuesByField.set(field, [...set].sort());
                        }
                        const updateField = (field, patch) => {
                          setFieldFilters(prev => {
                            const next = { ...prev };
                            const cur = { ...(next[field] || {}) };
                            Object.assign(cur, patch);
                            // Drop undefined values from the merged object
                            for (const k of Object.keys(cur)) if (cur[k] == null || cur[k] === "" || (Array.isArray(cur[k]) && !cur[k].length)) delete cur[k];
                            if (!Object.keys(cur).length) delete next[field];
                            else next[field] = cur;
                            return next;
                          });
                        };
                        const activeCount = Object.values(fieldFilters).filter(c =>
                          c && (c.has || c.min != null || c.max != null || c.values?.length)
                        ).length;
                        return (
                          <div>
                            {activeCount >= 2 && (
                              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
                                <div style={{ display: "inline-flex", border: "1px solid #e0dcd4", borderRadius: "10px", overflow: "hidden", fontFamily: "-apple-system,sans-serif" }}>
                                  <button onClick={() => setFieldFilterMode("all")}
                                    title="Match entries that satisfy ALL active field filters (AND)"
                                    style={{ background: fieldFilterMode === "all" ? NAVY : "transparent", color: fieldFilterMode === "all" ? "white" : "#888", border: "none", padding: "3px 10px", fontSize: "11px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                                    All
                                  </button>
                                  <button onClick={() => setFieldFilterMode("any")}
                                    title="Match entries that satisfy ANY active field filter (OR)"
                                    style={{ background: fieldFilterMode === "any" ? NAVY : "transparent", color: fieldFilterMode === "any" ? "white" : "#888", border: "none", padding: "3px 10px", fontSize: "11px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                                    Any
                                  </button>
                                </div>
                              </div>
                            )}
                            {fields.map(field => {
                              const meta = getFieldDisplay(field);
                              const cur = fieldFilters[field] || {};
                              const isNumeric = NUMERIC.has(field);
                              const values = valuesByField.get(field) || [];
                              const hasActive = !!cur.has || cur.min != null || cur.max != null || (cur.values?.length > 0);
                              return (
                                <div key={field} style={{ padding: "8px 0", borderBottom: "1px solid #f5f3ef" }}>
                                  <div onClick={() => {
                                      // Toggle: any active criterion → clear field; otherwise set has=true
                                      if (hasActive) updateField(field, { has: undefined, min: undefined, max: undefined, values: undefined });
                                      else updateField(field, { has: true });
                                    }}
                                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                                    <span style={{ fontSize: "14px", color: "#333", fontFamily: "-apple-system,sans-serif" }}>
                                      {meta.icon} {meta.label}
                                    </span>
                                    <div style={{ width: "38px", height: "22px", borderRadius: "11px", background: hasActive ? ORANGE : "#ddd", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                                      <div style={{ position: "absolute", top: "2px", left: hasActive ? "18px" : "2px", width: "18px", height: "18px", borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
                                    </div>
                                  </div>
                                  {hasActive && isNumeric && (
                                    <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "8px", paddingLeft: "20px" }}>
                                      <span style={{ fontSize: "11px", color: "#888" }}>Min</span>
                                      <input type="number" value={cur.min ?? ""} placeholder="any"
                                        onChange={e => updateField(field, { min: e.target.value === "" ? undefined : Number(e.target.value) })}
                                        onClick={e => e.stopPropagation()}
                                        style={{ width: "70px", padding: "4px 8px", fontSize: "12px", border: "1px solid #ddd", borderRadius: "6px", outline: "none", fontFamily: "-apple-system,sans-serif" }} />
                                      <span style={{ fontSize: "11px", color: "#888" }}>Max</span>
                                      <input type="number" value={cur.max ?? ""} placeholder="any"
                                        onChange={e => updateField(field, { max: e.target.value === "" ? undefined : Number(e.target.value) })}
                                        onClick={e => e.stopPropagation()}
                                        style={{ width: "70px", padding: "4px 8px", fontSize: "12px", border: "1px solid #ddd", borderRadius: "6px", outline: "none", fontFamily: "-apple-system,sans-serif" }} />
                                    </div>
                                  )}
                                  {hasActive && !isNumeric && values.length > 0 && (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "8px", paddingLeft: "20px", maxHeight: "100px", overflowY: "auto" }}>
                                      {values.map(v => {
                                        const checked = (cur.values || []).map(x => x.toLowerCase()).includes(v.toLowerCase());
                                        return (
                                          <button key={v}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const cv = cur.values || [];
                                              const lc = v.toLowerCase();
                                              const nextList = cv.some(x => x.toLowerCase() === lc) ? cv.filter(x => x.toLowerCase() !== lc) : [...cv, v];
                                              updateField(field, { values: nextList.length ? nextList : undefined });
                                            }}
                                            style={{
                                              padding: "3px 8px", fontSize: "11px", fontWeight: "600",
                                              border: "1px solid", borderColor: checked ? ORANGE : "#e0dcd4",
                                              borderRadius: "10px", cursor: "pointer",
                                              background: checked ? "rgba(200,56,3,0.08)" : "white",
                                              color: checked ? ORANGE : "#666", fontFamily: "-apple-system,sans-serif",
                                            }}>{v}</button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Entries */}
              {!isOpen ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <div style={{ fontSize: "32px", marginBottom: "12px" }}>📖</div>
                  <div style={{ fontSize: "16px", color: THEME.diarySubColor, fontFamily: "-apple-system,sans-serif", fontStyle: "italic", marginBottom: "20px" }}>{diary.name}</div>
                  <button onClick={() => setIsOpen(true)} style={{ background: ORANGE, color: "white", border: "none", padding: "12px 28px", borderRadius: "6px", fontSize: "15px", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>Open Diary</button>
                </div>
              ) : loading ? (
                <div style={{ padding: "40px", textAlign: "center", color: "#aaa", fontFamily: "-apple-system,sans-serif", fontStyle: "italic" }}>Loading...</div>
              ) : entries.length === 0 ? (
                <EmptyDiaryCover
                  diary={diary}
                  canEdit={canEdit}
                  onPickSuggestion={(title, tag) => {
                    setNewTitle(title || "");
                    setNewTags(tag || "");
                    setShowAdd(true);
                  }}
                  onAddEntry={() => setShowAdd(true)}
                  onImport={() => { setSettingsTab("import"); setShowSettings(true); }}
                />
              ) : filtered.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <p style={{ fontSize: "14px", color: THEME.diarySubColor, fontFamily: "-apple-system,sans-serif" }}>No entries match.</p>
                  <button onClick={clearAllFilters} style={{ background: "none", border: "none", color: ORANGE, cursor: "pointer", fontSize: "14px", fontFamily: "-apple-system,sans-serif" }}>Clear filters</button>
                </div>
              ) : (
                <div style={{ position: "relative", padding: "4px 16px 40px 16px" }}>

                  {/* Add button row — sits in the gap between the banners and
                      the first entry. Shows filter result count only when a
                      filter is active (otherwise the timeline visually conveys
                      the entry count via its dots). */}
                  {canEdit && !selectMode && filtered.length > 0 && viewMode !== "media" && (() => {
                    const isFiltered = activeFilterCount > 0 || activeLenses.size > 0 || search.trim();
                    // On desktop, the "+" lives inline in the search row, so
                    // this standalone row is only needed to show the entry
                    // count (when filters are active). On mobile, this row
                    // always shows the "+" since it doesn't fit in the
                    // search row on a narrow viewport.
                    if (!isNarrowViewport && !isFiltered) return null;
                    return (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px", paddingLeft: "8px", paddingRight: "0" }}>
                        <div style={{ fontSize: "12px", color: "#bbb", fontFamily: "-apple-system,sans-serif", fontStyle: "italic" }}>
                          {isFiltered ? `${filtered.length} of ${visibleEntries.length} ${visibleEntries.length === 1 ? "entry" : "entries"}` : ""}
                        </div>
                        {isNarrowViewport && (
                          <button onClick={() => setShowAdd(true)}
                            title="Add new entry"
                            style={{ width: "32px", height: "32px", borderRadius: "50%", background: "white", border: "1px solid #ddd", color: "#666", fontSize: "20px", fontWeight: 300, lineHeight: 1, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", flexShrink: 0 }}>+</button>
                        )}
                      </div>
                    );
                  })()}

                  {viewMode === "media" ? (
                    <MediaGrid entries={filtered} onOpenEntry={openEdit} compact={isCompactMode} />
                  ) : (
                  <div className="timeline-wrap" style={{ position: "relative", paddingLeft: "64px" }}>
                    {/* Timeline line — solid color instead of a percentage-
                        stop linear-gradient. The gradient was being
                        recomputed for the full height of the timeline on
                        every scroll frame in Edge, showing up as a green
                        flash in DevTools' paint-flashing view and a
                        visible rectangular repaint while scrolling. A
                        tall (thousands of px) element with a percentage-
                        stop gradient is one of the worst patterns for
                        Chromium-tile rasterization. The very-subtle
                        fade-in/fade-out at the ends was barely visible
                        anyway. */}
                    <div className="timeline-line" style={{ position: "absolute", left: "48px", top: "4px", bottom: "4px", width: "1px", background: "rgba(139,58,10,0.28)" }} />

                    {groupedEntries.map((group, gi) => (
                      <div key={group.key}>
                        <div style={{ position: "relative", paddingBottom: "16px", paddingTop: gi === 0 ? 0 : "12px" }}>
                          <div className="timeline-month" style={{ position: "absolute", left: "-64px", top: "0", width: "40px", textAlign: "right", fontFamily: "'Georgia', serif", fontSize: "9px", color: "rgba(139,58,10,0.55)", letterSpacing: "0.8px", textTransform: "uppercase", fontWeight: 600 }}>
                            {group.key}
                          </div>
                        </div>
                        {group.entries.map((e, ei) => {
                          const d = e.entryDate?.toDate ? e.entryDate.toDate() : (e.entryDate ? new Date(e.entryDate) : null);
                          const dayLabel = d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
                          const hasFollowup = (e.tags || []).some(isFollowupTag);
                          const isSelectedEntry = selectedIds.has(e.id);
                          const dotColor = hasFollowup ? "#C0392B" : ORANGE;
                          const isFirstDot = gi === 0 && ei === 0;
                          const showHintHere = isFirstDot && showTimelineHint && canEdit && !selectMode;
                          return (
                            <div key={e.id} style={{ position: "relative", paddingBottom: "14px" }}>
                              <div className="timeline-date" style={{ position: "absolute", left: "-60px", top: "12px", width: "36px", textAlign: "right", fontFamily: "'Georgia', serif", fontStyle: "italic", fontSize: "10px", color: ORANGE, whiteSpace: "nowrap" }}>
                                {dayLabel}
                              </div>
                              {/* Timeline dot — small by default, grows and turns navy when selected */}
                              {canEdit ? (
                                <button onClick={ev => { ev.stopPropagation(); toggleSelect(e.id); }}
                                  title={isSelectedEntry ? "Deselect" : "Select entry"}
                                  className={`timeline-dot${showHintHere ? " timeline-dot-pulse" : ""}`}
                                  style={{
                                    position: "absolute",
                                    left: isSelectedEntry ? "-24px" : "-20px",
                                    top: isSelectedEntry ? "10px" : "14px",
                                    width: isSelectedEntry ? "18px" : "10px",
                                    height: isSelectedEntry ? "18px" : "10px",
                                    borderRadius: "50%",
                                    background: isSelectedEntry ? NAVY : dotColor,
                                    border: "2px solid #f5f4f0",
                                    zIndex: 3,
                                    cursor: "pointer",
                                    padding: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "all 0.15s",
                                  }}>
                                  {isSelectedEntry && <span style={{ color: "white", fontSize: "11px", fontWeight: "700", lineHeight: 1 }}>✓</span>}
                                </button>
                              ) : (
                                <div className="timeline-dot" style={{ position: "absolute", left: "-20px", top: "14px", width: "10px", height: "10px", borderRadius: "50%", background: dotColor, border: "2px solid #f5f4f0", zIndex: 2 }} />
                              )}
                              {/* First-visit hint: a soft speech bubble whose tail
                                  points down-left at the pulsing dot. The tail is
                                  positioned to merge with the bubble's bottom-left
                                  corner so visually they read as one shape. */}
                              {showHintHere && (
                                <div style={{ position: "absolute", left: "-15px", top: "-42px", zIndex: 4, fontFamily: "-apple-system,sans-serif", pointerEvents: "auto" }}>
                                  <div style={{ position: "relative", background: "#3b5998", color: "white", fontSize: "11px", fontWeight: 500, padding: "7px 26px 7px 11px", borderRadius: "10px 10px 10px 0", whiteSpace: "nowrap", boxShadow: "0 2px 5px rgba(0,0,0,0.10)", lineHeight: 1.3 }}>
                                    Tap any dot to select an entry
                                    <button onClick={ev => { ev.stopPropagation(); dismissTimelineHint(); }}
                                      title="Dismiss"
                                      style={{ position: "absolute", top: "3px", right: "3px", background: "rgba(255,255,255,0.22)", border: "none", color: "white", fontSize: "10px", lineHeight: 1, width: "16px", height: "16px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>×</button>
                                    {/* Tail: a triangle that visually extends the
                                        bubble's bottom-left corner downward, so the
                                        whole shape reads as a speech bubble whose
                                        "mouth" is aligned with the dots column. */}
                                    <div style={{ position: "absolute", bottom: "-9px", left: "0px", width: 0, height: 0, borderTop: "10px solid #3b5998", borderRight: "10px solid transparent" }} />
                                  </div>
                                </div>
                              )}
                              {/* Entry card wrapper */}
                              <div style={{ background: hasFollowup ? "#fff5f5" : "white", border: `1px solid ${hasFollowup ? "rgba(192,57,43,0.25)" : "rgba(139,58,10,0.12)"}`, borderRadius: "6px", overflow: "hidden" }}>
                                {isCompactMode
                                  ? <ListEntry entry={e} diary={diary} canEdit={canEdit} onEdit={openEdit} isSelected={selectedIds.has(e.id)} selectMode={selectMode} onSelect={toggleSelect} onTagClick={jumpToTag} compact={true} />
                                  : <TimelineDetailEntry entry={e} diary={diary} canEdit={canEdit} onEdit={openEdit} isSelected={selectedIds.has(e.id)} selectMode={selectMode} onSelect={toggleSelect} onTagClick={jumpToTag} />
                                }
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  )}

                  <InsightsPanel entries={visibleEntries} activeLenses={activeLenses} />
                </div>
              )}

              {/* Pagination */}
              {isOpen && totalPages > 1 && filtered.length > 0 && (
                <div style={{ display: "flex", justifyContent: "center", gap: "10px", padding: "20px 16px", borderTop: "1px solid #e8e4d8" }}>
                  <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                    style={{ background: page === 0 ? "transparent" : ORANGE, border: `1px solid ${page === 0 ? "#ddd" : ORANGE}`, color: page === 0 ? "#ccc" : "white", padding: "6px 16px", borderRadius: "20px", cursor: page === 0 ? "default" : "pointer", fontSize: "13px" }}>← Prev</button>
                  <span style={{ fontSize: "13px", color: "#aaa", fontFamily: "-apple-system,sans-serif", alignSelf: "center" }}>{page + 1} / {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                    style={{ background: page >= totalPages - 1 ? "transparent" : ORANGE, border: `1px solid ${page >= totalPages - 1 ? "#ddd" : ORANGE}`, color: page >= totalPages - 1 ? "#ccc" : "white", padding: "6px 16px", borderRadius: "20px", cursor: page >= totalPages - 1 ? "default" : "pointer", fontSize: "13px" }}>Next →</button>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* ADD MODAL */}
      {showAdd && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", boxSizing: "border-box" }}>
          <div style={{ width: "100%", maxWidth: "600px", background: "white", borderRadius: "20px", display: "flex", flexDirection: "column", boxShadow: "0 10px 40px rgba(0,0,0,0.25)", maxHeight: "calc(100dvh - 40px)", overflow: "hidden" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid #f0ede8" }}>
              <button onClick={() => setShowAdd(false)} style={{ background: "none", border: "none", color: ORANGE, fontSize: "15px", fontWeight: "600", cursor: "pointer", fontFamily: "-apple-system,sans-serif", padding: 0 }}>Cancel</button>
              <span style={{ fontSize: "16px", fontWeight: "600", color: "#1a1a1a", fontFamily: "-apple-system,sans-serif" }}>New Entry</span>
              {(() => {
                const canSaveNew = !saving && (newTitle.trim().length > 0 || hasHtmlContent(newHtml) || newPhotos.length > 0);
                return (
                  <button onClick={handleAdd} disabled={!canSaveNew}
                    style={{ background: "none", border: "none", color: canSaveNew ? ORANGE : "#ccc", fontSize: "15px", fontWeight: "700", cursor: canSaveNew ? "pointer" : "default", fontFamily: "-apple-system,sans-serif", padding: 0 }}>
                    {saving ? "Saving..." : "Save"}
                  </button>
                );
              })()}
            </div>
            {/* Date row */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 20px", borderBottom: "1px solid #f0ede8" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ fontSize: "15px" }}>📅</span>
                <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
                  style={{ border: "none", outline: "none", fontSize: "14px", color: "#1a1a1a", fontFamily: "-apple-system,sans-serif", background: "transparent", cursor: "pointer" }} />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "14px", color: "#888", fontFamily: "-apple-system,sans-serif" }}>
                <span style={{ fontSize: "15px" }}>📎</span> Attach
                <input type="file" accept="image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,application/zip" multiple style={{ display: "none" }}
                  onChange={e => {
                    const picked = Array.from(e.target.files || []);
                    console.log("[LifeTrackr] Picker onChange fired. Files:", picked.length, picked.map(f => ({ name: f.name, type: f.type, size: f.size })));
                    if (picked.length === 0) {
                      setUploadState({ active: false, error: "No files were selected. On iOS, this sometimes happens with videos larger than a few seconds — try a shorter video, or email it to your diary instead." });
                      return;
                    }
                    // Free-tier photo cap: drop images if over the limit (keep
                    // videos/audio/docs), and surface the upgrade prompt.
                    const { kept, blockedPhotos } = filterForPhotoCap(picked);
                    if (blockedPhotos > 0 && window.__ltOpenUpgrade) window.__ltOpenUpgrade("photos");
                    if (kept.length) setNewPhotos(prev => [...prev, ...kept]);
                    // Defer reset so iOS fully commits the files first
                    setTimeout(() => { try { e.target.value = ""; } catch {} }, 100);
                  }} />
              </label>
            </div>
            {newPhotos.length > 0 && (
              <AttachmentPreviewStrip files={newPhotos} onRemove={i => setNewPhotos(prev => prev.filter((_, j) => j !== i))} classifyFile={classifyFile} />
            )}
            {/* Rich format toolbar */}
            <div style={{ display: "flex", gap: "2px", padding: "6px 12px", borderBottom: "1px solid #f0ede8", alignItems: "center", flexWrap: "wrap", background: "#fafafa" }}>
              {/* Bold, Italic, Underline */}
              {[["B","bold","700","normal","none"],["I","italic","400","italic","none"],["U","underline","400","normal","underline"]].map(([l,c,fw,fs,td]) => (
                <button key={c} onClick={() => { addEditorRef.current?.focus(); document.execCommand(c); }}
                  style={{ width: "30px", height: "28px", borderRadius: "5px", border: "none", background: "transparent", fontSize: "14px", cursor: "pointer", color: "#333", fontWeight: fw, fontStyle: fs, textDecoration: td, display: "flex", alignItems: "center", justifyContent: "center" }}>{l}</button>
              ))}
              <div style={{ width: "1px", height: "18px", background: "#e0dcd8", margin: "0 3px" }} />
              {/* Bullet list */}
              <button onClick={() => { addEditorRef.current?.focus(); document.execCommand("insertUnorderedList"); }}
                style={{ width: "30px", height: "28px", borderRadius: "5px", border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="2" cy="4" r="1.5" fill="#555"/><rect x="5" y="3" width="9" height="2" rx="1" fill="#555"/><circle cx="2" cy="8" r="1.5" fill="#555"/><rect x="5" y="7" width="9" height="2" rx="1" fill="#555"/><circle cx="2" cy="12" r="1.5" fill="#555"/><rect x="5" y="11" width="9" height="2" rx="1" fill="#555"/></svg>
              </button>
              {/* Numbered list */}
              <button onClick={() => { addEditorRef.current?.focus(); document.execCommand("insertOrderedList"); }}
                style={{ width: "30px", height: "28px", borderRadius: "5px", border: "none", background: "transparent", cursor: "pointer", fontSize: "13px", color: "#555", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><text x="0" y="5" fontSize="5" fill="#555" fontFamily="sans-serif">1.</text><rect x="6" y="3" width="8" height="2" rx="1" fill="#555"/><text x="0" y="9" fontSize="5" fill="#555" fontFamily="sans-serif">2.</text><rect x="6" y="7" width="8" height="2" rx="1" fill="#555"/><text x="0" y="13" fontSize="5" fill="#555" fontFamily="sans-serif">3.</text><rect x="6" y="11" width="8" height="2" rx="1" fill="#555"/></svg>
              </button>
              <div style={{ width: "1px", height: "18px", background: "#e0dcd8", margin: "0 3px" }} />
              {/* Font size */}
              <select onChange={e => { addEditorRef.current?.focus(); document.execCommand("fontSize", false, e.target.value); e.target.value = ""; }}
                defaultValue=""
                style={{ height: "28px", border: "1px solid #e0dcd8", borderRadius: "5px", fontSize: "12px", color: "#555", background: "white", padding: "0 4px", cursor: "pointer", outline: "none" }}>
                <option value="" disabled>Size</option>
                <option value="1">Small</option>
                <option value="3">Normal</option>
                <option value="5">Large</option>
                <option value="6">X-Large</option>
              </select>
              {/* Font color */}
              <label style={{ width: "30px", height: "28px", borderRadius: "5px", border: "1px solid #e0dcd8", background: "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", position: "relative" }}
                title="Font color">
                <span style={{ fontSize: "14px", fontWeight: "700", color: "#333" }}>A</span>
                <input type="color" defaultValue="#000000"
                  onChange={e => { addEditorRef.current?.focus(); document.execCommand("foreColor", false, e.target.value); }}
                  style={{ position: "absolute", opacity: 0, width: "100%", height: "100%", cursor: "pointer" }} />
              </label>
              <div style={{ width: "1px", height: "18px", background: "#e0dcd8", margin: "0 3px" }} />
              {/* Undo */}
              <button onClick={() => { addEditorRef.current?.focus(); document.execCommand("undo"); }}
                style={{ width: "30px", height: "28px", borderRadius: "5px", border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8C3 5.24 5.24 3 8 3c2.21 0 4.1 1.35 4.9 3.27" stroke="#555" strokeWidth="1.5" strokeLinecap="round"/><path d="M3 4v4h4" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              {/* Redo */}
              <button onClick={() => { addEditorRef.current?.focus(); document.execCommand("redo"); }}
                style={{ width: "30px", height: "28px", borderRadius: "5px", border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13 8C13 5.24 10.76 3 8 3c-2.21 0-4.1 1.35-4.9 3.27" stroke="#555" strokeWidth="1.5" strokeLinecap="round"/><path d="M13 4v4H9" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              {/* Clear formatting */}
              <button onClick={() => { addEditorRef.current?.focus(); document.execCommand("removeFormat"); }}
                style={{ width: "30px", height: "28px", borderRadius: "5px", border: "none", background: "transparent", cursor: "pointer", fontSize: "13px", color: "#aaa", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            </div>
            {/* Action row — Follow-up / Favorite / Share. Positioned right
                under the format toolbar so they're always reachable without
                scrolling past the body and tags. */}
            <div style={{ display: "flex", alignItems: "center", gap: "16px", padding: "8px 16px", borderBottom: "1px solid #f0ede8", background: "white" }}>
              <button onClick={() => setNewFollowup(f => !f)}
                data-tip="Flag this entry to come back to later"
                style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: "pointer", color: newFollowup ? ORANGE : "#888", padding: 0 }}>
                <span style={{ fontSize: "16px", filter: newFollowup ? "none" : "grayscale(1)", opacity: newFollowup ? 1 : 0.55 }}>🚩</span>
                <span style={{ fontSize: "12px", fontFamily: "-apple-system,sans-serif", fontWeight: newFollowup ? "700" : "500" }}>Follow-up</span>
              </button>
              <button onClick={() => setNewFavorite(f => !f)}
                data-tip="Star this entry as a favorite"
                style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: "pointer", color: newFavorite ? "#f5a623" : "#888", padding: 0 }}>
                <span style={{ fontSize: "16px" }}>{newFavorite ? "⭐" : "☆"}</span>
                <span style={{ fontSize: "12px", fontFamily: "-apple-system,sans-serif", fontWeight: newFavorite ? "700" : "500" }}>Favorite</span>
              </button>
              {/* Private toggle — when on, this entry is excluded from
                  share/export (Excel/CSV/image/share modal) and hidden
                  from collaborators on shared diaries. Owner still sees
                  it normally with a 🔒 indicator. Doesn't pretend to
                  be cryptographic — just a "don't leak this on share"
                  flag. */}
              <button onClick={() => setNewPrivate(p => !p)}
                data-tip="Keep this entry out of share/export. Hidden from collaborators."
                style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: "pointer", color: newPrivate ? "#5a3a82" : "#888", padding: 0 }}>
                <span style={{ fontSize: "16px", filter: newPrivate ? "none" : "grayscale(1)", opacity: newPrivate ? 1 : 0.55 }}>🔒</span>
                <span style={{ fontSize: "12px", fontFamily: "-apple-system,sans-serif", fontWeight: newPrivate ? "700" : "500" }}>Private</span>
              </button>
              <button onClick={() => {
                if (newPrivate) {
                  alert("This entry is marked Private. Turn off the Private toggle to share it.");
                  return;
                }
                const photoFiles = newPhotos.filter(f => f.type?.startsWith("image/"));
                const draft = {
                  title: newTitle,
                  bodyHtml: newHtml,
                  photos: photoFiles.map(f => ({ url: URL.createObjectURL(f), _file: f })),
                };
                setShareEntry(draft);
              }}
                data-tip={newPrivate ? "Private entries can't be shared. Turn off Private first." : "Share this entry as image, text, or link"}
                style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: newPrivate ? "not-allowed" : "pointer", color: "#888", padding: 0, opacity: newPrivate ? 0.4 : 1 }}>
                <span style={{ fontSize: "16px" }}>↗</span>
                <span style={{ fontSize: "12px", fontFamily: "-apple-system,sans-serif", fontWeight: "500" }}>Share</span>
              </button>
              {/* "Use current location" — prompts the browser for GPS,
                  calls the resolveCurrentLocation Cloud Function, and
                  fills in Location/City/Address chips. The button itself
                  doesn't do any geolocation client-side beyond getting
                  the lat/lng — all the Places API work happens server-
                  side so the API key isn't exposed. */}
              <button onClick={() => {
                if (!navigator.geolocation) {
                  alert("Your browser doesn't support geolocation.");
                  return;
                }
                setDroppingPin(true);
                navigator.geolocation.getCurrentPosition(
                  async (position) => {
                    try {
                      const { latitude, longitude } = position.coords;
                      const fn = httpsCallable(getFunctions(), "resolveCurrentLocation");
                      const result = await fn({ lat: latitude, lng: longitude });
                      const { place, city, address } = result.data || {};
                      // Merge into newStructured. Only fill empty fields —
                      // we don't want to silently overwrite a value the
                      // user already typed.
                      setNewStructured(prev => {
                        const next = { ...(prev || {}) };
                        if (place && (!next.location || !next.location.length)) {
                          next.location = [place];
                        }
                        if (city && !next.city) next.city = city;
                        if (address && !next.address) next.address = address;
                        return next;
                      });
                      if (!place && !city && !address) {
                        alert("Couldn't identify a place at your current location. Try typing it manually.");
                      }
                    } catch (err) {
                      console.error("resolveCurrentLocation failed", err);
                      alert("Couldn't resolve your location. Please try again or type it manually.");
                    } finally {
                      setDroppingPin(false);
                    }
                  },
                  (err) => {
                    setDroppingPin(false);
                    if (err.code === err.PERMISSION_DENIED) {
                      alert("Location permission denied. You can type a location manually instead.");
                    } else {
                      alert("Couldn't get your location. Please try again.");
                    }
                  },
                  { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
                );
              }}
                disabled={droppingPin}
                data-tip="Drop pin at current location"
                style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: droppingPin ? "wait" : "pointer", color: "#888", padding: 0, opacity: droppingPin ? 0.5 : 1 }}>
                {/* Pin emoji gets the same grayscale-when-inactive treatment
                    as the follow-up flag — otherwise its native red
                    competes visually with the muted toolbar palette.
                    The pin is never "on" the way follow-up/favorite can
                    be, so it's always grayscaled in this toolbar. */}
                <span style={{ fontSize: "16px", filter: "grayscale(1)", opacity: 0.55 }}>📍</span>
                <span style={{ fontSize: "12px", fontFamily: "-apple-system,sans-serif", fontWeight: "500" }}>{droppingPin ? "Locating…" : "Drop pin"}</span>
              </button>
            </div>
            {/* Writing area */}
            <div style={{ flex: 1, overflowY: "auto", padding: "12px 20px", minHeight: 0 }}>
              {/* Title input */}
              <input
                type="text"
                value={newTitle}
                onChange={e => {
                  const v = e.target.value;
                  setNewTitle(v);
                  // Sticky: any user keystroke counts as "manually touched"
                  // and the flag stays on for the rest of this modal session.
                  // Mirrors the edit-modal behavior — clearing the field
                  // doesn't re-engage the auto-generator. To get auto-gen
                  // back, close and reopen the modal.
                  setTitleManuallyTouched(true);
                }}
                onBlur={() => applyTitlePrefixToTags(newTitle, setNewTitle, newTags, setNewTags, setNewFollowup, newDate, setNewDate, setNewPrivate)}
                placeholder="Title (optional — auto-generated as you type)"
                style={{ width: "100%", border: "none", borderBottom: "1px solid #f0ede8", outline: "none", fontSize: "18px", fontWeight: "700", color: titleManuallyTouched ? "#0B1F4A" : "#5a6a8a", fontStyle: titleManuallyTouched ? "normal" : "italic", fontFamily: "-apple-system,sans-serif", padding: "0 0 10px 0", marginBottom: "12px", background: "transparent", boxSizing: "border-box" }}
              />
              <div ref={addEditorRef} contentEditable
                data-placeholder="Write your thoughts..."
                onPaste={e => {
                  // Image paste: capture clipboard images as attachments
                  // (NOT inline in body). User sees them in the attachment
                  // preview strip the same as if they'd clicked Attach.
                  // Runs BEFORE the text-strip branch so an image-only
                  // paste skips the no-op text path.
                  const images = extractClipboardImages(e.clipboardData);
                  if (images.length) {
                    e.preventDefault();
                    setNewPhotos(prev => [...prev, ...images]);
                    return;
                  }
                  // Strip formatting on paste — journal entries should look
                  // uniform regardless of paste source. Without this, pasted
                  // text retains styled <span> wrappers (different fonts,
                  // colors, sizes) that don't match the editor and can
                  // confuse parsers/extractors downstream.
                  e.preventDefault();
                  const plain = e.clipboardData?.getData("text/plain") || "";
                  if (!plain) return;
                  document.execCommand("insertText", false, plain);
                }}
                onInput={e => {
                  const target = e.currentTarget;
                  if (newInputTimerRef.current) clearTimeout(newInputTimerRef.current);
                  newInputTimerRef.current = setTimeout(() => {
                    if (!target) return;
                    const html = target.innerHTML;
                    // innerText preserves newlines between block elements
                    // (each <div>/<p>/<br> becomes a real \n). textContent
                    // would concatenate them and mangle subsequent #tag
                    // extraction (e.g. '#uhs\nall' → '#uhsall').
                    const text = target.innerText || target.textContent || "";
                    setNewHtml(html);
                    setNewBody(text);
                  }, 120);
                }}
                onBlur={e => {
                  if (newInputTimerRef.current) { clearTimeout(newInputTimerRef.current); newInputTimerRef.current = null; }
                  const html = e.currentTarget.innerHTML;
                  const text = e.currentTarget.innerText || e.currentTarget.textContent || "";
                  setNewHtml(html);
                  setNewBody(text);
                }}
                style={{ outline: "none", minHeight: "60px", fontSize: "16px", color: "#1a1a1a", fontFamily: "-apple-system,sans-serif", lineHeight: 1.7 }} />

              {/* Structured-fields icon row + chips. Tapping an icon opens
                  an inline input for that field. Filled fields show as
                  chips that can be tapped to edit/remove. */}
              <StructuredFieldsRow
                structured={newStructured}
                onChange={setNewStructured}
                editing={editingStructField}
                setEditing={setEditingStructField}
                draft={structFieldDraft}
                setDraft={setStructFieldDraft}
                hiddenBuiltins={hiddenBuiltins}
                enabledLibrary={enabledLibrary}
                allCustomFields={allCustomFields}
                onTouch={(key) => setTouchedStructFields(prev => {
                  if (prev.has(key)) return prev;
                  const next = new Set(prev);
                  next.add(key);
                  return next;
                })}
              />

              <TagPicker ref={newTagPickerRef} value={newTags} onChange={setNewTags} defaultOpen={true} diaryTags={myDiaryTags} bodyHtml={newHtml} setBodyHtml={setNewHtml}
                onDateOffset={off => {
                  // Apply a date shorthand (-1d, -1w, etc.) to newDate, which
                  // is YYYY-MM-DD. Keeps the user's flow uninterrupted: they
                  // can type "-1" in the tag box and the entry date moves
                  // back one day without leaving the picker.
                  setNewDate(applyOffsetToYmd(newDate, off));
                }} />
              {/* Visibility hint — appears only when this entry will end up
                  private to author+owner (i.e. no tags) AND the author
                  isn't the owner (owner sees everything anyway). Helps
                  team-diary members understand that a tagless entry won't
                  reach their teammates. */}
              {role !== "owner" && newTags.trim().length === 0 && (
                <div style={{ marginTop: "8px", padding: "8px 12px", background: "#fff8f0", border: "1px solid #f0d8b8", borderRadius: "6px", fontSize: "12px", color: "#8a5a2a", fontFamily: "-apple-system,sans-serif", lineHeight: 1.5 }}>
                  <strong>Just you and the owner will see this.</strong> Add tags above to share with teammates.
                </div>
              )}
            </div>
            {/* Save lives in the top-right header — bottom bar removed since
                Follow-up / Favorite / Share moved up under the toolbar. */}
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editEntry && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", boxSizing: "border-box" }}>
          <div style={{ width: "100%", maxWidth: "600px", background: "white", borderRadius: "20px", display: "flex", flexDirection: "column", boxShadow: "0 10px 40px rgba(0,0,0,0.25)", maxHeight: "calc(100dvh - 40px)", overflow: "hidden" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #f0ede8" }}>
              <button onClick={() => {
                if (editInputTimerRef.current) { clearTimeout(editInputTimerRef.current); editInputTimerRef.current = null; }
                setEditEntry(null);
              }} style={{ background: "none", border: "none", color: ORANGE, fontSize: "15px", fontWeight: "600", cursor: "pointer", fontFamily: "-apple-system,sans-serif", padding: 0 }}>Cancel</button>
              <span style={{ fontSize: "16px", fontWeight: "600", color: "#1a1a1a", fontFamily: "-apple-system,sans-serif" }}>Edit Entry</span>
              {(() => {
                const canSaveEdit = !updating && (editTitle.trim().length > 0 || hasHtmlContent(editHtml) || editPhotos.length > 0 || editExisting.length > 0 || editExistingVideos.length > 0 || editExistingAudio.length > 0 || editExistingDocs.length > 0);
                return (
                  <button onClick={handleUpdate} disabled={!canSaveEdit}
                    style={{ background: "none", border: "none", color: canSaveEdit ? ORANGE : "#ccc", fontSize: "15px", fontWeight: "700", cursor: canSaveEdit ? "pointer" : "default", fontFamily: "-apple-system,sans-serif", padding: 0 }}>
                    {updating ? "Saving..." : "Save"}
                  </button>
                );
              })()}
            </div>
            {/* Date row */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 20px", borderBottom: "1px solid #f0ede8" }}>
              {/* Click-target wrapper for the date control. We deliberately
                  drive the picker via input.showPicker() rather than relying
                  on the browser's auto-popup-on-input-click behavior, because
                  that auto-behavior is fragile here: clicks can be lost to
                  parent re-renders, focus shifts from the live-parse useEffects
                  that watch editBody/editTitle, or transient state updates
                  during the click. showPicker() is deterministic and immune
                  to those. We also stopPropagation on mousedown/click so any
                  ancestor handlers (none today, but defensive) can't dismiss
                  the picker by re-rendering or stealing focus.
                  Note: showPicker is supported in modern Chrome/Edge/Safari/
                  Firefox; on browsers where it's missing or rejects (e.g.
                  iOS Safari occasionally), we fall through silently and the
                  user can still tap the input directly to invoke the native
                  wheel picker. */}
              <div
                onMouseDown={e => e.stopPropagation()}
                onClick={e => {
                  e.stopPropagation();
                  const el = editDateInputRef.current;
                  if (!el) return;
                  if (typeof el.showPicker === "function") {
                    try { el.showPicker(); } catch { /* user-gesture or unsupported — ignore */ }
                  } else {
                    el.focus();
                  }
                }}
                style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", padding: "2px 0" }}>
                <span style={{ fontSize: "15px" }}>📅</span>
                <input
                  ref={editDateInputRef}
                  type="date"
                  value={editDate}
                  onChange={e => setEditDate(e.target.value)}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                  style={{ border: "none", outline: "none", fontSize: "14px", color: "#1a1a1a", fontFamily: "-apple-system,sans-serif", background: "transparent", cursor: "pointer" }} />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "14px", color: "#888", fontFamily: "-apple-system,sans-serif" }}>
                <span style={{ fontSize: "15px" }}>📎</span> Attach
                <input type="file" accept="image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,application/zip" multiple style={{ display: "none" }}
                  onChange={e => {
                    const picked = Array.from(e.target.files || []);
                    console.log("[LifeTrackr] Edit picker onChange. Files:", picked.length, picked.map(f => ({ name: f.name, type: f.type, size: f.size })));
                    if (picked.length === 0) {
                      setUploadState({ active: false, error: "No files were selected. On iOS, this sometimes happens with videos larger than a few seconds — try a shorter video, or email it to your diary instead." });
                      return;
                    }
                    // Free-tier photo cap: drop images if over the limit (keep
                    // videos/audio/docs), and surface the upgrade prompt.
                    const { kept, blockedPhotos } = filterForPhotoCap(picked);
                    if (blockedPhotos > 0 && window.__ltOpenUpgrade) window.__ltOpenUpgrade("photos");
                    if (kept.length) setEditPhotos(prev => [...prev, ...kept]);
                    setTimeout(() => { try { e.target.value = ""; } catch {} }, 100);
                  }} />
              </label>
            </div>
            {/* Existing media */}
            {(editExisting.length > 0 || editExistingVideos.length > 0 || editExistingAudio.length > 0 || editExistingDocs.length > 0) && (
              <div style={{ padding: "8px 20px", display: "flex", gap: "6px", flexWrap: "wrap", borderBottom: "1px solid #f0ede8" }}>
                {editExisting.map((p, i) => (
                  <div key={`img-${i}`} style={{ position: "relative" }}>
                    <img src={p.url} alt=""
                      onClick={() => openLightbox(editExisting, i)}
                      style={{ width: "56px", height: "56px", objectFit: "cover", borderRadius: "8px", border: "1px solid #ddd", cursor: "pointer" }} />
                    <button onClick={() => setEditExisting(prev => prev.filter((_,j) => j!==i))}
                      style={{ position: "absolute", top: "-5px", right: "-5px", width: "18px", height: "18px", borderRadius: "50%", background: ORANGE, border: "none", color: "white", fontSize: "11px", cursor: "pointer", lineHeight: 1 }}>×</button>
                  </div>
                ))}
                {editExistingVideos.map((v, i) => (
                  <div key={`vid-${i}`} style={{ position: "relative" }}>
                    <div onClick={() => openLightbox([v], 0)}
                      style={{ width: "56px", height: "56px", borderRadius: "8px", border: "1px solid #ddd", cursor: "pointer", background: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
                      <video src={v.url} muted playsInline preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.25)" }}>
                        <div style={{ width: "22px", height: "22px", borderRadius: "50%", background: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <div style={{ width: 0, height: 0, borderLeft: "7px solid #111", borderTop: "4px solid transparent", borderBottom: "4px solid transparent", marginLeft: "2px" }} />
                        </div>
                      </div>
                    </div>
                    <button onClick={() => setEditExistingVideos(prev => prev.filter((_,j) => j!==i))}
                      style={{ position: "absolute", top: "-5px", right: "-5px", width: "18px", height: "18px", borderRadius: "50%", background: ORANGE, border: "none", color: "white", fontSize: "11px", cursor: "pointer", lineHeight: 1 }}>×</button>
                  </div>
                ))}
                {editExistingAudio.map((a, i) => (
                  <div key={`aud-${i}`} style={{ position: "relative", display: "flex", alignItems: "center", gap: "6px", background: "#f5f3ef", border: "1px solid #e0dcd8", borderRadius: "8px", padding: "6px 10px 6px 8px", height: "42px" }}>
                    <span style={{ fontSize: "16px" }}>🎙️</span>
                    <audio src={a.url} controls style={{ height: "28px", maxWidth: "180px" }} />
                    <button onClick={() => setEditExistingAudio(prev => prev.filter((_,j) => j!==i))}
                      style={{ position: "absolute", top: "-5px", right: "-5px", width: "18px", height: "18px", borderRadius: "50%", background: ORANGE, border: "none", color: "white", fontSize: "11px", cursor: "pointer", lineHeight: 1 }}>×</button>
                  </div>
                ))}
                {editExistingDocs.map((d, i) => (
                  <div key={`doc-${i}`} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: "6px", background: "white", border: "1px solid #e0dcd8", borderRadius: "8px", padding: "6px 12px 6px 8px", maxWidth: "220px" }}>
                    <span style={{ fontSize: "18px", flexShrink: 0 }}>{docIcon(d)}</span>
                    <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "12px", color: NAVY, textDecoration: "none", fontFamily: "-apple-system,sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "150px" }} onClick={e => e.stopPropagation()}>{d.name}</a>
                    <button onClick={() => setEditExistingDocs(prev => prev.filter((_,j) => j!==i))}
                      style={{ position: "absolute", top: "-5px", right: "-5px", width: "18px", height: "18px", borderRadius: "50%", background: ORANGE, border: "none", color: "white", fontSize: "11px", cursor: "pointer", lineHeight: 1 }}>×</button>
                  </div>
                ))}
              </div>
            )}
            {/* Newly-picked attachment previews */}
            {editPhotos.length > 0 && (
              <AttachmentPreviewStrip files={editPhotos} onRemove={i => setEditPhotos(prev => prev.filter((_, j) => j !== i))} classifyFile={classifyFile} />
            )}
            {/* Rich format toolbar */}
            <div style={{ display: "flex", gap: "2px", padding: "6px 12px", borderBottom: "1px solid #f0ede8", alignItems: "center", flexWrap: "wrap", background: "#fafafa" }}>
              {/* Bold, Italic, Underline */}
              {[["B","bold","700","normal","none"],["I","italic","400","italic","none"],["U","underline","400","normal","underline"]].map(([l,c,fw,fs,td]) => (
                <button key={c} onClick={() => { editEditorRef.current?.focus(); document.execCommand(c); }}
                  style={{ width: "30px", height: "28px", borderRadius: "5px", border: "none", background: "transparent", fontSize: "14px", cursor: "pointer", color: "#333", fontWeight: fw, fontStyle: fs, textDecoration: td, display: "flex", alignItems: "center", justifyContent: "center" }}>{l}</button>
              ))}
              <div style={{ width: "1px", height: "18px", background: "#e0dcd8", margin: "0 3px" }} />
              {/* Bullet list */}
              <button onClick={() => { editEditorRef.current?.focus(); document.execCommand("insertUnorderedList"); }}
                style={{ width: "30px", height: "28px", borderRadius: "5px", border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="2" cy="4" r="1.5" fill="#555"/><rect x="5" y="3" width="9" height="2" rx="1" fill="#555"/><circle cx="2" cy="8" r="1.5" fill="#555"/><rect x="5" y="7" width="9" height="2" rx="1" fill="#555"/><circle cx="2" cy="12" r="1.5" fill="#555"/><rect x="5" y="11" width="9" height="2" rx="1" fill="#555"/></svg>
              </button>
              {/* Numbered list */}
              <button onClick={() => { editEditorRef.current?.focus(); document.execCommand("insertOrderedList"); }}
                style={{ width: "30px", height: "28px", borderRadius: "5px", border: "none", background: "transparent", cursor: "pointer", fontSize: "13px", color: "#555", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><text x="0" y="5" fontSize="5" fill="#555" fontFamily="sans-serif">1.</text><rect x="6" y="3" width="8" height="2" rx="1" fill="#555"/><text x="0" y="9" fontSize="5" fill="#555" fontFamily="sans-serif">2.</text><rect x="6" y="7" width="8" height="2" rx="1" fill="#555"/><text x="0" y="13" fontSize="5" fill="#555" fontFamily="sans-serif">3.</text><rect x="6" y="11" width="8" height="2" rx="1" fill="#555"/></svg>
              </button>
              <div style={{ width: "1px", height: "18px", background: "#e0dcd8", margin: "0 3px" }} />
              {/* Font size */}
              <select onChange={e => { editEditorRef.current?.focus(); document.execCommand("fontSize", false, e.target.value); e.target.value = ""; }}
                defaultValue=""
                style={{ height: "28px", border: "1px solid #e0dcd8", borderRadius: "5px", fontSize: "12px", color: "#555", background: "white", padding: "0 4px", cursor: "pointer", outline: "none" }}>
                <option value="" disabled>Size</option>
                <option value="1">Small</option>
                <option value="3">Normal</option>
                <option value="5">Large</option>
                <option value="6">X-Large</option>
              </select>
              {/* Font color */}
              <label style={{ width: "30px", height: "28px", borderRadius: "5px", border: "1px solid #e0dcd8", background: "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", position: "relative" }}
                title="Font color">
                <span style={{ fontSize: "14px", fontWeight: "700", color: "#333" }}>A</span>
                <input type="color" defaultValue="#000000"
                  onChange={e => { editEditorRef.current?.focus(); document.execCommand("foreColor", false, e.target.value); }}
                  style={{ position: "absolute", opacity: 0, width: "100%", height: "100%", cursor: "pointer" }} />
              </label>
              <div style={{ width: "1px", height: "18px", background: "#e0dcd8", margin: "0 3px" }} />
              {/* Undo */}
              <button onClick={() => { editEditorRef.current?.focus(); document.execCommand("undo"); }}
                style={{ width: "30px", height: "28px", borderRadius: "5px", border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8C3 5.24 5.24 3 8 3c2.21 0 4.1 1.35 4.9 3.27" stroke="#555" strokeWidth="1.5" strokeLinecap="round"/><path d="M3 4v4h4" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              {/* Redo */}
              <button onClick={() => { editEditorRef.current?.focus(); document.execCommand("redo"); }}
                style={{ width: "30px", height: "28px", borderRadius: "5px", border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13 8C13 5.24 10.76 3 8 3c-2.21 0-4.1 1.35-4.9 3.27" stroke="#555" strokeWidth="1.5" strokeLinecap="round"/><path d="M13 4v4H9" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              {/* Clear formatting */}
              <button onClick={() => { editEditorRef.current?.focus(); document.execCommand("removeFormat"); }}
                style={{ width: "30px", height: "28px", borderRadius: "5px", border: "none", background: "transparent", cursor: "pointer", fontSize: "13px", color: "#aaa", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            </div>
            {/* Action row — Follow-up / Favorite / Share / Delete. See
                comment in new-entry modal above. */}
            <div style={{ display: "flex", alignItems: "center", gap: "16px", padding: "8px 16px", borderBottom: "1px solid #f0ede8", background: "white" }}>
              <button onClick={() => setEditFollowup(f => !f)}
                data-tip="Flag this entry to come back to later"
                style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: "pointer", color: editFollowup ? ORANGE : "#888", padding: 0 }}>
                <span style={{ fontSize: "16px", filter: editFollowup ? "none" : "grayscale(1)", opacity: editFollowup ? 1 : 0.55 }}>🚩</span>
                <span style={{ fontSize: "12px", fontFamily: "-apple-system,sans-serif", fontWeight: editFollowup ? "700" : "500" }}>Follow-up</span>
              </button>
              <button onClick={() => setEditFavorite(f => !f)}
                data-tip="Star this entry as a favorite"
                style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: "pointer", color: editFavorite ? "#f5a623" : "#888", padding: 0 }}>
                <span style={{ fontSize: "16px" }}>{editFavorite ? "⭐" : "☆"}</span>
                <span style={{ fontSize: "12px", fontFamily: "-apple-system,sans-serif", fontWeight: editFavorite ? "700" : "500" }}>Favorite</span>
              </button>
              <button onClick={() => setEditPrivate(p => !p)}
                data-tip="Keep this entry out of share/export. Hidden from collaborators."
                style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: "pointer", color: editPrivate ? "#5a3a82" : "#888", padding: 0 }}>
                <span style={{ fontSize: "16px", filter: editPrivate ? "none" : "grayscale(1)", opacity: editPrivate ? 1 : 0.55 }}>🔒</span>
                <span style={{ fontSize: "12px", fontFamily: "-apple-system,sans-serif", fontWeight: editPrivate ? "700" : "500" }}>Private</span>
              </button>
              <button onClick={() => {
                  if (editPrivate) {
                    alert("This entry is marked Private. Turn off the Private toggle to share it.");
                    return;
                  }
                  setShareEntry(editEntry);
                }}
                data-tip={editPrivate ? "Private entries can't be shared. Turn off Private first." : "Share this entry as image, text, or link"}
                style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: editPrivate ? "not-allowed" : "pointer", color: "#888", padding: 0, opacity: editPrivate ? 0.4 : 1 }}>
                <span style={{ fontSize: "16px" }}>↗</span>
                <span style={{ fontSize: "12px", fontFamily: "-apple-system,sans-serif", fontWeight: "500" }}>Share</span>
              </button>
              {/* "Use current location" — same behavior as in new-entry
                  modal: prompts browser geolocation, calls Cloud Function,
                  fills empty Location/City/Address fields. */}
              <button onClick={() => {
                if (!navigator.geolocation) {
                  alert("Your browser doesn't support geolocation.");
                  return;
                }
                setDroppingPin(true);
                navigator.geolocation.getCurrentPosition(
                  async (position) => {
                    try {
                      const { latitude, longitude } = position.coords;
                      const fn = httpsCallable(getFunctions(), "resolveCurrentLocation");
                      const result = await fn({ lat: latitude, lng: longitude });
                      const { place, city, address } = result.data || {};
                      setEditStructured(prev => {
                        const next = { ...(prev || {}) };
                        if (place && (!next.location || !next.location.length)) {
                          next.location = [place];
                        }
                        if (city && !next.city) next.city = city;
                        if (address && !next.address) next.address = address;
                        return next;
                      });
                      if (!place && !city && !address) {
                        alert("Couldn't identify a place at your current location. Try typing it manually.");
                      }
                    } catch (err) {
                      console.error("resolveCurrentLocation failed", err);
                      alert("Couldn't resolve your location. Please try again or type it manually.");
                    } finally {
                      setDroppingPin(false);
                    }
                  },
                  (err) => {
                    setDroppingPin(false);
                    if (err.code === err.PERMISSION_DENIED) {
                      alert("Location permission denied. You can type a location manually instead.");
                    } else {
                      alert("Couldn't get your location. Please try again.");
                    }
                  },
                  { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
                );
              }}
                disabled={droppingPin}
                data-tip="Drop pin at current location"
                style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: droppingPin ? "wait" : "pointer", color: "#888", padding: 0, opacity: droppingPin ? 0.5 : 1 }}>
                <span style={{ fontSize: "16px", filter: "grayscale(1)", opacity: 0.55 }}>📍</span>
                <span style={{ fontSize: "12px", fontFamily: "-apple-system,sans-serif", fontWeight: "500" }}>{droppingPin ? "Locating…" : "Drop pin"}</span>
              </button>
              {/* Delete still lives in the bottom action bar — destructive
                  action; we don't want it next to the everyday toggles. */}
            </div>
            {/* Writing area */}
            <div style={{ flex: 1, overflowY: "auto", padding: "12px 20px", minHeight: 0 }}>
              {/* Title input. Once the user has typed (or cleared) anything,
                  we flip manuallyTouched=true and never flip it back. Going
                  back to false would re-engage the auto-generator and
                  silently fill in the field as soon as the user clears it
                  — surprising and breaks "I'm trying to type my own". */}
              <input
                type="text"
                value={editTitle}
                onChange={e => {
                  const v = e.target.value;
                  setEditTitle(v);
                  // Sticky: any user keystroke counts as "manually touched"
                  // and the flag stays on for the rest of this edit session.
                  setEditTitleManuallyTouched(true);
                }}
                onBlur={() => applyTitlePrefixToTags(editTitle, setEditTitle, editTags, setEditTags, setEditFollowup, editDate, setEditDate, setEditPrivate)}
                placeholder="Title (optional — auto-generated as you type)"
                style={{ width: "100%", border: "none", borderBottom: "1px solid #f0ede8", outline: "none", fontSize: "18px", fontWeight: "700", color: editTitleManuallyTouched ? "#0B1F4A" : "#5a6a8a", fontStyle: editTitleManuallyTouched ? "normal" : "italic", fontFamily: "-apple-system,sans-serif", padding: "0 0 10px 0", marginBottom: "12px", background: "transparent", boxSizing: "border-box" }}
              />
              <div ref={editEditorRef} contentEditable
                data-placeholder="Write your thoughts..."
                onPaste={e => {
                  // Image paste — see new-entry editor's onPaste for details.
                  const images = extractClipboardImages(e.clipboardData);
                  if (images.length) {
                    e.preventDefault();
                    setEditPhotos(prev => [...prev, ...images]);
                    return;
                  }
                  // Strip formatting on paste — see new-entry editor comment
                  e.preventDefault();
                  const plain = e.clipboardData?.getData("text/plain") || "";
                  if (!plain) return;
                  document.execCommand("insertText", false, plain);
                }}
                onInput={e => {
                  const target = e.currentTarget;
                  if (editInputTimerRef.current) clearTimeout(editInputTimerRef.current);
                  editInputTimerRef.current = setTimeout(() => {
                    if (!target) return;
                    const html = target.innerHTML;
                    // innerText preserves block-element newlines — see new-entry comment
                    const text = target.innerText || target.textContent || "";
                    setEditHtml(html);
                    setEditBody(text);
                  }, 120);
                }}
                onBlur={e => {
                  // Flush any pending debounced update on blur so Save reads the latest state
                  if (editInputTimerRef.current) { clearTimeout(editInputTimerRef.current); editInputTimerRef.current = null; }
                  const html = e.currentTarget.innerHTML;
                  const text = e.currentTarget.innerText || e.currentTarget.textContent || "";
                  setEditHtml(html);
                  setEditBody(text);
                }}
                style={{ outline: "none", minHeight: "60px", fontSize: "16px", color: "#1a1a1a", fontFamily: "-apple-system,sans-serif", lineHeight: 1.7 }} />

              <StructuredFieldsRow
                structured={editStructured}
                onChange={setEditStructured}
                editing={editingEditStructField}
                setEditing={setEditingEditStructField}
                draft={editStructFieldDraft}
                setDraft={setEditStructFieldDraft}
                hiddenBuiltins={hiddenBuiltins}
                enabledLibrary={enabledLibrary}
                allCustomFields={allCustomFields}
                onTouch={(key) => setEditTouchedStructFields(prev => {
                  if (prev.has(key)) return prev;
                  const next = new Set(prev);
                  next.add(key);
                  return next;
                })}
              />

              <TagPicker ref={editTagPickerRef} value={editTags} onChange={setEditTags} diaryTags={myDiaryTags} bodyHtml={editHtml} setBodyHtml={setEditHtml}
                onDateOffset={off => {
                  setEditDate(applyOffsetToYmd(editDate, off));
                }} />
              {/* Visibility hint — see comment in new-entry modal above */}
              {role !== "owner" && editTags.trim().length === 0 && (
                <div style={{ marginTop: "8px", padding: "8px 12px", background: "#fff8f0", border: "1px solid #f0d8b8", borderRadius: "6px", fontSize: "12px", color: "#8a5a2a", fontFamily: "-apple-system,sans-serif", lineHeight: 1.5 }}>
                  <strong>Just you and the owner will see this.</strong> Add tags above to share with teammates.
                </div>
              )}
            </div>
            {/* Bottom action bar — Delete only. Other actions (Follow-up,
                Favorite, Share) moved up under the toolbar. Delete stays
                here because it's destructive and we don't want it next to
                the everyday toggles where it could be tapped by accident. */}
            <div style={{ borderTop: "1px solid #f0ede8", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", flexShrink: 0, background: "white" }}>
              <button onClick={() => setConfirmDel(true)}
                style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: "pointer", color: "#c0392b", padding: "4px 8px", borderRadius: "5px" }}>
                <span style={{ fontSize: "16px" }}>🗑️</span>
                <span style={{ fontSize: "13px", fontFamily: "-apple-system,sans-serif", fontWeight: 600 }}>Delete</span>
              </button>
            </div>
            {/* Delete confirmation */}
            {confirmDel && (
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "20px 20px 0 0", zIndex: 10 }}>
                <div style={{ background: "white", borderRadius: "14px", padding: "24px", margin: "20px", textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.15)" }}>
                  <div style={{ fontSize: "16px", fontWeight: "600", color: "#1a1a1a", marginBottom: "8px", fontFamily: "-apple-system,sans-serif" }}>Delete this entry?</div>
                  <div style={{ fontSize: "13px", color: "#888", marginBottom: "20px", fontFamily: "-apple-system,sans-serif" }}>This cannot be undone.</div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button onClick={() => setConfirmDel(false)} style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "1px solid #ddd", background: "white", color: "#555", fontSize: "15px", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>Cancel</button>
                    <button onClick={handleDelete} style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "none", background: "#e85858", color: "white", fontSize: "15px", fontWeight: "700", cursor: "pointer", opacity: deleting ? 0.6 : 1, fontFamily: "-apple-system,sans-serif" }}>{deleting ? "Deleting..." : "Delete"}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
            {/* GMAIL-STYLE SELECT TOOLBAR — fixed, no layout impact */}
      {selectMode && (
        <div style={{
          position: "fixed", top: "200px", left: 0, right: 0, zIndex: 50,
          display: "flex", justifyContent: "center", pointerEvents: "none",
          padding: "0 12px",
        }}>
          <div style={{
            background: "white", borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
            padding: "8px 14px", display: "flex", alignItems: "center",
            gap: "4px", pointerEvents: "all", maxWidth: "min(756px,97vw)", width: "100%",
            border: `1px solid rgba(200,56,3,0.2)`, borderLeft: `3px solid ${ORANGE}`
          }}>
            {/* Selected count */}
            <span style={{ fontSize: "13px", color: ORANGE, fontWeight: "600", fontFamily: "-apple-system,sans-serif", marginRight: "8px", minWidth: "80px" }}>
              {selectedIds.size} selected
            </span>
            <div style={{ width: "1px", height: "20px", background: "#e0dcd8", margin: "0 4px" }} />
            {/* Select all / Deselect all */}
            <button title={selectedIds.size === filtered.length ? "Deselect all" : "Select all"}
              onClick={selectedIds.size === filtered.length ? () => setSelectedIds(new Set()) : selectAll}
              style={{ width: "36px", height: "36px", borderRadius: "6px", border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#555", position: "relative" }}
              onMouseEnter={isTouchDevice() ? undefined : e => { e.currentTarget.style.background = "#f5f3ef"; e.currentTarget.querySelector(".tip").style.display = "block"; }}
              onMouseLeave={isTouchDevice() ? undefined : e => { e.currentTarget.style.background = "transparent"; e.currentTarget.querySelector(".tip").style.display = "none"; }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                {selectedIds.size === filtered.length
                  ? <><rect x="2" y="2" width="14" height="14" rx="2" fill={ORANGE}/><path d="M5 9l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round"/></>
                  : <rect x="2" y="2" width="14" height="14" rx="2" stroke="#555" strokeWidth="1.5" fill="none"/>
                }
              </svg>
              <span className="tip" style={{ display: "none", position: "absolute", bottom: "42px", left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.75)", color: "white", fontSize: "11px", padding: "4px 8px", borderRadius: "4px", whiteSpace: "nowrap", fontFamily: "-apple-system,sans-serif" }}>
                {selectedIds.size === filtered.length ? "Deselect all" : "Select all"}
              </span>
            </button>
            {/* Delete (with confirmation) */}
            <button title="Delete selected"
              onClick={e => {
                e.stopPropagation();
                console.log("[BulkDelete] click, selected:", selectedIds.size);
                if (selectedIds.size === 0) return;
                const ok = window.confirm(`Delete ${selectedIds.size} ${selectedIds.size === 1 ? "entry" : "entries"}? This cannot be undone.`);
                if (ok) handleBulkDelete();
              }} disabled={selectedIds.size === 0 || bulkDeleting}
              style={{ width: "36px", height: "36px", borderRadius: "6px", border: "none", background: "transparent", cursor: selectedIds.size > 0 ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", color: selectedIds.size > 0 ? "#555" : "#ccc", position: "relative" }}
              onMouseEnter={isTouchDevice() ? undefined : e => { if(selectedIds.size > 0){e.currentTarget.style.background="#f5f3ef"; e.currentTarget.querySelector(".tip").style.display="block";} }}
              onMouseLeave={isTouchDevice() ? undefined : e => { e.currentTarget.style.background="transparent"; if(e.currentTarget.querySelector(".tip")) e.currentTarget.querySelector(".tip").style.display="none"; }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M2.5 5h13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M6.5 5V3.5a1 1 0 011-1h3a1 1 0 011 1V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <rect x="4" y="5" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M7 8v4M11 8v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span className="tip" style={{ display: "none", position: "absolute", bottom: "42px", left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.75)", color: "white", fontSize: "11px", padding: "4px 8px", borderRadius: "4px", whiteSpace: "nowrap", fontFamily: "-apple-system,sans-serif" }}>Delete</span>
            </button>
            {/* Share — opens menu with CSV / PDF / Email / Copy options */}
            <div style={{ position: "relative" }}>
              <button title="Share or export selected"
                onClick={() => setBulkShareMenuOpen(o => !o)}
                disabled={selectedIds.size === 0 || bulkBuilding}
                style={{ width: "36px", height: "36px", borderRadius: "6px", border: "none", background: bulkShareMenuOpen ? "#f5f3ef" : "transparent", cursor: selectedIds.size > 0 ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", color: selectedIds.size > 0 ? "#555" : "#ccc", position: "relative" }}
                onMouseEnter={isTouchDevice() ? undefined : e => { if(selectedIds.size > 0 && !bulkShareMenuOpen){ e.currentTarget.style.background="#f5f3ef"; e.currentTarget.querySelector(".tip").style.display="block"; } }}
                onMouseLeave={isTouchDevice() ? undefined : e => { if(!bulkShareMenuOpen) e.currentTarget.style.background="transparent"; if(e.currentTarget.querySelector(".tip")) e.currentTarget.querySelector(".tip").style.display="none"; }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M3 9v6a1 1 0 001 1h10a1 1 0 001-1V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M9 2v8M6 5l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="tip" style={{ display: "none", position: "absolute", bottom: "42px", left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.75)", color: "white", fontSize: "11px", padding: "4px 8px", borderRadius: "4px", whiteSpace: "nowrap", fontFamily: "-apple-system,sans-serif" }}>Share / export</span>
              </button>
              {bulkShareMenuOpen && (
                <>
                  {/* Click-away catcher */}
                  <div onClick={() => setBulkShareMenuOpen(false)}
                    style={{ position: "fixed", inset: 0, zIndex: 60 }} />
                  <div style={{ position: "absolute", top: "42px", left: 0, zIndex: 61, background: "white", border: "1px solid #e0dcd8", borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", minWidth: "210px", overflow: "hidden", fontFamily: "-apple-system,sans-serif" }}>
                    {[
                      { label: "Download Excel", icon: "📗", onClick: bulkDownloadXlsx, hint: `${selectedIds.size} ${selectedIds.size === 1 ? "entry" : "entries"}` },
                      { label: "Download Expenses Excel", icon: "📗", onClick: bulkDownloadExpensesXlsx, hint: "parsed format" },
                      { label: "Download CSV", icon: "📊", onClick: bulkDownloadCsv, hint: `${selectedIds.size} ${selectedIds.size === 1 ? "entry" : "entries"}` },
                      { label: "Download Expenses CSV", icon: "💰", onClick: bulkDownloadExpensesCsv, hint: "parsed format" },
                      { label: "Download PDF", icon: "📄", onClick: bulkDownloadPdf, hint: bulkBuilding ? "Building…" : null },
                      { label: "Download Word", icon: "📝", onClick: bulkDownloadDocx, hint: bulkBuilding ? "Building…" : null },
                      { label: "Email (PDF attached)", icon: "✉️", onClick: bulkEmailPdf, hint: bulkBuilding ? "Building…" : null },
                      { label: "Copy as text", icon: "📋", onClick: bulkCopyText },
                    ].map((item, i, arr) => (
                      <button key={i} onClick={item.onClick} disabled={bulkBuilding}
                        style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", background: "transparent", border: "none", cursor: bulkBuilding ? "default" : "pointer", textAlign: "left", borderBottom: i < arr.length - 1 ? "1px solid #f0ede8" : "none", color: "#222", fontSize: "13px", fontFamily: "-apple-system,sans-serif" }}
                        onMouseEnter={e => { if(!bulkBuilding) e.currentTarget.style.background = "#faf8f3"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                        <span style={{ fontSize: "15px" }}>{item.icon}</span>
                        <span style={{ flex: 1 }}>{item.label}</span>
                        {item.hint && <span style={{ fontSize: "11px", color: "#aaa", fontStyle: "italic" }}>{item.hint}</span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            {/* Assign tag */}
            <button title="Assign tag"
              onClick={() => setShowAssignTag(true)} disabled={selectedIds.size === 0}
              style={{ width: "36px", height: "36px", borderRadius: "6px", border: "none", background: "transparent", cursor: selectedIds.size > 0 ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", color: selectedIds.size > 0 ? "#555" : "#ccc", position: "relative" }}
              onMouseEnter={isTouchDevice() ? undefined : e => { if(selectedIds.size > 0){e.currentTarget.style.background="#f5f3ef"; e.currentTarget.querySelector(".tip").style.display="block";} }}
              onMouseLeave={isTouchDevice() ? undefined : e => { e.currentTarget.style.background="transparent"; if(e.currentTarget.querySelector(".tip")) e.currentTarget.querySelector(".tip").style.display="none"; }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M2 2h6l8 8-6 6-8-8V2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                <circle cx="6" cy="6" r="1.5" fill="currentColor"/>
              </svg>
              <span className="tip" style={{ display: "none", position: "absolute", bottom: "42px", left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.75)", color: "white", fontSize: "11px", padding: "4px 8px", borderRadius: "4px", whiteSpace: "nowrap", fontFamily: "-apple-system,sans-serif" }}>Assign tag</span>
            </button>
            <div style={{ flex: 1 }} />
            {/* Cancel / close */}
            <button onClick={clearSelect}
              style={{ width: "36px", height: "36px", borderRadius: "6px", border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#888", position: "relative" }}
              onMouseEnter={e => { e.currentTarget.style.background="#f5f3ef"; }}
              onMouseLeave={e => { e.currentTarget.style.background="transparent"; }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 3l10 10M13 3L3 13" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* UPLOAD PROGRESS / ERROR OVERLAY */}
      <UploadProgressOverlay state={uploadState} onDismiss={() => setUploadState({ active: false })} />

      {/* LIGHTBOX — full-screen photo viewer, stacks above all modals */}
      {lightbox && (
        <Lightbox photos={lightbox.photos} startIndex={lightbox.index} onClose={closeLightbox} />
      )}

      {/* ASSIGN TAG MODAL */}
      {showAssignTag && <AssignTagModal
        selectedIds={selectedIds}
        entries={entries}
        diary={diary}
        setDiary={setDiary}
        onClose={() => setShowAssignTag(false)}
        onDone={() => { setShowAssignTag(false); clearSelect(); }}
      />}

      {/* SETTINGS MODAL */}
      {showSettings && (
        <Modal onClose={() => setShowSettings(false)} maxWidth="760px">
          {/* Done button — top right, standalone row, so the tab strip below
              has the full modal width to fit all tabs without clipping. */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
            <button onClick={() => setShowSettings(false)}
              style={{ background: ORANGE, color: "white", border: "none", padding: "6px 16px", borderRadius: "6px", fontSize: "13px", fontWeight: 700, cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
              Done
            </button>
          </div>
          <div style={{ display: "flex", gap: 0, marginBottom: "24px", borderBottom: "1px solid #eee", alignItems: "center" }}>
            <div style={{ display: "flex", flexWrap: "wrap", flex: 1, minWidth: 0, rowGap: "2px" }}>
              {(() => {
                const tabs = [
                  { id: "info",    emoji: "ℹ️",  label: "Info" },
                  { id: "members", emoji: "👥", label: "Members" },
                  { id: "cover",   emoji: "📔", label: "Cover" },
                  { id: "pages",   emoji: "📄", label: "Pages" },
                  { id: "tags",    emoji: "🏷️", label: "Tags" },
                  ...(canEdit ? [{ id: "groups", emoji: "🗂️", label: "Groups" }] : []),
                  ...(canEdit ? [{ id: "fields", emoji: "🧩", label: "Fields" }] : []),
                  ...(canEdit ? [{ id: "import", emoji: "📥", label: "Import" }] : []),
                  ...(role === "owner" ? [{ id: "privacy", emoji: "🔒", label: "Privacy" }] : []),
                ];
                // The strip wraps to a second row when tabs don't fit (see
                // flexWrap on the container), so every tab is visible at any
                // width — no horizontal scrolling or clipping. We still drop
                // non-selected labels to emoji on very narrow viewports to
                // keep the wrapped rows compact on phones.
                return tabs.map(t => {
                  const isSelected = settingsTab === t.id;
                  const showLabel = !isNarrowViewport || isSelected;
                  return (
                    <button key={t.id} onClick={() => setSettingsTab(t.id)}
                      title={t.label}
                      aria-label={t.label}
                      style={{ flexShrink: 0, background: "transparent", border: "none", borderBottom: isSelected ? `2px solid ${ORANGE}` : "2px solid transparent", color: isSelected ? ORANGE : "#888", padding: "8px 16px", cursor: "pointer", fontSize: "14px", fontFamily: "-apple-system,sans-serif", fontWeight: isSelected ? "700" : "400", marginBottom: "-1px" }}>
                      {showLabel ? `${t.emoji} ${t.label}` : t.emoji}
                    </button>
                  );
                });
              })()}
            </div>
          </div>
          {settingsTab === "info" && (
            <>
              <h2 style={{ fontSize: "20px", fontWeight: "700", margin: "0 0 20px", color: NAVY, fontFamily: "-apple-system,sans-serif" }}>{diary.name}</h2>

              {/* Examples-first callout. Most users opening this tab want to
                  remember "how do I log an entry by email" — not a reference
                  manual. Show a worked example up top, then keep the
                  detailed rows below for users digging deeper. */}
              <div style={{ marginBottom: "24px", padding: "16px 18px", border: "1px solid #f0e3d8", borderRadius: "10px", background: "#fffaf3" }}>
                <div style={{ fontSize: "15px", fontWeight: "700", color: NAVY, fontFamily: "-apple-system,sans-serif", marginBottom: "8px" }}>
                  Log entries by email — it's easy
                </div>
                <div style={{ fontSize: "13px", color: "#555", lineHeight: 1.6, fontFamily: "-apple-system,sans-serif", marginBottom: "12px" }}>
                  Send a message to <CopyEmail
                    email={diary.inboundEmail}
                    diaryName={diary.name}
                    accentColor={ORANGE}
                    compact
                    style={{ background: "#f3efe7", padding: "1px 6px", borderRadius: "3px", fontSize: "12px", fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace", color: "#333" }}
                    onCopied={(ok) => showToast(ok ? "Logging address copied." : "Couldn't copy — select and copy manually.")}
                  /> from your registered address. The subject becomes the title, and tags go in parens at the start.
                </div>

                {/* Example 1 — basic tags */}
                <div style={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "10px", marginBottom: "4px", fontFamily: "-apple-system,sans-serif" }}>
                  Subject
                </div>
                <div style={{ fontSize: "13px", fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace", background: "white", border: "1px solid #ece4d8", borderRadius: "6px", padding: "8px 10px", color: "#333" }}>
                  (expense, fitness) Bought some kickbutt running shoes today
                </div>
                <div style={{ fontSize: "12px", color: "#555", lineHeight: 1.6, fontFamily: "-apple-system,sans-serif", marginTop: "8px" }}>
                  → creates an entry titled <em>Bought some kickbutt running shoes today</em> with tags
                  {" "}<span style={{ background: "#fff", border: `1px solid ${ORANGE}40`, color: ORANGE, padding: "1px 7px", borderRadius: "10px", fontSize: "11px", fontWeight: 600 }}>#expense</span>
                  {" "}<span style={{ background: "#fff", border: `1px solid ${ORANGE}40`, color: ORANGE, padding: "1px 7px", borderRadius: "10px", fontSize: "11px", fontWeight: 600 }}>#fitness</span>.
                </div>

                {/* Example 2 — combined: tags, follow-up, backdating */}
                <div style={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "14px", marginBottom: "4px", fontFamily: "-apple-system,sans-serif" }}>
                  Or combine tags with follow-up and backdating
                </div>
                <div style={{ fontSize: "13px", fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace", background: "white", border: "1px solid #ece4d8", borderRadius: "6px", padding: "8px 10px", color: "#333" }}>
                  (rent, !, -1d) bumped pricing by 650
                </div>
                <div style={{ fontSize: "12px", color: "#555", lineHeight: 1.6, fontFamily: "-apple-system,sans-serif", marginTop: "8px" }}>
                  → tagged <span style={{ background: "#fff", border: `1px solid ${ORANGE}40`, color: ORANGE, padding: "1px 7px", borderRadius: "10px", fontSize: "11px", fontWeight: 600 }}>#rent</span>,
                  {" "}flagged for follow-up, dated yesterday. The <code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "12px" }}>!</code> means follow-up;
                  {" "}<code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "12px" }}>-1d</code> means one day ago.
                </div>

                <div style={{ fontSize: "12px", color: "#888", lineHeight: 1.6, fontFamily: "-apple-system,sans-serif", marginTop: "14px", paddingTop: "12px", borderTop: "1px solid #f0e3d8" }}>
                  Skip the parens entirely and we'll auto-detect tags from the content. Full reference below.
                </div>
              </div>

              {/* Logging in the app — paragraph form, kept short on purpose.
                  The icon row in the composer is the actual UX; this just
                  tells users it exists and where to look. */}
              <div style={{ marginBottom: "20px", padding: "14px 16px", border: "1px solid #e8ece8", borderRadius: "10px", background: "#fafbfa" }}>
                <div style={{ fontSize: "15px", fontWeight: "700", color: NAVY, fontFamily: "-apple-system,sans-serif", marginBottom: "6px" }}>
                  Logging in the app
                </div>
                <div style={{ fontSize: "13px", color: "#555", lineHeight: 1.6, fontFamily: "-apple-system,sans-serif" }}>
                  Tap <strong>+</strong> on the entries page to write directly. Above the text box you'll see icons for common fields: <strong>💰 amount</strong>, <strong>📍 location</strong>, <strong>👥 people</strong>, <strong>⏱️ time</strong>, <strong>📏 distance</strong>, <strong>🔥 calories</strong>, <strong>😊 mood</strong>, <strong>⭐ rating</strong>, <strong>🎾 score</strong>. Tap any icon to add a value. You can also just type naturally — <em>"ran 5 miles in 45 min, felt great"</em> — and we'll fill the right fields in for you.
                </div>
                <div style={{ fontSize: "12px", color: "#888", lineHeight: 1.6, fontFamily: "-apple-system,sans-serif", marginTop: "8px" }}>
                  For things like <em>property</em>, <em>medication</em>, <em>cuisine</em>, or your own custom fields, see the <strong>🧩 Fields</strong> tab to enable or define them.
                </div>
              </div>

              {/* Collapsible shorthand reference. Closed by default — most
                  users won't need it; the icon row in the composer is
                  enough. Power users emailing entries from their phone
                  appreciate having the syntax map in one place. */}
              <details style={{ marginBottom: "24px", border: "1px solid #eee", borderRadius: "10px", padding: "0", background: "white" }}>
                <summary style={{ cursor: "pointer", padding: "12px 16px", fontSize: "14px", fontWeight: "600", color: NAVY, fontFamily: "-apple-system,sans-serif", listStyle: "revert", outline: "none" }}>
                  Shorthand reference — type fields in plain prose
                </summary>
                <div style={{ padding: "0 16px 16px", borderTop: "1px solid #f3f3f3" }}>
                  <div style={{ fontSize: "13px", color: "#555", lineHeight: 1.6, fontFamily: "-apple-system,sans-serif", marginTop: "12px", marginBottom: "12px" }}>
                    These work in subjects (when emailing) and in the entry body (when typing in the app). The parser runs automatically — fields appear as chips above the body as you type.
                  </div>
                  {(() => {
                    // Each row: { icon, label, syntax: [{form, example}], note }
                    // Keep examples short and copy-pasteable. Syntax forms
                    // are listed in order from most-used to least-used.
                    const rows = [
                      { icon: "💰", label: "Amount",     syntax: [{ form: "$<num>", example: "$25.50" }, { form: "@<num>.<dec>", example: "@9.99" }],
                        note: "Comma grouping OK ($1,234.56). Bare @-numbers without a decimal stay as locations." },
                      { icon: "📍", label: "Location",   syntax: [{ form: "@<name>", example: "@Lowes" }, { form: '@"<two words>"', example: '@"Home Depot"' }],
                        note: "Multiple OK: \"lunch @toms then @smithys\". 1-2 word names captured." },
                      { icon: "👥", label: "People",     syntax: [{ form: "w:<name>", example: "w:Sue" }, { form: "with:<name>", example: "with:Coach Sylvano" }, { form: "vs:<name>", example: "vs:Bob" }],
                        note: "Use \"vs\" for opponents (renders differently in titles). Capitalize first letter, or names already known to your diary work too." },
                      { icon: "⏱️", label: "Time",       syntax: [{ form: "<n>h", example: "1.5h" }, { form: "<n>min", example: "45min" }, { form: "combined", example: "1h 30min" }],
                        note: "Hours, minutes, days, weeks all add up: \"2h 30m\" → 150 minutes." },
                      { icon: "📏", label: "Distance",   syntax: [{ form: "<n>mi", example: "5mi" }, { form: "<n> miles", example: "3.2 miles" }, { form: "<n>km", example: "10km" }],
                        note: "Km gets converted to miles automatically." },
                      { icon: "🔥", label: "Calories",   syntax: [{ form: "<n>cal", example: "500cal" }, { form: "cal:<n>", example: "cal:1200" }],
                        note: "Use the field for either consumed or burned — context comes from your tags." },
                      { icon: "😊", label: "Mood",       syntax: [{ form: "feeling <word>", example: "feeling tired" }, { form: "feeling: <a>, <b>", example: "feeling: tired, content" }],
                        note: "Also: typing #happy or #frustrated as a tag populates mood automatically." },
                      { icon: "⭐", label: "Rating",     syntax: [{ form: "rating:<val>", example: "rating:8/10" }, { form: "rate:<val>", example: "rate:A+" }],
                        note: "Numeric (8/10), letter (A-, B+), or any short token." },
                      { icon: "🎾", label: "Score",      syntax: [{ form: "<n>-<n>", example: "6-3 6-2" }, { form: "with tiebreak", example: "7-6(3) 6-4" }],
                        note: "Tennis-style. Tiebreaks must be parenthesized." },
                    ];
                    return rows.map((r, i) => (
                      <div key={r.label} style={{ padding: "10px 0", borderBottom: i === rows.length - 1 ? "none" : "1px solid #f3f3f3" }}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: "4px" }}>
                          <span style={{ fontSize: "15px" }}>{r.icon}</span>
                          <span style={{ fontSize: "13px", fontWeight: "700", color: "#333", fontFamily: "-apple-system,sans-serif" }}>{r.label}</span>
                        </div>
                        <div style={{ fontSize: "12px", color: "#555", fontFamily: "-apple-system,sans-serif", lineHeight: 1.7, marginLeft: "23px" }}>
                          {r.syntax.map((s, j) => (
                            <span key={j}>
                              {j > 0 && <span style={{ color: "#bbb", margin: "0 6px" }}>·</span>}
                              <code style={{ background: "#f3efe7", padding: "1px 6px", borderRadius: "3px", fontSize: "12px", fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace" }}>{s.example}</code>
                            </span>
                          ))}
                        </div>
                        {r.note && (
                          <div style={{ fontSize: "11px", color: "#999", fontFamily: "-apple-system,sans-serif", marginTop: "4px", marginLeft: "23px", lineHeight: 1.5 }}>
                            {r.note}
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                  <div style={{ fontSize: "12px", color: "#888", lineHeight: 1.6, fontFamily: "-apple-system,sans-serif", marginTop: "16px", paddingTop: "12px", borderTop: "1px solid #f3f3f3" }}>
                    Example combining several: <code style={{ background: "#f3efe7", padding: "1px 6px", borderRadius: "3px", fontSize: "12px", fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace" }}>$60 @safeway groceries</code> — captures amount and location. Or: <code style={{ background: "#f3efe7", padding: "1px 6px", borderRadius: "3px", fontSize: "12px", fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace" }}>tennis vs:Bob 6-3 6-2 rating:8/10</code> — captures opponent, score, rating.
                  </div>
                </div>
              </details>

              {[
                { l: "Inbound email", v: (
                  <CopyEmail
                    email={diary.inboundEmail}
                    diaryName={diary.name}
                    accentColor={ORANGE}
                    style={{ fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace", fontSize: "13px", color: "#333" }}
                    onCopied={(ok) => showToast(ok ? "Logging address copied." : "Couldn't copy — select and copy manually.")}
                  />
                ) },
                { l: "Your role", v: role },
                { l: "Backdating", v: (
                  <>
                    Put the offset in the subject as <code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "13px" }}>(-1d)</code> for yesterday,
                    {" "}<code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "13px" }}>(-2w)</code> for two weeks ago,
                    {" "}<code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "13px" }}>(-3mo)</code> for three months,
                    {" "}<code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "13px" }}>(-1y)</code> for one year.
                    Use <code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "13px" }}>(-2026-04-01)</code> for an exact date.
                    A bare number like <code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "13px" }}>(-2)</code> still works and means days.
                  </>
                )},
                { l: "Tagging", v: (
                  <>
                    Add <code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "13px" }}>(tennis, food)</code> at the start of your subject —
                    or leave blank and we'll auto-detect tags from the content.
                  </>
                )},
                { l: "Follow-up", v: (
                  <>
                    Add <code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "13px" }}>!!</code> anywhere in the subject,
                    or include <code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "13px" }}>!</code> as a token inside the tag parens.
                    Both <code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "13px" }}>(rent, !)</code> and
                    {" "}<code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "13px" }}>(rent, !!)</code> work.
                    On the web, the toggle inside the entry editor does the same thing.
                  </>
                )},
                { l: "Combine everything", v: (
                  <>
                    Tags, follow-up, and backdating can all live in one set of parens, in any order:
                    {" "}<code style={{ background: "#f3efe7", padding: "1px 5px", borderRadius: "3px", fontSize: "13px" }}>(rent, !, -1d) bumped pricing</code>
                    {" "}saves with tag <em>rent</em>, flagged for follow-up, dated yesterday.
                  </>
                )},
                { l: "Email size limit", v: "Don't attach videos — they push emails over the 25 MB email-routing cap and the message will bounce. Tip: when sending photos from iOS Mail, pick \"Medium\" size when prompted. Upload videos directly in the app instead." },
              ].map((r, i) => (
                <div key={i} style={{ borderBottom: "1px solid #eee", padding: "12px 0" }}>
                  <div style={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>{r.l}</div>
                  <div style={{ fontSize: "14px", color: "#333", lineHeight: 1.6 }}>{r.v}</div>
                </div>
              ))}

              {role === "owner" && (
                <div style={{ marginTop: "32px", padding: "16px", border: "1px solid #f0c7be", borderRadius: "10px", background: "#fff8f6" }}>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#c83803", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>Danger zone</div>
                  <div style={{ fontSize: "13px", color: "#555", lineHeight: 1.5, marginBottom: "12px" }}>
                    Permanently delete this diary and all of its entries. This cannot be undone.
                  </div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                    <input type="text" value={deleteDiaryConfirm}
                      onChange={e => setDeleteDiaryConfirm(e.target.value)}
                      placeholder={`Type "${diary.name}" to confirm`}
                      style={{ flex: "1 1 200px", minWidth: 0, background: "white", border: "1px solid #ddd", borderRadius: "6px", padding: "8px 12px", color: "#333", fontSize: "13px", fontFamily: "-apple-system,sans-serif", outline: "none", boxSizing: "border-box" }} />
                    <button onClick={handleDeleteDiary}
                      disabled={deleteDiaryConfirm.trim() !== diary.name || deletingDiary}
                      style={{
                        background: (deleteDiaryConfirm.trim() === diary.name && !deletingDiary) ? "#c83803" : "#ddd",
                        color: "white", border: "none", borderRadius: "6px",
                        padding: "8px 16px", fontSize: "13px", fontWeight: "700",
                        cursor: (deleteDiaryConfirm.trim() === diary.name && !deletingDiary) ? "pointer" : "default",
                        fontFamily: "-apple-system,sans-serif", whiteSpace: "nowrap",
                      }}>
                      {deletingDiary ? "Deleting..." : "Delete diary"}
                    </button>
                  </div>
                </div>
              )}

              {role !== "owner" && (
                <div style={{ marginTop: "32px", padding: "16px", border: "1px solid #e5e0d8", borderRadius: "10px", background: "#fafaf7" }}>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#555", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>Leave this diary</div>
                  <div style={{ fontSize: "13px", color: "#555", lineHeight: 1.5, marginBottom: "12px" }}>
                    Remove yourself from this shared diary. The owner and other members keep access to all entries.
                  </div>
                  <button onClick={handleLeaveDiary} disabled={leaving}
                    style={{ background: "white", color: "#c83803", border: "1px solid #f0c7be", borderRadius: "6px", padding: "8px 16px", fontSize: "13px", fontWeight: "700", cursor: leaving ? "default" : "pointer", fontFamily: "-apple-system,sans-serif" }}>
                    {leaving ? "Leaving..." : "Leave diary"}
                  </button>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                <button onClick={() => setShowSettings(false)} style={{ background: ORANGE, color: "white", border: "none", padding: "10px 20px", borderRadius: "6px", fontSize: "14px", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>Done</button>
              </div>
            </>
          )}
          {settingsTab === "members" && (
            <>
              <h2 style={{ fontSize: "20px", fontWeight: "700", margin: "0 0 8px", color: NAVY, fontFamily: "-apple-system,sans-serif" }}>Members</h2>
              <p style={{ fontSize: "13px", color: "#999", margin: "0 0 20px", lineHeight: 1.6 }}>
                {role === "owner" ? "Invite other LifeTrackr users to share this diary. Editors can add and edit entries; viewers can only read." : "People who have access to this diary."}
              </p>

              {/* Pending ownership transfer banner — owner side. Shown when a
                  transfer was initiated and is waiting for the recipient to
                  accept. Owner can cancel from here at any time. */}
              {role === "owner" && diary?.pendingOwnerTransfer && (
                <div style={{ marginBottom: "20px", padding: "14px 16px", background: "#fff5ec", borderRadius: "10px", border: `1px solid ${ORANGE}` }}>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: ORANGE, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>Ownership transfer pending</div>
                  <div style={{ fontSize: "13px", color: "#444", fontFamily: "-apple-system,sans-serif", lineHeight: 1.5, marginBottom: "10px" }}>
                    Waiting for{" "}
                    <strong>
                      {diary.pendingOwnerTransfer.toUid && memberProfiles[diary.pendingOwnerTransfer.toUid]?.name
                        ? memberProfiles[diary.pendingOwnerTransfer.toUid].name
                        : (diary.pendingOwnerTransfer.toEmail || "the recipient")}
                    </strong>
                    {" "}to accept. You'll become an Editor once they do.
                  </div>
                  <button onClick={handleCancelTransfer}
                    style={{ background: "white", border: `1px solid ${ORANGE}`, borderRadius: "5px", color: ORANGE, cursor: "pointer", fontSize: "12px", fontWeight: "600", padding: "6px 14px", fontFamily: "-apple-system,sans-serif" }}>
                    Cancel transfer
                  </button>
                </div>
              )}

              {/* Current members list */}
              <div style={{ marginBottom: role === "owner" ? "24px" : "16px" }}>
                {Object.entries(diary.members || {}).map(([uid, memberEntry]) => {
                  const memberRole = getMemberRole(memberEntry);
                  const access = getMemberAccess(memberEntry);
                  const p = memberProfiles[uid];
                  const isSelf = uid === user?.uid;
                  const initials = (p?.name || "?").slice(0, 1).toUpperCase();
                  return (
                    <div key={uid} style={{ padding: "10px 0", borderBottom: "1px solid #f0ede8" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      {p?.photoURL ? (
                        <img src={p.photoURL} alt="" referrerPolicy="no-referrer"
                          style={{ width: "36px", height: "36px", borderRadius: "50%", flexShrink: 0, objectFit: "cover" }} />
                      ) : (
                        <div style={{ width: "36px", height: "36px", borderRadius: "50%", flexShrink: 0, background: "#e8e4dc", color: NAVY, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "700", fontSize: "15px", fontFamily: "-apple-system,sans-serif" }}>{initials}</div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "14px", fontWeight: "600", color: "#333", fontFamily: "-apple-system,sans-serif" }}>
                          {p?.name || "Loading..."} {isSelf && <span style={{ color: "#999", fontWeight: 400, fontSize: "12px" }}>(you)</span>}
                        </div>
                        <div style={{ fontSize: "12px", color: "#888", fontFamily: "-apple-system,sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {p?.email || ""}
                        </div>
                      </div>
                      {role === "owner" && !isSelf ? (
                        <>
                          <button onClick={() => setTransferTarget(uid)}
                            disabled={!!diary?.pendingOwnerTransfer}
                            title={diary?.pendingOwnerTransfer ? "Cancel the pending transfer first" : "Transfer ownership to this member"}
                            style={{ background: "white", border: "1px solid #e0dcd8", borderRadius: "5px", color: diary?.pendingOwnerTransfer ? "#bbb" : ORANGE, cursor: diary?.pendingOwnerTransfer ? "not-allowed" : "pointer", fontSize: "11px", fontWeight: "600", padding: "4px 8px", fontFamily: "-apple-system,sans-serif", flexShrink: 0 }}>
                            Make owner
                          </button>
                          <select value={memberRole} onChange={e => handleChangeRole(uid, e.target.value)}
                            style={{ border: "1px solid #e0dcd8", borderRadius: "6px", padding: "5px 8px", fontSize: "12px", color: "#555", background: "white", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                            <option value="contributor">Editor</option>
                            <option value="viewer">Viewer</option>
                          </select>
                          <button onClick={() => handleRemoveMember(uid)} title="Remove member"
                            style={{ background: "transparent", border: "none", color: "#c83803", cursor: "pointer", fontSize: "18px", padding: "0 6px", lineHeight: 1 }}>×</button>
                        </>
                      ) : (
                        <span style={{ fontSize: "11px", fontWeight: "700", color: memberRole === "owner" ? ORANGE : "#888", textTransform: "uppercase", letterSpacing: "0.5px", background: memberRole === "owner" ? "rgba(200,56,3,0.08)" : "#f0ede8", padding: "4px 8px", borderRadius: "10px", fontFamily: "-apple-system,sans-serif" }}>
                          {memberRole === "owner" ? "Owner" : memberRole === "contributor" ? "Editor" : "Viewer"}
                        </span>
                      )}
                      </div>
                      {/* Access controls — owner editing a non-owner member */}
                      {role === "owner" && memberRole !== "owner" && !isSelf && (
                        <MemberAccessEditor
                          uid={uid}
                          access={access}
                          allTags={usedTags}
                          entries={entries}
                          lastVisitedMs={memberLastVisited[uid]}
                          onChange={(next) => handleChangeMemberAccess(uid, next)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Pending invites (owner only) */}
              {role === "owner" && pendingInvites.length > 0 && (
                <div style={{ marginBottom: "20px", padding: "14px 16px", background: "#fffbf5", borderRadius: "10px", border: "1px solid #f5e6d0" }}>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#8a5a1a", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>Pending invitations</div>
                  {pendingInvites.map((inv, idx) => {
                    const sentAt = inv.emailSentAt?.toDate ? inv.emailSentAt.toDate() : (inv.createdAt?.toDate ? inv.createdAt.toDate() : null);
                    const sentLabel = sentAt ? formatRelativeTime(sentAt) : "sending...";
                    const hasError = !!inv.emailError;
                    const isExpanded = resendingInviteId === inv.id;
                    return (
                      <div key={inv.id} style={{ borderTop: idx === 0 ? "none" : "1px solid #f5e6d0" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0" }}>
                          <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: hasError ? "#fde4dd" : "#f5e6d0", color: hasError ? "#c83803" : "#8a5a1a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", flexShrink: 0 }}>{hasError ? "⚠" : "⏳"}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "13px", color: "#333", fontFamily: "-apple-system,sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.email}</div>
                            <div style={{ fontSize: "11px", color: hasError ? "#c83803" : "#999", fontFamily: "-apple-system,sans-serif" }}>
                              {hasError
                                ? `Send failed — ${inv.emailError.slice(0, 60)}`
                                : <>Invited {sentLabel} · Will join as {inv.role === "contributor" ? "Editor" : (inv.role === "owner" ? "Owner" : "Viewer")}</>
                              }
                            </div>
                          </div>
                          {!isExpanded && (
                            <>
                              <button onClick={() => openResendEditor(inv)} title="Edit access and resend invitation"
                                style={{ background: "white", border: "1px solid #e0dcd8", borderRadius: "5px", color: ORANGE, cursor: "pointer", fontSize: "11px", fontWeight: "600", padding: "4px 10px", fontFamily: "-apple-system,sans-serif", flexShrink: 0 }}>Resend</button>
                              <button onClick={() => handleCancelPendingInvite(inv.id)} title="Cancel invitation"
                                style={{ background: "transparent", border: "none", color: "#c83803", cursor: "pointer", fontSize: "16px", padding: "0 6px", lineHeight: 1, flexShrink: 0 }}>×</button>
                            </>
                          )}
                        </div>
                        {isExpanded && (
                          <div style={{ padding: "12px", background: "#fafaf7", borderRadius: "8px", border: "1px solid #f0ede8", marginBottom: "8px" }}>
                            <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px", fontFamily: "-apple-system,sans-serif", fontWeight: 600 }}>Adjust access before resending</div>
                            <div style={{ display: "flex", gap: "6px", marginBottom: "8px" }}>
                              <button onClick={() => { setResendDraftTags(null); setResendDraftUntagged(true); }}
                                style={{ background: resendDraftTags === null ? "#3a3a3a" : "white", color: resendDraftTags === null ? "white" : "#555", border: `1px solid ${resendDraftTags === null ? "#3a3a3a" : "#ddd"}`, borderRadius: "12px", padding: "4px 10px", fontSize: "11px", fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                                All tags
                              </button>
                              <button onClick={() => { setResendDraftTags([]); setResendDraftUntagged(false); }}
                                style={{ background: (Array.isArray(resendDraftTags) && resendDraftTags.length === 0) ? "#3a3a3a" : "white", color: (Array.isArray(resendDraftTags) && resendDraftTags.length === 0) ? "white" : "#555", border: `1px solid ${(Array.isArray(resendDraftTags) && resendDraftTags.length === 0) ? "#3a3a3a" : "#ddd"}`, borderRadius: "12px", padding: "4px 10px", fontSize: "11px", fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                                No tags
                              </button>
                            </div>
                            {usedTags.length > 0 && (
                              <>
                                <div style={{ fontSize: "10px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px", fontWeight: 600, fontFamily: "-apple-system,sans-serif" }}>Visible tags</div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "10px" }}>
                                  {usedTags.map(tag => {
                                    const meta = getTagMeta(tag);
                                    const allowed = resendDraftTags === null || (Array.isArray(resendDraftTags) && resendDraftTags.includes(tag));
                                    return (
                                      <button key={tag} onClick={() => {
                                        if (resendDraftTags === null) {
                                          setResendDraftTags(usedTags.filter(t => t !== tag));
                                        } else if (resendDraftTags.includes(tag)) {
                                          setResendDraftTags(resendDraftTags.filter(t => t !== tag));
                                        } else {
                                          setResendDraftTags([...resendDraftTags, tag]);
                                        }
                                      }}
                                        style={{ background: allowed ? meta.color : "white", color: allowed ? "white" : meta.color, border: `1px solid ${allowed ? meta.color : meta.border}`, borderRadius: "12px", padding: "4px 10px", fontSize: "12px", fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                                        {tag}
                                      </button>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#666", fontFamily: "-apple-system,sans-serif", cursor: "pointer", marginBottom: "12px" }}>
                              <input type="checkbox" checked={resendDraftUntagged} onChange={() => setResendDraftUntagged(v => !v)} />
                              Include entries with no tags
                            </label>
                            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                              <button onClick={closeResendEditor} disabled={resendSaving}
                                style={{ background: "white", border: "1px solid #ddd", borderRadius: "6px", padding: "6px 14px", fontSize: "12px", color: "#555", cursor: resendSaving ? "default" : "pointer", fontFamily: "-apple-system,sans-serif", fontWeight: 600 }}>
                                Cancel
                              </button>
                              <button onClick={saveAndResend} disabled={resendSaving}
                                style={{ background: resendSaving ? "#ddd" : ORANGE, color: "white", border: "none", borderRadius: "6px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, cursor: resendSaving ? "default" : "pointer", fontFamily: "-apple-system,sans-serif" }}>
                                {resendSaving ? "Saving..." : "Save & Resend"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Invite form (owner only) */}
              {role === "owner" && (
                <div style={{ padding: "16px", background: "#fafaf7", borderRadius: "10px", border: "1px solid #f0ede8" }}>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#555", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>Invite someone</div>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <input type="email" value={inviteEmail}
                      onChange={e => { setInviteEmail(e.target.value); if (inviteMsg) setInviteMsg(null); }}
                      onKeyDown={e => { if (e.key === "Enter" && !inviting) handleInviteMember(); }}
                      placeholder="their-email@example.com"
                      autoComplete="email"
                      inputMode="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      style={{ flex: "1 1 200px", minWidth: 0, background: "white", border: "1px solid #ddd", borderRadius: "6px", padding: "8px 12px", color: "#333", fontSize: "13px", fontFamily: "-apple-system,sans-serif", outline: "none", boxSizing: "border-box" }} />
                    <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
                      style={{ border: "1px solid #ddd", borderRadius: "6px", padding: "8px 10px", fontSize: "13px", color: "#555", background: "white", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                      <option value="contributor">Editor</option>
                      <option value="viewer">Viewer</option>
                      {!diary?.pendingOwnerTransfer && <option value="owner">Owner (transfer)</option>}
                    </select>
                    <button onClick={handleInviteMember} disabled={inviting || !inviteEmail.trim()}
                      style={{ background: (!inviting && inviteEmail.trim()) ? ORANGE : "#ddd", color: "white", border: "none", borderRadius: "6px", padding: "8px 18px", fontSize: "13px", fontWeight: "700", cursor: (!inviting && inviteEmail.trim()) ? "pointer" : "default", fontFamily: "-apple-system,sans-serif" }}>
                      {inviting ? "Inviting..." : "Invite"}
                    </button>
                  </div>
                  {inviteMsg && (
                    <div style={{ marginTop: "10px", fontSize: "12px", color: inviteMsg.type === "ok" ? "#2a7a3a" : "#c83803", fontFamily: "-apple-system,sans-serif" }}>
                      {inviteMsg.text}
                    </div>
                  )}
                  {/* Tag access selector — collapsed by default */}
                  <div style={{ marginTop: "12px" }}>
                    <button onClick={() => setInviteAccessOpen(o => !o)}
                      style={{ background: "transparent", border: "none", color: "#888", fontSize: "12px", cursor: "pointer", padding: "2px 0", fontFamily: "-apple-system,sans-serif", display: "flex", alignItems: "center", gap: "6px", textAlign: "left" }}>
                      <span style={{ fontWeight: 600, color: "#666" }}>Sharing:</span>
                      <span style={{ color: "#888", fontWeight: 400 }}>
                        {inviteTags === null
                          ? "All tags" + (inviteIncludeUntagged === false ? " (untagged hidden)" : "")
                          : inviteTags.length === 0
                            ? (inviteIncludeUntagged ? "Untagged only" : "Nothing")
                            : inviteTags.join(", ") + (inviteIncludeUntagged ? " + untagged" : "")}
                      </span>
                      <svg width="11" height="11" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, opacity: 0.55, marginLeft: "1px" }} aria-hidden="true">
                        <path d="M9.5 1.8 L12.2 4.5 L4.5 12.2 L1.5 12.5 L1.8 9.5 Z" stroke="#666" strokeWidth="1.2" strokeLinejoin="round" fill="none"/>
                        <path d="M8.6 2.7 L11.3 5.4" stroke="#666" strokeWidth="1.2"/>
                      </svg>
                    </button>
                    {inviteAccessOpen && (
                      <div style={{ marginTop: "8px", padding: "10px 12px", background: "white", borderRadius: "6px", border: "1px solid #ece8e0" }}>
                        <div style={{ display: "flex", gap: "6px", marginBottom: "10px" }}>
                          <button onClick={() => { setInviteTags(null); setInviteIncludeUntagged(true); }}
                            style={{ background: inviteTags === null ? ORANGE : "transparent", color: inviteTags === null ? "white" : "#666", border: `1px solid ${inviteTags === null ? ORANGE : "#ddd"}`, borderRadius: "12px", padding: "4px 12px", fontSize: "12px", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>All tags</button>
                          <button onClick={() => { setInviteTags([]); setInviteIncludeUntagged(false); }}
                            style={{ background: inviteTags !== null && inviteTags.length === 0 ? ORANGE : "transparent", color: inviteTags !== null && inviteTags.length === 0 ? "white" : "#666", border: `1px solid ${inviteTags !== null && inviteTags.length === 0 ? ORANGE : "#ddd"}`, borderRadius: "12px", padding: "4px 12px", fontSize: "12px", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>No tags</button>
                        </div>
                        <div style={{ fontSize: "10px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px", fontWeight: 600, fontFamily: "-apple-system,sans-serif" }}>Visible tags</div>
                        {usedTags.length === 0 ? (
                          <div style={{ fontSize: "12px", color: "#aaa", fontStyle: "italic", fontFamily: "-apple-system,sans-serif" }}>This diary has no tags yet.</div>
                        ) : (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                            {usedTags.map(tag => {
                              const meta = getTagMeta(tag);
                              const allowed = inviteTags === null || inviteTags.includes(tag);
                              return (
                                <button key={tag} onClick={() => {
                                  if (inviteTags === null) {
                                    // First customization: start from "everything except this tag"
                                    setInviteTags(usedTags.filter(t => t !== tag));
                                  } else if (inviteTags.includes(tag)) {
                                    setInviteTags(inviteTags.filter(t => t !== tag));
                                  } else {
                                    setInviteTags([...inviteTags, tag]);
                                  }
                                }}
                                  style={{ background: allowed ? meta.bg : "white", color: allowed ? meta.color : "#aaa", border: `1px solid ${allowed ? meta.border : "#e0dcd8"}`, borderRadius: "12px", padding: "4px 10px", fontSize: "12px", fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,sans-serif", opacity: allowed ? 1 : 0.7, transition: "all 0.1s" }}>
                                  {meta.emoji} {tag}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        <label style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "12px", cursor: "pointer", fontSize: "12px", color: "#555", fontFamily: "-apple-system,sans-serif" }}>
                          <input type="checkbox" checked={inviteIncludeUntagged} onChange={() => setInviteIncludeUntagged(v => !v)}
                            style={{ cursor: "pointer", width: "14px", height: "14px" }} />
                          Include entries with no tags
                        </label>
                        {(() => {
                          const isUnrestricted = inviteTags === null;
                          const allowedSet = new Set(inviteTags || []);
                          const matchCount = isUnrestricted
                            ? entries.length
                            : entries.filter(e => {
                                const t = e.tags || [];
                                if (t.length === 0) return inviteIncludeUntagged !== false;
                                return t.every(tag => allowedSet.has(tag));
                              }).length;
                          return (
                            <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #ece8e0", fontSize: "12px", color: "#aaa", fontFamily: "-apple-system,sans-serif" }}>
                              {matchCount} of {entries.length} {entries.length === 1 ? "entry" : "entries"}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Authorized senders section */}
              {(() => {
                const memberEmails = new Set(
                  Object.keys(diary.members || {})
                    .map(uid => memberProfiles[uid]?.email?.toLowerCase())
                    .filter(Boolean)
                );
                const allAuthorized = (diary.authorizedEmails || []).map(e => e.toLowerCase());
                const extraEmails = allAuthorized.filter(e => !memberEmails.has(e));
                return (
                  <div style={{ marginTop: "24px", padding: "16px", background: "#fafaf7", borderRadius: "10px", border: "1px solid #f0ede8" }}>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#555", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                      Authorized senders
                    </div>
                    <p style={{ fontSize: "12px", color: "#888", margin: "0 0 12px", lineHeight: 1.5 }}>
                      Only these email addresses can log entries by emailing <strong style={{ color: ORANGE }}>{diary.inboundEmail}</strong>. All members are authorized by default.
                      {role === "owner" && " You can add additional addresses below (e.g. a work email)."}
                    </p>

                    {/* Member-derived authorized emails (read-only, just for visibility) */}
                    <div style={{ fontSize: "11px", color: "#999", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.3px" }}>From members</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "12px" }}>
                      {[...memberEmails].length === 0 ? (
                        <span style={{ fontSize: "12px", color: "#aaa", fontStyle: "italic" }}>No member emails yet</span>
                      ) : (
                        [...memberEmails].map(email => (
                          <span key={email} style={{ background: "white", border: "1px solid #e0dcd8", borderRadius: "12px", padding: "3px 10px", fontSize: "12px", color: "#555", fontFamily: "-apple-system,sans-serif" }}>
                            {email}
                          </span>
                        ))
                      )}
                    </div>

                    {/* Extra authorized emails (editable) */}
                    {extraEmails.length > 0 && (
                      <>
                        <div style={{ fontSize: "11px", color: "#999", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.3px" }}>Additional</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "12px" }}>
                          {extraEmails.map(email => (
                            <span key={email} style={{ background: "white", border: "1px solid #e0dcd8", borderRadius: "12px", padding: "3px 6px 3px 10px", fontSize: "12px", color: "#555", fontFamily: "-apple-system,sans-serif", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                              {email}
                              {role === "owner" && (
                                <button onClick={() => handleRemoveExtraEmail(email)} title="Remove"
                                  style={{ background: "transparent", border: "none", color: "#c83803", cursor: "pointer", fontSize: "14px", padding: "0 2px", lineHeight: 1 }}>×</button>
                              )}
                            </span>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Add form (owner only) */}
                    {role === "owner" && (
                      <div>
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                          <input type="email" value={extraEmailInput}
                            onChange={e => { setExtraEmailInput(e.target.value); if (extraMsg) setExtraMsg(null); }}
                            onKeyDown={e => { if (e.key === "Enter" && !savingExtra) handleAddExtraEmail(); }}
                            placeholder="Add an extra authorized email..."
                            style={{ flex: "1 1 200px", minWidth: 0, background: "white", border: "1px solid #ddd", borderRadius: "6px", padding: "7px 11px", color: "#333", fontSize: "13px", fontFamily: "-apple-system,sans-serif", outline: "none", boxSizing: "border-box" }} />
                          <button onClick={handleAddExtraEmail} disabled={savingExtra || !extraEmailInput.trim()}
                            style={{ background: (!savingExtra && extraEmailInput.trim()) ? ORANGE : "#ddd", color: "white", border: "none", borderRadius: "6px", padding: "7px 16px", fontSize: "13px", fontWeight: "700", cursor: (!savingExtra && extraEmailInput.trim()) ? "pointer" : "default", fontFamily: "-apple-system,sans-serif" }}>
                            {savingExtra ? "Adding..." : "Add"}
                          </button>
                        </div>
                        {extraMsg && (
                          <div style={{ marginTop: "8px", fontSize: "12px", color: extraMsg.type === "ok" ? "#2a7a3a" : "#c83803", fontFamily: "-apple-system,sans-serif" }}>
                            {extraMsg.text}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                <button onClick={() => setShowSettings(false)} style={{ background: ORANGE, color: "white", border: "none", padding: "10px 20px", borderRadius: "6px", fontSize: "14px", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>Done</button>
              </div>
            </>
          )}
          {settingsTab === "cover" && (
            <>
              <h2 style={{ fontSize: "20px", fontWeight: "700", margin: "0 0 8px", color: NAVY, fontFamily: "-apple-system,sans-serif" }}>Cover</h2>
              <p style={{ fontSize: "13px", color: "#999", margin: "0 0 20px", lineHeight: 1.6 }}>Change the name, pick a book theme, or upload a custom cover image. This is what shows on your dashboard when the diary is closed.</p>

              {/* Name */}
              <div style={{ marginBottom: "24px" }}>
                <div style={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>Name</div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input type="text" value={coverNameDraft}
                    onChange={e => setCoverNameDraft(e.target.value)}
                    disabled={!canEdit}
                    style={{ flex: 1, background: canEdit ? "#f8f8f8" : "#f0f0f0", border: "1px solid #ddd", borderRadius: "8px", padding: "10px 14px", color: "#333", fontSize: "14px", fontFamily: "-apple-system,sans-serif", outline: "none", boxSizing: "border-box" }} />
                  <button onClick={saveCoverName}
                    disabled={!canEdit || savingCoverName || !coverNameDraft.trim() || coverNameDraft.trim() === diary.name}
                    style={{
                      background: (!canEdit || !coverNameDraft.trim() || coverNameDraft.trim() === diary.name) ? "#ddd" : ORANGE,
                      color: "white", border: "none", borderRadius: "8px", padding: "0 18px",
                      fontSize: "13px", fontWeight: "700",
                      cursor: (!canEdit || !coverNameDraft.trim() || coverNameDraft.trim() === diary.name) ? "default" : "pointer",
                      fontFamily: "-apple-system,sans-serif",
                    }}>
                    {savingCoverName ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>

              {/* Cover image — moved up so it sits right below Name */}
              <div style={{ marginBottom: "24px" }}>
                <div style={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>Custom cover image</div>
                {diary.coverImageUrl ? (
                  <div>
                    <div style={{ width: "140px", height: "180px", borderRadius: "4px", overflow: "hidden", border: "1px solid #eee", background: "#f0f0f0", marginBottom: "10px", position: "relative" }}>
                      <img src={diary.coverImageUrl} alt="cover" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.7) 100%)" }} />
                      <div style={{ position: "absolute", bottom: "10px", left: "10px", right: "10px", color: "white", fontSize: "12px", fontWeight: 700, fontFamily: "Georgia, serif", textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}>
                        {diary.name}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <label style={{ background: "#f5f5f5", color: "#333", border: "1px solid #ddd", borderRadius: "6px", padding: "8px 14px", fontSize: "13px", cursor: canEdit ? "pointer" : "default", fontFamily: "-apple-system,sans-serif" }}>
                        Replace
                        <input type="file" accept="image/*" disabled={!canEdit || uploadingCover} onChange={e => e.target.files?.[0] && uploadCoverImage(e.target.files[0])} style={{ display: "none" }} />
                      </label>
                      <button onClick={removeCoverImage} disabled={!canEdit || uploadingCover}
                        style={{ background: "white", color: "#c83803", border: "1px solid #f0c7be", borderRadius: "6px", padding: "8px 14px", fontSize: "13px", cursor: canEdit ? "pointer" : "default", fontFamily: "-apple-system,sans-serif" }}>
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <label style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "140px", height: "180px", border: "2px dashed #ccc", borderRadius: "4px", cursor: canEdit ? "pointer" : "default", color: "#888", fontSize: "12px", fontFamily: "-apple-system,sans-serif", gap: "6px", background: "#fafafa" }}>
                    <span style={{ fontSize: "28px" }}>📷</span>
                    <span>{uploadingCover ? "Uploading..." : "Upload cover image"}</span>
                    <input type="file" accept="image/*" disabled={!canEdit || uploadingCover} onChange={e => e.target.files?.[0] && uploadCoverImage(e.target.files[0])} style={{ display: "none" }} />
                  </label>
                )}
                <p style={{ fontSize: "12px", color: "#aaa", marginTop: "8px", lineHeight: 1.5 }}>
                  A custom image replaces the themed cover entirely. The diary name will appear overlaid at the bottom.
                </p>
              </div>

              {/* Theme picker — only shown when no cover image is set */}
              {!diary.coverImageUrl && (
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                    Book theme {savingCoverTheme && <span style={{ color: ORANGE, fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>— saving...</span>}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: "10px" }}>
                    {COVER_THEMES.map(t => {
                      const active = (diary.themeId || "notebook") === t.id;
                      return (
                        <button key={t.id} onClick={() => canEdit && saveCoverTheme(t.id)}
                          disabled={!canEdit}
                          style={{ background: "none", border: `2px solid ${active ? ORANGE : "#eee"}`, borderRadius: "8px", padding: "6px", cursor: canEdit ? "pointer" : "default", display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", transition: "border-color 0.15s" }}>
                          <div style={{ display: "flex", width: "100%", height: "50px", borderRadius: "2px", overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.15)" }}>
                            <div style={{ width: "7px", background: t.spine, flexShrink: 0 }} />
                            <div style={{ flex: 1, background: t.cover, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <span style={{ fontSize: "9px", color: t.nameColor, fontWeight: 700, letterSpacing: "0.3px" }}>Aa</span>
                            </div>
                          </div>
                          <span style={{ fontSize: "11px", color: active ? ORANGE : "#555", fontWeight: active ? 700 : 500, fontFamily: "-apple-system,sans-serif" }}>
                            {t.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                <button onClick={() => setShowSettings(false)} style={{ background: ORANGE, color: "white", border: "none", padding: "10px 20px", borderRadius: "6px", fontSize: "14px", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>Done</button>
              </div>
            </>
          )}

          {settingsTab === "pages" && (
            <>
              <h2 style={{ fontSize: "20px", fontWeight: "700", margin: "0 0 8px", color: NAVY, fontFamily: "-apple-system,sans-serif" }}>Pages</h2>
              <p style={{ fontSize: "13px", color: "#999", margin: "0 0 20px", lineHeight: 1.6 }}>Customize the colors of the open diary — the spine on the left, the page background, and the text on the spine.</p>

              {/* Page colors */}
              {(() => {
                const SPINE_DEFAULT = THEME.spineBg;
                const PAGE_BG_DEFAULT = "#f5efe5";
                const SPINE_TEXT_DEFAULT = "rgba(255,220,180,0.96)";
                const spineCol = diary.pageSpineColor || SPINE_DEFAULT;
                const pageBg = diary.pageBgColor || PAGE_BG_DEFAULT;
                const spineTextCol = diary.pageSpineTextColor || SPINE_TEXT_DEFAULT;
                return (
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                      Page colors {savingCoverTheme && <span style={{ color: ORANGE, fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>— saving...</span>}
                    </div>
                    {/* Live preview — mini open diary */}
                    <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
                      <div style={{ display: "flex", width: "180px", height: "120px", borderRadius: "3px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>
                        <div style={{ width: "20px", background: spineCol, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "10px" }}>
                          <span style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: "9px", fontWeight: 900, color: spineTextCol, letterSpacing: "2px", textTransform: "uppercase", fontFamily: "-apple-system,sans-serif" }}>
                            {diary.name}
                          </span>
                        </div>
                        <div style={{ flex: 1, background: pageBg, padding: "10px 8px" }}>
                          <div style={{ height: "8px", width: "60%", background: "rgba(0,0,0,0.1)", borderRadius: "2px", marginBottom: "4px" }} />
                          <div style={{ height: "5px", width: "85%", background: "rgba(0,0,0,0.06)", borderRadius: "2px", marginBottom: "3px" }} />
                          <div style={{ height: "5px", width: "70%", background: "rgba(0,0,0,0.06)", borderRadius: "2px" }} />
                        </div>
                      </div>
                      <div style={{ fontSize: "11px", color: "#999", lineHeight: 1.5 }}>
                        Live preview.<br />Changes save automatically.
                      </div>
                    </div>
                    {/* Three color pickers */}
                    {[
                      { key: "pageSpineColor", label: "Spine", value: spineCol, defaultVal: SPINE_DEFAULT },
                      { key: "pageBgColor", label: "Page background", value: pageBg, defaultVal: PAGE_BG_DEFAULT },
                      { key: "pageSpineTextColor", label: "Spine text", value: spineTextCol, defaultVal: SPINE_TEXT_DEFAULT },
                    ].map(picker => (
                      <div key={picker.key} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 0", borderBottom: "1px solid #f5f3ef" }}>
                        <div style={{ fontSize: "13px", color: "#333", fontFamily: "-apple-system,sans-serif", width: "140px", flexShrink: 0 }}>{picker.label}</div>
                        <input type="color" value={picker.value.startsWith("rgba") ? "#ffdcb4" : picker.value}
                          disabled={!canEdit}
                          onChange={e => saveCoverColors({ [picker.key]: e.target.value })}
                          style={{ width: "40px", height: "32px", border: "1px solid #ddd", borderRadius: "6px", cursor: canEdit ? "pointer" : "default", padding: "2px", background: "white" }} />
                        <input type="text" value={picker.value}
                          disabled={!canEdit}
                          onChange={e => {
                            const v = e.target.value;
                            if (/^#[0-9a-fA-F]{6}$/.test(v) || /^#[0-9a-fA-F]{3}$/.test(v)) {
                              saveCoverColors({ [picker.key]: v });
                            } else {
                              setDiary(prev => ({ ...prev, [picker.key]: v }));
                            }
                          }}
                          style={{ width: "110px", padding: "6px 10px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "12px", fontFamily: "monospace", color: "#555", outline: "none" }} />
                        {diary[picker.key] && diary[picker.key] !== picker.defaultVal && canEdit && (
                          <button onClick={() => saveCoverColors({ [picker.key]: null })}
                            title="Reset to default"
                            style={{ background: "none", border: "none", color: "#aaa", fontSize: "12px", cursor: "pointer", padding: "4px 8px", marginLeft: "auto" }}>
                            Reset
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                <button onClick={() => setShowSettings(false)} style={{ background: ORANGE, color: "white", border: "none", padding: "10px 20px", borderRadius: "6px", fontSize: "14px", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>Done</button>
              </div>
            </>
          )}
          {settingsTab === "tags" && (
            <TagManagement diary={diary} entries={entries} canEdit={canEdit} setDiary={setDiary} />
          )}
          {settingsTab === "groups" && canEdit && (
            <GroupsManagement diary={diary} entries={entries} setDiary={setDiary} />
          )}
          {settingsTab === "fields" && canEdit && (
            <FieldsManagement diary={diary} entries={entries} setDiary={setDiary} />
          )}
          {settingsTab === "import" && canEdit && (
            <ImportTab diary={diary} user={user} entries={entries} onDone={() => setShowSettings(false)} />
          )}
          {settingsTab === "privacy" && role === "owner" && (
            <PasscodeSettings
              diary={diary}
              uid={user?.uid}
              onSaved={async (patch) => {
                // Persist the passcode hash change (set/change uses a hex
                // string; remove uses deleteField()). Mirror into local diary
                // state so the gate + status reflect immediately.
                await updateDoc(doc(db, "diaries", id), patch);
                setDiary(prev => {
                  const next = { ...prev };
                  if (patch.passcodeHash && typeof patch.passcodeHash === "string") {
                    next.passcodeHash = patch.passcodeHash;
                  } else {
                    delete next.passcodeHash; // deleteField() sentinel → drop locally
                  }
                  return next;
                });
              }}
              onLockNow={() => {
                // Lock immediately and return to the diaries list (same as the
                // on-page lock button), rather than dropping onto this diary's
                // own passcode screen.
                clearSessionUnlock(id);
                setShowSettings(false);
                setUnlocked(false);
                navigate("/dashboard");
              }}
            />
          )}
        </Modal>
      )}

      {/* Upgrade prompt modal. Opened via window.__ltOpenUpgrade(reason) from
          gated controls. Until Stripe checkout is wired (paygate step 3), the
          CTA is informational rather than a live purchase. */}
      {upgradeReason && (
        <Modal onClose={() => setUpgradeReason(null)} maxWidth="420px">
          <div style={{ fontFamily: "-apple-system,sans-serif", textAlign: "center", padding: "4px" }}>
            <div style={{ fontSize: "34px", marginBottom: "8px" }}>⭐</div>
            <h2 style={{ fontSize: "20px", fontWeight: 700, margin: "0 0 8px", color: NAVY }}>Upgrade to Pro</h2>
            <p style={{ fontSize: "14px", color: "#555", lineHeight: 1.6, margin: "0 0 16px" }}>
              {upgradeReason === "ocr"
                ? "You've used all your free receipt scans. Pro gives you unlimited receipt scanning, plus unlimited attachments."
                : "You've reached the free limit of attachments. Pro unlocks unlimited photos, videos, files, and receipt scans across all your diaries."}
            </p>
            <div style={{ background: "#FAF8F4", border: "1px solid #eee", borderRadius: "10px", padding: "14px 16px", marginBottom: "16px", textAlign: "left" }}>
              <div style={{ fontSize: "13px", color: "#333", lineHeight: 1.9 }}>
                <div>✓ Unlimited attachments (photos, videos, files)</div>
                <div>✓ Unlimited receipt scans</div>
                <div>✓ Everything in the free plan</div>
              </div>
            </div>
            <p style={{ fontSize: "12px", color: "#999", margin: "0 0 14px" }}>
              Your diaries and entries always stay safe and exportable.
            </p>
            <button onClick={async () => {
                try {
                  setUpgradeBusy(true);
                  const fn = httpsCallable(getFunctions(), "createCheckoutSession");
                  const res = await fn();
                  const url = res?.data?.url;
                  if (url) { window.location.assign(url); return; }
                  throw new Error("No checkout URL returned");
                } catch (e) {
                  console.error("createCheckoutSession failed", e);
                  setUpgradeBusy(false);
                  alert("Sorry, we couldn't start checkout. Please try again.");
                }
              }}
              disabled={upgradeBusy}
              style={{ background: ORANGE, color: "#fff", border: "none", padding: "11px 24px", borderRadius: "8px", fontSize: "15px", fontWeight: 700, cursor: upgradeBusy ? "default" : "pointer", opacity: upgradeBusy ? 0.7 : 1, width: "100%" }}>
              {upgradeBusy ? "Starting checkout…" : "Upgrade to Pro — $2.99/month"}
            </button>
            <button onClick={() => setUpgradeReason(null)}
              style={{ background: "transparent", color: "#999", border: "none", padding: "10px", borderRadius: "8px", fontSize: "13px", cursor: "pointer", marginTop: "6px" }}>
              Maybe later
            </button>
          </div>
        </Modal>
      )}

      {/* Quick set-passcode prompt, opened by the on-page lock icon when the
          diary has no passcode yet. On success it sets the hash, then locks
          and returns to the diaries list (matching the lock-and-leave flow). */}
      {showSetPasscode && (
        <QuickSetPasscode
          diary={diary}
          uid={user?.uid}
          onClose={() => setShowSetPasscode(false)}
          onSet={async (hash) => {
            await updateDoc(doc(db, "diaries", id), { passcodeHash: hash });
            setDiary(prev => ({ ...prev, passcodeHash: hash }));
            // Lock immediately and leave: the diary is now protected, so drop
            // to the diaries list rather than sitting here unlocked.
            clearSessionUnlock(id);
            setShowSetPasscode(false);
            setUnlocked(false);
            navigate("/dashboard");
          }}
        />
      )}

      {/* Share modal */}
      <ShareModal entry={shareEntry} onClose={() => setShareEntry(null)} />

      {/* Ownership transfer confirmation. Owner picked a member to transfer
          ownership to; this asks for explicit confirmation before writing
          the pendingOwnerTransfer field. The recipient still has to accept
          on their Dashboard before the swap actually happens. */}
      {transferTarget && (
        <Modal onClose={() => !transferring && setTransferTarget(null)}>
          <h2 style={{ fontSize: "18px", fontWeight: "700", margin: "0 0 12px", color: NAVY, fontFamily: "-apple-system,sans-serif" }}>Transfer ownership?</h2>
          <p style={{ fontSize: "14px", color: "#444", lineHeight: 1.6, margin: "0 0 16px", fontFamily: "-apple-system,sans-serif" }}>
            Transfer ownership of <strong>{diary?.name || "this diary"}</strong> to{" "}
            <strong>{memberProfiles[transferTarget]?.name || "this member"}</strong>?
          </p>
          <ul style={{ fontSize: "13px", color: "#666", lineHeight: 1.7, margin: "0 0 20px", paddingLeft: "20px", fontFamily: "-apple-system,sans-serif" }}>
            <li>They'll get a request on their Dashboard. The transfer happens only when they accept.</li>
            <li>You'll become an Editor on this diary once they accept.</li>
            <li>You can cancel the request anytime before they accept.</li>
          </ul>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <button onClick={() => setTransferTarget(null)} disabled={transferring}
              style={{ background: "white", border: "1px solid #ddd", borderRadius: "6px", padding: "8px 16px", fontSize: "13px", color: "#555", cursor: transferring ? "default" : "pointer", fontFamily: "-apple-system,sans-serif" }}>
              Cancel
            </button>
            <button onClick={() => handleInitiateTransfer(transferTarget)} disabled={transferring}
              style={{ background: transferring ? "#ddd" : ORANGE, color: "white", border: "none", borderRadius: "6px", padding: "8px 18px", fontSize: "13px", fontWeight: "700", cursor: transferring ? "default" : "pointer", fontFamily: "-apple-system,sans-serif" }}>
              {transferring ? "Sending..." : "Send transfer request"}
            </button>
          </div>
        </Modal>
      )}

    </div>
  );
}

// ── ASSIGN TAG MODAL ─────────────────────────────────────────────────────────

function AssignTagModal({ selectedIds, entries, diary, setDiary, onClose, onDone }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newTagText, setNewTagText] = useState("");

  // The tag list shown in the picker is what's actually relevant to THIS diary:
  // tags used on its entries, plus any custom tags defined for the diary, plus
  // "follow-up" which is universal. Sorted alphabetically with follow-up last.
  const diaryTags = (() => {
    const used = new Set(entries.flatMap(e => e.tags || []));
    const custom = new Set(diary?.customTags || []);
    const all = new Set([...used, ...custom]);
    all.delete("follow-up"); // pinned to end
    const sorted = [...all].sort();
    return [...sorted, "follow-up"];
  })();

  const toggle = tag => setSelected(prev =>
    prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
  );

  // Add one or more tags inline. Input is split on commas and semicolons, so
  // "roof, gutter, paint" becomes three separate tags instead of one tag
  // literally named "roof, gutter, paint". Two modes:
  //   - Enter key: save all parsed tags, apply them (with any already-selected
  //     tags) to all selected entries, and close the modal.
  //   - Add button: just save & pre-select; user can pick more tags before
  //     clicking Apply.
  const addNewTag = async (applyAndClose = false) => {
    // Split on commas/semicolons, normalize each part, drop empties + dupes.
    const pieces = newTagText
      .split(/[,;]/)
      .map(s => s.trim().toLowerCase().replace(/\s+/g, "-"))
      .filter(Boolean);
    if (!pieces.length) return;
    const uniquePieces = [...new Set(pieces)];

    // Separate into "already exists in this diary" vs "needs to be saved".
    const knownSet = new Set(diaryTags);
    const toSave = uniquePieces.filter(p => !knownSet.has(p));

    // Persist new tags to diary.customTags (single batched write).
    if (toSave.length) {
      try {
        const updatedCustomTags = [...new Set([...(diary?.customTags || []), ...toSave])];
        await updateDoc(doc(db, "diaries", diary.id), { customTags: updatedCustomTags });
        if (typeof setDiary === "function") {
          setDiary(d => d ? { ...d, customTags: updatedCustomTags } : d);
        }
      } catch (e) {
        console.error("Couldn't save new tags", e);
        alert(`Couldn't save tags: ${e?.message || "unknown error"}`);
        return;
      }
    }

    // Add ALL parsed tags (known + new) to the selected set.
    const next = [...new Set([...selected, ...uniquePieces])];
    setSelected(next);
    setNewTagText("");
    if (applyAndClose) await saveAndClose(next);
  };

  // Shared apply logic — used by both the Apply button and the Enter shortcut.
  const saveAndClose = async (tagsToApply) => {
    const toApply = tagsToApply || selected;
    if (!toApply.length) return;
    setSaving(true);
    try {
      const updates = [];
      for (const eid of selectedIds) {
        const existing = entries.find(e => e.id === eid);
        const before = Array.isArray(existing?.tags) ? existing.tags : [];
        const merged = [...new Set([...before, ...toApply])];
        if (merged.length === before.length && merged.every(t => before.includes(t))) continue;
        updates.push({ eid, tags: merged });
      }
      await Promise.all(updates.map(u =>
        updateDoc(doc(db, "entries", u.eid), { tags: u.tags, autoTagged: false })
      ));
      onDone();
    } catch (e) {
      console.error(e);
      alert(`Couldn't apply tags: ${e?.message || "unknown error"}`);
    }
    setSaving(false);
  };

  const handleSave = () => saveAndClose();

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", boxSizing: "border-box" }} onClick={onClose}>
      <div style={{ width: "100%", maxWidth: "600px", background: "white", borderRadius: "20px", overflowY: "auto", padding: "20px", boxShadow: "0 10px 40px rgba(0,0,0,0.25)", maxHeight: "calc(100dvh - 40px)", boxSizing: "border-box" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
          <span style={{ fontSize: "16px", fontWeight: "600", color: "#1a1a1a", fontFamily: "-apple-system,sans-serif" }}>
            Add tags to {selectedIds.size} {selectedIds.size === 1 ? "entry" : "entries"}
          </span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#aaa", fontSize: "20px", cursor: "pointer", padding: 0 }}>×</button>
        </div>
        <p style={{ fontSize: "12px", color: "#aaa", marginBottom: "14px", fontFamily: "-apple-system,sans-serif" }}>
          Picked tags will be added to each entry. Existing tags are kept.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "14px" }}>
          {diaryTags.length === 0 && (
            <p style={{ fontSize: "13px", color: "#aaa", margin: 0, fontStyle: "italic" }}>No tags yet — add one below.</p>
          )}
          {diaryTags.map(tag => {
            const m = getTagMeta(tag);
            const active = selected.includes(tag);
            return (
              <button key={tag} onClick={() => toggle(tag)}
                style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: active ? m.color : m.bg, color: active ? "white" : m.color, border: `2px solid ${active ? m.color : m.border}`, borderRadius: "20px", padding: "7px 14px", fontSize: "14px", fontWeight: "600", cursor: "pointer", fontFamily: "-apple-system,sans-serif", transition: "all 0.1s" }}>
                {m.emoji} {tag}
                {active && <span style={{ fontSize: "12px", marginLeft: "2px" }}>✓</span>}
              </button>
            );
          })}
        </div>
        {/* Add a new tag inline */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px", borderTop: "1px solid #f0ede8", paddingTop: "12px" }}>
          <input type="text" value={newTagText}
            onChange={e => setNewTagText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addNewTag(true); } }}
            placeholder="Add tags (comma-separated). Enter to apply…"
            style={{ flex: 1, padding: "8px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", fontFamily: "-apple-system,sans-serif", outline: "none" }} />
          <button onClick={() => addNewTag(false)} disabled={!newTagText.trim()}
            style={{ background: newTagText.trim() ? "#555" : "#ddd", border: "none", borderRadius: "8px", color: "white", fontSize: "14px", fontWeight: "600", cursor: newTagText.trim() ? "pointer" : "default", fontFamily: "-apple-system,sans-serif", padding: "8px 16px" }}>
            Add
          </button>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "4px" }}>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#888", fontSize: "14px", cursor: "pointer", fontFamily: "-apple-system,sans-serif", padding: "8px 0" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || !selected.length}
            style={{ background: selected.length ? ORANGE : "#ddd", border: "none", borderRadius: "8px", color: "white", fontSize: "14px", fontWeight: "600", cursor: selected.length ? "pointer" : "default", fontFamily: "-apple-system,sans-serif", padding: "9px 20px" }}>
            {saving ? "Saving..." : `Apply${selected.length ? " (" + selected.length + " tag" + (selected.length > 1 ? "s" : "") + ")" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── TAG MANAGEMENT COMPONENT ──────────────────────────────────────────────────

// CSV-import settings tab. Reads a CSV with the schema documented inline,
// previews the first few rows, then on confirm batch-creates entries in the
// current diary. Photo URLs in `photo_urls` (pipe-separated) are fetched and
// uploaded to Storage; failures are logged per-entry without aborting the
// whole import.
function ImportTab({ diary, user, entries = [], onDone }) {
  const [parsed, setParsed] = useState(null); // { rows, errors, headers, dupCount }
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, log: [] });
  // Indices (into parsed.rows) of flagged-duplicate rows the user has chosen
  // to import anyway. Default empty = every duplicate is skipped. Reset on
  // each new file. The per-row checkboxes and the action buttons all read
  // and write this set.
  const [acceptedDupes, setAcceptedDupes] = useState(() => new Set());
  const toggleDupe = (i) => setAcceptedDupes(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });
  // Set when an import finishes successfully — used to switch the UI into
  // a "completed" state with timestamp + count, and prevent re-running the
  // same parsed batch by accident.
  const [completed, setCompleted] = useState(null);
  const fileInputRef = useRef(null);

  // Minimal CSV parser. Supports quoted fields with embedded commas, escaped
  // quotes ("" inside a quoted field), and \r\n / \n line endings.
  const parseCsv = (text) => {
    const rows = [];
    let cur = [];
    let field = "";
    let inQuotes = false;
    let i = 0;
    while (i < text.length) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"' && text[i + 1] === '"') { field += '"'; i += 2; continue; }
        if (ch === '"') { inQuotes = false; i++; continue; }
        field += ch; i++; continue;
      }
      if (ch === '"') { inQuotes = true; i++; continue; }
      if (ch === ",") { cur.push(field); field = ""; i++; continue; }
      if (ch === "\r") { i++; continue; }
      if (ch === "\n") { cur.push(field); rows.push(cur); cur = []; field = ""; i++; continue; }
      field += ch; i++;
    }
    if (field.length || cur.length) { cur.push(field); rows.push(cur); }
    return rows.filter(r => r.length > 1 || (r.length === 1 && r[0].trim()));
  };

  // Parse a date string into a JS Date. Accepts:
  //   "2024-03-15" or "2024-03-15T10:30"
  //   "M/D/YYYY", "M/D/YY"  (interpreted as 21st-century year if 2 digits)
  //   "March 15, 2024"
  const parseDate = (s) => {
    if (!s) return null;
    const t = s.trim();
    let m;
    // ISO-ish: 2024-03-15 [HH:MM]
    if ((m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s](\d{1,2}):(\d{2}))?$/))) {
      return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 12), +(m[5] || 0));
    }
    // M/D/YY[YY]
    if ((m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/))) {
      let y = +m[3];
      if (y < 100) y = 2000 + y;
      return new Date(y, +m[1] - 1, +m[2], 12, 0);
    }
    // Natural: "April 16, 2026" or "April 16, 2026 9:01 AM". If no time
    // component appears in the string, default to noon (matching ISO and
    // M/D/YY behavior above).
    const d = new Date(t);
    if (!Number.isFinite(d.getTime())) return null;
    const hasTime = /\d{1,2}:\d{2}|\b[AaPp][Mm]\b/.test(t);
    if (!hasTime) { d.setHours(12); d.setMinutes(0); d.setSeconds(0); }
    return d;
  };

  const parseTime = (s) => {
    if (!s) return null;
    const m = s.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
    if (!m) return null;
    let h = +m[1];
    const min = +m[2];
    const ampm = (m[3] || "").toLowerCase();
    if (ampm === "pm" && h < 12) h += 12;
    if (ampm === "am" && h === 12) h = 0;
    return { h, min };
  };

  const parseBool = (s) => /^(true|yes|y|1)$/i.test((s || "").trim());

  // ── Duplicate detection ───────────────────────────────────────────────
  // The most common way to accidentally double-up entries is re-importing
  // an overlapping export — e.g. you download this month's bank/expense CSV
  // and it shares rows with last month's import. We fingerprint every row
  // and flag (skip by default) any that match an entry already in the diary
  // OR an earlier row in the same file. Flagged rows aren't dropped — they
  // stay in `parsed.rows` with a `_dupOf` marker so the user can override
  // and "import anyway" (the date+amount+vendor key has real false-positive
  // cases, like two identical small purchases the same day, and silently
  // dropping those would be unrecoverable data loss).
  //
  // Two fingerprint shapes, chosen by whether the row is expense-like:
  //   - has an amount OR vendor → date | vendor | amount | tags
  //     (title/body intentionally ignored: the same transaction can be
  //      described differently across two exports, but date+vendor+amount
  //      pins it down)
  //   - no amount AND no vendor → date | title | body | tags
  //     (journal / workout entries; date+title+body must match — the date
  //      alone is far too weak — many real entries share a calendar day)
  //
  // TAGS are part of BOTH fingerprints (added per product decision): two
  // rows with the same date/vendor/amount but DIFFERENT tags are treated as
  // distinct, not duplicates. This fits tag-as-key workflows (e.g. property
  // management, where the unit tag "2425" vs "31leese" is what distinguishes
  // two same-day same-amount expenses). Tradeoff to be aware of: because any
  // tag difference defeats a match, if an entry is imported and later gains a
  // tag in-app, re-importing the same source row will NOT be flagged (the
  // stored entry's tag set no longer matches). Tag order is normalized away.
  //
  // All shapes use the *calendar date* (local Y-M-D), never the timestamp:
  // import defaults a missing time to noon, so a re-import of the same day
  // must not be defeated by a 9:30-vs-12:00 difference.

  // Vendor normalization: lowercase, strip a trailing store number
  // ("Home Depot #6317" → "home depot"), collapse whitespace. Deliberately
  // conservative — we don't want to merge unrelated vendors.
  const normVendor = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/#\s*\d+\s*$/, "")
      .replace(/\s+/g, " ")
      .trim();

  // Title/body normalization for the non-expense fingerprint. Lowercase +
  // whitespace-collapse only; case and spacing differences from a re-export
  // shouldn't defeat an otherwise-exact match.
  const normText = (s) =>
    String(s || "").toLowerCase().replace(/\s+/g, " ").trim();

  // Cheap HTML→text for existing entries that lack a plain `body` field.
  // We avoid the module-level toText() here because it mounts each node
  // into the DOM to read innerText — fine for a single entry, but this runs
  // across every existing entry in the diary on each file selection, so a
  // pure-string strip is the right tool.
  const stripTags = (html) =>
    String(html || "")
      .replace(/<[^>]+>/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&nbsp;/g, " ");

  // Local-timezone Y-M-D. Accepts a Firestore Timestamp (existing entries),
  // a JS Date (freshly parsed rows), or anything Date can swallow.
  const localYmd = (date) => {
    if (!date) return "";
    const d = date.toDate ? date.toDate() : (date instanceof Date ? date : new Date(date));
    if (!d || !Number.isFinite(d.getTime())) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  // Extract (vendor, amount) using the SAME cascade as the expense export
  // (see buildExpensesCsv) so dedup agrees with what the app considers an
  // expense. Amount: structured.amount → expenseData.amount → re-parse from
  // title+body. Vendor: expenseData.vendor (or slash-title parse) →
  // structured.location. Works on both incoming rows and existing entries.
  const extractExpenseKey = (entryLike) => {
    const sd = entryLike.structured || {};
    const parsed = entryLike.expenseData
      || parseExpenseTitle(entryLike.title || "")
      || { vendor: "", amount: "" };
    let amount = null;
    if (typeof sd.amount === "number") amount = sd.amount;
    else if (typeof parsed.amount === "number") amount = parsed.amount;
    else if (parsed.amount) {
      const n = parseFloat(String(parsed.amount).replace(/[^0-9.-]/g, ""));
      amount = Number.isFinite(n) ? n : null;
    } else {
      // Last resort: scan title+body for a $-amount, matching the export.
      const bodyText = entryLike.body || stripTags(entryLike.bodyHtml);
      const ps = parseStructuredFields(`${entryLike.title || ""} ${bodyText}`.trim());
      if (ps.amount != null) amount = ps.amount;
    }
    let vendor = parsed.vendor || "";
    if (!vendor && sd.location) {
      vendor = Array.isArray(sd.location) ? (sd.location[0] || "") : String(sd.location);
    }
    return { amount, vendor };
  };

  // Tag normalization for the fingerprint: lowercase, trim, drop blanks,
  // sort so order doesn't matter ("plumbing, 2425" === "2425, plumbing").
  const normTags = (tags) =>
    (Array.isArray(tags) ? tags : [])
      .map(t => String(t || "").toLowerCase().trim())
      .filter(Boolean)
      .sort()
      .join("|");

  // Build the duplicate-detection key for a row or existing entry.
  const fingerprintOf = (entryLike) => {
    const dateKey = localYmd(entryLike.entryDate || entryLike.date);
    const tagKey = normTags(entryLike.tags);
    const { amount, vendor } = extractExpenseKey(entryLike);
    const hasAmount = typeof amount === "number" && Number.isFinite(amount);
    const v = normVendor(vendor);
    if (hasAmount || v) {
      // Round to whole cents so float noise (e.g. 5.7299999) can't split a
      // match. Signed — a +25 income row and a -25 expense row are distinct.
      const cents = hasAmount ? Math.round(amount * 100) : "";
      return `e|${dateKey}|${v}|${cents}|${tagKey}`;
    }
    // Non-expense: date + title + body (+ tags) must all match.
    const bodyText = entryLike.body || stripTags(entryLike.bodyHtml);
    return `n|${dateKey}|${normText(entryLike.title)}|${normText(bodyText)}|${tagKey}`;
  };

  // Short human-readable descriptor of a row/entry, used to render the
  // duplicate-review list (and to show what an incoming row matched).
  const describeForDup = (entryLike) => {
    const { amount, vendor } = extractExpenseKey(entryLike);
    const d = entryLike.entryDate?.toDate
      ? entryLike.entryDate.toDate()
      : (entryLike.date instanceof Date ? entryLike.date
        : (entryLike.entryDate || entryLike.date ? new Date(entryLike.entryDate || entryLike.date) : null));
    return {
      dateStr: d && Number.isFinite(d.getTime()) ? d.toLocaleDateString() : "",
      title: entryLike.title || "",
      vendor: vendor || "",
      amount: (typeof amount === "number" && Number.isFinite(amount)) ? amount : null,
      tags: (Array.isArray(entryLike.tags) ? entryLike.tags : []).join(", "),
    };
  };

  const handleFile = async (file) => {
    if (!file) return;
    // Clear any previous import completion so the UI returns to the
    // "choose/preview/import" flow.
    setCompleted(null);
    setProgress({ done: 0, total: 0, log: [] });
    setAcceptedDupes(new Set());
    // Read the file into `rawRows` (array of string arrays) from either a
    // CSV or an Excel workbook, then run the identical downstream pipeline.
    // Excel support matters because (a) our own export produces .xlsx, so
    // the natural round-trip is "export → tweak in Excel → re-import", and
    // (b) handing a raw .xlsx to the CSV text parser used to fail with
    // "missing required columns: date" — file.text() returns the ZIP bytes,
    // not cells, so the header row was garbage. SheetJS is already a
    // dependency (lazy-loaded in the export path), so this adds no bundle.
    const name = (file.name || "").toLowerCase();
    const isExcel = /\.xlsx?$/.test(name)
      || (file.type || "").includes("sheet")      // .xlsx mime
      || (file.type || "").includes("ms-excel");   // .xls mime
    let rawRows;
    if (isExcel) {
      try {
        const XLSX = await import("xlsx");
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        if (!ws) {
          setParsed({ rows: [], errors: ["That spreadsheet has no readable sheet."], headers: [] });
          return;
        }
        // header:1 → rows as arrays (parallel to parseCsv's output).
        // raw:false → use each cell's *formatted* text, so a date cell reads
        //   "5/21/2026" (which parseDate handles) rather than the underlying
        //   serial number, and "$24.99" stays parseable. Verified against
        //   SheetJS 0.18 for both real date cells and text dates.
        // defval:"" → empty cells become "" so downstream .trim() is safe.
        // blankrows:false → drop fully-empty rows (matches parseCsv).
        rawRows = XLSX.utils
          .sheet_to_json(ws, { header: 1, raw: false, defval: "", blankrows: false })
          .map(r => (Array.isArray(r) ? r.map(c => (c == null ? "" : String(c))) : []));
      } catch (e) {
        setParsed({ rows: [], errors: [`Couldn't read that Excel file — ${e.message || e}. Try saving it as CSV and importing that.`], headers: [] });
        return;
      }
    } else {
      const text = await file.text();
      rawRows = parseCsv(text);
    }
    if (rawRows.length < 2) {
      setParsed({ rows: [], errors: ["File is empty or has only a header row."], headers: [] });
      return;
    }
    const headers = rawRows[0].map(h => h.trim().toLowerCase());
    const required = ["date"];
    const missing = required.filter(r => !headers.includes(r));
    if (missing.length) {
      setParsed({ rows: [], errors: [`File is missing the required "${missing.join(", ")}" column. The first row must be the column headers (see the downloadable template).`], headers });
      return;
    }
    // Column-name resolution. We accept multiple synonyms for some columns
    // so users can use intuitive names (subject, price, category) while older
    // CSVs that use the technical names (title, amount, unit) still import.
    const idxAny = (...names) => {
      for (const n of names) {
        const i = headers.indexOf(n);
        if (i >= 0) return i;
      }
      return -1;
    };
    const cIdx = {
      date: idxAny("date"), time: idxAny("time"),
      title: idxAny("subject", "title"),
      body: idxAny("body"),
      tags: idxAny("tags"),
      favorite: idxAny("favorite"), follow_up: idxAny("follow_up", "followup"),
      vendor: idxAny("vendor"),
      amount: idxAny("price", "amount"),
      unit: idxAny("category", "unit"),
      description: idxAny("description"),
      photo_urls: idxAny("photo_urls", "photos"),
      // Structured shorthand fields — direct columns in the CSV. Users can
      // fill these instead of typing shorthand in subject/body. If subject
      // is empty, we auto-generate it from body + structured fields.
      location: idxAny("location"),
      people: idxAny("people"),
      time_minutes: idxAny("time_minutes", "time_min", "minutes"),
      distance_mi: idxAny("distance_mi", "distance"),
      calories: idxAny("calories"),
      mood: idxAny("mood"),
      rating: idxAny("rating"),
      score: idxAny("score"),
    };
    // Library + custom column resolution. Any header that matches a
    // FIELD_LIBRARY key (medication, symptom, body_part, property, item,
    // etc.) becomes a library column. Headers that aren't built-ins or
    // library keys become custom columns. Track which library keys appear
    // so we can auto-enable them on the diary after a successful import.
    const RECOGNIZED_HEADERS = new Set([
      "date","time","subject","title","body","tags","favorite","follow_up","followup",
      "vendor","price","amount","unit","category","description","photo_urls","photos",
      "location","people","time_minutes","time_min","minutes","distance_mi","distance",
      "calories","mood","rating","score",
    ]);
    const libraryColumns = {};   // libKey -> headerIdx
    const customColumns = {};    // name -> headerIdx
    const importedLibraryKeys = new Set();
    const importedCustomKeys = new Set();  // custom names that had ≥1 value
    for (let i = 0; i < headers.length; i++) {
      const h = headers[i];
      if (!h || RECOGNIZED_HEADERS.has(h)) continue;
      if (FIELD_LIBRARY[h]) {
        libraryColumns[h] = i;
      } else {
        customColumns[h] = i;
      }
    }
    const rows = [];
    const errors = [];
    // Helper: build a structured object from CSV columns. Returns null if
    // no structured field was provided.
    const buildStructuredFromCsv = (get, row) => {
      const s = {};
      const amtRaw = get("amount");
      if (amtRaw) {
        const n = parseFloat(amtRaw.replace(/[$,\s]/g, ""));
        if (Number.isFinite(n)) s.amount = n;
      }
      const locRaw = get("location");
      if (locRaw) {
        const list = locRaw.split(/[,;|]/).map(l => l.trim()).filter(Boolean);
        if (list.length) s.location = list;
      }
      const peopleRaw = get("people");
      if (peopleRaw) {
        const list = peopleRaw.split(/[,;|]/).map(p => p.trim()).filter(Boolean);
        if (list.length) s.people = list;
      }
      const tmRaw = get("time_minutes");
      if (tmRaw) {
        // Accept either "90" (minutes) or shorthand like "1.5h", "30min"
        const asNum = parseFloat(tmRaw);
        if (Number.isFinite(asNum) && /^\s*\d+(\.\d+)?\s*$/.test(tmRaw)) {
          s.timeMinutes = Math.round(asNum);
        } else {
          // Try shorthand parsing
          const ps = parseStructuredFields(tmRaw);
          if (ps.timeMinutes !== null) s.timeMinutes = ps.timeMinutes;
        }
      }
      const distRaw = get("distance_mi");
      if (distRaw) {
        const n = parseFloat(distRaw);
        if (Number.isFinite(n)) s.distanceMiles = n;
      }
      const calRaw = get("calories");
      if (calRaw) {
        const n = parseInt(calRaw, 10);
        if (Number.isFinite(n)) s.calories = n;
      }
      const moodRaw = get("mood");
      if (moodRaw) {
        const list = moodRaw.split(/[,;|]/).map(m => m.trim()).filter(Boolean);
        if (list.length) s.mood = list;
      }
      if (get("rating")) s.rating = get("rating");
      if (get("score")) s.score = get("score");

      // Library fields (medication, symptom, body_part, property, item,
      // etc.) — read each column, parse via the field's own parser, and
      // store under s.library[<key>]. Track which keys had values so we
      // can auto-enable them on the diary after import.
      for (const [libKey, colIdx] of Object.entries(libraryColumns)) {
        const raw = (row[colIdx] || "").trim();
        if (!raw) continue;
        const def = FIELD_LIBRARY[libKey];
        if (!def) continue;
        const parsed = def.parse ? def.parse(raw) : raw;
        if (parsed != null && !(Array.isArray(parsed) && !parsed.length)) {
          if (!s.library) s.library = {};
          s.library[libKey] = parsed;
          importedLibraryKeys.add(libKey);
        }
      }

      // Custom fields (any unrecognized column header). Stored as
      // comma-separated arrays for multi-value fields, scalar otherwise.
      // Only a column that actually carries a value on some row should ever
      // become a declared custom field — an empty column describes nothing
      // and would just pollute the diary's field list (this is exactly how
      // template placeholders like custom_1/2/3 used to leak in). We mirror
      // the library-field pattern above: the `if (!raw) continue` guard means
      // importedCustomKeys only collects names with real data.
      for (const [name, colIdx] of Object.entries(customColumns)) {
        const raw = (row[colIdx] || "").trim();
        if (!raw) continue;
        const list = raw.split(/[,;|]/).map(v => v.trim()).filter(Boolean);
        if (!s.custom) s.custom = {};
        s.custom[name] = list.length > 1 ? list : raw;
        importedCustomKeys.add(name);
      }

      return Object.keys(s).length ? s : null;
    };
    // Helper: when title is empty, auto-generate one from body + structured
    // fields. Delegates to module-level generateTitleFromStructured.
    const generateTitle = (body, structured, tags) => {
      return generateTitleFromStructured(body, structured);
    };
    for (let r = 1; r < rawRows.length; r++) {
      const row = rawRows[r];
      const get = (k) => cIdx[k] >= 0 ? (row[cIdx[k]] || "").trim() : "";
      const dateStr = get("date");
      // Hint/example rows in the downloadable template have a placeholder
      // date (e.g. "— hint: 2024-03-15") instead of a real date. Skip
      // them silently so the user doesn't see a confusing "couldn't parse
      // date" error for a row they were never meant to import. Detect by
      // a leading em-dash or the literal string "e.g." or "example".
      const looksLikeHintRow = /^\s*(—|--|e\.?g\.?\b|example\b|hint\b)/i.test(dateStr || "");
      if (looksLikeHintRow) continue;  // silent skip — this is the docs row
      const d = parseDate(dateStr);
      if (!d) {
        errors.push(`Row ${r + 1}: couldn't parse date "${dateStr}" — skipping`);
        continue;
      }
      const tm = parseTime(get("time"));
      if (tm) { d.setHours(tm.h); d.setMinutes(tm.min); }
      let title = get("title");
      const body = get("body");
      const description = get("description");
      const tagsRaw = get("tags");
      const tags = tagsRaw ? tagsRaw.split(/[,;|]/).map(t => t.trim()).filter(Boolean) : [];
      if (parseBool(get("follow_up")) && !tags.includes("follow-up")) tags.push("follow-up");
      const structured = buildStructuredFromCsv(get, row);
      // Row needs date AND (title OR body OR description OR any structured
      // field). Tags alone don't count as content — they're for organizing
      // entries that already have content.
      if (!title && !body && !description && !structured) {
        errors.push(`Row ${r + 1}: needs a subject, body, description, or at least one structured field — skipping`);
        continue;
      }
      // Auto-generate title from body + structured fields if title is empty.
      // Falls back to description (expense-style item descriptor) when
      // body is empty — typical for imported expense rows where the
      // user puts "groceries" or "butt splice for wires" in Description
      // instead of Body. Without this fallback, expense titles would
      // omit the item entirely ("$5.42 @home depot" instead of
      // "$5.42 @home depot butt splice for wires").
      if (!title) {
        title = generateTitle(body || description, structured, tags);
      }
      const photoUrlsRaw = get("photo_urls");
      const photoUrls = photoUrlsRaw ? photoUrlsRaw.split(/[|;]/).map(u => u.trim()).filter(Boolean) : [];
      const entry = {
        date: d,
        title,
        body,
        tags,
        favorite: parseBool(get("favorite")),
        photoUrls,
      };
      // Attach structured data if any structured fields were provided
      if (structured) entry.structured = structured;
      // Expense fields → if any present, attach to .expenseData and add expense tag
      const vendor = get("vendor"), amount = get("amount"), unit = get("unit");
      if (vendor || amount) {
        const num = parseFloat((amount || "").replace(/[$,\s]/g, ""));
        entry.expenseData = {
          cash: false,
          vendor,
          amount: Number.isFinite(num) ? num : amount,
          unit,
          description,
        };
        if (!tags.includes("expense")) tags.push("expense");
      }
      rows.push(entry);
    }
    // ── Duplicate-detection pass ──
    // Fingerprint every existing entry in the diary (the live snapshot
    // passed in via props), then walk the freshly parsed rows. A row that
    // matches an existing fingerprint is marked "existing"; one that matches
    // an earlier row in THIS batch is marked "batch". The first occurrence
    // of any fingerprint is always left clean. Each flagged row also gets a
    // `_dupMatch` descriptor (of what it collided with) for the review list.
    // Rows are not removed here — the user decides per-row in the preview.
    const existingByFp = new Map();
    for (const e of (entries || [])) {
      const fp = fingerprintOf(e);
      if (!existingByFp.has(fp)) existingByFp.set(fp, e);
    }
    const batchByFp = new Map();
    let dupCount = 0;
    for (const row of rows) {
      const fp = fingerprintOf(row);
      if (existingByFp.has(fp)) {
        row._dupOf = "existing";
        row._dupMatch = describeForDup(existingByFp.get(fp));
        dupCount++;
      } else if (batchByFp.has(fp)) {
        row._dupOf = "batch";
        row._dupMatch = describeForDup(batchByFp.get(fp));
        dupCount++;
      } else {
        row._dupOf = null;
        row._dupMatch = null;
        batchByFp.set(fp, row);
      }
    }
    setParsed({ rows, errors, headers, dupCount, importedLibraryKeys: [...importedLibraryKeys], importedCustomKeys: [...importedCustomKeys] });
  };

  const runImport = async (rowsToImport) => {
    if (!parsed?.rows.length) return;
    // Caller passes the exact rows to write (clean rows plus whichever
    // flagged duplicates the user accepted). Fallback to clean-only.
    const toImport = Array.isArray(rowsToImport)
      ? rowsToImport
      : parsed.rows.filter(r => !r._dupOf);
    const skippedDupes = parsed.rows.length - toImport.length;
    if (!toImport.length) return;
    setImporting(true);
    setProgress({ done: 0, total: toImport.length, log: [] });
    const log = [];
    let done = 0;

    // Photo upload helper — fetches from URL, uploads to Storage, returns
    // { url, path }. On failure returns null and logs why.
    const uploadPhoto = async (url, entryIdx) => {
      try {
        const res = await fetch(url, { mode: "cors" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const ext = (blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
        const filename = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const path = `diaries/${diary.id}/photos/${filename}`;
        const sref = ref(storage, path);
        await uploadBytes(sref, blob, { contentType: blob.type });
        const downloadUrl = await getDownloadURL(sref);
        return { url: downloadUrl, path };
      } catch (e) {
        log.push(`Entry ${entryIdx + 1}: photo failed — ${url} — ${e.message || e}`);
        return null;
      }
    };

    for (let i = 0; i < toImport.length; i++) {
      const r = toImport[i];
      try {
        // Upload photos in parallel for this entry
        const photos = [];
        if (r.photoUrls.length) {
          const results = await Promise.all(r.photoUrls.map(u => uploadPhoto(u, i)));
          for (const x of results) if (x) photos.push(x);
        }
        const entryDoc = {
          diaryId: diary.id,
          authorId: user.uid,
          title: r.title,
          body: r.body,
          bodyHtml: r.body ? `<p>${r.body.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\n/g, "<br>")}</p>` : "",
          entryDate: r.date,
          createdAt: serverTimestamp(),
          tags: r.tags,
          favorite: r.favorite,
          source: "import",
          private: false, // explicit so the non-private query matches it
          photos,
        };
        if (r.expenseData) entryDoc.expenseData = r.expenseData;
        if (r.structured) entryDoc.structured = r.structured;
        await addDoc(collection(db, "entries"), entryDoc);
        done++;
        setProgress({ done, total: toImport.length, log: [...log] });
      } catch (e) {
        log.push(`Entry ${i + 1}: failed to save — ${e.message || e}`);
        setProgress({ done, total: toImport.length, log: [...log] });
      }
    }
    setImporting(false);
    // Auto-enable imported library fields + declare custom fields on the
    // diary. Without this, imported entries' library/custom data would be
    // present on the entry docs but invisible in the UI (chips, table)
    // because the diary doesn't know to show those fields.
    const libToEnable = parsed.importedLibraryKeys || [];
    const customToDeclare = parsed.importedCustomKeys || [];
    if (libToEnable.length || customToDeclare.length) {
      try {
        const update = {};
        let newLibCount = 0;
        let newCustomCount = 0;
        if (libToEnable.length) {
          const existing = new Set(diary.enabledLibrary || []);
          const before = existing.size;
          libToEnable.forEach(k => existing.add(k));
          newLibCount = existing.size - before;
          if (newLibCount > 0) update.enabledLibrary = [...existing];
        }
        if (customToDeclare.length) {
          // diary.customFields is an array of string field NAMES. (An older
          // version of this code wrote { name, multi } objects, which broke
          // the diary render — every consumer lowercases the entries as
          // strings. We now write names and normalize any pre-existing
          // objects, so each import rewrites the list back to clean strings.)
          const existingCustom = (diary.customFields || [])
            .map(c => (typeof c === "string" ? c : (c && c.name) || ""))
            .filter(Boolean);
          const existingNames = new Set(existingCustom);
          const additions = customToDeclare.filter(n => !existingNames.has(n));
          newCustomCount = additions.length;
          // Write the normalized list when there are additions OR the stored
          // list still contains non-string entries to clean up.
          const hadObjectForm = (diary.customFields || []).some(c => typeof c !== "string");
          if (additions.length || hadObjectForm) {
            update.customFields = [...existingCustom, ...additions];
          }
        }
        if (Object.keys(update).length) {
          await updateDoc(doc(db, "diaries", diary.id), update);
        }
        // Report only what was NEWLY added — re-importing a file whose fields
        // already exist should say nothing here, not "3 custom fields" again.
        if (newLibCount > 0 || newCustomCount > 0) {
          const parts = [];
          if (newLibCount > 0) parts.push(`enabled ${newLibCount} library field${newLibCount === 1 ? "" : "s"}`);
          if (newCustomCount > 0) parts.push(`added ${newCustomCount} custom field${newCustomCount === 1 ? "" : "s"}`);
          log.push(`Diary updated: ${parts.join(" and ")}.`);
        }
      } catch (e) {
        log.push(`Couldn't auto-enable fields on diary — ${e.message || e}`);
      }
    }
    setProgress(p => ({ ...p, log: [
      ...log,
      `Done. Imported ${done} of ${toImport.length} entries.`
        + (skippedDupes ? ` Skipped ${skippedDupes} likely duplicate${skippedDupes === 1 ? "" : "s"}.` : ""),
    ] }));
    // Capture completion metadata so the UI can switch to a "complete"
    // state and prevent re-running the same parsed batch by accident
    // (clicking Import twice would otherwise duplicate everything).
    setCompleted({
      at: new Date(),
      total: toImport.length,
      done,
      skippedDupes,
    });
    // Clear the parsed preview so the "Import N entries" button goes away.
    // User can pick a different file to import more.
    setParsed(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div style={{ fontFamily: "-apple-system,sans-serif" }}>
      <div style={{ fontSize: "13px", color: "#555", marginBottom: "14px", lineHeight: 1.6 }}>
        Bulk import entries from a <b>CSV or Excel</b> file into your <b>{diary.name}</b> diary. Each row needs a <b>date</b> plus content (a subject, body, or any structured field).
        <div style={{ marginTop: "10px", padding: "8px 12px", background: "#f0f7fa", border: "1px solid #cfe0e8", borderRadius: "6px", fontSize: "12px", color: "#446070" }}>
          <b>Column order doesn't matter.</b> Headers match by name (case-insensitive). You can include or omit any column.
        </div>
        <div style={{ marginTop: "12px", marginBottom: "4px", fontWeight: 600 }}>Columns:</div>
        <ul style={{ margin: "0 0 0 18px", padding: 0 }}>
          <li><b>date</b><span style={{ color: "#c83803", fontWeight: 700, marginLeft: "2px" }}>*</span> — <code>2024-03-15</code>, <code>3/15/2024</code>, or <code>March 15, 2024</code></li>
          <li><b>time</b> — <code>9:30</code> or <code>9:30 AM</code>. Defaults to noon if omitted.</li>
          <li><b>subject</b> — short title (auto-generated from other fields if blank)</li>
          <li style={{ listStyle: "none", marginLeft: "-18px", padding: "4px 0 4px 18px" }}>
            <span style={{ display: "inline-block", padding: "2px 14px", background: "#f0ebe0", color: "#7a6b50", borderRadius: "10px", fontSize: "11px", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase" }}>or</span>
          </li>
          <li><b>body</b> — the entry text (memories, notes)</li>
          <li><b>tags</b> — comma-separated (e.g. <code>tennis, lesson</code>); recommended for filtering</li>
          <li><b>amount</b> — money spent (e.g. <code>25.99</code>) or received (e.g. <code>-25.99</code>)</li>
          <li><b>location</b> — store, restaurant, vendor, park, wherever (e.g. <code>Chipotle</code>, <code>Home Depot</code>, <code>Harding Golf Course</code>, <code>Paris</code>, <code>Yellowstone National Park</code>)</li>
          <li><b>people</b> — comma-separated (e.g. <code>Coach Sylvano, Mike</code>)</li>
          <li><b>time_minutes</b> — duration in minutes (e.g. <code>90</code>)</li>
          <li><b>distance_mi</b> — miles (e.g. <code>3.5</code>)</li>
          <li><b>calories</b> — number (e.g. <code>500</code>)</li>
          <li><b>mood</b> — comma-separated (e.g. <code>energized, focused</code>)</li>
          <li><b>rating</b> — number, fraction, or word (e.g. <code>9/10</code>, <code>great</code>)</li>
          <li><b>score</b> — for matches (e.g. <code>6-2 6-3</code>)</li>
        </ul>
        <div style={{ marginTop: "12px", fontSize: "12px", color: "#777", lineHeight: 1.5, padding: "10px 12px", background: "#fafaf7", borderRadius: "8px", border: "1px solid #efebe2" }}>
          <b>Library fields:</b> structured columns like <code>medication</code>, <code>symptom</code>, <code>body_part</code>, <code>property</code>, <code>project</code>, <code>payment_type</code>, <code>spend_category</code>, <code>cuisine</code>, <code>subject</code>. Use any of them and they'll be auto-enabled on this diary after import. (All available in the downloadable template below.)
          <br /><br />
          <b>Custom fields:</b> any other column name (e.g. <code>condition</code>, <code>brand</code>) becomes a custom field, declared on the diary automatically. The template includes <code>custom_1</code>, <code>custom_2</code>, <code>custom_3</code> placeholders — rename them to whatever fits.
        </div>
        <div style={{ marginTop: "10px", fontSize: "11px", color: "#999", fontStyle: "italic" }}>
          <span style={{ color: "#c83803", fontWeight: 700 }}>*</span> required
        </div>
      </div>

      {/* Download template button — gives users a CSV with all column headers
          and three example rows showing different use cases. Includes any
          library fields the diary has enabled, so users see immediately
          which structured columns are available for their use cases. */}
      <div style={{ marginBottom: "14px" }}>
        <button onClick={() => {
          // Built-in columns always shown. ALL library fields are included
          // too — not just enabled ones — so users see every available
          // structured column up front. Adding values to an unenabled field
          // auto-enables it on import, so there's no downside. Three
          // numbered "custom_*" placeholders give users a visible reminder
          // that arbitrary columns become custom fields.
          // Column order mirrors the Excel/CSV export so users can
          // round-trip download → edit → re-import without remapping
          // columns. Matches the expense format's lead columns
          // (Date, Location, Amount, Tags, Title=subject) then Time,
          // Body, then library/custom fields.
          const builtinCols = ["date", "location", "amount", "tags", "subject", "time", "body", "people", "time_minutes", "distance_mi", "calories", "mood", "rating", "score"];
          // Library keys, but excluding any that collide with a built-in
          // header. Notably "subject" exists both as a built-in (the
          // short-headline column the importer maps to "title") AND as a
          // library field (school/topic). The built-in wins; the library
          // version stays parseable via in-app shorthand only.
          const builtinSet = new Set(builtinCols);
          const libraryCols = Object.keys(FIELD_LIBRARY).filter(k => !builtinSet.has(k));
          const customCols = ["custom_1", "custom_2", "custom_3"];
          const headers = [...builtinCols, ...libraryCols, ...customCols];

          // Hint row — explains each column with a short example. Importer
          // recognizes the "— hint:" prefix in the date cell and skips
          // silently so the user doesn't see a parse error. They can leave
          // the row in (as reference) or delete it before importing.
          const hints = {
            date: "— hint: 2024-03-15 or 3/15/2024 (required)",
            time: "9:30 or 9:30 AM (defaults to noon)",
            subject: "Short title (auto-generated if blank)",
            body: "Free-form text — what happened, notes, story",
            tags: "comma-separated, e.g. tennis, lesson",
            amount: "25.99 (spent) or -25.99 (received)",
            location: "Chipotle / Paris / Yellowstone / wherever",
            people: "comma-separated, e.g. Jane, Mike",
            time_minutes: "duration in min, e.g. 90",
            distance_mi: "miles, e.g. 3.5",
            calories: "number, e.g. 500",
            mood: "comma-separated, e.g. focused, calm",
            rating: "1-10 or 7/10 or great",
            score: "tennis, e.g. 6-2 6-3",
            // Library fields
            payment_type: "cash / credit / debit / venmo",
            spend_category: "groceries / dining / travel",
            body_part: "left knee / right shoulder",
            medication: "Advil / Lisinopril",
            cuisine: "Italian / Thai / BBQ",
            project: "kitchen reno / Q3 launch",
            symptom: "headache / sore throat",
            property: "Home / Cabin / 123 Main St",
            // Custom
            custom_1: "Rename to anything — e.g. brand",
            custom_2: "Rename to anything — e.g. mileage",
            custom_3: "Rename to anything — e.g. doctor",
          };

          // Sample values for the library fields on the example rows below.
          const sampleLibraryValue = (key, rowIndex) => {
            if (rowIndex !== 0) return "";
            const samples = {
              property: "Home",
              payment_type: "credit",
              spend_category: "home_improvement",
              project: "kitchen reno",
              medication: "",
              symptom: "",
              body_part: "",
              subject: "",
              cuisine: "",
            };
            return samples[key] || "";
          };
          const rows = [
            // Expense row
            { date: "2024-03-15", time: "9:30", subject: "", body: "Bought paint and brushes for kitchen reno", tags: "expense, kitchen, paint", amount: "52.99", location: "Home Depot" },
            // Run row
            { date: "2024-03-16", time: "7:15", subject: "", body: "Morning run on the trail", tags: "fitness, run", location: "Sequoia", people: "Jane", time_minutes: "45", distance_mi: "3.5", calories: "400", mood: "energized" },
            // Tennis row
            { date: "2024-03-17", time: "14:00", subject: "", body: "Tough match in straight sets", tags: "tennis, match", people: "Yi Cheng", time_minutes: "90", mood: "excited", rating: "8/10", score: "6-2 6-3" },
          ];

          // CSV-escape helper — wrap in quotes if value has comma/quote/newline.
          const escape = (v) => {
            if (v === "" || v == null) return "";
            const s = String(v);
            if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
            return s;
          };

          const csvLines = [headers.join(",")];
          // Hint row first (row 2 in spreadsheet). Importer auto-skips it.
          csvLines.push(headers.map(h => escape(hints[h] || "")).join(","));
          // Then the three realistic scenario rows.
          rows.forEach((row, idx) => {
            const cells = headers.map(h => {
              const v = (h in row) ? row[h] : (libraryCols.includes(h) ? sampleLibraryValue(h, idx) : "");
              return escape(v);
            });
            csvLines.push(cells.join(","));
          });
          const template = csvLines.join("\n");
          const blob = new Blob([template], { type: "text/csv;charset=utf-8" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "lifetrackr-import-template.csv";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }}
          style={{ background: "transparent", border: "1px solid #ddd", color: "#555", padding: "6px 14px", borderRadius: "6px", fontSize: "13px", cursor: "pointer", fontFamily: "-apple-system,sans-serif", display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M8 2v9M4 7l4 4 4-4M2 14h12" />
          </svg>
          Download template
        </button>
        <span style={{ fontSize: "11px", color: "#999", marginLeft: "8px" }}>
          (CSV with all available columns + a hint row explaining each one)
        </span>
      </div>

      {/* Import completion banner. Shows after a successful import with
          count + timestamp. Includes a "Import another file" button to
          reset back to the file-picker UI. */}
      {completed && !importing && (
        <div style={{
          background: "#e8f5e9",
          border: "1px solid #66bb6a",
          borderRadius: "8px",
          padding: "14px 16px",
          marginBottom: "14px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}>
          <div style={{ fontSize: "22px" }}>✅</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: "14px", color: "#1b5e20" }}>
              Import complete
            </div>
            <div style={{ fontSize: "12px", color: "#2e7d32", marginTop: "2px" }}>
              {completed.done} {completed.done === 1 ? "entry" : "entries"} imported at{" "}
              {completed.at.toLocaleString(undefined, {
                month: "short", day: "numeric", year: "numeric",
                hour: "numeric", minute: "2-digit",
              })}
              {completed.done < completed.total && (
                <> · {completed.total - completed.done} failed</>
              )}
              {completed.skippedDupes > 0 && (
                <> · {completed.skippedDupes} duplicate{completed.skippedDupes === 1 ? "" : "s"} skipped</>
              )}
            </div>
          </div>
          <button onClick={() => { setCompleted(null); setProgress({ done: 0, total: 0, log: [] }); if (fileInputRef.current) fileInputRef.current.value = ""; }}
            style={{ background: "transparent", border: "1px solid #66bb6a", color: "#1b5e20", borderRadius: "6px", padding: "6px 12px", fontSize: "12px", cursor: "pointer", fontWeight: 600 }}>
            Import another file
          </button>
        </div>
      )}

      {!parsed && !importing && !completed && (
        <div>
          <input ref={fileInputRef} type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={e => handleFile(e.target.files?.[0])}
            style={{ display: "block", marginBottom: "10px" }} />
        </div>
      )}

      {parsed && !importing && (() => {
        // Rows split into clean (always importable) and flagged duplicates.
        // Clean rows always import. A flagged duplicate imports only if the
        // user ticks its "import anyway" box (tracked in acceptedDupes by
        // index). The middle action button writes clean + accepted; "Import
        // all" writes everything; "Cancel" discards the batch.
        const cleanRows = parsed.rows.filter(r => !r._dupOf);
        const dupEntries = parsed.rows
          .map((r, i) => ({ r, i }))
          .filter(({ r }) => r._dupOf);
        const acceptedCount = dupEntries.filter(({ i }) => acceptedDupes.has(i)).length;
        const selectedRows = parsed.rows.filter((r, i) => !r._dupOf || acceptedDupes.has(i));
        const middleCount = cleanRows.length + acceptedCount;
        const totalCount = parsed.rows.length;
        const reset = () => { setParsed(null); setAcceptedDupes(new Set()); if (fileInputRef.current) fileInputRef.current.value = ""; };
        return (
        <div>
          <div style={{ background: "#f7f6f2", border: "1px solid #ddd", borderRadius: "8px", padding: "12px", marginBottom: "12px" }}>
            <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "8px" }}>
              {cleanRows.length} {cleanRows.length === 1 ? "entry" : "entries"} ready to import
              {parsed.dupCount > 0 && (
                <span style={{ fontWeight: 400, color: "#8a6d00" }}> · {parsed.dupCount} possible duplicate{parsed.dupCount === 1 ? "" : "s"}</span>
              )}
            </div>
            {parsed.errors.length > 0 && (
              <div style={{ fontSize: "12px", color: "#a00", marginBottom: "8px" }}>
                <b>{parsed.errors.length} skipped:</b>
                <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                  {parsed.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                  {parsed.errors.length > 5 && <li>…and {parsed.errors.length - 5} more</li>}
                </ul>
              </div>
            )}
            {/* Duplicate review. Each flagged row is shown with its details
                and what it collided with, plus an "import anyway" checkbox
                (default off = skip). Tags are part of the match key, so rows
                that share date/vendor/amount but differ in tags do NOT
                appear here. */}
            {dupEntries.length > 0 && (
              <div style={{ fontSize: "12px", color: "#6b5800", marginBottom: "10px", padding: "10px 12px", background: "#fff8e1", border: "1px solid #ffe082", borderRadius: "6px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px", gap: "8px" }}>
                  <span>
                    <b>Possible Duplicates ({dupEntries.length})</b>
                    <span style={{ display: "block", fontSize: "11px", color: "#9a8350", marginTop: "1px" }}>
                      These will NOT be imported unless selected.
                    </span>
                  </span>
                  <span style={{ display: "flex", gap: "10px", flexShrink: 0 }}>
                    <button onClick={() => setAcceptedDupes(new Set(dupEntries.map(({ i }) => i)))}
                      style={{ background: "transparent", border: "none", color: "#8a6d00", textDecoration: "underline", cursor: "pointer", fontSize: "11px", padding: 0 }}>Select all</button>
                    <button onClick={() => setAcceptedDupes(new Set())}
                      style={{ background: "transparent", border: "none", color: "#8a6d00", textDecoration: "underline", cursor: "pointer", fontSize: "11px", padding: 0 }}>Clear all</button>
                  </span>
                </div>
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {dupEntries.map(({ r, i }) => {
                    const checked = acceptedDupes.has(i);
                    const amtStr = r._dupMatch && r._dupMatch.amount != null ? ` · $${r._dupMatch.amount}` : "";
                    const label = r.title || (r._dupMatch && r._dupMatch.vendor) || "(no title)";
                    const tagStr = r.tags && r.tags.length ? ` · [${r.tags.join(", ")}]` : "";
                    return (
                      <li key={i} style={{ marginBottom: "6px" }}>
                        <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer" }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleDupe(i)} style={{ marginTop: "2px", flexShrink: 0 }} />
                          <span style={{ opacity: checked ? 1 : 0.75 }}>
                            <span><i>{r.date.toLocaleDateString()}</i> — <b>{label}</b>{amtStr}{tagStr}</span>
                            <div style={{ fontSize: "10.5px", color: "#9a8350", marginTop: "1px" }}>
                              {r._dupOf === "existing"
                                ? "Already in your diary"
                                : "Repeats an earlier row in this file"}
                              {checked ? " · will import" : " · will skip"}
                            </div>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {cleanRows.length > 0 && (
              <div style={{ fontSize: "12px", color: "#555" }}>
                <b>Preview (first 3):</b>
                <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                  {cleanRows.slice(0, 3).map((r, i) => (
                    <li key={i} style={{ marginBottom: "4px" }}>
                      <i>{r.date.toLocaleDateString()}</i> — <b>{r.title || "(no title)"}</b>
                      {r.tags.length > 0 && <span style={{ color: "#888" }}> [{r.tags.join(", ")}]</span>}
                      {r.photoUrls.length > 0 && <span style={{ color: "#888" }}> · {r.photoUrls.length} photo{r.photoUrls.length === 1 ? "" : "s"}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {/* Action row. With no duplicates: Cancel + a single Import button.
              With duplicates: Cancel + "Import non-duplicates" (which grows to
              "Import N" as rows are ticked) + "Import all". */}
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button onClick={reset}
              style={{ background: "transparent", border: "1px solid #ddd", color: "#555", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", fontSize: "14px" }}>
              Cancel
            </button>
            {parsed.dupCount === 0 ? (
              <button onClick={() => runImport(parsed.rows)} disabled={!totalCount}
                style={{ background: totalCount ? ORANGE : "#ddd", border: "none", color: "white", padding: "8px 18px", borderRadius: "8px", cursor: totalCount ? "pointer" : "default", fontSize: "14px", fontWeight: 600 }}>
                Import {totalCount} {totalCount === 1 ? "entry" : "entries"}
              </button>
            ) : (
              <>
                <button onClick={() => runImport(selectedRows)} disabled={!middleCount}
                  style={{ background: middleCount ? ORANGE : "#ddd", border: "none", color: "white", padding: "8px 18px", borderRadius: "8px", cursor: middleCount ? "pointer" : "default", fontSize: "14px", fontWeight: 600 }}>
                  Import {middleCount} {middleCount === 1 ? "entry" : "entries"}
                </button>
                <button onClick={() => runImport(parsed.rows)}
                  style={{ background: "transparent", border: `1px solid ${ORANGE}`, color: ORANGE, padding: "8px 18px", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 600 }}>
                  Import all ({totalCount})
                </button>
              </>
            )}
          </div>
        </div>
        );
      })()}

      {importing && (
        <div>
          <div style={{ marginBottom: "8px", fontSize: "14px" }}>
            Importing… <b>{progress.done} of {progress.total}</b>
          </div>
          <div style={{ background: "#eee", borderRadius: "4px", height: "8px", overflow: "hidden", marginBottom: "10px" }}>
            <div style={{ background: ORANGE, height: "100%", width: `${(progress.done / Math.max(1, progress.total)) * 100}%`, transition: "width 0.2s" }} />
          </div>
        </div>
      )}

      {!importing && progress.log.length > 0 && (
        <div style={{ marginTop: "12px", maxHeight: "200px", overflow: "auto", background: "#f7f6f2", border: "1px solid #ddd", borderRadius: "6px", padding: "8px", fontSize: "11px", color: "#555", fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
          {progress.log.join("\n")}
        </div>
      )}

      {!importing && progress.done > 0 && !completed && (
        <div style={{ marginTop: "12px" }}>
          <button onClick={onDone}
            style={{ background: ORANGE, border: "none", color: "white", padding: "8px 18px", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 600 }}>
            Done
          </button>
        </div>
      )}
    </div>
  );
}


// ── TAG GROUPS MANAGEMENT (settings tab) ──────────────────────────────────────
//
// Lets users define a hierarchy of tag groups (e.g. LLC → Building → Unit)
// so that filtering by a parent shows all entries tagged with any of its
// descendant tags. Stage 1 (this file): data model + UI for defining the
// hierarchy. Stage 2 (later): wire groups into the entry filter UI so
// clicking a group filters by its tag set.
//
// Data shape persisted on the diary doc:
//   diary.tagGroups = [
//     { id, name, parentId: null | "group_xxx", tags: ["1589sacu2", ...] }
//   ]
//
// Membership rules:
//   - Each tag belongs to at most one group (enforced at add-time).
//   - Adding a tag to a new group automatically removes it from any other
//     group it was in. This keeps the mental model clean: one home per tag.
//   - Groups can nest arbitrarily deep via parentId pointers. No circular
//     references — we don't allow setting a group's parent to one of its
//     own descendants.
//   - Deleting a group orphans its children (they move to root). Tags in
//     the deleted group revert to "unassigned" (not in any group).

function GroupsManagement({ diary, entries, setDiary }) {
  const groups = Array.isArray(diary?.tagGroups) ? diary.tagGroups : [];
  // All tags that appear anywhere — entries + customTags. Used to populate
  // the "add tag" picker and to compute the unassigned-tags list.
  const allTags = useMemo(() => {
    const s = new Set();
    for (const e of (entries || [])) {
      for (const t of (e.tags || [])) s.add(t);
    }
    for (const t of (diary?.customTags || [])) s.add(t);
    return [...s].sort();
  }, [entries, diary?.customTags]);

  // Map tag → groupId for quick "is this tag already in a group?" checks.
  const tagToGroup = useMemo(() => {
    const m = new Map();
    for (const g of groups) {
      for (const t of (g.tags || [])) m.set(t, g.id);
    }
    return m;
  }, [groups]);

  // Tags not assigned to any group, alphabetized.
  const unassignedTags = allTags.filter(t => !tagToGroup.has(t));

  // UI state.
  const [newGroupName, setNewGroupName] = useState("");
  const [creatingUnder, setCreatingUnder] = useState(null);  // parentId or null = root
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(() => new Set(groups.map(g => g.id)));
  // Add-tag picker state: { groupId } when open, null otherwise.
  const [pickingForGroup, setPickingForGroup] = useState(null);
  // Group pending delete confirmation. null = no modal open.
  const [deletingGroup, setDeletingGroup] = useState(null);

  // Persist a partial diary update. Optimistic — local setDiary first, then write.
  const persist = async (update) => {
    setDiary(prev => ({ ...prev, ...update }));
    try {
      await updateDoc(doc(db, "diaries", diary.id), update);
    } catch (e) {
      console.error("Failed to update diary tag groups:", e);
    }
  };

  const persistGroups = (next) => persist({ tagGroups: next });

  // Generate a stable group id. Random + prefix so it's clearly a group ref
  // and unlikely to collide.
  const newGroupId = () =>
    "group_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

  const addGroup = () => {
    const name = newGroupName.trim();
    if (!name) return;
    // Reject duplicates within the same parent — keeps the tree readable.
    const dup = groups.some(g => g.parentId === creatingUnder && g.name.toLowerCase() === name.toLowerCase());
    if (dup) {
      alert(`A group named "${name}" already exists at this level.`);
      return;
    }
    const next = [...groups, { id: newGroupId(), name, parentId: creatingUnder, tags: [] }];
    persistGroups(next);
    setNewGroupName("");
    setCreatingUnder(null);
  };

  const renameGroup = (id, name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const target = groups.find(g => g.id === id);
    if (!target) return;
    const dup = groups.some(g => g.id !== id && g.parentId === target.parentId && g.name.toLowerCase() === trimmed.toLowerCase());
    if (dup) {
      alert(`A group named "${trimmed}" already exists at this level.`);
      return;
    }
    const next = groups.map(g => g.id === id ? { ...g, name: trimmed } : g);
    persistGroups(next);
    setEditingGroupId(null);
    setEditingName("");
  };

  // Delete a group. Children orphan to root (parentId → null). The group's
  // tags become unassigned (they're not stored anywhere else).
  const deleteGroup = (id) => {
    const next = groups
      .filter(g => g.id !== id)
      .map(g => g.parentId === id ? { ...g, parentId: null } : g);
    persistGroups(next);
    setDeletingGroup(null);
  };

  const addTagToGroup = (groupId, tag) => {
    // Remove from any other group first (one-home rule).
    const next = groups.map(g => {
      const filtered = (g.tags || []).filter(t => t !== tag);
      if (g.id === groupId) return { ...g, tags: [...filtered, tag] };
      return { ...g, tags: filtered };
    });
    persistGroups(next);
  };

  const removeTagFromGroup = (groupId, tag) => {
    const next = groups.map(g => g.id === groupId ? { ...g, tags: (g.tags || []).filter(t => t !== tag) } : g);
    persistGroups(next);
  };

  const toggleExpanded = (id) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Build a tree by parentId for rendering. childrenOf[parentId] = [groups].
  const childrenOf = useMemo(() => {
    const m = new Map();
    for (const g of groups) {
      const key = g.parentId || "__root__";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(g);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.name.localeCompare(b.name));
    return m;
  }, [groups]);

  // Collect a group's descendants (children, grandchildren, ...) by walking
  // childrenOf. Used to prevent the user from re-parenting a group to one
  // of its own descendants, which would create a cycle in the tree.
  const descendantIds = (groupId) => {
    const out = new Set();
    const walk = (id) => {
      const kids = childrenOf.get(id) || [];
      for (const k of kids) {
        if (out.has(k.id)) continue;
        out.add(k.id);
        walk(k.id);
      }
    };
    walk(groupId);
    return out;
  };

  // Re-parent a group. Validates: target must be a valid group id (or null
  // for top level) and must not be the group itself or any descendant.
  const moveGroup = (groupId, newParentId) => {
    if (groupId === newParentId) return;
    if (newParentId !== null && descendantIds(groupId).has(newParentId)) {
      alert("Can't move a group inside one of its own subgroups — that would create a circular nesting.");
      return;
    }
    const next = groups.map(g => g.id === groupId ? { ...g, parentId: newParentId } : g);
    persistGroups(next);
  };

  // Recursive renderer for a group + its children. depth controls indentation.
  const renderGroup = (group, depth) => {
    const isExpanded = expandedGroups.has(group.id);
    const children = childrenOf.get(group.id) || [];
    const isEditing = editingGroupId === group.id;
    return (
      <div key={group.id} style={{ marginLeft: depth * 18, marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", background: depth === 0 ? "#f7f7f7" : "transparent", borderRadius: 6 }}>
          <button onClick={() => toggleExpanded(group.id)}
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, fontSize: 12, color: "#666", width: 16 }}
            aria-label={isExpanded ? "Collapse" : "Expand"}>
            {isExpanded ? "▼" : "▶"}
          </button>
          {isEditing ? (
            <>
              <input value={editingName} onChange={e => setEditingName(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") renameGroup(group.id, editingName); if (e.key === "Escape") { setEditingGroupId(null); setEditingName(""); } }}
                autoFocus
                style={{ flex: 1, padding: "4px 8px", border: `1px solid ${ORANGE}`, borderRadius: 4, fontSize: 14 }} />
              <button onClick={() => renameGroup(group.id, editingName)}
                style={{ background: ORANGE, color: "white", border: "none", borderRadius: 4, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>Save</button>
              <button onClick={() => { setEditingGroupId(null); setEditingName(""); }}
                style={{ background: "transparent", border: "1px solid #ccc", borderRadius: 4, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
            </>
          ) : (
            <>
              <span style={{ fontSize: 14, fontWeight: depth === 0 ? 600 : 400, color: NAVY, flex: 1 }}
                onClick={() => { setEditingGroupId(group.id); setEditingName(group.name); }}
                title="Click to rename">
                {group.name}
              </span>
              <span style={{ fontSize: 11, color: "#999" }}>
                {(group.tags || []).length} tag{(group.tags || []).length === 1 ? "" : "s"}
                {children.length > 0 && `, ${children.length} subgroup${children.length === 1 ? "" : "s"}`}
              </span>
              <button onClick={() => setPickingForGroup(group.id)}
                title="Add tags or groups to this group"
                style={{ background: "transparent", border: "1px solid #ccc", borderRadius: 4, padding: "2px 8px", fontSize: 11, cursor: "pointer", color: "#666" }}>+ Add</button>
              <button onClick={() => setDeletingGroup(group)}
                title="Delete group"
                style={{ background: "transparent", border: "none", cursor: "pointer", padding: 2, fontSize: 14, color: "#c83803" }}>🗑</button>
            </>
          )}
        </div>
        {isExpanded && (
          <div style={{ marginLeft: 22, marginTop: 4 }}>
            {(group.tags || []).length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                {(group.tags || []).sort().map(tag => (
                  <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", background: "#eee", borderRadius: 12, fontSize: 12, color: NAVY }}>
                    {tag}
                    <button onClick={() => removeTagFromGroup(group.id, tag)}
                      title="Remove from group"
                      style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, fontSize: 14, color: "#888", lineHeight: 1 }}>×</button>
                  </span>
                ))}
              </div>
            )}
            {children.map(c => renderGroup(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const rootGroups = childrenOf.get("__root__") || [];

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 10px", color: NAVY, fontFamily: "-apple-system,sans-serif" }}>Tag Groups</h2>
      <p style={{ fontSize: 13, color: "#666", margin: "0 0 20px", lineHeight: 1.5 }}>
        Organize your tags into a hierarchy (e.g., LLC → Building → Unit). When you filter entries
        by a group, you'll see entries tagged with any of the tags inside it. Each tag can belong
        to one group.
      </p>

      {/* Top-level "new group" entry */}
      {creatingUnder === null ? (
        <div style={{ marginBottom: 16 }}>
          <button onClick={() => setCreatingUnder("__root__")}
            style={{ background: "transparent", border: `1px dashed ${ORANGE}`, color: ORANGE, borderRadius: 6, padding: "8px 14px", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>
            + New top-level group
          </button>
        </div>
      ) : creatingUnder === "__root__" ? (
        <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
          <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { setCreatingUnder(null); addGroup(); } if (e.key === "Escape") { setNewGroupName(""); setCreatingUnder(null); } }}
            placeholder="Group name (e.g., Potrero Ventures LLC)"
            autoFocus
            style={{ flex: 1, padding: "8px 12px", border: `1px solid ${ORANGE}`, borderRadius: 6, fontSize: 14 }} />
          <button onClick={() => { setCreatingUnder(null); addGroup(); }}
            style={{ background: ORANGE, color: "white", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>Add</button>
          <button onClick={() => { setNewGroupName(""); setCreatingUnder(null); }}
            style={{ background: "transparent", border: "1px solid #ccc", borderRadius: 6, padding: "8px 16px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
        </div>
      ) : null}

      {/* Group tree */}
      {rootGroups.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#999", fontSize: 13, background: "#f7f7f7", borderRadius: 6, marginBottom: 20 }}>
          No groups yet. Create one above to start organizing your tags.
        </div>
      ) : (
        <div style={{ marginBottom: 24 }}>
          {rootGroups.map(g => renderGroup(g, 0))}
        </div>
      )}

      {/* Unassigned tags section */}
      {unassignedTags.length > 0 && (
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 8px", color: NAVY, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Unassigned Tags ({unassignedTags.length})
          </h3>
          <p style={{ fontSize: 12, color: "#888", margin: "0 0 10px" }}>
            Tags not in any group. They'll still work for filtering individually.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {unassignedTags.map(tag => (
              <span key={tag} style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", background: "#f0f0f0", borderRadius: 12, fontSize: 12, color: "#555" }}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Add picker modal — open when pickingForGroup is set. Shows both
          eligible groups (ones not already nested here or descendants of
          here) AND tags. Click either to add it under this group. */}
      {pickingForGroup && (() => {
        const targetGroup = groups.find(g => g.id === pickingForGroup);
        if (!targetGroup) return null;
        // Eligible groups: not the target itself, not already a direct child
        // of the target, and not a descendant of the target (would cycle).
        const targetDescendants = descendantIds(targetGroup.id);
        const eligibleGroups = groups.filter(g =>
          g.id !== targetGroup.id
          && g.parentId !== targetGroup.id
          && !targetDescendants.has(g.id)
        ).sort((a, b) => a.name.localeCompare(b.name));
        // Eligible tags: not already in this group.
        const eligibleTags = allTags.filter(t => !(targetGroup.tags || []).includes(t));

        return (
          <div onClick={() => setPickingForGroup(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: "white", borderRadius: 8, padding: 20, maxWidth: 480, width: "90%", maxHeight: "70vh", overflowY: "auto" }}>
              <h3 style={{ margin: "0 0 12px", fontSize: 16, color: NAVY }}>
                Add to "{targetGroup.name}"
              </h3>
              <p style={{ fontSize: 12, color: "#666", margin: "0 0 14px" }}>
                Click to add. Items already in another group will be moved here.
              </p>

              {/* Groups section */}
              {eligibleGroups.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                    Groups
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {eligibleGroups.map(g => {
                      const currentParent = g.parentId ? groups.find(p => p.id === g.parentId) : null;
                      return (
                        <button key={g.id}
                          onClick={() => { moveGroup(g.id, targetGroup.id); }}
                          title={currentParent ? `Currently under: ${currentParent.name}. Click to move here.` : "Currently top-level. Click to move here."}
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", background: currentParent ? "#fff3e0" : "#e3f2fd", border: currentParent ? `1px solid ${ORANGE}` : "1px solid #90caf9", borderRadius: 12, fontSize: 12, color: NAVY, cursor: "pointer" }}>
                          🗂️ {g.name}
                          {currentParent && <span style={{ fontSize: 10, color: ORANGE }}>(in {currentParent.name})</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tags section */}
              {eligibleTags.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                    Tags
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {eligibleTags.map(tag => {
                      const otherGroupId = tagToGroup.get(tag);
                      const otherGroup = otherGroupId && groups.find(g => g.id === otherGroupId);
                      return (
                        <button key={tag}
                          onClick={() => addTagToGroup(targetGroup.id, tag)}
                          title={otherGroup ? `Currently in: ${otherGroup.name}. Click to move here.` : "Click to add"}
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", background: otherGroup ? "#fff3e0" : "#f7f7f7", border: otherGroup ? `1px solid ${ORANGE}` : "1px solid #ddd", borderRadius: 12, fontSize: 12, color: NAVY, cursor: "pointer" }}>
                          {tag}
                          {otherGroup && <span style={{ fontSize: 10, color: ORANGE }}>(in {otherGroup.name})</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {eligibleGroups.length === 0 && eligibleTags.length === 0 && (
                <div style={{ color: "#999", fontSize: 13, padding: "20px 0", textAlign: "center" }}>
                  Nothing left to add to this group.
                </div>
              )}

              {/* Inline "create new group" — handy when picker is open and
                  you realize you need a fresh group. */}
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #eee" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                  Or create a new group here
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        const name = newGroupName.trim();
                        if (!name) return;
                        const next = [...groups, { id: newGroupId(), name, parentId: targetGroup.id, tags: [] }];
                        persistGroups(next);
                        setNewGroupName("");
                      }
                    }}
                    placeholder="New group name"
                    style={{ flex: 1, padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }} />
                  <button onClick={() => {
                      const name = newGroupName.trim();
                      if (!name) return;
                      const next = [...groups, { id: newGroupId(), name, parentId: targetGroup.id, tags: [] }];
                      persistGroups(next);
                      setNewGroupName("");
                    }}
                    style={{ background: ORANGE, color: "white", border: "none", borderRadius: 4, padding: "6px 14px", fontSize: 12, cursor: "pointer" }}>Create</button>
                </div>
              </div>

              <div style={{ marginTop: 16, textAlign: "right" }}>
                <button onClick={() => setPickingForGroup(null)}
                  style={{ background: ORANGE, color: "white", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete confirmation modal */}
      {deletingGroup && (
        <div onClick={() => setDeletingGroup(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "white", borderRadius: 8, padding: 20, maxWidth: 400, width: "90%" }}>
            <h3 style={{ margin: "0 0 10px", fontSize: 16, color: NAVY }}>Delete this group?</h3>
            <p style={{ fontSize: 13, color: "#666", margin: "0 0 16px" }}>
              "{deletingGroup.name}" will be deleted. Any tags inside it become unassigned, and any
              subgroups will move to the top level. Your entries and tags themselves are not affected.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setDeletingGroup(null)}
                style={{ background: "transparent", border: "1px solid #ccc", borderRadius: 6, padding: "8px 16px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => deleteGroup(deletingGroup.id)}
                style={{ background: "#c83803", color: "white", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── FIELDS MANAGEMENT (settings tab) ─────────────────────────────────────────

function FieldsManagement({ diary, entries, setDiary }) {
  const hidden = new Set((diary?.hiddenBuiltins || []).map(k => k.toLowerCase()));
  const enabled = new Set((diary?.enabledLibrary || []));
  // customFields is an array of string names. Normalize defensively: older
  // imports mistakenly wrote { name, multi } objects, and those must coerce
  // to their name here so add/remove (which compare by string) work and so
  // any persist() rewrites the field list back to clean strings.
  const customs = (diary?.customFields || [])
    .map(c => (typeof c === "string" ? c : (c && c.name) || ""))
    .filter(Boolean);

  const [savingKey, setSavingKey] = useState(null);
  const [newCustomDraft, setNewCustomDraft] = useState("");
  const [adding, setAdding] = useState(false);
  // Custom field pending confirmation for removal. null = no modal open.
  const [deletingField, setDeletingField] = useState(null);

  // Count entries that have captured data for a given custom field name.
  // Data lives on entries at entry.structured.custom[name] regardless of
  // whether the field is currently declared on the diary.
  const countForCustom = (name) => {
    const k = name.toLowerCase();
    return (entries || []).filter(e => {
      const c = e?.structured?.custom;
      if (!c || typeof c !== "object") return false;
      const v = c[k];
      if (v == null) return false;
      if (Array.isArray(v)) return v.length > 0;
      return String(v).trim().length > 0;
    }).length;
  };

  // Persist a partial diary update. Optimistic — local setDiary first, then write.
  const persist = async (update) => {
    setDiary(prev => ({ ...prev, ...update }));
    try {
      await updateDoc(doc(db, "diaries", diary.id), update);
    } catch (e) {
      console.error("Failed to update diary fields config:", e);
    }
  };

  const toggleBuiltin = async (key) => {
    setSavingKey(key);
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key); else next.add(key);
    await persist({ hiddenBuiltins: [...next] });
    setSavingKey(null);
  };

  const toggleLibrary = async (key) => {
    setSavingKey(`lib:${key}`);
    const next = new Set(enabled);
    if (next.has(key)) next.delete(key); else next.add(key);
    await persist({ enabledLibrary: [...next] });
    setSavingKey(null);
  };

  const addCustom = async () => {
    const clean = newCustomDraft.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "");
    if (!clean) { setAdding(false); setNewCustomDraft(""); return; }
    if (customs.includes(clean)) { setAdding(false); setNewCustomDraft(""); return; }
    setSavingKey(`cust-add`);
    await persist({ customFields: [...customs, clean] });
    setSavingKey(null);
    setAdding(false);
    setNewCustomDraft("");
  };

  const removeCustom = async (name) => {
    setSavingKey(`cust:${name}`);
    await persist({ customFields: customs.filter(c => c !== name) });
    setSavingKey(null);
    setDeletingField(null);
  };

  // Built-ins (keep in sync with BUILT_IN_FIELDS in StructuredFieldsRow)
  const BUILTINS = [
    { key: "amount", icon: "💰", label: "Amount" },
    { key: "location", icon: "📍", label: "Location" },
    { key: "city", icon: "🏙️", label: "City" },
    { key: "address", icon: "📮", label: "Address" },
    { key: "people", icon: "👥", label: "People" },
    { key: "timeMinutes", icon: "⏱️", label: "Time" },
    { key: "distanceMiles", icon: "🏃", label: "Distance" },
    { key: "calories", icon: "🔥", label: "Calories" },
    { key: "mood", icon: "😊", label: "Mood" },
    { key: "rating", icon: "⭐", label: "Rating" },
    { key: "score", icon: "🏆", label: "Score" },
  ];

  return (
    <div style={{ fontFamily: "-apple-system,sans-serif" }}>
      <div style={{ fontSize: "13px", color: "#555", marginBottom: "20px", lineHeight: 1.6 }}>
        Customize which structured fields are available when adding entries to <b>{diary.name}</b>. Hide what's not relevant. Enable extra fields from the library. Or add your own.
      </div>

      {/* Structured data table — when to show it on entries. Configured
          per-diary so the same diary can have a consistent presentation
          (mom's medical diary always-on, casual journal off, etc.). */}
      <div style={{ marginBottom: "24px", padding: "14px 16px", border: "1px solid #ede9e0", borderRadius: "10px", background: "#fafaf7" }}>
        <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#555", marginBottom: "6px" }}>Data table on entries</div>
        <div style={{ fontSize: "12px", color: "#888", lineHeight: 1.5, marginBottom: "10px" }}>
          When entries have data fields like distance, mood, or score, show them in a small table below the body.
        </div>
        {[
          { v: "smart", label: "Smart", desc: "Show table only when entries have 3+ data fields" },
          { v: "always", label: "Always", desc: "Always show the table when any data is captured" },
          { v: "never", label: "Never", desc: "Hide the table on all entries" },
        ].map(opt => {
          const current = diary.tableMode || "smart";
          const checked = current === opt.v;
          return (
            <label key={opt.v} style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "6px 4px", cursor: "pointer", borderRadius: "6px", background: checked ? "rgba(200,56,3,0.05)" : "transparent" }}>
              <input type="radio" name="tableMode" checked={checked}
                onChange={() => persist({ tableMode: opt.v })}
                style={{ marginTop: "3px", flexShrink: 0, accentColor: ORANGE }} />
              <div>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#333" }}>{opt.label}</div>
                <div style={{ fontSize: "12px", color: "#888", lineHeight: 1.4 }}>{opt.desc}</div>
              </div>
            </label>
          );
        })}
      </div>

      {/* Built-in fields */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#888", marginBottom: "10px" }}>Built-in fields</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {BUILTINS.map(b => {
            const isHidden = hidden.has(b.key.toLowerCase());
            const isSaving = savingKey === b.key;
            return (
              <button key={b.key} onClick={() => toggleBuiltin(b.key.toLowerCase())} disabled={isSaving}
                style={{
                  background: isHidden ? "transparent" : "#f0f0f0",
                  color: isHidden ? "#aaa" : "#333",
                  border: `1px ${isHidden ? "dashed" : "solid"} ${isHidden ? "rgba(0,0,0,0.15)" : "#ddd"}`,
                  borderRadius: "16px",
                  padding: "5px 12px",
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor: isSaving ? "wait" : "pointer",
                  fontFamily: "-apple-system,sans-serif",
                  textDecoration: isHidden ? "line-through" : "none",
                  opacity: isSaving ? 0.5 : 1,
                }}>
                {b.icon} {b.label}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: "11px", color: "#aaa", marginTop: "6px" }}>Tap to hide/show. Hidden fields don't appear in the composer.</div>
      </div>

      {/* Library fields */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#888", marginBottom: "10px" }}>Library</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {Object.values(FIELD_LIBRARY).map(def => {
            const isOn = enabled.has(def.key);
            const isSaving = savingKey === `lib:${def.key}`;
            return (
              <button key={def.key} onClick={() => toggleLibrary(def.key)} disabled={isSaving}
                title={`Shorthand: ${def.parsePrefixes.map(p => `${p}:`).join(" / ")}`}
                style={{
                  background: isOn ? "#f0f0f0" : "transparent",
                  color: isOn ? "#333" : "#888",
                  border: `1px ${isOn ? "solid" : "dashed"} ${isOn ? "#ddd" : "rgba(0,0,0,0.15)"}`,
                  borderRadius: "16px",
                  padding: "5px 12px",
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor: isSaving ? "wait" : "pointer",
                  fontFamily: "-apple-system,sans-serif",
                  opacity: isSaving ? 0.5 : 1,
                }}>
                {def.icon} {def.label}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: "11px", color: "#aaa", marginTop: "6px" }}>Tap to enable. In the body type <code>pay:</code>, <code>cat:</code>, <code>body:</code>, <code>med:</code>, <code>sub:</code>, <code>food:</code>, <code>proj:</code>, or <code>sx:</code> followed by a value (hover any field for its shorthand options).</div>
      </div>

      {/* Custom fields */}
      <div>
        <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#888", marginBottom: "10px" }}>Your custom fields</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
          {customs.length === 0 && !adding && (
            <div style={{ fontSize: "12px", color: "#aaa", fontStyle: "italic" }}>No custom fields yet. Add one to capture data unique to this diary.</div>
          )}
          {customs.map(name => {
            const isSaving = savingKey === `cust:${name}`;
            const count = countForCustom(name);
            return (
              <div key={name}
                style={{ background: "#f0f0f0", color: "#333", border: "1px solid #ddd", borderRadius: "16px", padding: "5px 6px 5px 12px", fontSize: "12px", fontWeight: "600", fontFamily: "-apple-system,sans-serif", display: "inline-flex", alignItems: "center", gap: "8px", opacity: isSaving ? 0.5 : 1 }}>
                <span>🏷️ {name}</span>
                {count > 0 && (
                  <span style={{ fontSize: "11px", color: "#888", fontWeight: 500 }}>
                    {count} {count === 1 ? "entry" : "entries"}
                  </span>
                )}
                <button onClick={() => setDeletingField(name)} disabled={isSaving}
                  title={`Remove "${name}" field`}
                  aria-label={`Remove ${name} field`}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "22px", height: "22px", borderRadius: "50%", background: "transparent", color: "#c83803", cursor: isSaving ? "wait" : "pointer", border: "none", padding: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 5h10M6 5V3h4v2M5 5l1 9h4l1-9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
              </div>
            );
          })}
          {adding ? (
            <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff8f0", border: `1px solid ${ORANGE}`, borderRadius: "16px", padding: "2px 4px 2px 8px" }}>
              <span style={{ fontSize: "12px", color: "#666" }}>🏷️</span>
              <input type="text" autoFocus value={newCustomDraft} onChange={e => setNewCustomDraft(e.target.value)}
                placeholder="field name (e.g., shot)"
                onKeyDown={e => {
                  if (e.key === "Enter") { e.preventDefault(); addCustom(); }
                  else if (e.key === "Escape") { setAdding(false); setNewCustomDraft(""); }
                }}
                onBlur={() => addCustom()}
                style={{ width: "140px", border: "none", outline: "none", fontSize: "12px", background: "transparent", fontFamily: "-apple-system,sans-serif" }} />
            </div>
          ) : (
            <button onClick={() => { setAdding(true); setNewCustomDraft(""); }}
              style={{ background: "transparent", color: "#888", border: "1px dashed rgba(0,0,0,0.15)", borderRadius: "16px", padding: "5px 12px", fontSize: "12px", fontWeight: "600", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
              + Add custom field
            </button>
          )}
        </div>
        <div style={{ fontSize: "11px", color: "#aaa", marginTop: "6px" }}>Type any field name. Use it in entries with shorthand like <code>fieldname: value</code> in the body.</div>
      </div>

      {/* Delete confirmation — custom field removal */}
      {deletingField && (() => {
        const count = countForCustom(deletingField);
        const isSaving = savingKey === `cust:${deletingField}`;
        return (
          <div onClick={() => !isSaving && setDeletingField(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: "white", borderRadius: "14px", padding: "24px", maxWidth: "420px", width: "100%", boxShadow: "0 10px 40px rgba(0,0,0,0.25)", fontFamily: "-apple-system,sans-serif" }}>
              <div style={{ fontSize: "17px", fontWeight: "700", color: "#1a1a1a", marginBottom: "10px" }}>Remove "{deletingField}" field?</div>
              <p style={{ fontSize: "13px", color: "#666", lineHeight: 1.5, margin: "0 0 20px" }}>
                {count === 0
                  ? "No entries use this field. It'll just be removed from your field list."
                  : `${count} ${count === 1 ? "entry has" : "entries have"} data for this field. Their data stays put — the field just won't appear as a suggestion in the composer. You can re-add it later and the existing data will still be there.`}
              </p>
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button onClick={() => setDeletingField(null)} disabled={isSaving}
                  style={{ background: "transparent", color: "#555", border: "1px solid #ddd", borderRadius: "6px", padding: "8px 16px", fontSize: "14px", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>Cancel</button>
                <button onClick={() => removeCustom(deletingField)} disabled={isSaving}
                  style={{ background: "#c83803", color: "white", border: "none", borderRadius: "6px", padding: "8px 16px", fontSize: "14px", fontWeight: 700, cursor: isSaving ? "wait" : "pointer", fontFamily: "-apple-system,sans-serif" }}>
                  {isSaving ? "Removing..." : "Remove"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── TAG MANAGEMENT (settings tab) ────────────────────────────────────────────

function TagManagement({ diary, entries, canEdit, setDiary }) {
  // Every tag that appears on any entry
  const allUsedTags = [...new Set(entries.flatMap(e => e.tags || []))].filter(t => !isFollowupTag(t));

  // Tag rows ordered by: (1) diary.tagOrder, (2) other used tags alphabetical, (3) manually-added tags that aren't on any entry yet
  const tagRows = (() => {
    const order = diary?.tagOrder || [];
    const orderedSet = new Set(order);
    const ordered = order.filter(t => allUsedTags.includes(t) || (diary?.customTags || []).includes(t));
    const unordered = allUsedTags.filter(t => !orderedSet.has(t)).sort();
    const customUnused = (diary?.customTags || []).filter(t => !orderedSet.has(t) && !allUsedTags.includes(t));
    return [...ordered, ...unordered, ...customUnused];
  })();

  const hidden = new Set((diary?.hiddenTags || []).map(t => t.toLowerCase()));
  const countForTag = tag => entries.filter(e => (e.tags || []).includes(tag)).length;

  const [renamingTag, setRenamingTag] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [deletingTag, setDeletingTag] = useState(null);
  const [newTagInput, setNewTagInput] = useState("");
  const [addingTag, setAddingTag] = useState(false);
  const [dragTag, setDragTag] = useState(null);
  const [working, setWorking] = useState(false);
  const [msg, setMsg] = useState(null);

  // Drag-to-reorder handlers
  const scrollTimerRef = useRef(null);
  const findScrollParent = el => {
    let node = el?.parentElement;
    while (node) {
      const style = window.getComputedStyle(node);
      if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) return node;
      node = node.parentElement;
    }
    return null;
  };
  const stopAutoScroll = () => {
    if (scrollTimerRef.current) {
      clearInterval(scrollTimerRef.current);
      scrollTimerRef.current = null;
    }
  };
  const handleDragStart = (e, tag) => {
    setDragTag(tag);
  };
  const handleDragOver = e => {
    e.preventDefault();
    // Find the scrollable modal container and auto-scroll when dragging near top/bottom
    const container = findScrollParent(e.currentTarget);
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const edge = 60; // px
    const topDist = e.clientY - rect.top;
    const bottomDist = rect.bottom - e.clientY;
    let direction = 0;
    if (topDist < edge) direction = -1;
    else if (bottomDist < edge) direction = 1;
    if (direction === 0) { stopAutoScroll(); return; }
    if (!scrollTimerRef.current) {
      scrollTimerRef.current = setInterval(() => {
        container.scrollTop += direction * 12;
      }, 16);
    }
  };
  const handleDrop = async (e, targetTag) => {
    e.preventDefault();
    stopAutoScroll();
    if (!dragTag || dragTag === targetTag || !canEdit) { setDragTag(null); return; }
    const current = tagRows.slice();
    const fromIdx = current.indexOf(dragTag);
    const toIdx = current.indexOf(targetTag);
    if (fromIdx < 0 || toIdx < 0) { setDragTag(null); return; }
    current.splice(fromIdx, 1);
    current.splice(toIdx, 0, dragTag);
    setDragTag(null);
    try {
      await updateDoc(doc(db, "diaries", diary.id), { tagOrder: current });
      setDiary(prev => ({ ...prev, tagOrder: current }));
    } catch (e) { console.error(e); }
  };
  const handleDragEnd = () => {
    stopAutoScroll();
    setDragTag(null);
  };
  // Clean up scroll timer on unmount
  useEffect(() => () => stopAutoScroll(), []);

  // Toggle hide-from-lens
  const handleToggleHidden = async tag => {
    if (!canEdit) return;
    const isHidden = hidden.has(tag);
    try {
      await updateDoc(doc(db, "diaries", diary.id), {
        hiddenTags: isHidden ? arrayRemove(tag) : arrayUnion(tag),
      });
      setDiary(prev => ({
        ...prev,
        hiddenTags: isHidden
          ? (prev.hiddenTags || []).filter(t => t !== tag)
          : [...new Set([...(prev.hiddenTags || []), tag])],
      }));
    } catch (e) { console.error(e); }
  };

  // Rename (merges if target exists)
  const handleRename = async (oldTag) => {
    const newTag = renameDraft.trim().toLowerCase();
    if (!newTag || newTag === oldTag) { setRenamingTag(null); return; }
    setWorking(true);
    try {
      const snap = await getDocs(query(collection(db, "entries"), where("diaryId", "==", diary.id)));
      const toUpdate = snap.docs.filter(d => (d.data().tags || []).includes(oldTag));
      // Firestore batch limit is 500 — we chunk if needed
      for (let i = 0; i < toUpdate.length; i += 400) {
        const batch = writeBatch(db);
        toUpdate.slice(i, i + 400).forEach(d => {
          const tags = (d.data().tags || []).filter(t => t !== oldTag);
          if (!tags.includes(newTag)) tags.push(newTag);
          batch.update(d.ref, { tags });
        });
        await batch.commit();
      }
      // Update diary-level fields (tagOrder, hiddenTags, customTags)
      const updates = {};
      const newOrder = (diary.tagOrder || []).map(t => t === oldTag ? newTag : t).filter((t, i, a) => a.indexOf(t) === i);
      updates.tagOrder = newOrder;
      if ((diary.hiddenTags || []).includes(oldTag)) {
        const newHidden = (diary.hiddenTags || []).filter(t => t !== oldTag);
        if (hidden.has(newTag)) { /* already hidden, fine */ } else { newHidden.push(newTag); }
        updates.hiddenTags = newHidden;
      }
      if ((diary.customTags || []).includes(oldTag)) {
        const newCustom = (diary.customTags || []).filter(t => t !== oldTag);
        if (!newCustom.includes(newTag)) newCustom.push(newTag);
        updates.customTags = newCustom;
      }
      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(db, "diaries", diary.id), updates);
        setDiary(prev => ({ ...prev, ...updates }));
      }
      setMsg({ type: "ok", text: `Renamed "${oldTag}" → "${newTag}" on ${toUpdate.length} entr${toUpdate.length === 1 ? "y" : "ies"}.` });
    } catch (e) {
      console.error(e);
      setMsg({ type: "error", text: "Couldn't rename. Please try again." });
    }
    setRenamingTag(null);
    setRenameDraft("");
    setWorking(false);
  };

  // Delete — removes tag from every entry
  const handleConfirmDelete = async (tag) => {
    setWorking(true);
    try {
      const snap = await getDocs(query(collection(db, "entries"), where("diaryId", "==", diary.id)));
      const toUpdate = snap.docs.filter(d => (d.data().tags || []).includes(tag));
      for (let i = 0; i < toUpdate.length; i += 400) {
        const batch = writeBatch(db);
        toUpdate.slice(i, i + 400).forEach(d => {
          const tags = (d.data().tags || []).filter(t => t !== tag);
          batch.update(d.ref, { tags });
        });
        await batch.commit();
      }
      // Clean diary-level fields
      const updates = {
        tagOrder: (diary.tagOrder || []).filter(t => t !== tag),
        hiddenTags: (diary.hiddenTags || []).filter(t => t !== tag),
        customTags: (diary.customTags || []).filter(t => t !== tag),
      };
      await updateDoc(doc(db, "diaries", diary.id), updates);
      setDiary(prev => ({ ...prev, ...updates }));
      setMsg({ type: "ok", text: `Removed "${tag}" from ${toUpdate.length} entr${toUpdate.length === 1 ? "y" : "ies"}.` });
    } catch (e) {
      console.error(e);
      setMsg({ type: "error", text: "Couldn't delete. Please try again." });
    }
    setDeletingTag(null);
    setWorking(false);
  };

  // Add one or more new tags (comma-separated) — just registers them so they appear in the picker
  const handleAddNewTag = async () => {
    const raw = newTagInput.trim();
    if (!raw) return;
    // Accept comma-separated list. Also accepts whitespace-separated as a fallback for single entries.
    const parts = raw.split(",").map(t => t.trim().toLowerCase()).filter(Boolean);
    if (parts.length === 0) return;

    const existing = new Set([...allUsedTags, ...(diary.customTags || [])]);
    const toAdd = [];
    const skipped = [];
    for (const t of parts) {
      if (existing.has(t) || toAdd.includes(t)) skipped.push(t);
      else toAdd.push(t);
    }

    if (toAdd.length === 0) {
      setMsg({ type: "error", text: parts.length === 1 ? "That tag already exists." : "All of those tags already exist." });
      return;
    }

    setAddingTag(true);
    try {
      await updateDoc(doc(db, "diaries", diary.id), {
        customTags: arrayUnion(...toAdd),
      });
      setDiary(prev => ({
        ...prev,
        customTags: [...new Set([...(prev.customTags || []), ...toAdd])],
      }));
      setNewTagInput("");
      const addedMsg = toAdd.length === 1
        ? `Added "${toAdd[0]}".`
        : `Added ${toAdd.length} tags: ${toAdd.map(t => `"${t}"`).join(", ")}.`;
      const skippedMsg = skipped.length > 0
        ? ` Skipped ${skipped.length} that already existed.`
        : "";
      setMsg({ type: "ok", text: addedMsg + skippedMsg });
    } catch (e) {
      console.error(e);
      setMsg({ type: "error", text: "Couldn't add tag(s)." });
    }
    setAddingTag(false);
  };

  if (tagRows.length === 0 && !canEdit) {
    return <p style={{ color: "#aaa", fontSize: "14px", fontFamily: "-apple-system,sans-serif" }}>No tags yet.</p>;
  }

  return (
    <div>
      <h2 style={{ fontSize: "20px", fontWeight: "700", margin: "0 0 8px", color: NAVY, fontFamily: "-apple-system,sans-serif" }}>Tags</h2>
      <p style={{ fontSize: "13px", color: "#999", margin: "0 0 16px", lineHeight: 1.6, fontFamily: "-apple-system,sans-serif" }}>
        {canEdit
          ? "Drag to reorder. Toggle the eye to hide a tag from the lens bar. Rename or delete with the icons on the right."
          : "Tags used in this diary."}
      </p>

      {msg && (
        <div style={{ marginBottom: "12px", padding: "8px 12px", borderRadius: "6px", fontSize: "13px", fontFamily: "-apple-system,sans-serif", background: msg.type === "ok" ? "rgba(42,122,58,0.08)" : "rgba(200,56,3,0.08)", color: msg.type === "ok" ? "#2a7a3a" : "#c83803", border: `1px solid ${msg.type === "ok" ? "rgba(42,122,58,0.2)" : "rgba(200,56,3,0.2)"}` }}>
          {msg.text}
        </div>
      )}

      {tagRows.length === 0 && (
        <p style={{ color: "#aaa", fontSize: "14px", fontStyle: "italic", fontFamily: "-apple-system,sans-serif", marginBottom: "20px" }}>No tags yet. Add one below.</p>
      )}

      {tagRows.map(tag => {
        const m = getTagMeta(tag);
        const count = countForTag(tag);
        const isHidden = hidden.has(tag);
        const isRenaming = renamingTag === tag;
        const isDragging = dragTag === tag;
        return (
          <div key={tag}
            draggable={canEdit && !isRenaming}
            onDragStart={e => canEdit && handleDragStart(e, tag)}
            onDragOver={handleDragOver}
            onDrop={e => handleDrop(e, tag)}
            onDragEnd={handleDragEnd}
            style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "8px 0",
              borderBottom: `1px solid ${isDragging ? ORANGE : "#f5f3ef"}`,
              background: isDragging ? "rgba(200,56,3,0.04)" : "transparent",
              opacity: isHidden ? 0.55 : 1,
              cursor: canEdit && !isRenaming ? "grab" : "default",
            }}>
            {/* Drag handle */}
            {canEdit && !isRenaming && (
              <span style={{ color: "#ccc", fontSize: "14px", cursor: "grab", flexShrink: 0, userSelect: "none" }}>⋮⋮</span>
            )}

            {/* Tag pill OR rename input */}
            {isRenaming ? (
              <div style={{ flex: 1, display: "flex", gap: "6px", alignItems: "center" }}>
                <input type="text" value={renameDraft} autoFocus
                  onChange={e => setRenameDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleRename(tag); if (e.key === "Escape") { setRenamingTag(null); setRenameDraft(""); } }}
                  style={{ flex: 1, border: "1px solid #ddd", borderRadius: "6px", padding: "6px 10px", fontSize: "13px", outline: "none", fontFamily: "-apple-system,sans-serif" }} />
                <button onClick={() => handleRename(tag)} disabled={working}
                  style={{ background: ORANGE, color: "white", border: "none", borderRadius: "6px", padding: "6px 12px", fontSize: "12px", fontWeight: 700, cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>Save</button>
                <button onClick={() => { setRenamingTag(null); setRenameDraft(""); }}
                  style={{ background: "transparent", color: "#888", border: "none", fontSize: "16px", cursor: "pointer", padding: 0 }}>×</button>
              </div>
            ) : (
              <>
                <span style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}`, borderRadius: "20px", padding: "3px 10px", fontSize: "13px", fontWeight: "600", fontFamily: "-apple-system,sans-serif", flexShrink: 0 }}>
                  {m.emoji} {tag}
                </span>
                <span style={{ fontSize: "12px", color: "#aaa", fontFamily: "-apple-system,sans-serif", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {count} {count === 1 ? "entry" : "entries"}
                </span>

                {/* Action icons — owner/editor only */}
                {canEdit && (
                  <>
                    {/* Hide / show from lens */}
                    <button onClick={() => handleToggleHidden(tag)}
                      title={isHidden ? "Show in lens bar" : "Hide from lens bar"}
                      style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px", color: isHidden ? "#aaa" : "#666", display: "flex", alignItems: "center" }}>
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        {isHidden ? (
                          /* eye closed / hidden */
                          <>
                            <path d="M2 9s2.5-4.5 7-4.5c1.2 0 2.3.3 3.2.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none"/>
                            <path d="M16 9s-2.5 4.5-7 4.5c-1.2 0-2.3-.3-3.2-.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none"/>
                            <path d="M3 3l12 12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                          </>
                        ) : (
                          /* eye open */
                          <>
                            <path d="M2 9s2.5-4.5 7-4.5 7 4.5 7 4.5-2.5 4.5-7 4.5S2 9 2 9z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            <circle cx="9" cy="9" r="2" stroke="currentColor" strokeWidth="1.3" fill="none"/>
                          </>
                        )}
                      </svg>
                    </button>
                    {/* Rename */}
                    <button onClick={() => { setRenamingTag(tag); setRenameDraft(tag); }}
                      title="Rename"
                      style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px", color: "#666", display: "flex", alignItems: "center" }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M11 2l3 3-8 8H3v-3l8-8z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                    {/* Delete */}
                    <button onClick={() => setDeletingTag(tag)}
                      title="Delete tag"
                      style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px", color: "#c83803", display: "flex", alignItems: "center" }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 5h10M6 5V3h4v2M5 5l1 9h4l1-9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* Add new tag */}
      {canEdit && (
        <div style={{ marginTop: "16px", padding: "14px", background: "#fafaf7", borderRadius: "8px", border: "1px solid #f0ede8" }}>
          <div style={{ fontSize: "11px", fontWeight: "700", color: "#555", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px", fontFamily: "-apple-system,sans-serif" }}>Add a new tag</div>
          <div style={{ display: "flex", gap: "6px" }}>
            <input type="text" value={newTagInput}
              onChange={e => { setNewTagInput(e.target.value); if (msg) setMsg(null); }}
              onKeyDown={e => { if (e.key === "Enter" && !addingTag) handleAddNewTag(); }}
              placeholder="e.g. lease, roof, vacation (comma-separated for multiple)"
              style={{ flex: 1, border: "1px solid #ddd", borderRadius: "6px", padding: "7px 10px", fontSize: "13px", outline: "none", fontFamily: "-apple-system,sans-serif", boxSizing: "border-box" }} />
            <button onClick={handleAddNewTag} disabled={addingTag || !newTagInput.trim()}
              style={{ background: (!addingTag && newTagInput.trim()) ? ORANGE : "#ddd", color: "white", border: "none", borderRadius: "6px", padding: "7px 16px", fontSize: "13px", fontWeight: 700, cursor: (!addingTag && newTagInput.trim()) ? "pointer" : "default", fontFamily: "-apple-system,sans-serif" }}>
              {addingTag ? "..." : "Add"}
            </button>
          </div>
          <p style={{ fontSize: "11px", color: "#aaa", margin: "6px 0 0", fontFamily: "-apple-system,sans-serif" }}>
            Separate multiple tags with commas. They become available in the tag picker when creating or editing entries.
          </p>
        </div>
      )}

      {/* Delete confirmation */}
      {deletingTag && (
        <div onClick={() => setDeletingTag(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "white", borderRadius: "14px", padding: "24px", maxWidth: "420px", width: "100%", boxShadow: "0 10px 40px rgba(0,0,0,0.25)", fontFamily: "-apple-system,sans-serif" }}>
            <div style={{ fontSize: "17px", fontWeight: "700", color: "#1a1a1a", marginBottom: "10px" }}>Remove "{deletingTag}"?</div>
            <p style={{ fontSize: "13px", color: "#666", lineHeight: 1.5, margin: "0 0 20px" }}>
              {(() => {
                const c = countForTag(deletingTag);
                if (c === 0) return `No entries use this tag. It'll just be removed from your tag list.`;
                return `This will remove the tag from ${c} ${c === 1 ? "entry" : "entries"}. The entries themselves won't be deleted. This can't be undone.`;
              })()}
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => setDeletingTag(null)} disabled={working}
                style={{ background: "transparent", color: "#555", border: "1px solid #ddd", borderRadius: "6px", padding: "8px 16px", fontSize: "14px", cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>Cancel</button>
              <button onClick={() => handleConfirmDelete(deletingTag)} disabled={working}
                style={{ background: "#c83803", color: "white", border: "none", borderRadius: "6px", padding: "8px 16px", fontSize: "14px", fontWeight: 700, cursor: "pointer", fontFamily: "-apple-system,sans-serif" }}>
                {working ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
